import React, { useState, useRef, useEffect, useMemo, useCallback, Suspense, lazy } from 'react';
import { useAuth } from '../contexts/AuthContext';
// 🔧 优化：懒加载对话框组件（按需加载，减少初始 bundle 大小）
const PlaybackDurationDialog = lazy(() => import('./PlaybackDurationDialog'));
const ABLoopCountDialog = lazy(() => import('./ABLoopCountDialog'));
const PlaybackModeSelectionDialog = lazy(() => import('./PlaybackModeSelectionDialog'));

declare global {
  interface Window {
    __checkplay_autoplay_flag?: boolean;
  }
}
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { 
  AppBar,
  Toolbar,
  Typography, 
  Button, 
  Container,
  Box, 
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tab,
  Tabs,
  Paper,
  Grid,
  CircularProgress,
  LinearProgress,
  Alert,
  Snackbar,
  Menu,
  Stack,
  Tooltip,
  Divider,
  Link,
  Slider,
  Switch,
  Popover,
  Modal,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Drawer,
  useMediaQuery as useMuiMediaQuery
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Google as GoogleIcon,
  Facebook as FacebookIcon,
  RestartAlt as RestartAltIcon,
  FastRewind as FastRewindIcon,
  FastForward as FastForwardIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  VolumeOff as VolumeOffIcon,
  VolumeUp as VolumeUpIcon,
  CameraAlt as CameraAltIcon,
  BurstMode as BurstModeIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Movie as MovieIcon,
  Image as ImageIcon,
  CropFree as CropFreeIcon,
  GridOn as GridOnIcon,
  AccessTime as AccessTimeIcon,
  Replay as ReplayIcon,
  SkipPrevious as SkipPreviousIcon,
  SkipNext as SkipNextIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  YouTube as YouTubeIcon,
  AttachFile as AttachFileIcon,
  Share as ShareIcon,
  Close as CloseIcon,
  Menu as MenuIcon,
  AccountCircle as AccountCircleIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  Help as HelpIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  MoreVert as MoreVertIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Camera as CameraIcon,
  FolderOpen as FolderOpenIcon,
  Subtitles as SubtitlesIcon,
  CloudUpload as CloudUploadIcon,
  QrCode as QrCodeIcon,
  Public as PublicIcon,
  Group as GroupIcon,
  Note as NoteIcon,
  History as HistoryIcon,
  CastConnected as CastConnectedIcon
} from '@mui/icons-material';
// 🔧 优化：懒加载大型第三方库（只在需要时加载）
const heic2anyPromise = import('heic2any').then(module => module.default);
const ImageManager = lazy(() => import('./ImageManager'));
const LightboxPromise = import('yet-another-react-lightbox');
const ZoomPromise = import('yet-another-react-lightbox/plugins/zoom');
// Lightbox 样式仍需同步加载（较小）
import 'yet-another-react-lightbox/styles.css';
import { auth } from '../firebase';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import GoogleDrivePlayer from './GoogleDrivePlayer';
import YouTubePlayer from './YouTubePlayer';
import { generateShareUrl } from '../services/shareService';
import OptimizedTooltip from './OptimizedTooltip';
import { generateShortUrl } from '../services/urlShortenerService';
import { decodeVBIDData } from '../services/shareService';
const VbidDialog = lazy(() => import('./VbidDialog'));
import { 
  MButtonState,
  NumberKeyState, 
  KeyToPlay, 
  ABPoints, 
  ABPoint,
  VideoMode,
  ReactPlayerInstance,
  VBID,
  ImageData
} from '../types';
import { ShareableVBID } from '../types/vbid';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { loginWithFacebook } from '../services/authService';
import { APP_CONFIG } from "../config/app.config";
import { downloadYouTubeCaptions } from '../services/captionService';
import VideoPlayer from './VideoPlayer';
import GoogleDriveIframe, { GoogleDriveIframeRef } from './GoogleDriveIframe';
import PlayerControls from './PlayerControls';
import ABPointPanel from './ABPointPanel';
import NumberKeyPanel from './NumberKeyPanel';
import SwipeableControlPanel from './SwipeableControlPanel';
// 🔧 优化：懒加载对话框组件（按需加载）
const NoteRecordsDialog = lazy(() => import('./NoteRecordsDialog'));
const VersionManagementDialog = lazy(() => import('./VersionManagementDialog'));
const CollaborationModeDialog = lazy(() => import('./CollaborationModeDialog'));
const WelcomeDialog = lazy(() => import('./WelcomeDialog'));
const EnhancedShareDialog = lazy(() => import('./EnhancedShareDialog'));
const AuthDialog = lazy(() => import('./AuthDialog'));
const EnhancedKnowledgeManagementDialog = lazy(() => import('./KnowledgeManagement').then(module => ({ default: module.EnhancedKnowledgeManagementDialog })));
const ScreenshotDialog = lazy(() => import('./ScreenshotDialog'));
const WatermarkDialog = lazy(() => import('./WatermarkDialog'));
const SRTDisplayDialog = lazy(() => import('./SRTDisplayDialog'));
const LanguageSelectionDialog = lazy(() => import('./LanguageSelectionDialog'));
const SRTGenerationDialog = lazy(() => import('./SRTGenerationDialog'));
const ViewpointManager = lazy(() => import('./ViewpointManager').then(module => ({ default: module.ViewpointManager })));
// ReactPlayer 保持同步导入（核心播放器功能）
import ReactPlayer from 'react-player';
import { formatTimecode } from '../utils/vbidUtils';
import { convertNumberKeysToVBIDs } from '../utils/vbidUtils';
import { db } from '../firebase';
import { doc, getDoc, setDoc, addDoc, collection, updateDoc, query, where, orderBy, limit, getDocs, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { vbidsToNumberKeys } from '../utils/numberKeyUtils';
// 🔧 优化：导入工具函数，减少 MainPage 代码量
import { convertTimestampToNumber, validateTimecode } from '../utils/timestampUtils';
import { 
  getInitialNumberKeyState, 
  filterDuplicateVBIDs,
  findFirstEmptyKey,
  getActiveKeysCount,
  getActiveKeyNumbers 
} from '../utils/numberKeyUtils';
import { 
  getClockPositionStyle,
  calculateOptimalScale,
  calculateControlPanelWidth 
} from '../utils/styleUtils';
import { 
  formatDuration, 
  formatScreenshotName, 
  formatClockTime, 
  formatCurrentTimeWithDuration as formatTimeWithDuration,
  formatRemainingTime as formatRemaining,
  calculateRemainingTime 
} from '../utils/formatUtils';
import { 
  parsePlaybackModeFromURL, 
  extractYouTubeID, 
  isYouTubeURL, 
  isCloudFileURL, 
  isLiveStream 
} from '../utils/videoUrlUtils';
// 🔧 优化：懒加载设置和对话框组件
// 🔧 重構：移除 SettingsMenu 和 ApiMonitorDialog，統一由 MainLayout 管理
const RegisterDialog = lazy(() => import('./RegisterDialog'));
const ProfileDialog = lazy(() => import('./ProfileDialog'));
const MVThemeDialog = lazy(() => import('./MVThemeDialog'));
// 🔧 新增：提取的 UI 组件
import TimecodeSettingsMenu from './TimecodeSettingsMenu';
import NotificationManager from './NotificationManager';
import VolumeControlPopover from './VolumeControlPopover';
import FileSelectionDialog from './FileSelectionDialog';
import UploadProgressDialog from './UploadProgressDialog';
// 工具函数保持同步导入（小文件）
import { importVBIDsFromFile, exportVBIDsToFile, saveVbidsToFirestore, exportVideoFirestoreData, cleanupDuplicateVbids, extractYouTubeId } from '../utils/vbidIO';
import { smartFileMatch, createPlaybackUrl, normalizeFilePath, validateFileType } from '../utils/fileManager';
import { youtubeApi } from '../services/youtubeApi';

// 🔧 重構：自訂 Hooks
import useScreenshotManagement from './MainPage/hooks/useScreenshotManagement';
import useKeyboardShortcuts from './MainPage/hooks/useKeyboardShortcuts';
import useTimecodeDisplay from './MainPage/hooks/useTimecodeDisplay';
import usePlaybackControl from './MainPage/hooks/usePlaybackControl';
import useABLoop from './MainPage/hooks/useABLoop';
import useNoteManagement from './MainPage/hooks/useNoteManagement';
import useCollaborationMode from './MainPage/hooks/useCollaborationMode';
import useSubtitleManagement from './MainPage/hooks/useSubtitleManagement';
import useVideoPlayer from './MainPage/hooks/useVideoPlayer';

// 🔧 重構：自訂元件
import PlayerOverlay from './MainPage/components/PlayerSection/PlayerOverlay';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import AutoHideBars from './AutoHideBars';
import SyncPlayManager from './SyncPlayManager';
import OnAirIndicator from './OnAirIndicator';
import { QRCodeSVG } from 'qrcode.react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { googleDriveService } from '../services/googleDriveService';
import { saveDocument } from '../services/firestoreService';
import { incrementAttention } from '../services/firebase';

// YouTube 影片資訊快取（組件外部）
const youtubeInfoCache: { [videoId: string]: any } = {};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`video-tabpanel-${index}`}
      aria-labelledby={`video-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 0, m: 0, pt: 0, pb: 0 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const formatTime = (seconds: number): string => {
  const pad = (num: number): string => num.toString().padStart(2, '0');
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return hours > 0 
    ? `${hours}:${pad(minutes)}:${pad(secs)}`
    : `${minutes}:${pad(secs)}`;
};

type TimecodePosition = 'topLeft' | 'topCenter' | 'topRight' | 'bottomLeft' | 'bottomCenter' | 'bottomRight';
type ScreenshotSize = 'original' | '70' | '50' | '1/2' | '1/4' | '1:1';
type StepUnit = 'second' | 'frame';
type BurstCount = 3 | 5 | 7;
type BurstUnit = 'second' | 'frame';

interface PositionStyle {
  top?: string | number;
  bottom?: string | number;
  left?: string | number;
  right?: string | number;
  margin: number;
  transform?: string;
  transformOrigin: string;
  position?: 'absolute';
  whiteSpace?: 'nowrap';
}

const getPositionStyle = (position: TimecodePosition): PositionStyle => {
  // 使用視窗大小的百分比來計算邊距
  const margin = '8%';

  // 基礎樣式
  const baseStyle = {
    margin: 0,
    transformOrigin: position.includes('bottom') ? 'bottom' : 'top',
    position: 'absolute' as const,
    whiteSpace: 'nowrap' as const
  };

  // 根據位置返回對應的樣式
  switch (position) {
    case 'topLeft':
      return {
        ...baseStyle,
        top: margin,
        left: margin
      };
    case 'topCenter':
      return {
        ...baseStyle,
        top: margin,
        left: '50%',
        transform: 'translateX(-50%)'
      };
    case 'topRight':
      return {
        ...baseStyle,
        top: margin,
        right: margin
      };
    case 'bottomLeft':
      return {
        ...baseStyle,
        bottom: margin,
        left: margin
      };
    case 'bottomCenter':
      return {
        ...baseStyle,
        bottom: margin,
        left: '50%',
        transform: 'translateX(-50%)'
      };
    case 'bottomRight':
      return {
        ...baseStyle,
        bottom: margin,
        right: margin
      };
    default:
      return {
        ...baseStyle,
        bottom: margin,
        left: '50%',
        transform: 'translateX(-50%)'
      };
  }
};

// 時鐘位置樣式函數
// 🔧 getClockPositionStyle 已从 '../utils/styleUtils' 导入

interface MainPageProps {
  navigationState?: any;
  pageState?: any;
  onStateChange?: (newState: any) => void;
  onNavigateBack?: (targetPage: string) => void;
  isEmbedded?: boolean;
  handleTitleClick?: () => void;
  subtitleFillLines?: number;
  currentFirestoreId?: string;
}

// 🔧 优化：懒加载组件的简单 fallback（对话框通常很小，加载很快）
const DialogFallback: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
    <CircularProgress size={24} />
  </Box>
);

/**
 * ═══════════════════════════════════════════════════════════════════════
 * MainPage 組件 - CheckPlay 核心頁面
 * ═══════════════════════════════════════════════════════════════════════
 * 
 * 📦 模組索引（快速導航）:
 * 
 * 搜尋以下關鍵字即可跳轉到對應模組：
 * - "🎬 影片播放器模組" - 影片載入、播放控制、進度管理
 * - "🔢 數字鍵管理模組" - CHECK 按鈕、數字鍵點擊、重點管理
 * - "💬 字幕同步模組" - AI 字幕下載、自動填充、SRT 處理
 * - "⏱️ 時間碼處理模組" - 時間碼顯示、格式化、位置控制
 * - "⏯️ 播放控制模組" - 快進快退、單步控制、播放速度
 * - "🎭 PP 播放模式模組" - PP 模式、AB 循環、播放時長
 * - "📸 截圖功能模組" - 截圖、連拍、浮水印
 * - "🔄 同步播放模組" - 同步連線、狀態管理、On Air
 * - "💾 資料載入保存模組" - Firestore 讀寫、觀點管理、版本控制
 * - "💬 對話框管理模組" - 各種對話框的開啟關閉
 * - "⌨️ 鍵盤快捷鍵模組" - 鍵盤事件處理
 * - "🎨 UI 控制渲染模組" - Tab 切換、模式切換、UI 渲染
 * 
 * 總行數: ~19,938 行
 * 使用方式: Ctrl+F 搜尋模組名稱（如 "🔢 數字鍵管理模組"）即可跳轉
 * ═══════════════════════════════════════════════════════════════════════
 */

const MainPage: React.FC<MainPageProps> = ({ 
  navigationState, 
  pageState, 
  onStateChange,
  onNavigateBack,
  isEmbedded = false,
  handleTitleClick: propHandleTitleClick,
  subtitleFillLines = 5,
  currentFirestoreId: propCurrentFirestoreId
}) => {
  // 🔧 調試：檢查 props 接收情況（移除過多日誌避免性能問題）
  // MainPage props（日誌已移除） 
  //   hasNavigationState: !!navigationState, 
  //   navigationState, 
  //   hasPageState: !!pageState,
  //   isEmbedded 
  // });
  
  // 🔧 修復：同步來自父組件的 currentFirestoreId
  useEffect(() => {
    if (propCurrentFirestoreId && propCurrentFirestoreId !== currentFirestoreId) {
      setCurrentFirestoreId(propCurrentFirestoreId);
      // 同步 currentFirestoreId 從父組件
    }
  }, [propCurrentFirestoreId]);
  
  // 🔧 新增：監聽來自父組件的設定開啟事件
  useEffect(() => {
    const handleOpenSettingsEvent = (event: Event) => {
      // 调试日志已移除
      // 創建一個虛擬錨點元素來開啟設定菜單
      const anchor = document.createElement('div');
      anchor.style.position = 'fixed';
      anchor.style.top = '50%';
      anchor.style.left = '50%';
      anchor.style.transform = 'translate(-50%, -50%)';
      anchor.style.width = '1px';
      anchor.style.height = '1px';
      anchor.style.pointerEvents = 'none';
      anchor.style.visibility = 'hidden';
      document.body.appendChild(anchor);
      setSettingsAnchorEl(anchor);
      // 設定菜單已開啟
    };

    window.addEventListener('openSettings', handleOpenSettingsEvent as EventListener);
    
    return () => {
      window.removeEventListener('openSettings', handleOpenSettingsEvent as EventListener);
    };
  }, []);

  // 音量 fade 相關狀態
  const [isFading, setIsFading] = useState(false);
  
  // 🔧 極端優化：移除 UPDATE_YOUTUBE_INFO 消息處理，避免無限循環

  // 🔧 優化：處理從 NotesPage 和 ManagePage 傳遞的 navigationState（SPA 模式）
  useEffect(() => {
    if (!navigationState?.shouldNavigateToMain || !navigationState?.navigationData) {
      return;
    }

    const { vbids, jumpToCheckpoint, videoId, firestoreId, documentId, fromManagePages, source, hasSRTCache, cloudFile, videoUrl: providedVideoUrl, mode } = navigationState.navigationData;
    
    // 🔧 修復：處理來自 ManagePage 的導航數據
    if (fromManagePages && videoId) {
      // SPA 模式：處理來自 ManagePage 的導航數據
      
      const videoUrl = `https://www.youtube.com/watch?v=${videoId}`;
      
      // 🔧 修復：從 ManagePages 導航時，禁用觀看進度恢復（避免干擾用戶操作）
      setIsJumpingToCheckpoint(true);
      isJumpingToCheckpointRef.current = true; // 立即生效
      console.log('🔧 從 ManagePages 導航，已禁用觀看進度恢復');
      // 3 秒後清除標記，允許正常的觀看進度功能
      setTimeout(() => {
        setIsJumpingToCheckpoint(false);
        isJumpingToCheckpointRef.current = false;
        console.log('🔧 已恢復觀看進度功能');
      }, 3000);
      
      // 🔧 關鍵修復：先同步設置 firestoreId，確保後續的 loadVideo 使用正確的文檔ID
      // 🔧 優先使用 firestoreId，如果沒有才使用 documentId
      const effectiveFirestoreId = firestoreId || documentId;
      if (effectiveFirestoreId) {
        setCurrentFirestoreId(effectiveFirestoreId);
        // 設置 firestoreId
      }
      
      requestAnimationFrame(async () => {
        setVideoUrl(videoUrl);
        setVideoMode('YouTube');
        setTabValue(3);
        
        // 🔥 優化：如果 ManagePage 已經傳遞了 vbids 數據，直接使用以加快顯示
        if (vbids && vbids.length > 0) {
          // SPA 模式：直接使用 ManagePage 傳遞的 vbids 數據
          console.log('🔥 從 ManagePages 接收到 vbids:', vbids.length, '個重點');
          const { vbidsToNumberKeys } = await import('../utils/numberKeyUtils');
          const convertedKeys = vbidsToNumberKeys(vbids);
          console.log('🔥 轉換後的 numberKeys:', Object.keys(convertedKeys).length, '個');
          setNumberKeys(convertedKeys);
          console.log('✅ 已立即設置數字鍵');
        } else {
          console.log('⚠️ ManagePages 沒有傳遞 vbids 或 vbids 為空');
        }
        
         // 🔥 修復：根據 ManagePages 的 AI Tag 狀態設置 AI 按鈕（不使用樂觀 UI）
         if (hasSRTCache) {
           // SPA 模式：檢測到 AI Tag，立即啟動 AI 按鈕
           setIsAutoFillMode(true);  // 立即變紅！
           setHasSRTCache(true);
           setSrtContent('_READY_'); // 設置標記，表示有字幕但尚未載入
           
           // 在背景載入字幕內容
           setTimeout(() => {
             checkSRTCacheAvailability(videoId);
           }, 0);
         } else {
           // SPA 模式：沒有 AI Tag
           setIsAutoFillMode(false);
           setHasSRTCache(false);
           setSrtContent('');
         }
        
        // 🔧 然後從 Firestore 獲取最新的完整數據（背景更新）
        // 在背景從 Firestore 載入完整重點數據
        setTimeout(async () => {
          try {
            await loadVideo(videoUrl, true, effectiveFirestoreId);
            // SPA 模式：背景更新完成
          } catch (error) {
            console.error('🔧 SPA 模式：背景更新失敗:', error);
          }
        }, 0);
        
        // 清除導航狀態
        if (onStateChange) {
          onStateChange({
            shouldNavigateToMain: false,
            navigationData: null,
            source: ''
          });
        }
      });
      
      return; // 提前返回，不執行後面的 NotesPage 邏輯
    }
    
    // 🔧 修復：處理來自 NotesPage 的導航數據（有 vbids）
    if (!videoId || !vbids) {
      return;
    }
    
    // 🔧 修復：如果有 jumpToCheckpoint，禁用觀看進度恢復
    if (jumpToCheckpoint) {
      setIsJumpingToCheckpoint(true);
      isJumpingToCheckpointRef.current = true; // 立即生效
      console.log('🔧 從 NotesPage 跳轉到重點，已禁用觀看進度恢復');
      // 5 秒後清除標記，允許正常的觀看進度功能
      setTimeout(() => {
        setIsJumpingToCheckpoint(false);
        isJumpingToCheckpointRef.current = false;
        console.log('🔧 已恢復觀看進度功能');
      }, 5000);
    }

    // 🔧 修復：識別雲端檔案（根據 source、mode 或 videoId 格式）
    const isCloudFile = source === 'cloud_upload' || 
                        mode === 'VIDEO' || 
                        videoId.startsWith('file_');
    
    // 🔧 優化：批量更新狀態，減少重渲染
    // 🔧 修復：優先使用 providedVideoUrl，如果沒有才根據 videoId 生成
    let videoUrl: string;
    if (providedVideoUrl) {
      // 🔧 最優先：直接使用 NotesPage 傳遞的 videoUrl
      videoUrl = providedVideoUrl;
    } else if (isCloudFile) {
      // 🔧 次要：從 cloudFile.url 獲取
      if (cloudFile?.url) {
        videoUrl = cloudFile.url;
      } else {
        // 🔧 修復：如果沒有 cloudFile.url，從 Firestore 載入
        console.warn('⚠️ 雲端檔案缺少 URL，將從 Firestore 載入');
        videoUrl = ''; // 暫時為空，等待 loadVideo 從 Firestore 載入
      }
    } else {
      // 🔧 最後：生成 YouTube URL
      videoUrl = `https://www.youtube.com/watch?v=${videoId}`;
    }
    
    // NotesPage 跳轉（日誌已移除）
    
    // 將 vbids 轉換為 numberKeys 格式
    const newNumberKeys: any = {};
    vbids.forEach((vbid: any) => {
      newNumberKeys[vbid.keyNo.toString()] = {
        keyNo: vbid.keyNo,
        isActive: true, // 從 NotesPage 來的都是活躍的
        isABPoint: false,
        isPickUpState: jumpToCheckpoint === vbid.keyNo, // 設置 pickup 狀態
        timestamp: vbid.timestamp,
        videoMode: 'YouTube',
        frame: null,
        timecode: vbid.timecode || '00:00:00 00',
        note: vbid.note || '',
        screenshot: ''
      };
    });
    
    // SPA 模式轉換後的 newNumberKeys（日誌已移除）

    // 🔧 關鍵修復：在 requestAnimationFrame 之前就設置來自 NotesPage 的標記，確保 VideoPlayer 創建時能正確讀取
    setIsFromNotesPage(true);
    // 設置 isFromNotesPage = true

    // 🔧 優化：使用 requestAnimationFrame 批量更新
    requestAnimationFrame(async () => {
      
      setVideoUrl(videoUrl);
      // 🔧 修復：根據檔案類型設置正確的 videoMode（優先使用 navigationData 的 mode）
      const targetMode = mode || (isCloudFile ? 'VIDEO' : 'YouTube');
      setVideoMode(targetMode);
      setTabValue(targetMode === 'VIDEO' ? 2 : 3); // 🔧 修復：VIDEO 使用標籤頁 2，YouTube 使用標籤頁 3
      setNumberKeys(newNumberKeys);
      
      // 🔧 關鍵修復：同時更新 tabNumberKeys（VIDEO Tab = 2）
      setTabNumberKeys(prev => {
        const newState = { ...prev };
        newState[targetMode === 'VIDEO' ? 2 : 3] = { ...newNumberKeys };
        // 調試日誌已移除
        return newState;
      });
      
      // 🔧 關鍵優化：如果有 jumpToCheckpoint，設置 initialStartTime，讓影片直接從目標時間開始載入
      if (jumpToCheckpoint) {
        const targetVbid = vbids.find((vbid: any) => vbid.keyNo === jumpToCheckpoint);
        if (targetVbid?.timestamp) {
          const startTime = Math.floor(targetVbid.timestamp); // YouTube start 參數要求整數
          setInitialStartTime(startTime);
          // 設置 YouTube 起始時間
        }
      } else {
        // 沒有跳轉需求時，清空 initialStartTime
        setInitialStartTime(null);
      }
      
      // 設置影片模式（日誌已移除）
      
      // 🔧 修復：設置 firestoreId（優先使用 firestoreId，避免重複設置）
      const effectiveFirestoreId = firestoreId || documentId;
      if (effectiveFirestoreId && currentFirestoreId !== effectiveFirestoreId) {
        setCurrentFirestoreId(effectiveFirestoreId);
        // 設置 firestoreId (NotesPage)
      }
      
      // 🔧 優化：NotesPage 已經提供了完整的 vbids 數據，跳過 loadVideo 避免覆蓋
      // 只檢查 SRT 緩存，不重新載入 numberKeys
      setTimeout(async () => {
        try {
          const currentPickupKey = jumpToCheckpoint;
          // 背景載入前保存 pickup 狀態
          
          // 🔧 關鍵修復：只檢查 SRT 緩存，不調用 loadVideo（避免覆蓋 numberKeys）
          if (effectiveFirestoreId) {
            // 檢查 SRT 緩存
            const cacheKey = effectiveFirestoreId || videoId || videoUrl;
            if (cacheKey) {
              try {
                const { getSRTFromCache } = await import('../services/srtCacheService');
                const cachedSRT = await getSRTFromCache(cacheKey);
                if (cachedSRT) {
                  // 成功日誌已移除
                } else {
                  // 调试日志已移除
                }
              } catch (error) {
                console.log('⚠️ NotesPage 跳轉：SRT 緩存檢查失敗');
              }
            }
          }
          
          // SPA 模式：跳過 loadVideo
          
          // 🔧 關鍵：設置 ref 標記，防止後續 useEffect(id) 覆蓋 numberKeys
          isFromNotesPageRef.current = true;
          // 已設置 isFromNotesPageRef = true
        } catch (error) {
          console.error('🔧 SPA 模式（NotesPage）：背景處理失敗:', error);
        }
      }, 100); // 🔧 優化：短延遲，讓 UI 先渲染
      
      // 同時更新 MainLayout 的狀態
      if (onStateChange) {
        onStateChange({
          videoUrl: videoUrl,
          videoMode: 'YouTube',
          tabValue: 3,
          numberKeys: newNumberKeys
        });
      }
    });
    
    // 處理跳轉到特定重點
    if (jumpToCheckpoint) {
      // 🔧 新增：SPA 模式自動翻頁到 pickup 鍵所在的頁面
      const pickupKeyPage = Math.floor((jumpToCheckpoint - 1) / 6);
      // 調試日誌已移除
      setKeySetIndex(pickupKeyPage);
      
      const targetVbid = vbids.find((vbid: any) => vbid.keyNo === jumpToCheckpoint);
      
      // 調試日誌已移除
      // 調試日誌已移除
      
      if (targetVbid?.timestamp) {
        // 🔧 關鍵優化：如果已設置 initialStartTime，影片會直接從目標時間開始載入，跳過 postMessage 跳轉
        if (initialStartTime !== null) {
          // 調試日誌已移除
          return;
        }
        
        // 🔧 修復：從 navigationData 中獲取 videoId，避免依賴 videoUrl
        const videoId = navigationState.navigationData.videoId || videoUrl?.match(/[?&]v=([^&]+)/)?.[1] || 'unknown';
        const jumpKey = `jump_${videoId}_${jumpToCheckpoint}_${targetVbid.timestamp}`;
        const lastJumpTime = sessionStorage.getItem(jumpKey);
        const currentTime = Date.now();
        
        // 調試日誌已移除
        
        // 如果5秒內已經執行過相同的跳轉，則跳過
        if (lastJumpTime && (currentTime - parseInt(lastJumpTime)) < 5000) {
          // 調試日誌已移除
          return;
        }
        
        // 記錄本次跳轉時間
        sessionStorage.setItem(jumpKey, currentTime.toString());
        // 調試日誌已移除
        // postMessage 日誌已移除;
        
        // 🔧 修復：區分 YouTube 和雲端檔案的跳轉邏輯
        const attemptSeekTo = () => {
          // 調試日誌已移除
          // 調試日誌已移除
          
          // 🔧 關鍵修復：雲端檔案使用 playerRef，YouTube 使用 postMessage
          if (isCloudFile || mode === 'VIDEO') {
            // 雲端檔案：直接使用 playerRef.current.seekTo()
            if (playerRef.current) {
              try {
                playerRef.current.seekTo(targetVbid.timestamp, 'seconds');
                // 調試日誌已移除
                
                // 自動播放
                setTimeout(() => {
                  setIsPlaying(true);
                  // 調試日誌已移除
                }, 300);
              } catch (error) {
                console.error(`❌ 雲端檔案跳轉失敗:`, error);
              }
            } else {
              console.warn(`⚠️ playerRef 尚未準備好，稍後重試`);
            }
          } else {
            // YouTube 影片：使用 postMessage
            const iframe = document.querySelector('iframe[src*="youtube.com"]') as HTMLIFrameElement;
            if (iframe?.contentWindow) {
              try {
                const message = JSON.stringify({
                  event: 'command',
                  func: 'seekTo',
                  args: [targetVbid.timestamp, true]
                });
                
                const playMessage = JSON.stringify({
                  event: 'command',
                  func: 'playVideo',
                  args: []
                });
                
                const origins = ['*', 'https://www.youtube.com'];
                
                for (const origin of origins) {
                  try {
                    iframe.contentWindow.postMessage(message, origin);
                    // 調試日誌已移除
                    
                    iframe.contentWindow.postMessage(playMessage, origin);
                    // 調試日誌已移除
                    
                    break;
                  } catch (error) {
                    // postMessage 日誌已移除;
                    continue;
                  }
                }
                
              } catch (error) {
                console.error(`❌ seekTo 命令發送失敗:`, error);
              }
            } else {
              console.error(`❌ 找不到 YouTube iframe 或 contentWindow`);
            }
          }
        };
        
        // 開始跳轉嘗試
        setTimeout(() => {
          attemptSeekTo();
        }, isCloudFile ? 1500 : 2000); // 🔧 雲端檔案準備更快，1.5秒；YouTube 2秒
      } else {
        console.warn(`⚠️ 找不到目標重點 ${jumpToCheckpoint} 或時間戳記為空:`, targetVbid);
      }
    }
  }, [navigationState?.shouldNavigateToMain, navigationState?.navigationData]); // 🔧 修復：移除 onStateChange 依賴避免無限循環
  
  // SwipeableControlPanel 的 activePanel 狀態管理
  const [activePanel, setActivePanel] = useState<number>(1); // 🔧 修改：預設為 Note 記錄 (1)
  const handleActivePanelChange = useCallback((panelIndex: number) => {
    setActivePanel(panelIndex);
  }, []);
  const fadeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // 🔧 新增：标记是否从 NotesPage 跳转（用于跳过 useEffect(id) 的覆盖）
  const isFromNotesPageRef = useRef(false);
  
  // 🔧 新增：存储传统模式下的 jumpToCheckpoint（用于延迟跳转）
  const pendingJumpToCheckpointRef = useRef<number | null>(null);
  
  // Fade in/out 效果開關（預設啟用）
  const [enableFadeEffect, setEnableFadeEffect] = useState<boolean>(() => {
    const saved = localStorage.getItem('enableFadeEffect');
    return saved !== null ? saved === 'true' : true; // 預設為 true
  });
  
  // 畫面 fade in/out 相關狀態
  const [videoFadeOpacity, setVideoFadeOpacity] = useState(0);

  // 即時保存相關狀態
  const [lastSaveTime, setLastSaveTime] = useState<number>(Date.now());
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [autoSaveInterval, setAutoSaveInterval] = useState<NodeJS.Timeout | null>(null);
  const [pendingSave, setPendingSave] = useState<boolean>(false);
  
  // 自動保存提示相關狀態
  const [autoSaveSnackbarOpen, setAutoSaveSnackbarOpen] = useState(false);
  const [autoSaveMessage, setAutoSaveMessage] = useState('');
  
  // 🔧 新增：追蹤是否來自 NotesPage
  const [isFromNotesPage, setIsFromNotesPage] = useState(false);
  
  // 🔧 新增：追蹤來源頁面，用於返回導航
  const [sourcePage, setSourcePage] = useState<string | null>(null);
  
  // 🔧 新增：追蹤是否正在進行數字鍵跳轉（用於禁用觀看進度恢復）
  const [isJumpingToCheckpoint, setIsJumpingToCheckpoint] = useState(false);
  const isJumpingToCheckpointRef = useRef(false); // 使用 ref 來立即生效

  const fadeOut = (duration: number = 300): Promise<void> => {
    return new Promise((resolve) => {
      if (!enableFadeEffect) {
        resolve();
        return;
      }
      
      if (!playerRef.current) {
        resolve();
        return;
      }

      const internalPlayer = playerRef.current.getInternalPlayer?.();
      if (!internalPlayer) {
        resolve();
        return;
      }

      console.log('[AudioFade] fadeOut start', {
        startVolume: volumeRef.current,
        isMuted: mutedRef.current
      });

      setIsFading(true);
      const startVolume = Math.max(volume, 0.1); // 確保起始音量至少是 0.1
      const steps = 10;
      const stepDuration = duration / steps;
      let currentStep = 0;

      const fadeStep = () => {
        currentStep++;
        const progress = currentStep / steps;
        const newVolume = startVolume * (1 - progress);
        
        setVolume(newVolume);
        
        if (currentStep < steps) {
          fadeTimeoutRef.current = setTimeout(fadeStep, stepDuration);
        } else {
          setIsFading(false);
          console.log('[AudioFade] fadeOut complete', {
            finalVolume: volumeRef.current,
            isMuted: true
          });
          resolve();
        }
      };

      fadeStep();
    });
  };

  // 音量 fade in 函數
  const fadeIn = (duration: number = 300): Promise<void> => {
    return new Promise((resolve) => {
      if (!enableFadeEffect) {
        resolve();
        return;
      }
      
      if (!playerRef.current) {
        resolve();
        return;
      }

      const internalPlayer = playerRef.current.getInternalPlayer?.();
      if (!internalPlayer) {
        resolve();
        return;
      }

      console.log('[AudioFade] fadeIn start', {
        currentVolume: volumeRef.current,
        isMuted: mutedRef.current
      });

      if (mutedRef.current) {
        setIsMuted(false);
      }

      setIsFading(true);
      const targetVolume = Math.max(volume, 0.5); // 確保目標音量至少是 0.5
      const steps = 10;
      const stepDuration = duration / steps;
      let currentStep = 0;

      const fadeStep = () => {
        currentStep++;
        const progress = currentStep / steps;
        const newVolume = targetVolume * progress;
        
        setVolume(newVolume);
        
        if (currentStep < steps) {
          fadeTimeoutRef.current = setTimeout(fadeStep, stepDuration);
        } else {
          // 確保恢復到目標音量
          setVolume(targetVolume);
          console.log('[AudioFade] fadeIn complete', {
            finalVolume: targetVolume,
            isMuted: false
          });
          setIsFading(false);
          resolve();
        }
      };

      fadeStep();
    });
  };

  // 畫面 fade out 函數
  const fadeOutVideo = (duration: number = 200): Promise<void> => {
    return new Promise((resolve) => {
      if (!enableFadeEffect) {
        resolve();
        return;
      }
      
      setVideoFadeOpacity(1);
      setTimeout(() => {
        resolve();
      }, duration);
    });
  };

  // 畫面 fade in 函數
  const fadeInVideo = (duration: number = 200): Promise<void> => {
    return new Promise((resolve) => {
      if (!enableFadeEffect) {
        resolve();
        return;
      }
      
      setVideoFadeOpacity(0);
      setTimeout(() => {
        resolve();
      }, duration);
    });
  };

  // 🔧 已移至 timestampUtils.ts
  // convertTimestampToNumber 函数已从 '../utils/timestampUtils' 导入

  // 帶 fade 效果的跳轉函數
  const seekToWithFade = async (timestamp: number | any, fadeDuration: number = 200) => {
    if (!playerRef.current) return;

    // 🔧 修復：使用輔助函數處理 Firestore Timestamp 對象
    const timestampValue = convertTimestampToNumber(timestamp);
    
    // 🔧 新增：檢查是否為長時間影片（超過 2 小時）
    const isLongVideo = duration > 7200; // 2 小時
    const isYouTubeVideo = videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be');
    
    // 🔧 關鍵修復：如果 timestamp 超過 duration，使用 timestamp 作為最小 duration
    const effectiveDuration = Math.max(duration, timestampValue + 100);
    
    console.log('🎯 準備跳轉:', {
      目標時間: timestampValue.toFixed(2) + 's',
      儲存的時長: duration.toFixed(0) + 's',
      有效時長: effectiveDuration.toFixed(0) + 's',
      是否長影片: isLongVideo,
      是否YouTube: isYouTubeVideo
    });

    if (!enableFadeEffect) {
      // 如果關閉 fade 效果，直接跳轉
      // 🔧 關鍵修復：YouTube 播放器在暫停狀態下 seekTo 不生效，需要先播放
      const wasPlaying = isPlaying;
      if (!wasPlaying) {
        // 處理日誌已移除
        setIsPlaying(true);
        // 等待播放器狀態更新
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // 🔧 直播影片特殊處理：使用有效時長計算 fraction
      if (isLongVideo && isYouTubeVideo && effectiveDuration > 0) {
        const fraction = timestampValue / effectiveDuration;
        console.log('📺 長時間 YouTube 影片，使用比例跳轉:', {
          秒數: timestampValue,
          有效時長: effectiveDuration,
          比例: fraction.toFixed(4)
        });
        playerRef.current.seekTo(fraction, 'fraction');
      } else {
        // 一般影片：使用秒數跳轉
        console.log('📺 使用秒數跳轉:', timestampValue);
        playerRef.current.seekTo(timestampValue, 'seconds');
      }
      
      // 🔧 關鍵優化：增加重試次數和等待時間，提升跳轉成功率
      let retryCount = 0;
      const maxRetries = 3; // 🔧 從 1 增加到 3，提升成功率
      
      const verifyAndRetry = () => {
        setTimeout(() => {
          if (playerRef.current && retryCount < maxRetries) {
            const currentTime = playerRef.current.getCurrentTime();
            const timeDiff = Math.abs(currentTime - timestampValue);
            
            // 調試日誌已移除
            
            // 🔧 優化：放寬偏差容忍度（從 10s 增加到 15s），減少誤判
            if (timeDiff > 15) {  // 🔧 從 10 改為 15
              retryCount++;
              
              if (retryCount < maxRetries) {
                console.warn(`⚠️ 第 ${retryCount} 次跳轉偏差過大，執行重試...`);
                // 🔧 關鍵修復：重試時確保播放狀態
                if (!isPlaying) {
                  setIsPlaying(true);
                }
                // 🔧 直播影片特殊處理：使用有效時長計算 fraction
                if (isLongVideo && isYouTubeVideo && effectiveDuration > 0) {
                  const fraction = timestampValue / effectiveDuration;
                  playerRef.current.seekTo(fraction, 'fraction');
                } else {
                  playerRef.current.seekTo(timestampValue, 'seconds');
                }
                verifyAndRetry(); // 遞迴重試
              } else {
                // 🔧 優化：跳轉失敗時不彈出對話框，改為靜默處理
                console.warn(`⚠️ seekTo 跳轉偏差較大: ${timeDiff.toFixed(2)}s（目標 ${timestampValue.toFixed(2)}s）`);
                console.warn(`ℹ️ 這可能是正常的播放器行為，不影響使用`);
                // 🔧 移除 confirm 對話框，避免阻塞用戶操作
                // 用戶可以手動拖動進度條來調整時間
              }
            } else {
              // 調試日誌已移除
            }
          }
        }, 800);  // 🔧 從 300ms 增加到 800ms，讓 YouTube 有足夠時間完成跳轉
      };
      
      verifyAndRetry();
      return;
    }

    // 保存原始音量，確保至少是 0.5
    const originalVolume = Math.max(volume, 0.5);

    try {
      // 🔧 關鍵修復：YouTube 播放器在暫停狀態下 seekTo 不生效，需要先播放
      const wasPlaying = isPlaying;
      if (!wasPlaying) {
        // 處理日誌已移除
        setIsPlaying(true);
        // 等待播放器狀態更新
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // 同時進行聲音和畫面的 fade out
      await Promise.all([
        fadeOut(fadeDuration),
        fadeOutVideo(fadeDuration)
      ]);
      
      // 🔥 跳轉到目標時間
      // 🔧 直播影片特殊處理：使用有效時長計算 fraction
      if (isLongVideo && isYouTubeVideo && effectiveDuration > 0) {
        const fraction = timestampValue / effectiveDuration;
        console.log('📺 長時間 YouTube 影片（有 fade），使用比例跳轉:', {
          秒數: timestampValue,
          有效時長: effectiveDuration,
          比例: fraction.toFixed(4)
        });
        playerRef.current.seekTo(fraction, 'fraction');
      } else {
        // 一般影片：使用秒數跳轉
        console.log('📺 使用秒數跳轉:', timestampValue);
        playerRef.current.seekTo(timestampValue, 'seconds');
      }
      
      // 🔧 關鍵優化：增加等待時間（從 200ms 增加到 800ms），讓 YouTube 播放器有足夠時間完成跳轉
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // 🔧 關鍵優化：增加重試次數到 3 次，提升成功率
      let seekSuccess = false;
      for (let i = 0; i < 3; i++) {  // 🔧 從 1 改為 3
        const currentTime = playerRef.current.getCurrentTime();
        const timeDiff = Math.abs(currentTime - timestampValue);
        
        // 調試日誌已移除
        
        // 🔧 優化：放寬偏差容忍度（從 5s 增加到 15s），減少誤判
        if (timeDiff > 15) {  // 🔧 從 10 改為 15
          if (i < 2) {  // 🔧 從 i < 0 改為 i < 2（允許重試）
            console.warn(`⚠️ 第 ${i + 1} 次跳轉偏差過大，執行重試...`);
            // 🔧 關鍵修復：重試時確保播放狀態
            if (!isPlaying) {
              setIsPlaying(true);
            }
            // 🔧 直播影片特殊處理：使用有效時長計算 fraction
            if (isLongVideo && isYouTubeVideo && effectiveDuration > 0) {
              const fraction = timestampValue / effectiveDuration;
              playerRef.current.seekTo(fraction, 'fraction');
            } else {
              playerRef.current.seekTo(timestampValue, 'seconds');
            }
            await new Promise(resolve => setTimeout(resolve, 500));  // 🔧 重試時等待 500ms
              } else {
                // 🔧 優化：最後一次重試失敗時不彈出對話框，改為靜默處理
                console.warn(`⚠️ seekTo 跳轉偏差較大: ${timeDiff.toFixed(2)}s（目標 ${timestampValue.toFixed(2)}s）`);
                console.warn(`ℹ️ 這可能是正常的播放器行為，不影響使用`);
                // 🔧 移除 confirm 對話框，避免阻塞用戶操作
                // 用戶可以手動拖動進度條來調整時間
              }
        } else {
          // 調試日誌已移除
          seekSuccess = true;
          break;
        }
      }
      
      // 同時進行聲音和畫面的 fade in
      await Promise.all([
        fadeIn(fadeDuration),
        fadeInVideo(fadeDuration)
      ]);
      
      // 確保最終音量是原始音量
      setVolume(originalVolume);
    } catch (error) {
      console.error('Fade 跳轉失敗:', error);
      // 發生錯誤時也要恢復原始音量和畫面
      setVolume(originalVolume);
      setVideoFadeOpacity(0);
    }
  };

  // 清理 fade timeout
  useEffect(() => {
    return () => {
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }
    };
  }, []);

  // 在組件加載時解析 URL 中的播放模式參數
  useEffect(() => {
    parsePlaybackModeFromURL();
  }, []);

  // 手機橫式佈局下自動滾動到播放器位置
  useEffect(() => {
    const handleResize = () => {
      // 檢查是否為手機橫式佈局
      const isMobileLandscape = windowDimensions.width <= 800 && windowDimensions.width > windowDimensions.height;
      
      if (isMobileLandscape) {
                  // 延遲執行，確保DOM已渲染完成
          const timer = setTimeout(() => {
            // 滾動到一個適中的位置，隱藏輸入框但不過度滾動
            const scrollPosition = 100; // 從150px調整為100px，回來一點
            
            window.scrollTo({
              top: scrollPosition,
              behavior: 'smooth'
            });
            
            // 滾動位置日誌已移除
          }, 100);
        
        return () => clearTimeout(timer);
      }
    };

    // 初始檢查
    handleResize();
    
    // 添加視窗尺寸變化監聽器
    window.addEventListener('resize', handleResize, { passive: true });
    
    // 清理函數
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); // 只在組件掛載時執行一次

    // AppBar 自動隱藏/顯示邏輯（手機模式和桌面模式）
  useEffect(() => {
    let hideTimeout: NodeJS.Timeout | null = null;
    let scrollTimeout: NodeJS.Timeout | null = null;
    let isScrolling = false; // 防止滾動過程中重複觸發
    let lastScrollTime = 0; // 記錄上次滾動時間
    let scrollCount = 0; // 記錄連續滾動次數
    let lastUserActivity = Date.now(); // 記錄最後用戶活動時間
    const appBar = document.querySelector('.MuiAppBar-root') as HTMLElement;
    
    if (appBar) {
      // 檢查是否為桌面版（使用螢幕比例判斷）
      const aspectRatio = windowDimensions.width / windowDimensions.height;
      const isDesktop = aspectRatio <= 1.8; // 比例小於等於1.8:1視為桌面版
      
      // 手機模式切換後，智能延遲隱藏 AppBar
      const autoHideAppBar = () => {
        const now = Date.now();
        const timeSinceLastActivity = now - lastUserActivity;
        
        // 檢查用戶是否正在主動操作（滾動、點擊等）
        if (isScrolling || appBar.classList.contains('bounce-back')) {
          // 如果用戶正在操作，延長隱藏時間
          hideTimeout = setTimeout(autoHideAppBar, 2000); // 2秒後再檢查
          return;
        }
        
        // 檢查用戶活動時間：如果最近有活動，延長隱藏時間
        if (timeSinceLastActivity < 3000) { // 3秒內有活動
          hideTimeout = setTimeout(autoHideAppBar, 3000); // 3秒後再檢查
          return;
        }
        
        // 手機直式模式：使用螢幕比例判斷
        const aspectRatio = windowDimensions.width / windowDimensions.height;
        const isMobilePortrait = aspectRatio > 1.8 && windowDimensions.height > windowDimensions.width;
        
        // 所有模式都支持自動隱藏
        if (isMobilePortrait) {
          // 手機直式模式：完全隱藏（滑出）
          // 狀態日誌已移除
          appBar.classList.remove('bounce-back');
          appBar.classList.add('scrolled-up');
        } else {
          // 手機橫式/桌面模式：完全隱藏
          // 狀態日誌已移除
          appBar.classList.remove('bounce-back');
          appBar.classList.add('scrolled-up');
        }
      };
      
      // 根據設備模式設置初始隱藏邏輯
      if (isDesktop) {
        // 桌面版：AppBar 保持顯示，不自動隱藏，但支持點擊顯示
        // 狀態日誌已移除
      } else {
        const aspectRatio = windowDimensions.width / windowDimensions.height;
        const isMobilePortrait = aspectRatio > 1.8 && windowDimensions.height > windowDimensions.width;
        
        if (isMobilePortrait) {
          // 手機直式模式：設置隱藏計時器，1秒後完全隱藏（滑出）
          hideTimeout = setTimeout(autoHideAppBar, 1000);
          // 狀態日誌已移除
        } else {
          // 手機橫式模式：設置隱藏計時器
          hideTimeout = setTimeout(autoHideAppBar, 2000);
          // 狀態日誌已移除
        }
      }
      
      // 點擊事件處理：只有點擊影片區域時才顯示 AppBar
      const handleClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        
        // 檢查是否點擊了控制面版區域
        const isControlPanel = target.closest('.control-panel-group-ui') || 
                              target.closest('.bottom-control-buttons') ||
                              target.closest('.control-panel-ui') ||
                              target.closest('.control-panel') ||
                              target.closest('.player-controls') ||
                              target.closest('.volume-controls') ||
                              target.closest('.playback-controls');
        
        // 檢查是否點擊了影片播放器區域
        const isVideoPlayer = target.closest('.react-player') || 
                             target.closest('.video-player') ||
                             target.closest('.player-wrapper') ||
                             target.closest('[data-testid="react-player"]');
        
        // 只有點擊影片播放器區域才觸發顯示
        if (isVideoPlayer && !isControlPanel) {
          // 如果正在滾動，跳過點擊處理
          if (isScrolling) {
            return;
          }
          
          appBar.classList.remove('scrolled-up');
          appBar.classList.add('bounce-back');
          
          // 動畫結束後移除 class
          setTimeout(() => {
            appBar.classList.remove('bounce-back');
          }, 600);
          
          // 根據設備模式設置隱藏計時器
          if (isDesktop) {
            // 桌面版：點擊後 AppBar 保持顯示，不自動隱藏
            // 狀態日誌已移除
          } else {
            const aspectRatio = windowDimensions.width / windowDimensions.height;
        const isMobilePortrait = aspectRatio > 1.8 && windowDimensions.height > windowDimensions.width;
            
            if (isMobilePortrait) {
              // 手機直式模式：點擊後 3秒後部分隱藏
              hideTimeout = setTimeout(autoHideAppBar, 3000);
              // 狀態日誌已移除
            } else {
              // 手機橫式模式：5秒後自動隱藏
              hideTimeout = setTimeout(autoHideAppBar, 5000);
              // 狀態日誌已移除
            }
          }
        }
      };
      
      // 滑動事件處理：上下滑動頁面時顯示 AppBar（使用增強觸控防抖）
      const handleScroll = () => {
        const now = Date.now();
        
        // 檢測觸控螢幕的快速連續滾動
        if (now - lastScrollTime < 100) { // 100ms 內的快速滾動
          scrollCount++;
          lastScrollTime = now;
          
          // 手機橫式模式下的觸控更敏感，需要更嚴格的防抖
          const aspectRatio = windowDimensions.width / windowDimensions.height;
          const isMobileLandscape = aspectRatio > 1.8 && windowDimensions.width > windowDimensions.height;
          
          if (isMobileLandscape) {
            // 手機橫式模式：更嚴格的防抖策略
            if (scrollCount > 2) { // 降低觸發閾值
              if (scrollTimeout) {
                clearTimeout(scrollTimeout);
              }
              scrollTimeout = setTimeout(() => {
                isScrolling = false;
                scrollCount = 0;
              }, 1000); // 手機橫式：1秒防抖
              return;
            }
          } else {
            // 手機直式模式：原有防抖策略
            if (scrollCount > 3) {
              if (scrollTimeout) {
                clearTimeout(scrollTimeout);
              }
              scrollTimeout = setTimeout(() => {
                isScrolling = false;
                scrollCount = 0;
              }, 800);
              return;
            }
          }
        } else {
          // 重置計數
          scrollCount = 0;
        }
        
        lastScrollTime = now;
        
        // 如果正在滾動，跳過
        if (isScrolling) {
          return;
        }
        
        // 標記正在滾動
        isScrolling = true;
        
        // 更新用戶活動時間
        lastUserActivity = Date.now();
        
        // 清除之前的計時器
        if (hideTimeout) {
          clearTimeout(hideTimeout);
        }
        
        // 顯示 AppBar
        appBar.classList.remove('scrolled-up');
        appBar.classList.add('bounce-back');
        
        // 動畫結束後移除 class
        setTimeout(() => {
          appBar.classList.remove('bounce-back');
        }, 600);
        
        // 根據設備模式設置隱藏計時器
        const isMobilePortrait = aspectRatio > 1.8 && windowDimensions.height > windowDimensions.width;
        
        if (isMobilePortrait) {
          // 手機直式模式：滾動後 2秒後部分隱藏
          const hideDelay = scrollCount > 2 ? 3000 : 2000; // 快速滾動時延長到3秒
          hideTimeout = setTimeout(autoHideAppBar, hideDelay);
          // AppBar 隱藏時間日誌已移除
        } else {
          // 手機橫式/桌面模式：根據滾動模式調整隱藏時間
          const hideDelay = scrollCount > 2 ? 8000 : 5000; // 快速滾動時延長到8秒
          hideTimeout = setTimeout(autoHideAppBar, hideDelay);
          // AppBar 隱藏時間日誌已移除
        }
        
        // 智能防抖：根據設備模式和滾動模式調整防抖時間
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
        
        const isMobileLandscape = aspectRatio > 1.8 && windowDimensions.width > windowDimensions.height;
        let debounceTime;
        
        if (isMobileLandscape) {
          // 手機橫式模式：更長的防抖時間
          debounceTime = scrollCount > 1 ? 800 : 500;
        } else {
          // 手機直式模式：原有防抖時間
          debounceTime = scrollCount > 2 ? 500 : 300;
        }
        
        scrollTimeout = setTimeout(() => {
          isScrolling = false;
          scrollCount = 0;
        }, debounceTime);
      };
      
      // 添加點擊和滾動監聽器
      document.addEventListener('click', handleClick, { passive: true });
      window.addEventListener('scroll', handleScroll, { passive: true });
      
      // 清理函數
      return () => {
        document.removeEventListener('click', handleClick);
        window.removeEventListener('scroll', handleScroll);
        if (hideTimeout) {
          clearTimeout(hideTimeout);
        }
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
      };
    }
  }, []);

  // 手機模式下的自動向上滾動功能（暫時停用）
  useEffect(() => {
    // 暫時停用手機直式自動滾動功能
    // 狀態日誌已移除
    
    // 如果需要重新啟用，請取消註釋以下代碼
    /*
    let isScrolling = false; // 防止重複滾動
    
    const handleAutoScroll = () => {
      // 如果正在滾動，跳過
      if (isScrolling) {
        return;
      }
      
      // 檢查是否為手機模式（使用螢幕比例判斷）
      const aspectRatio = windowDimensions.width / windowDimensions.height;
      const isMobile = aspectRatio > 1.8; // 比例大於1.8:1視為手機版
      
      if (isMobile) {
        isScrolling = true; // 標記正在滾動
        
        // 延遲執行，確保DOM已渲染完成
        const timer = setTimeout(() => {
          // 手機模式下自動向上滾動158px
          const scrollPosition = 158;
          
          window.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
          });
          
          // 自動滾動位置日誌已移除
          
          // 滾動完成後重置標記
          setTimeout(() => {
            isScrolling = false;
          }, 1000); // 1秒後允許再次滾動
        }, 100);
        
        return () => clearTimeout(timer);
      }
    };

    // 初始檢查
    handleAutoScroll();
    
    // 添加視窗尺寸變化監聽器（使用防抖）
    let resizeTimeout: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleAutoScroll, 300); // 300ms 防抖
    };
    
    window.addEventListener('resize', debouncedResize, { passive: true });
    
    // 清理函數
    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(resizeTimeout);
    };
    */
  }, []); // 只在組件掛載時執行一次

  // 監聽手機方向變化，觸發 AppBar 自動隱藏
  useEffect(() => {
    const appBar = document.querySelector('.MuiAppBar-root') as HTMLElement;
    
    if (appBar) {
      let isProcessing = false; // 防止重複處理
      
      const handleOrientationChange = () => {
        // 如果正在處理，跳過
        if (isProcessing) {
          return;
        }
        
        // 檢查是否為桌面版（使用螢幕比例判斷）
        const isDesktop = aspectRatio <= 1.8; // 比例小於等於1.8:1視為桌面版
        
        // 如果是桌面版，不啟用隱藏功能
        if (isDesktop) {
          return;
        }
        
        // 檢查是否為手機模式（使用螢幕比例判斷）
        const isMobile = aspectRatio > 1.8; // 比例大於1.8:1視為手機版
        
        if (isMobile) {
          isProcessing = true; // 標記正在處理
          
          // 🔧 修復：方向變化後，檢查是否為手機直式模式
          setTimeout(() => {
            if (appBar) {
              // 檢查是否為手機直式模式：寬度小於800px且高度大於500px
              const aspectRatio = windowDimensions.width / windowDimensions.height;
        const isMobilePortrait = aspectRatio > 1.8 && windowDimensions.height > windowDimensions.width;
              
              if (isMobilePortrait) {
                // 手機直式模式：顯示AppBar，1秒後完全隱藏
                // 影片日誌已移除
                appBar.classList.remove('scrolled-up');
                appBar.classList.remove('bounce-back');
                // 設置1秒後完全隱藏
                setTimeout(() => {
                  hideTimeout = setTimeout(autoHideAppBar, 1000);
                }, 1000);
                
                // 🔧 新增：檢查是否在「Note記錄」面板，如果是則自動滾動到280px
                const currentActivePanel = activePanel; // 使用當前的activePanel狀態
                // 調試日誌已移除
                
                if (currentActivePanel === 1) {
                  // 在「Note記錄」面板，執行自動滾動
                  // 影片日誌已移除
                  setTimeout(() => {
                    const currentPosition = window.pageYOffset;
                    const targetPosition = 280;
                    // 調試日誌已移除
                  }, 500); // 延遲500ms確保DOM已更新
                }
              } else {
                // 手機橫式/桌面模式：隱藏 AppBar
                // 影片日誌已移除
                appBar.classList.remove('bounce-back');
                appBar.classList.add('scrolled-up');
              }
            }
            
            // 處理完成後重置標記
            setTimeout(() => {
              isProcessing = false;
            }, 1000); // 1秒後允許再次處理
          }, 3000);
        }
      };
      
      // 監聽方向變化（只監聽 orientationchange，移除 resize 避免衝突）
      window.addEventListener('orientationchange', handleOrientationChange, { passive: true });
      
      return () => {
        window.removeEventListener('orientationchange', handleOrientationChange);
      };
    }
  }, []);

  // 檢測網絡狀態變化
  useEffect(() => {
    const handleOnline = () => {
      // 狀態日誌已移除
      setIsOnline(true);
      // 網絡恢復時立即保存
      if (pendingSave) {
        // saveCurrentState(); // 暫時註釋，等函數重新定義後啟用
        setPendingSave(false);
      }
    };

    const handleOffline = () => {
      // 狀態日誌已移除
      setIsOnline(false);
      // 離線前立即保存
      // saveCurrentState(); // 暫時註釋，等函數重新定義後啟用
    };

    window.addEventListener('online', handleOnline, { passive: true });
    window.addEventListener('offline', handleOffline, { passive: true });

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [pendingSave]);

  // 🔧 移除：定期自動保存機制，避免額外的抖動
  // 定期自動保存（每 30 秒）- 已停用以避免抖動
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     // 暫時註釋，等 numberKeys 定義後啟用
  //     // if (Object.keys(numberKeys).length > 0) {
  //     //   // 调试日志已移除
  //     //   saveCurrentState();
  //     // }
  //   }, 30000); // 30 秒

  //   setAutoSaveInterval(interval);

  //   return () => {
  //     if (interval) {
  //       clearInterval(interval);
  //     }
  //   };
  // }, []); // 暫時移除 numberKeys 依賴

  // 頁面卸載前保存
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // 頁面卸載日誌已移除
      
      // 如果有未保存的變更，顯示警告
      if (pendingSave) {
        event.preventDefault();
        event.returnValue = '您有未保存的變更，確定要離開嗎？';
        return event.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload, { passive: true });

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [pendingSave]);

  // 🔧 修復：組件卸載時保存 - 移除依賴避免循環
  useEffect(() => {
    return () => {
      // MainPage 組件卸載，保存當前狀態
      
      // 清理定時器
      if (autoSaveInterval) {
        clearInterval(autoSaveInterval);
      }
    };
  }, []); // 移除 autoSaveInterval 依賴



  // 緊急恢復音量函數
  const emergencyRestoreVolume = () => {
    // 调试日志已移除
    setVolume(0.7);
    setIsFading(false);
    setVideoFadeOpacity(0);
    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }
  };

  // 處理 enableFadeEffect 變更
  const handleEnableFadeEffectChange = (enabled: boolean) => {
    setEnableFadeEffect(enabled);
    localStorage.setItem('enableFadeEffect', enabled.toString());
  };

  // M button state
  const [mButtonState, setMButtonState] = useState<MButtonState>('M');
  
  // 字幕白板狀態
  const [isSubtitleBoardActive, setIsSubtitleBoardActive] = useState<boolean>(false);
  const [currentSubtitleKey, setCurrentSubtitleKey] = useState<number | null>(null);
  const [subtitleContent, setSubtitleContent] = useState<string>('');
  
  // 🔧 新增：追蹤文字內容來源的狀態
  const [subtitleContentWithSource, setSubtitleContentWithSource] = useState<{
    text: string;
    isFromSRT: boolean;
  }>({ text: '', isFromSRT: false });
  
  // 🔧 新增：同步 subtitleContent 和 subtitleContentWithSource 狀態
  // 注意：只在 text 不匹配時更新，避免覆蓋 isFromSRT 狀態
  useEffect(() => {
    setSubtitleContentWithSource(prev => {
      if (prev.text !== subtitleContent) {
        return {
          ...prev,
          text: subtitleContent
        };
      }
      return prev;
    });
  }, [subtitleContent]);
  
  // ═══════════════════════════════════════════════════════════════════════
  // 💬 字幕同步模組
  // ═══════════════════════════════════════════════════════════════════════
  // 負責：AI 字幕下載、自動填充、SRT 內容管理、字幕快取
  // State: srtContent, isAutoFillMode, hasSRTCache, cachedSRTLanguage, subtitleContent
  // Handlers: handleDownloadCaptions, handleAutoFillNoteFromSRT, handleLanguageConfirm
  // 依賴：數字鍵（填充目標）、時間碼（定位字幕）、Firestore（快取）
  
  // ═══════════════════════════════════════════════════════════════════════
  // 💬 對話框統一管理（19 個 useState → 1 個）
  // ═══════════════════════════════════════════════════════════════════════
  // 📝 說明：將所有對話框狀態整合到單一對象中，簡化狀態管理
  // 優點：(1) 減少 useState 數量  (2) 統一管理  (3) 易於維護
  
  const [dialogs, setDialogs] = useState({
    srt: false,                    // SRT 字幕顯示對話框
    language: false,               // 語言選擇對話框
    srtGeneration: false,          // SRT 生成對話框
    playbackDuration: false,       // 播放時長設定對話框
    abLoopCount: false,            // AB 循環次數對話框
    playbackModeSelection: false,  // 播放模式選擇對話框
    fileSelection: false,          // 檔案選擇對話框
    welcome: false,                // 歡迎對話框
    vbid: false,                   // VBID 對話框
    register: false,               // 註冊對話框
    profile: false,                // 個人資料對話框
    qr: false,                     // QR Code 對話框
    theme: false,                  // 主題設定對話框
    noteRecords: false,            // 筆記記錄對話框
    versionManagement: false,      // 版本管理對話框
    km: false,                     // KM 對話框
    collaboration: false,          // 協作對話框
    auth: false,                   // 認證對話框
    share: false,                  // 分享對話框
  });
  
  // 📌 輔助函數：開啟對話框
  const openDialog = useCallback((name: keyof typeof dialogs) => {
    setDialogs(prev => ({ ...prev, [name]: true }));
  }, []);
  
  // 📌 輔助函數：關閉對話框
  const closeDialog = useCallback((name: keyof typeof dialogs) => {
    setDialogs(prev => ({ ...prev, [name]: false }));
  }, []);
  
  // 📌 輔助函數：切換對話框狀態
  const toggleDialog = useCallback((name: keyof typeof dialogs) => {
    setDialogs(prev => ({ ...prev, [name]: !prev[name] }));
  }, []);
  
  // 🔧 保留：SRT 相關狀態（非對話框開關）
  const [srtContent, setSrtContent] = useState('');
  const [currentVideoId, setCurrentVideoId] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('zh-TW');
  // 🔧 保留：SRT 生成進度相關狀態（非對話框開關）
  const [srtGenerationStatus, setSrtGenerationStatus] = useState<'idle' | 'downloading' | 'processing' | 'transcribing' | 'completed' | 'error'>('idle');
  const [srtGenerationProgress, setSrtGenerationProgress] = useState(0);
  const [srtGenerationMessage, setSrtGenerationMessage] = useState('');
  const [srtGenerationError, setSrtGenerationError] = useState<string | undefined>();
  
  // 🔧 保留：字幕存在狀態（用於 AI 按鈕顯示）
  const [hasSRTCache, setHasSRTCache] = useState(false);
  const [cachedSRTLanguage, setCachedSRTLanguage] = useState<string>('');
  
  // 🔥 新增：檢查狀態的即時標記（ref，避免異步狀態更新延遲）
  const srtCheckInProgressRef = useRef<string>('');
  
  // 🔧 新增：全自動字幕填充狀態
  const [isAutoFillMode, setIsAutoFillMode] = useState(false);
  
  // 🔧 新增：字幕填充按鈕雙擊計時器
  const subtitleButtonClickTimer = useRef<NodeJS.Timeout | null>(null);
  const subtitleButtonClickCount = useRef<number>(0);
  
  // 🔧 新增：AI 按鈕雙擊計時器
  const aiButtonClickTimer = useRef<NodeJS.Timeout | null>(null);
  const aiButtonClickCount = useRef<number>(0);
  
  // 🔧 新增：知識管理相關狀態
  const [videoZones, setVideoZones] = useState<string[]>([]);
  const [videoTags, setVideoTags] = useState<string[]>([]);
  const [videoGroupIds, setVideoGroupIds] = useState<string[]>([]);
  
  // ═══════════════════════════════════════════════════════════════════════
  // 🎨 UI 顯示狀態統一管理（13 個 useState → 1 個）
  // ═══════════════════════════════════════════════════════════════════════
  // 📝 說明：將所有顯示/隱藏控制狀態整合到單一對象中
  // 優點：(1) 減少 useState 數量  (2) 統一管理  (3) 易於維護
  
  const [uiDisplay, setUiDisplay] = useState({
    subtitleFillTooltip: false,      // 字幕填充 Tooltip
    aiTooltip: false,                // AI 按鈕 Tooltip
    caption: false,                  // 字幕顯示
    safetyFrame: false,              // 安全框
    crosshair: localStorage.getItem('uiDisplay.crosshair') !== 'false',  // 十字線
    gridLines: false,                // 網格線
    volumeSlider: false,             // 音量滑桿
    movieMask: localStorage.getItem('uiDisplay.movieMask') === 'true',   // 電影遮罩
    watermarkInOutput: localStorage.getItem('uiDisplay.watermarkInOutput') !== 'false',  // 輸出浮水印
    clock: localStorage.getItem('uiDisplay.clock') !== 'false',           // 時鐘顯示
    currentTime: localStorage.getItem('uiDisplay.currentTime') !== 'false',  // 當前時間
    remainingTime: localStorage.getItem('uiDisplay.remainingTime') !== 'false',  // 剩餘時間
    watermark: false,                // 浮水印
  });
  
  // 📌 輔助函數：顯示 UI 元素
  const showUI = useCallback((name: keyof typeof uiDisplay) => {
    setUiDisplay(prev => ({ ...prev, [name]: true }));
  }, []);
  
  // 📌 輔助函數：隱藏 UI 元素
  const hideUI = useCallback((name: keyof typeof uiDisplay) => {
    setUiDisplay(prev => ({ ...prev, [name]: false }));
  }, []);
  
  // 📌 輔助函數：切換 UI 元素
  const toggleUI = useCallback((name: keyof typeof uiDisplay) => {
    setUiDisplay(prev => ({ ...prev, [name]: !prev[name] }));
  }, []);
  
  // 🔧 保留：字幕下載狀態（非顯示控制）
  const [isDownloadingSubtitles, setIsDownloadingSubtitles] = useState(false);
  const subtitleFillTooltipTimerRef = useRef<NodeJS.Timeout | null>(null);
  const aiTooltipTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasShownTooltipRef = useRef(false); // 記錄是否已顯示過 Tooltip

  // 🔧 新增：當檢測到字幕緩存時，立即啟動 AI 按鈕
  useEffect(() => {
    // 只在第一次檢測到字幕緩存時執行
    if (hasSRTCache && !hasShownTooltipRef.current) {
      // 檢測到字幕緩存，啟動 AI 按鈕
      hasShownTooltipRef.current = true; // 標記已顯示過
      
      // 🔥 優化：立即啟動 AI 按鈕，不延遲
      // 啟動全自動字幕填充功能
      setIsAutoFillMode(true);
      
      // 300ms 後顯示 Tooltip（給用戶一點反應時間）
      const showTimer = setTimeout(() => {
        // 顯示 AI 按鈕 Tooltip
        showUI('aiTooltip');
        
        // 2秒後隱藏 Tooltip
        const hideTimer = setTimeout(() => {
          // 隱藏 AI 按鈕 Tooltip
          hideUI('aiTooltip');
        }, 2000);
        
        aiTooltipTimerRef.current = hideTimer;
      }, 300); // 🔥 從 1000ms 減少到 300ms
      
      return () => {
        clearTimeout(showTimer);
        if (aiTooltipTimerRef.current) {
          clearTimeout(aiTooltipTimerRef.current);
        }
      };
    }
  }, [hasSRTCache]);

  // 添加點擊計時器的 ref
  const checkClickTimer = useRef<NodeJS.Timeout | null>(null);

  const checkClickCount = useRef<number>(0);
  const { id, sessionId } = useParams();

  // Auth and routing
  const { user, isAuthenticated, logout, handleGoogleLogin: authHandleGoogleLogin, handleEmailLogin: authHandleEmailLogin, handleRegister: authHandleRegister, error: authError } = useAuth();
  
  // 在頁面載入和用戶登入後載入 Note 數據
  // 🔧 修復：移除這個 useEffect，避免與 loadVideoData 產生競爭條件
  // useEffect(() => {
  //   if (user && id) {
  //     loadNotesFromFirestore();
  //   }
  // }, [user, id]);

  // 處理同步會話URL - 當sessionId存在時自動加入會話
  useEffect(() => {
    if (sessionId) {
      // 檢測到同步會話URL，自動打開同步播放管理器
      
      // 立即打開同步播放管理器，不延遲
      setSyncManagerOpen(true);
    }
  }, [sessionId]);

  // 🔧 新增：監聽來自 MainLayout 的打開同步管理器事件
  useEffect(() => {
    const handleOpenSyncManagerEvent = () => {
      // 收到打開同步播放管理器事件
      setSyncManagerOpen(true);
    };
    
    const handleOpenImageManagerEvent = () => {
      // 收到打開圖片管理器事件
      setImageManagerOpen(true);
    };
    
    window.addEventListener('openSyncManager', handleOpenSyncManagerEvent);
    window.addEventListener('openImageManager', handleOpenImageManagerEvent);
    
    return () => {
      window.removeEventListener('openSyncManager', handleOpenSyncManagerEvent);
      window.removeEventListener('openImageManager', handleOpenImageManagerEvent);
    };
  }, []); // 移除依賴，直接設置狀態

  // 🆕 監聽字幕緩存檢測結果（從 pages/MainPage.tsx 發送）
  useEffect(() => {
    const handleSRTCacheDetected = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { videoId, hasSRT } = customEvent.detail || {};
      
      // 調試日誌已移除
      
      if (hasSRT) {
        // 調試日誌已移除
        setCurrentVideoId(videoId);
        setHasSRTCache(true);
        setIsAutoFillMode(true); // 立即變紅！
        setCachedSRTLanguage('zh'); // 預設中文
        setSrtContent('_READY_'); // 標記字幕已就緒但尚未載入
        
        // 顯示提示
        setSnackbarMessage('✅ AI 字幕已就緒');
        setSnackbarOpen(true);
        setTimeout(() => {
          setSnackbarOpen(false);
        }, 1000);
      }
    };
    
    window.addEventListener('srtCacheDetected', handleSRTCacheDetected as EventListener);
    
    return () => {
      window.removeEventListener('srtCacheDetected', handleSRTCacheDetected as EventListener);
    };
  }, []);

  
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  
  // 🔧 調試：記錄移動端布局狀態（減少頻率）
  // 调试日志已移除
  //   windowWidth: windowDimensions.width,
  //   windowHeight: windowDimensions.height,
  //   aspectRatio: aspectRatio,
  //   isPortrait: isPortrait,
  //   isMobile: isMobile
  // });
  const initializedRef = useRef(false);

  // ═══════════════════════════════════════════════════════════════════════
  // 🎬 影片播放器模組
  // ═══════════════════════════════════════════════════════════════════════
  // 負責：影片載入、播放控制、進度追蹤、音量管理
  // State: videoUrl, currentTime, duration, isPlaying, volume, playedSeconds
  // Handlers: loadVideo, handlePlayPause, handleProgress, handleDuration
  
  // File and video states
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [videoError, setVideoError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [originalFileName, setOriginalFileName] = useState<string>('');
  const [fileRelativePath, setFileRelativePath] = useState<string>('');
  const [videoMode, setVideoMode] = useState<VideoMode>(null);
  const [videoTitle, setVideoTitle] = useState<string>(''); // 新增：影片標題狀態
  const [initialStartTime, setInitialStartTime] = useState<number | null>(null); // 🔧 新增：初始起始時間（用於 YouTube start 參數）
  
  // Blob URL 管理狀態
  const [currentBlobUrl, setCurrentBlobUrl] = useState<string | null>(null);

  // ═══════════════════════════════════════════════════════════════════════
  // 🎭 PP 播放模式模組
  // ═══════════════════════════════════════════════════════════════════════
  // 負責：PP 模式播放、AB 循環、播放時長設定
  // State: ppMode.isPlaying, ppMode.currentPlayIndex, ppMode.keysToPlay, ppMode.duration
  // Handlers: handlePlaybackDurationConfirm, handleABLoopCountConfirm
  
  // ═══════════════════════════════════════════════════════════════════════
  // 🎭 PP 播放模式統一管理（8 個 useState → 1 個）
  // ═══════════════════════════════════════════════════════════════════════
  // 📝 說明：將所有 PP 播放模式相關狀態整合到單一對象中
  // 優點：(1) 減少 useState 數量  (2) 邏輯集中  (3) 易於維護
  
  const [ppMode, setPPMode] = useState({
    isPlaying: false,                                                          // PP 播放中
    currentPlayIndex: 0,                                                       // 當前播放索引
    keysToPlay: [] as KeyToPlay[],                                            // 待播放的重點列表
    interval: null as NodeJS.Timeout | null,                                  // 播放間隔定時器
    duration: Number(localStorage.getItem('ppPlayDuration')) || 5,            // PP 播放時長
    playbackDuration: Number(localStorage.getItem('playbackDuration')) || 5,  // 播放時長
    abLoopCount: 3 as number | 'infinite',                                    // AB 循環次數
    sharedMode: null as {                                                      // 分享的播放模式
      mode: string;
      selectedKeys: number[];
      duration?: number;
      loopCount?: number | 'infinite';
    } | null,
  });
  
  // 📌 輔助函數：更新 PP 模式狀態
  const updatePPMode = useCallback(<K extends keyof typeof ppMode>(
    key: K,
    value: typeof ppMode[K]
  ) => {
    setPPMode(prev => ({ ...prev, [key]: value }));
  }, []);
  
  // 📌 輔助函數：批量更新 PP 模式狀態
  const updatePPModeMultiple = useCallback((updates: Partial<typeof ppMode>) => {
    setPPMode(prev => ({ ...prev, ...updates }));
  }, []);

  // 🔧 保留：檔案上傳進度狀態（非對話框開關）
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // UI states
  const [tabValue, setTabValue] = useState(3);  // Set to 3 for YouTube mode
  const [currentTab, setCurrentTab] = useState(0);
  const [vbids, setVbids] = useState<VBID[]>([]);

  // 🔧 修復：組件卸載前強制保存 Note 內容（使用useRef捕獲最新值）
  const latestNoteRef = useRef({ currentSubtitleKey, subtitleContent, tabValue, user });
  
  // 🔧 更新ref為最新值
  useEffect(() => {
    latestNoteRef.current = { currentSubtitleKey, subtitleContent, tabValue, user };
  }, [currentSubtitleKey, subtitleContent, tabValue, user]);
  
  // 🔧 組件卸載時保存
  useEffect(() => {
    return () => {
      // MainPage 組件卸載，強制保存 Note 內容
      
      const { currentSubtitleKey: key, subtitleContent: content, tabValue: tab, user: currentUser } = latestNoteRef.current;
      
      // 強制保存當前的 Note 內容 - 只保存到 Firestore，不更新本地狀態
      if (key !== null && content.trim() !== '' && currentUser) {
        // 調試日誌已移除
        
        // 只保存到 Firestore，避免在 cleanup 中調用 setState
        saveNoteToFirestore(key, content, tab).then(() => {
          // 組件卸載前 Note 保存完成
        }).catch((error) => {
          console.error('❌ 組件卸載前 Note 保存失敗:', error);
        });
      }
    };
  }, []); // 空依賴項，只在組件卸載時執行
  const [currentVbids, setCurrentVbids] = useState<VBID[]>([]);
  const [isBookmarkEnabled, setIsBookmarkEnabled] = useState(false);
  const [tabVbids, setTabVbids] = useState<{ [key: number]: VBID[] }>({});
  // 添加 Tab 獨立的數字鍵狀態
  const [tabNumberKeys, setTabNumberKeys] = useState<{ [key: number]: Record<string, NumberKeyState> }>(() => {
    // 初始化所有 Tab 的數字鍵狀態
    const initialTabNumberKeys: { [key: number]: Record<string, NumberKeyState> } = {};
    for (let i = 0; i <= 6; i++) {
      // 內聯初始化函數，避免調用未定義的 getInitialNumberKeyState
      const defaultState: Record<string, NumberKeyState> = {};
      for (let j = 1; j <= 18; j++) { // 使用固定的 maxNumberKeys = 18
        defaultState[j.toString()] = {
          keyNo: j,
          isActive: false,
          isABPoint: false,
          isPickUpState: false,
          timestamp: null,
          timecode: '00:00:00 00',
          videoMode: null,
          frame: null,
          note: '',
          screenshot: '',
          flag: 'off',
          playMode: 'M',
          // MV 模式專用欄位
          mvUrl: '',
          mvTitle: '',
          mvDescription: ''
        };
      }
      initialTabNumberKeys[i] = defaultState;
    }
    return initialTabNumberKeys;
  });
  // 按鈕音效開關
  const [isSoundEnabled, setIsSoundEnabled] = useState<boolean>(
    localStorage.getItem('isSoundEnabled') === 'true' || false
  );

  // 自動還原分享資料的 useEffect
  const restoredFromShare = useRef(false);
  const [isRestoredFromShare, setIsRestoredFromShare] = useState(false);
  const [selectedKey, setSelectedKey] = useState<number | null>(null);
  // 在檔案開頭 hooks 區新增 pendingSeek state
  const [pendingSeek, setPendingSeek] = useState<number | null>(null);

  // 1. 新增 checkpointCount state
  const [checkpointCount, setCheckpointCount] = useState<number>(0);

  // 🔧 新增：待載入的 Firestore ID（用於從 ManagePages 來的本機檔案）
  const [pendingFirestoreLoad, setPendingFirestoreLoad] = useState<string | null>(null);
  
  // 🔧 優化：使用 useMemo 計算 videoId，避免重複計算
  const videoId = useMemo(() => {
    if (videoUrl && (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be'))) {
      const match = videoUrl.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
      return match ? match[1] : undefined;
    }
    return undefined;
  }, [videoUrl]);

  // 🔧 新增：從 Firestore 加載影片的 Zone/Tag/Group 資訊
  useEffect(() => {
    const loadVideoMetadata = async () => {
      if (!videoUrl || !user?.uid) {
        console.log('🔍 loadVideoMetadata 跳過:', { hasVideoUrl: !!videoUrl, hasUser: !!user?.uid });
        return;
      }

      try {
        const videoId = extractYouTubeId(videoUrl);
        if (!videoId) {
          console.log('⚠️ 無法提取 videoId:', videoUrl);
          return;
        }
        
        console.log('🔍 開始載入影片元數據:', videoId);

        // 優先從 sharedVbids 讀取（與 ManagePages 一致）
        const sharedVbidsDocRef = doc(db, 'sharedVbids', videoId);
        const sharedVbidsSnap = await getDoc(sharedVbidsDocRef);

        if (sharedVbidsSnap.exists()) {
          const data = sharedVbidsSnap.data();
          setVideoZones(data.zones || []);
          setVideoTags(data.tags || []);
          setVideoGroupIds(data.groupIds || []);
          
          // 🔧 修復：同時載入 numberKeys 或 vbids 數據
          if (data.numberKeys && Object.keys(data.numberKeys).length > 0) {
            console.log('✅ 從 Firestore 載入 numberKeys:', Object.keys(data.numberKeys).length, '個重點');
            setNumberKeys(data.numberKeys);
          } else if (data.vbids && data.vbids.length > 0) {
            console.log('✅ 從 Firestore 載入 vbids:', data.vbids.length, '個重點');
            const { vbidsToNumberKeys } = await import('../utils/numberKeyUtils');
            const convertedKeys = vbidsToNumberKeys(data.vbids);
            setNumberKeys(convertedKeys);
          }
          
          // 影片元數據已加載 (from sharedVbids)（日誌已移除）
        } else {
          // 如果 sharedVbids 不存在，嘗試從 videos 讀取
          const videosDocRef = doc(db, 'videos', `${user.uid}_${videoId}`);
          const videosSnap = await getDoc(videosDocRef);
          
          if (videosSnap.exists()) {
            const data = videosSnap.data();
            setVideoZones(data.zones || []);
            setVideoTags(data.tags || []);
            setVideoGroupIds(data.groupIds || []);
            
            // 🔧 修復：同時載入 numberKeys 或 vbids 數據
            if (data.numberKeys && Object.keys(data.numberKeys).length > 0) {
              console.log('✅ 從 videos collection 載入 numberKeys:', Object.keys(data.numberKeys).length, '個重點');
              setNumberKeys(data.numberKeys);
            } else if (data.vbids && data.vbids.length > 0) {
              console.log('✅ 從 videos collection 載入 vbids:', data.vbids.length, '個重點');
              const { vbidsToNumberKeys } = await import('../utils/numberKeyUtils');
              const convertedKeys = vbidsToNumberKeys(data.vbids);
              setNumberKeys(convertedKeys);
            }
            
            // 影片元數據已加載 (from videos)（日誌已移除）
          } else {
            // 如果都不存在，重置為空
            setVideoZones([]);
            setVideoTags([]);
            setVideoGroupIds([]);
          }
        }
      } catch (error: any) {
        // 🔧 修復：區分權限錯誤和其他錯誤，優雅處理
        if (error?.code === 'permission-denied') {
          console.warn('⚠️ [影片元數據] Firestore 權限不足（可忽略，不影響主要功能）');
          // 不影響主要功能，只是無法載入標籤/分區，重置為空
          setVideoZones([]);
          setVideoTags([]);
          setVideoGroupIds([]);
        } else {
          console.error('❌ 加載影片元數據失敗:', error);
        }
      }
    };

    loadVideoMetadata();
  }, [videoUrl, user]);

  // （移至 isPlaying 宣告之後）

  // 🔧 新增：自動滾動功能狀態
  const [hasAutoScrolled, setHasAutoScrolled] = useState(false);

  // 🔧 新增：根據影片比例自動滾動函數
  const autoScrollBasedOnVideoRatio = useCallback(() => {
    // 🚀 優化：檢查是否有 fastLoad 標記，如果有則跳過自動滾動
    const state = location.state as any;
    if (state?.fastLoad) {
      // 檢測到 fastLoad 標記，跳過自動滾動
      return;
    }
    
    // 自動滾動函數被調用
    
    // 只在手機直式模式下啟用自動滾動
    const aspectRatio = windowDimensions.width / windowDimensions.height;
    const isMobilePortrait = windowDimensions.width < 800 || (aspectRatio > 1.8 && windowDimensions.height > windowDimensions.width);
    
    // 設備檢查（日誌已移除）
    
    if (!isMobilePortrait || hasAutoScrolled || !videoUrl) {
      // 自動滾動日誌已移除
      return;
    }

    // 🔧 修復：使用更智能的方式獲取影片尺寸
    const tryGetVideoDimensions = () => {
      let videoWidth = 0;
      let videoHeight = 0;

      if (playerRef.current) {
        try {
          const videoElement = playerRef.current.getInternalPlayer() as HTMLVideoElement;
          // 調試日誌已移除
          
          if (videoElement) {
            // 嘗試多種方式獲取尺寸
            videoWidth = videoElement.videoWidth || videoElement.clientWidth || videoElement.offsetWidth || 0;
            videoHeight = videoElement.videoHeight || videoElement.clientHeight || videoElement.offsetHeight || 0;
            
            // 如果還是0，嘗試從 iframe 獲取
            if ((videoWidth === 0 || videoHeight === 0) && videoElement.tagName === 'IFRAME') {
              const iframe = videoElement as unknown as HTMLIFrameElement;
              videoWidth = iframe.clientWidth || iframe.offsetWidth || 0;
              videoHeight = iframe.clientHeight || iframe.offsetHeight || 0;
            }
            
            // 調試日誌已移除
          }
        } catch (error) {
          console.warn('⚠️ 無法獲取影片尺寸:', error);
        }
      } else {
        // playerRef 檢查日誌已移除
      }

      return { videoWidth, videoHeight };
    };

    // 🔧 修復：簡化自動滾動邏輯，直接使用預設滾動
    const attemptAutoScroll = () => {
      // 執行簡化自動滾動
      // 直接使用預設滾動，避免複雜的尺寸檢測
      executeAutoScroll(298); // 預設為 16:9 影片的滾動距離
    };

    // 🔧 新增：執行自動滾動的函數
    const executeAutoScroll = (scrollDistance: number) => {
      // 準備執行自動滾動
      setTimeout(() => {
        const container = document.querySelector('[data-testid="main-content"]') || document.documentElement;
        // 滾動容器已找到
        
        if (container) {
          container.scrollTo({
            top: scrollDistance,
            behavior: 'smooth'
          });
          // 調試日誌已移除
          setHasAutoScrolled(true);
        } else {
          // 滾動容器日誌已移除
        }
      }, 100); // 進一步減少延遲時間
    };

    // 開始嘗試獲取影片尺寸
    attemptAutoScroll();

  }, [videoUrl, hasAutoScrolled]);

  // 🔧 新增：當影片 URL 改變時重置自動滾動狀態和 AI Tooltip 狀態
  useEffect(() => {
    if (videoUrl) {
      setHasAutoScrolled(false);
      hasShownTooltipRef.current = false; // 🔥 重置 AI Tooltip 標記，確保每個影片都能觸發
      // 影片 URL 改變，重置自動滾動狀態
    }
  }, [videoUrl]);

  // 🔧 新增：獲取檔案修改日期的函數
  const getFileAddedDate = async (fileName: string): Promise<string> => {
    if (videoMode === 'VIDEO' && fileName) {
      try {
        const { getFileModificationDate } = await import('../utils/fileManager');
        const fileDate = await getFileModificationDate(fileName);
        // 檔案修改日期日誌已移除
        return fileDate;
      } catch (error) {
        // 檔案修改日期錯誤日誌已移除（使用預設時間）
      }
    }
    return new Date().toISOString();
  };

  // 🔧 新增：載入 Firestore 資料的函數
  const loadFirestoreData = async (firestoreId: string) => {
    // 調試日誌已移除
    
    try {
      // 檢查快取
      const cacheKey = `firestore_${firestoreId}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      const cacheTimestamp = sessionStorage.getItem(`${cacheKey}_timestamp`);
      const now = Date.now();
      const cacheAge = cacheTimestamp ? now - parseInt(cacheTimestamp) : 0;
      const CACHE_DURATION = 30 * 1000;
      
      if (cachedData && cacheAge < CACHE_DURATION) {
        // 調試日誌已移除
        const data = JSON.parse(cachedData);
        
        // 設定影片資料
        if (data.path) {
          setFileRelativePath(data.path);
          setOriginalFileName(data.path);
          
          // 🔧 新方案：智能檔案匹配系統
          if (data.mode === 'VIDEO' && data.path && !data.path.includes('youtube.com') && !data.path.includes('youtu.be')) {
            // 調試日誌已移除
            
            // 標準化檔案名
            const fileName = normalizeFilePath(data.path);
            // 調試日誌已移除
            
            // 使用智能檔案匹配
            const matchResult = await smartFileMatch(fileName);
            
            if (matchResult.success && matchResult.filePath) {
              // 調試日誌已移除
              
              // smartFileMatch 現在直接返回 blob URL
              setVideoUrl(matchResult.filePath);
            } else {
              // 智能檔案匹配日誌已移除
              // 顯示檔案選擇對話框
              openDialog('fileSelection');
            }
          } else {
            // 🔧 修復：使用 loadVideo 函數設定影片 URL，確保字幕內容被正確重置
            loadVideo(data.path, false);
          }
        }
        
        // 設定其他狀態
        if (data.numberKeys) {
          // 調試日誌已移除
          setNumberKeys(data.numberKeys);
          // 🔧 修復：同時更新 tabNumberKeys 以顯示重點
          setTabNumberKeys(prev => {
            const newState = { ...prev };
            // 更新當前 Tab (VIDEO Tab = 2) 的數字鍵狀態
            newState[2] = { ...newState[2], ...data.numberKeys };
            // 調試日誌已移除
            return newState;
          });
        } else {
          // 快取檢查日誌已移除
        }
        if (data.vbids) {
          // 調試日誌已移除
          setCurrentVbids(data.vbids);
        }
        if (data.mode) {
          setVideoMode(data.mode);
        }
        
        return;
      }
      
      // 從 Firestore 載入
      // 調試日誌已移除
      const docRef = doc(db, 'sharedVbids', firestoreId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // 設定影片資料
        if (data.path) {
          setFileRelativePath(data.path);
          setOriginalFileName(data.path);
          
          // 🔧 新方案：智能檔案匹配系統
          if (data.mode === 'VIDEO' && data.path && !data.path.includes('youtube.com') && !data.path.includes('youtu.be')) {
            // 調試日誌已移除
            
            // 標準化檔案名
            const fileName = normalizeFilePath(data.path);
            // 調試日誌已移除
            
            // 使用智能檔案匹配
            const matchResult = await smartFileMatch(fileName);
            
            if (matchResult.success && matchResult.filePath) {
              // 調試日誌已移除
              
              // smartFileMatch 現在直接返回 blob URL
              setVideoUrl(matchResult.filePath);
            } else {
              // 智能檔案匹配日誌已移除
              // 顯示檔案選擇對話框
              openDialog('fileSelection');
            }
          } else {
            // 🔧 修復：使用 loadVideo 函數設定影片 URL，確保字幕內容被正確重置
            loadVideo(data.path, false);
          }
        }
        
        // 設定其他狀態
        if (data.numberKeys) {
          // 調試日誌已移除
          setNumberKeys(data.numberKeys);
          // 🔧 修復：同時更新 tabNumberKeys 以顯示重點
          setTabNumberKeys(prev => {
            const newState = { ...prev };
            // 更新當前 Tab (VIDEO Tab = 2) 的數字鍵狀態
            newState[2] = { ...newState[2], ...data.numberKeys };
            // 調試日誌已移除
            return newState;
          });
        } else {
          // Firestore 檢查日誌已移除
        }
        if (data.vbids) {
          // 調試日誌已移除
          setCurrentVbids(data.vbids);
        }
        if (data.mode) {
          setVideoMode(data.mode);
        }
        
        // 成功日誌已移除
      } else {
        console.error('❌ 找不到 Firestore 文檔:', firestoreId);
      }
    } catch (error) {
      console.error('❌ 載入 Firestore 資料失敗:', error);
    }
  };

  // Share and QR Code states
  const [shareUrl, setShareUrl] = useState('');

  // 動態最大按鈕數量狀態
  const [maxNumberKeys, setMaxNumberKeys] = useState<number>(18);

  // 同步播放相關狀態
  const [syncManagerOpen, setSyncManagerOpen] = useState(false);
  const [isSyncParticipant, setIsSyncParticipant] = useState(false);
  const [syncSessionId, setSyncSessionId] = useState<string>('');
  const [isOnAir, setIsOnAir] = useState(false); // OnAir 狀態
  const [isHost, setIsHost] = useState(false); // 是否為主機
  const [participantCount, setParticipantCount] = useState(0); // 參與者數量

  // 🔧 保留：字幕文字內容（非顯示控制）
  const [currentCaptionText, setCurrentCaptionText] = useState<string>('');
  


  // 🔧 保留：MV 模式主題輸入相關狀態（非對話框開關）
  const [themeInputValue, setThemeInputValue] = useState<string>('');
  const [currentThemeKey, setCurrentThemeKey] = useState<number | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);

  // 🔧 保留：版本和知識管理相關狀態（非對話框開關）
  const [currentVersion, setCurrentVersion] = useState('v1');
  const [versionCreatedAt, setVersionCreatedAt] = useState<string>('');
  const [thumbnailPreview, setThumbnailPreview] = useState<string>('');
  
  // 🔧 保留：觀點管理相關狀態（非對話框開關）
  const [viewpointManagerOpen, setViewpointManagerOpen] = useState<boolean>(false);
  const [currentViewpointDocId, setCurrentViewpointDocId] = useState<string>('');
  const [viewpoints, setViewpoints] = useState<any[]>([]);
  const [currentViewpointName, setCurrentViewpointName] = useState<string>(''); // 當前觀點名稱
  
  // 🔧 保留：協作模式原始文檔資訊（非對話框開關）
  const [originalDocumentInfo, setOriginalDocumentInfo] = useState<{
    userId: string;
    version: string;
    videoId: string;
  } | null>(null);
  const [currentDocumentId, setCurrentDocumentId] = useState<string>('');
  const [collaborationState, setCollaborationState] = useState<{
    hasExistingVersion: boolean;
    isCollaborativeEnabled: boolean;
    isLoading: boolean;
  }>({
    hasExistingVersion: false,
    isCollaborativeEnabled: false,
    isLoading: false
  });

  // 添加 handleTimecodePositionChange 函數
  const handleTimecodePositionChange = (position: string) => setTimecodePosition(position as TimecodePosition);
  
  // 添加 handleSyncManagerClose 和 handleSyncUpdate 函數
  const handleSyncManagerClose = () => {
    setSyncManagerOpen(false);
  };
  
  // 處理同步狀態變化
  const handleSyncStatusChange = (isConnected: boolean, isHostMode: boolean) => {
    // 同步狀態變化
    
    const newIsSyncParticipant = isConnected && !isHostMode;
    // 調試日誌已移除
    setIsSyncParticipant(newIsSyncParticipant);
    isSyncParticipantRef.current = newIsSyncParticipant; // 🔧 同時更新 ref
    // 調試日誌已移除
    setIsHost(isHostMode); // 設置主機狀態
    
    // 連接成功時自動設置 OnAir 為 true
    if (isConnected) {
      // 連接狀態日誌已移除
      setIsOnAir(true);
      // OnAir 狀態日誌已移除
    } else {
      // 連接斷開日誌已移除
      setIsOnAir(false);
      
      // 🔧 新增：當連接斷開時，自動關閉同步播放管理視窗
      if (syncManagerOpen) {
        // 視窗關閉日誌已移除
        setSyncManagerOpen(false);
      }
      
      // 🔧 新增：當同步播放斷開時，自動導航回首頁
      // 導航日誌已移除
      setTimeout(() => {
        navigate('/manage', { 
          state: { 
            currentSection: 'personalHistory', // 瀏覽記錄分頁
            expandedMain: ['home', 'myPoint'] // 確保 myPoint 主項目是展開的
          } 
        });
      }, 1000); // 1秒後導航，讓用戶看到提示訊息
    }
    
    // 狀態設置完成（日誌已移除）
  };

  // 處理參與者數量變化
  const handleParticipantCountChange = (count: number) => {
    // 參與者數量日誌已移除
    setParticipantCount(count);
  };
  
  // 監控 OnAir 狀態變化（使用 useEffect，避免在渲染中輸出日誌）
  useEffect(() => {
    if (isOnAir || isSyncParticipant) {
      // OnAir 標籤狀態更新
    }
  }, [isOnAir, isSyncParticipant, isHost, participantCount]);
  
  // 點擊 OnAir 切換同步狀態
  const handleOnAirClick = () => {
    // 點擊 OnAir
    
    if (isOnAir) {
      // 離線：斷開同步但不結束會話（對主機）或離開會話（對參與者）
      setIsOnAir(false);
      // 離線狀態日誌已移除
      
      // 🔧 新增：當用戶手動點擊OnAir離線時，自動導航回首頁
      // 導航日誌已移除
      setTimeout(() => {
        navigate('/manage', { 
          state: { 
            currentSection: 'personalHistory', // 瀏覽記錄分頁
            expandedMain: ['home', 'myPoint'] // 確保 myPoint 主項目是展開的
          } 
        });
      }, 1000); // 1秒後導航，讓用戶看到離線狀態
    } else {
      // 重新上線：重新啟用同步
      setIsOnAir(true);
      // 上線狀態日誌已移除
    }
  };



  // 重點播放時長選擇 Dialog 確認處理函數
  const handlePlaybackDurationConfirm = (duration: number, selectedKeys?: number[]) => {
    // 用戶選擇播放時長
    
    // 更新播放時長
    updatePPMode('duration', duration);
    updatePPMode('playbackDuration', duration);
    
    // 保存到 localStorage
    localStorage.setItem('ppMode.duration', duration.toString());
    localStorage.setItem('ppMode.playbackDuration', duration.toString());
    
    // 獲取要播放的重點
    if (selectedKeys && selectedKeys.length > 0) {
        // 自選重點播放模式
        // 調試日誌已移除
        
        // 🔧 調試：檢查每個選中重點的狀態
        selectedKeys.forEach(keyNo => {
          const keyState = numberKeys[keyNo.toString()];
          // 調試日誌已移除
        });
        
        ppMode.keysToPlay = Object.entries(numberKeys)
          .filter(([key, keyState]) => {
            const keyNo = Number(key);
            const isSelected = selectedKeys.includes(keyNo);
            // 🔧 修復：允許 timestamp >= 0，包括 00:00:00 00 的重點
            const hasTimestamp = keyState.timestamp !== null && keyState.timestamp !== undefined && keyState.timestamp >= 0;
            const isNotABPoint = !keyState.isABPoint;
            const isActive = keyState.isActive;
            
            // 調試日誌已移除
            
            return isSelected && hasTimestamp && isNotABPoint && isActive;
          })
          .map(([key, keyState]) => ({
            keyNo: Number(key),
            timestamp: keyState.timestamp!,
            isABPoint: keyState.isABPoint,
            isPickUpState: keyState.isPickUpState
          }))
          .sort((a, b) => a.keyNo - b.keyNo); // 按數字鍵順序排序
      } else {
        // 播放所有重點模式
        // 操作日誌已移除
        
        const allKeys = Object.entries(numberKeys)
          .filter(([_, keyState]) => keyState.timestamp !== null && !keyState.isABPoint)
          .map(([key, keyState]) => ({
            keyNo: Number(key),
            timestamp: keyState.timestamp!,
            isABPoint: keyState.isABPoint,
            isPickUpState: keyState.isPickUpState
          }))
          .sort((a, b) => a.keyNo - b.keyNo); // 按數字鍵順序排序

        const firstGreenKey = allKeys.find(key => key.isPickUpState);

        // 如果沒有綠色重點，使用所有重點；如果有綠色重點，從該重點開始
        if (firstGreenKey) {
          const filteredKeys = allKeys.filter(key => key.keyNo >= firstGreenKey.keyNo);
          ppMode.keysToPlay = filteredKeys.sort((a, b) => a.keyNo - b.keyNo);
        } else {
          ppMode.keysToPlay = allKeys;
        }
      }

    // 調試日誌已移除
    // 調試日誌已移除
    // 調試日誌已移除
    
    updatePPMode('currentPlayIndex', 0);
    updatePPMode('keysToPlay', ppMode.keysToPlay);
    
    // 將所有按鈕變回藍色，然後將第一個重點設為綠色
    setNumberKeys(prev => {
      const newState = { ...prev };
      // 先清除所有綠色狀態
      Object.keys(newState).forEach(k => {
        const numKey = Number(k);
        if (!isNaN(numKey) && newState[numKey]?.isPickUpState) {
          newState[numKey] = {
            ...newState[numKey],
            isPickUpState: false
          };
        }
      });
      
      // 將第一個重點設為綠色
      const firstKey = ppMode.keysToPlay[0].keyNo;
      if (!isNaN(firstKey)) {
        newState[firstKey] = {
          ...newState[firstKey],
          isPickUpState: true
        };
      }
      
      return newState;
    });
  };

  // AB 播放循環次數選擇 Dialog 確認處理函數
  const handleABLoopCountConfirm = (loopCount: number | 'infinite') => {
    // 用戶選擇 AB 播放循環次數
    
    // 更新 AB 播放循環次數
    if (loopCount === 'infinite') {
      updatePPMode('abLoopCount', 'infinite');
    } else {
      updatePPMode('abLoopCount', loopCount);
    }
    
    // 保存到 localStorage
    localStorage.setItem('ppMode.abLoopCount', loopCount === 'infinite' ? 'infinite' : loopCount.toString());
    
    // 關閉 Dialog
    closeDialog('abLoopCount');
    
    // 開始 AB 播放（這裡只是設置狀態，實際播放邏輯在 AB 點設置完成後）
    // AB 播放循環次數已設置
  };

  // 解析 URL 中的播放模式參數
  const parsePlaybackModeFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    const keys = urlParams.get('keys');
    const duration = urlParams.get('duration');
    const loopCount = urlParams.get('loopCount');

    if (mode && keys) {
      const selectedKeys = keys.split(',').map(k => parseInt(k.trim())).filter(k => !isNaN(k));
      
      updatePPMode('sharedMode', {
        mode,
        selectedKeys,
        duration: duration ? parseInt(duration) : undefined,
        loopCount: loopCount === 'infinite' ? 'infinite' : (loopCount ? parseInt(loopCount) : undefined)
      });

      // 彈出播放模式選擇 Dialog
      openDialog('playbackModeSelection');
    }
  };

  // 處理播放模式選擇確認
  const handlePlaybackModeSelectionConfirm = (playAll: boolean) => {
    if (!ppMode.sharedMode) return;

    const { mode, selectedKeys, duration, loopCount } = ppMode.sharedMode;

    if (playAll) {
      // 播放所有重點
      if (mode === 'PB') {
        // 設置為 PB 模式，播放所有重點
        setMButtonState('PB');
        updatePPMode('duration', duration || 5);
        // 彈出播放時長選擇 Dialog
        openDialog('playbackDuration');
      } else if (mode === 'AB') {
        // 設置為 AB 模式，無限循環
        setMButtonState('AB');
        updatePPMode('abLoopCount', 'infinite');
        // 開始 AB 播放
        // 狀態追蹤日誌已移除
      }
    } else {
      // 按原設定播放
      if (mode === 'PB') {
        // 設置為 PB 模式，只播放選中的重點
        setMButtonState('PB');
        updatePPMode('duration', duration || 5);
        
        // 直接開始播放選中的重點
        ppMode.keysToPlay = Object.entries(numberKeys)
          .filter(([key, keyState]) => 
            selectedKeys.includes(Number(key)) && 
            keyState.timestamp !== null && 
            !keyState.isABPoint
          )
          .map(([key, keyState]) => ({
            keyNo: Number(key),
            timestamp: keyState.timestamp!,
            isABPoint: keyState.isABPoint,
            isPickUpState: keyState.isPickUpState
          }))
          .sort((a, b) => a.keyNo - b.keyNo);

        if (ppMode.keysToPlay.length > 0) {
          // 使用現有的播放邏輯
          updatePPMode('currentPlayIndex', 0);
          updatePPMode('keysToPlay', ppMode.keysToPlay);
          // 開始處理（日誌已移除）
        }
      } else if (mode === 'AB') {
        // 設置為 AB 模式，設置循環次數
        setMButtonState('AB');
        updatePPMode('abLoopCount', loopCount || 3);
        // 開始 AB 播放
        // 調試日誌已移除
      }
    }

    // 清除分享的播放模式
    updatePPMode('sharedMode', null);
  };
  
  // 追蹤是否已載入過影片（避免重複載入）
  const syncVideoLoadedRef = useRef<string | null>(null);
  // 🔧 追蹤最後同步的時間，避免重複 seekTo
  const lastSyncTimeRef = useRef<number>(0);
  // 🔧 追蹤最後的播放狀態，避免閉包問題
  const lastIsPlayingRef = useRef<boolean>(false);
  // 🔧 追蹤參與者狀態，避免閉包問題
  const isSyncParticipantRef = useRef<boolean>(false);
  
  const handleSyncUpdate = (syncData: any) => {
    // 調試日誌已移除
    // 調試日誌已移除
    // 調試日誌已移除
    // 狀態變化（日誌已移除）
    // 狀態變化（日誌已移除）
    // 調試日誌已移除
    
    // 🔧 檢查是否為參與者模式（使用 ref 避免閉包問題）
    const currentIsSyncParticipant = isSyncParticipantRef.current;
    // 調試日誌已移除
    
    if (!currentIsSyncParticipant) {
      // 同步模式日誌已移除
      return;
    }
    
    // 🔧 移除 OnAir 檢查，讓參與者始終能接收同步更新
    // OnAir 只用於顯示狀態，不影響數據接收
    // if (isSyncParticipant && !isOnAir && syncData.videoUrl) {
    // 调试日志已移除
    //   return;
    // }
    
    try {
      // 同步影片URL（只在第一次或影片切換時載入）
      if (syncData.videoUrl && syncData.videoUrl !== syncVideoLoadedRef.current) {
        // 調試日誌已移除
        // 狀態追蹤日誌已移除
        setVideoUrl(syncData.videoUrl);
        loadVideo(syncData.videoUrl);
        syncVideoLoadedRef.current = syncData.videoUrl;
      } else if (!syncData.videoUrl) {
        // 狀態追蹤日誌已移除
      } else {
        // 狀態追蹤日誌已移除
      }
      
      // 🔧 修復：檢查是否有重點切換（通過檢查 numberKeys 中的 pickup 狀態）
      const hasPickupStateChange = syncData.numberKeys && (() => {
        // 檢查同步數據中是否有任何鍵的 isPickUpState 為 true
        const syncPickupKeys = Object.entries(syncData.numberKeys)
          .filter(([_, state]: [string, any]) => state?.isPickUpState === true)
          .map(([k]) => Number(k));
        
        // 檢查當前 numberKeys 中的 pickup 狀態
        const currentPickupKeys = Object.entries(numberKeys)
          .filter(([_, state]: [string, any]) => state?.isPickUpState === true)
          .map(([k]) => Number(k));
        
        // 如果 pickup 鍵不同，說明有重點切換
        const hasChange = JSON.stringify(syncPickupKeys.sort()) !== JSON.stringify(currentPickupKeys.sort());
        
        if (hasChange) {
          // 調試日誌已移除
        }
        
        return hasChange;
      })();
      
      // 同步播放時間（使用 ref 追蹤，避免閉包問題）
      if (syncData.currentTime !== undefined) {
        const timeDiff = Math.abs(syncData.currentTime - lastSyncTimeRef.current);
        
        // 🔧 修復：當有重點切換時，強制同步時間（降低閾值或移除閾值）
        const shouldSync = hasPickupStateChange || timeDiff > 0.2;
        
        if (shouldSync) {
          // 調試日誌已移除
          if (playerRef.current) {
            playerRef.current.seekTo(syncData.currentTime);
            // 🔧 立即更新 ref，避免重複觸發
            lastSyncTimeRef.current = syncData.currentTime;
            setPlayedSeconds(syncData.currentTime);
          }
        } else {
          // 時間差異日誌已移除
          // 🔧 即使不跳轉，也更新 ref 以保持同步
          lastSyncTimeRef.current = syncData.currentTime;
        }
      }
      
      // 同步播放狀態（直接執行，不比較狀態，讓播放器自己處理重複命令）
      if (syncData.isPlaying !== undefined) {
        const lastIsPlaying = lastIsPlayingRef.current;
        // 狀態變化（日誌已移除）
        
        // 🔧 先更新 ref，無論播放器是否準備好
        lastIsPlayingRef.current = syncData.isPlaying;
        
        // 🔧 如果播放器已準備好，執行命令
        if (playerRef.current) {
          if (syncData.isPlaying) {
            // 播放命令日誌已移除
            
            // 🔧 優化：延遲執行播放命令，確保 seekTo 完成緩衝
            setTimeout(() => {
              if (playerRef.current && lastIsPlayingRef.current) {
                try {
                  const internalPlayer = playerRef.current.getInternalPlayer();
                  if (internalPlayer?.playVideo) {
                    internalPlayer.playVideo();
                    // 成功日誌已移除
                    
                    // 🔧 添加重試機制，確保播放成功
                    setTimeout(() => {
                      if (playerRef.current && lastIsPlayingRef.current) {
                        const player = playerRef.current.getInternalPlayer();
                        const playerState = player?.getPlayerState?.();
                        // 調試日誌已移除
                        
                        // 如果不是播放中(1)或緩衝中(3)，再次嘗試播放
                        if (playerState !== 1 && playerState !== 3) {
                          // 播放器重試日誌已移除
                          player?.playVideo?.();
                        }
                      }
                    }, 500); // 500ms 後檢查播放狀態
                  }
                } catch (error) {
                  console.warn('⚠️ 播放命令執行失敗:', error);
                }
              }
            }, 300); // 300ms 延遲，等待 seekTo 緩衝
          } else {
            // 暫停命令日誌已移除
            try {
              playerRef.current.getInternalPlayer()?.pauseVideo?.();
            } catch (error) {
              console.warn('⚠️ 暫停命令執行失敗:', error);
            }
          }
        } else {
          // 播放器準備狀態日誌已移除
        }
      }
      
      // 🔧 修復：同步數字鍵狀態，確保 pickup 狀態也被同步
      if (syncData.numberKeys) {
        const syncKeysCount = Object.keys(syncData.numberKeys).length;
        const currentKeysCount = Object.keys(numberKeys).length;
        
        // 🔧 檢查 pickup 狀態的變化
        const syncPickupKeys = Object.entries(syncData.numberKeys)
          .filter(([_, state]: [string, any]) => state?.isPickUpState === true)
          .map(([k]) => Number(k));
        const currentPickupKeys = Object.entries(numberKeys)
          .filter(([_, state]: [string, any]) => state?.isPickUpState === true)
          .map(([k]) => Number(k));
        
        // 調試日誌已移除
        
        if (JSON.stringify(syncData.numberKeys) !== JSON.stringify(numberKeys)) {
          // 狀態變化（日誌已移除）
          
          // 🔧 修復：確保同步時保留當前的 tab 上下文
          // 如果是 YouTube Tab，直接更新 numberKeys
          const isYouTubeTab = tabValue === 3;
          if (isYouTubeTab) {
            setNumberKeys(syncData.numberKeys);
          } else {
            // 對於其他 Tab，同步到 tabNumberKeys
            setTabNumberKeys(prev => ({
              ...prev,
              [tabValue]: syncData.numberKeys
            }));
          }
          
          // 🔧 新增：同步 pickup 狀態後，更新 currentSubtitleKey
          if (syncPickupKeys.length > 0) {
            const pickupKey = syncPickupKeys[0]; // 通常只有一個 pickup 鍵
            // 設置狀態（日誌已移除）
            setCurrentSubtitleKey(pickupKey);
            
            // 同步載入該鍵的 Note 內容
            const pickupKeyState = syncData.numberKeys[pickupKey];
            if (pickupKeyState?.note) {
              // 調試日誌已移除
              setSubtitleContent(pickupKeyState.note);
            }
          }
        } else {
          // 狀態變化（日誌已移除）
        }
      } else {
        // 狀態追蹤日誌已移除
      }
    } catch (error) {
      console.error('🔄 同步更新處理失敗:', error);
    }
  };

  // MV 模式主題輸入對話框處理函數
  const handleThemeDialogOpen = (key: number) => {
    setCurrentThemeKey(key);
    // 獲取當前數字鍵的主題（如果有的話）
    const isYouTubeTab = tabValue === 3;
    const currentNumberKeys = isYouTubeTab ? numberKeys : tabNumberKeys[tabValue];
    const currentKeyState = currentNumberKeys[key];
    setThemeInputValue(currentKeyState?.mvTitle || '');
    openDialog('theme');
  };

  const handleThemeDialogClose = () => {
    closeDialog('theme');
    setThemeInputValue('');
    setCurrentThemeKey(null);
    setThumbnailFile(null);
    setThumbnailPreview('');
  };

  const handleThemeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setThemeInputValue(event.target.value);
  };

  const handleThemeSubmit = () => {
    if (themeInputValue.trim()) {
      const isYouTubeTab = tabValue === 3;
      
      // 處理縮圖
      let thumbnailUrl = '';
      if (thumbnailFile) {
        // 這裡可以上傳縮圖到Firebase Storage
        // 暫時使用本地URL作為示例
        thumbnailUrl = URL.createObjectURL(thumbnailFile);
      }
      
      // 獲取當前數字鍵狀態
      const currentNumberKeys = isYouTubeTab ? numberKeys : tabNumberKeys[tabValue];
      
      // 找到所有MV模式的影片
      const mvVideoKeys = Object.entries(currentNumberKeys)
        .filter(([key, state]) => state.isActive && state.mvUrl)
        .map(([key]) => parseInt(key))
        .sort((a, b) => a - b);
      
      if (isYouTubeTab) {
        setNumberKeys(prev => {
          const newState = { ...prev };
          // 為所有MV影片設置相同的主題
          mvVideoKeys.forEach(key => {
            newState[key] = {
              ...newState[key],
              mvTitle: themeInputValue.trim(),
              mvThumbnail: thumbnailUrl
            };
          });
          return newState;
        });
      } else {
        setTabNumberKeys(prev => {
          const newState = { ...prev };
          newState[tabValue] = { ...newState[tabValue] };
          // 為所有MV影片設置相同的主題
          mvVideoKeys.forEach(key => {
            newState[tabValue][key] = {
              ...newState[tabValue][key],
              mvTitle: themeInputValue.trim(),
              mvThumbnail: thumbnailUrl
            };
          });
          return newState;
        });
      }
      
      setSnackbarMessage(`已為MV系列設置主題：${themeInputValue.trim()}`);
      setSnackbarOpen(true);
      handleThemeDialogClose();
    }
  };

  const handleThumbnailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setThumbnailPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };


  useEffect(() => {
    const shared = localStorage.getItem('sharedVideoData');
    if (shared) {
      try {
        const parsed = JSON.parse(shared);
        // 還原影片網址
        if (parsed.path) {
          // 對於 YouTube URL，先進行過濾處理
          let processedUrl = parsed.path;
          if (parsed.path.includes('youtube.com') || parsed.path.includes('youtu.be')) {
            processedUrl = filterYoutubeUrl(parsed.path);
            // 調試日誌已移除
          }
          setVideoUrl(processedUrl);
        } else if (parsed.videoId) {
          let vid = parsed.videoId;
          if (typeof vid === 'string' && vid.includes('youtube.com')) {
            const match = vid.match(/(?:v=|youtu\.be\/|embed\/|v\/)([a-zA-Z0-9_-]{11})/);
            if (match && match[1]) {
              vid = match[1];
            }
          }
          setVideoUrl(`https://www.youtube.com/watch?v=${vid}`);
        }
        // 還原影片模式
        if (parsed.mode) setVideoMode(parsed.mode);
        // 還原數字鍵（優先用 numberKeys）
        if (parsed.numberKeys && Object.keys(parsed.numberKeys).length > 0) {
          setNumberKeys(parsed.numberKeys);
          // 🔧 修復：移除自動跳轉到第一個重點，讓用戶自己選擇要跳轉的位置
          // const firstKey = Object.values(parsed.numberKeys as { [key: string]: { timestamp?: number } }).find((k) => k.timestamp !== undefined);
          // if (firstKey && firstKey.timestamp !== undefined) setPendingSeek(firstKey.timestamp);
          setIsDeleteMode(false);
          setSelectedKey(null);
          setMButtonState('M');
          // 調試日誌已移除
        } else if (parsed.vbids && parsed.vbids.length > 0) {
          setNumberKeys(vbidsToNumberKeys(parsed.vbids));
          // 🔧 修復：移除自動跳轉到第一個重點，讓用戶自己選擇要跳轉的位置
          // setPendingSeek(parsed.vbids[0].timestamp);
          setIsDeleteMode(false);
          setSelectedKey(null);
          setMButtonState('M');
          // 調試日誌已移除
        } else {
          setNumberKeys(getInitialNumberKeyState(maxNumberKeys));
          setIsDeleteMode(false);
          setSelectedKey(null);
          setMButtonState('M');
          // 初始狀態日誌已移除
        }
        // 還原所有標記資料
        if (parsed.vbids && Array.isArray(parsed.vbids)) {
          setCurrentVbids(parsed.vbids);
        }
        // 還原其他狀態（如 tabValue、mButtonState...）
        if (parsed.tabValue !== undefined) setTabValue(parsed.tabValue);
        if (parsed.mButtonState !== undefined) setMButtonState(parsed.mButtonState);
        // ...可根據需要還原更多欄位...
        restoredFromShare.current = true; // 設定 flag
      } catch (e) {
        console.error('❌ 還原分享資料失敗:', e);
      }
      localStorage.removeItem('sharedVideoData');
    }
  }, []);

  // 初始化 default state，但如果剛剛有還原分享資料就不做
  useEffect(() => {
    if (!restoredFromShare.current) {
      setNumberKeys(getInitialNumberKeyState());
    }
  }, []);

  // 檢查是否從ManagePages進入，設定初始activePanel
  useEffect(() => {
    const state = location.state as any;
    // 如果從ManagePages進入（有fromUpload或其他ManagePages相關狀態），則預設為Note記錄
    if (state?.fromUpload || state?.fromManagePages) {
      setActivePanel(1); // Note記錄
      setSourcePage('manage'); // 記錄來源頁面
      
      // 🔥 新增：處理從 ManagePages 傳遞的字幕狀態（傳統路由模式）
      if (state?.hasSRTCache !== undefined) {
        // 調試日誌已移除
        setHasSRTCache(state.hasSRTCache);
        if (state.hasSRTCache) {
          setIsAutoFillMode(true); // AI 按鈕變紅
          setSrtContent('_READY_'); // 標記有字幕但尚未載入
          // 快速操作日誌已移除
        } else {
          setIsAutoFillMode(false);
          setSrtContent('');
        }
      }
    }
  }, [location.state]);

  // 處理從 OpenPage 或 NotesPage 傳來的狀態
  useEffect(() => {
    const state = location.state as any;
    // 🔧 修復：移除過多的調試日誌，避免性能問題
    // 调试日志已移除
    
    // 🚀 優化：檢查是否需要跳過自動滾動
    if (state?.skipAutoScroll) {
      // 載入日誌已移除
      setHasAutoScrolled(true); // 直接標記為已滾動，避免觸發自動滾動
    }
    
    // 🔧 新增：處理嵌入式模式的導航狀態
    if (isEmbedded && navigationState) {
      // 调试日志已移除
      
      if (navigationState.shouldNavigateToMain) {
        const data = navigationState.navigationData;
        const source = navigationState.source;
        
        // 调试日志已移除
        
        // 設置來源頁面
        setSourcePage(source);
        
        if (source === 'notes' && data.isFromNotesPage && data.vbids && data.jumpToCheckpoint) {
          // 處理從 NotesPage 來的數據
          setIsFromNotesPage(true);
          
          // 將 vbids 轉換為 numberKeys 格式
          const numberKeysFromVbids: Record<string, any> = {};
          data.vbids.forEach((vbid: any) => {
            if (vbid.keyNo) {
              numberKeysFromVbids[vbid.keyNo.toString()] = {
                timestamp: vbid.timestamp || 0,
                timecode: vbid.timecode || '00:00:00 00',
                note: vbid.note || '',
                isActive: vbid.isActive !== false,
                hasNote: !!(vbid.note && vbid.note.trim() !== ''),
                frame: vbid.frame || 0
              };
            }
          });
          
          // 設定 numberKeys 狀態
          setNumberKeys(numberKeysFromVbids);
          
          // 設定目標重點編號為 pickup 狀態
          const targetKeyNumber = data.jumpToCheckpoint;
          setCurrentSubtitleKey(targetKeyNumber);
          
          // 🔧 修復：設置目標重點的 note 內容到字幕白板
          if (numberKeysFromVbids[targetKeyNumber] && numberKeysFromVbids[targetKeyNumber].note) {
            // 調試日誌已移除
            setSubtitleContent(numberKeysFromVbids[targetKeyNumber].note);
          }
          
          if (numberKeysFromVbids[targetKeyNumber]) {
            const updatedNumberKeys = { ...numberKeysFromVbids };
            updatedNumberKeys[targetKeyNumber] = {
              ...updatedNumberKeys[targetKeyNumber],
              isPickUpState: true
            };
            setNumberKeys(updatedNumberKeys);
            
            // 同時更新 tabNumberKeys
            setTabNumberKeys(prev => {
              const newTabNumberKeys = { ...prev };
              if (!newTabNumberKeys[3]) {
                newTabNumberKeys[3] = {};
              }
              newTabNumberKeys[3] = { ...updatedNumberKeys };
              return newTabNumberKeys;
            });

            // 設置全局 pickup 狀態標記
            (window as any).__checkplay_pickup_state = {
              targetKeyNumber,
              timestamp: Date.now()
            };
            
            // 🔧 新增：嵌入式模式自動翻頁到 pickup 鍵所在的頁面
            const pickupKeyPage = Math.floor((targetKeyNumber - 1) / 6);
            // 調試日誌已移除
            setKeySetIndex(pickupKeyPage);
            
            // 自動跳轉到對應的時間碼
            const targetKeyState = updatedNumberKeys[targetKeyNumber];
            if (targetKeyState && targetKeyState.timestamp !== null && targetKeyState.timestamp !== undefined) {
              const timestampValue = convertTimestampToNumber(targetKeyState.timestamp);
              // 🔧 修復：擴大時間範圍以支持長時間直播影片（最多支持約 278 小時）
              if (timestampValue >= 0 && timestampValue <= 1000000) {
                // 调试日志已移除
                
                setTimeout(() => {
                  if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
                    playerRef.current.seekTo(timestampValue);
                    setIsPlaying(true);
                    
                    // 🔧 修復：移除強制播放邏輯，避免事件循環
                    // setTimeout(() => {
                    //   const internalPlayer = playerRef.current?.getInternalPlayer?.();
                    //   if (internalPlayer && typeof internalPlayer.playVideo === 'function') {
                    //     try {
                    //       const playerState = internalPlayer.getPlayerState?.();
                    //       if (playerState !== undefined && playerState !== null) {
                    //         internalPlayer.playVideo();
                    //         // 開始處理（日誌已移除）
                    //       }
                    //     } catch (error) {
                    //       console.warn('⚠️ 嵌入式模式 YouTube playVideo 調用失敗:', error);
                    //     }
                    //   }
                    // }, 500);
                  }
                }, 100);
              }
            }
          }
          
          // 🔧 新增：調用 loadVideo 以觸發字幕緩存檢查（跳過重置數字鍵）
          const videoUrl = data.videoUrl || `https://www.youtube.com/watch?v=${data.videoId}`;
          loadVideo(videoUrl, true).then(() => {
            // 調試日誌已移除
          }).catch((error) => {
            console.error('🔧 嵌入式模式（NotesPage）：loadVideo 調用失敗:', error);
          });
          
          // 清除導航狀態
          if (onStateChange) {
            onStateChange({
              shouldNavigateToMain: false,
              navigationData: null,
              source: ''
            });
          }
        }
      }
    }
    
    // 處理從 OpenPage 來的 YouTube URL
    if (state?.videoUrl && state?.source === 'youtube') {
      // 調試日誌已移除
      // 調試日誌已移除
      
      // 🔧 修復：確保設定 firestoreId，讓觀看記錄能正確更新
      if (state?.documentId) {
        // 調試日誌已移除
        setCurrentFirestoreId(state.documentId);
      } else {
        // 如果沒有 documentId，嘗試從 videoUrl 提取 videoId 作為 firestoreId
        const videoId = youtubeApi.extractVideoId(state.videoUrl);
        if (videoId) {
          // 調試日誌已移除
          setCurrentFirestoreId(videoId);
        }
      }
      
      // 🔧 修復：使用 loadVideo 函數設定影片 URL，確保字幕內容被正確重置
      loadVideo(state.videoUrl, false);
      
      // 設定為 YouTube 模式
      setVideoMode('YouTube');
      
      // 🔧 新增：處理從 NotesPage 傳來的 vbids 數據
      if (state?.vbids && Array.isArray(state.vbids)) {
        // 調試日誌已移除
        
        
        // 將 vbids 轉換為 numberKeys 格式
        const numberKeysFromVbids: Record<string, any> = {};
        state.vbids.forEach((vbid: any) => {
          if (vbid.keyNo) {
            numberKeysFromVbids[vbid.keyNo.toString()] = {
              timestamp: vbid.timestamp || 0,
              timecode: vbid.timecode || '00:00:00 00',
              note: vbid.note || '',
              isActive: vbid.isActive || true,
              hasNote: !!(vbid.note && vbid.note.trim() !== ''),
              frame: vbid.frame || 0
            };
          }
        });
        
        // 調試日誌已移除
        
        // 設定 numberKeys 狀態
        setNumberKeys(numberKeysFromVbids);
        
        // 如果有目標重點編號，設定為 pickup 狀態並自動跳轉
        if (state?.jumpToCheckpoint) {
          // 調試日誌已移除
          setCurrentSubtitleKey(state.jumpToCheckpoint);
          
          // 🔧 修復：設置目標重點的 note 內容到字幕白板
          const targetKeyNumber = state.jumpToCheckpoint;
          if (numberKeysFromVbids[targetKeyNumber] && numberKeysFromVbids[targetKeyNumber].note) {
            // 調試日誌已移除
            setSubtitleContent(numberKeysFromVbids[targetKeyNumber].note);
          }
          
          // 🔧 新增：設置對應數字鍵的 pickup 狀態
          const updatedNumberKeys = { ...numberKeysFromVbids };
          
          if (updatedNumberKeys[targetKeyNumber]) {
            updatedNumberKeys[targetKeyNumber] = {
              ...updatedNumberKeys[targetKeyNumber],
              isPickUpState: true
            };
            // 設置狀態（日誌已移除）
            // 調試日誌已移除
            setNumberKeys(updatedNumberKeys);
            
            // 🔧 新增：自動翻頁到 pickup 鍵所在的頁面
            const pickupKeyPage = Math.floor((targetKeyNumber - 1) / 6);
            // 調試日誌已移除
            setKeySetIndex(pickupKeyPage);
            
            // 🔧 新增：自動跳轉到對應的時間碼
            const targetKeyState = updatedNumberKeys[targetKeyNumber];
            if (targetKeyState && targetKeyState.timestamp !== null && targetKeyState.timestamp !== undefined) {
              const timestampValue = convertTimestampToNumber(targetKeyState.timestamp);
              // 🔧 修復：擴大時間範圍以支持長時間直播影片（最多支持約 278 小時）
              if (timestampValue >= 0 && timestampValue <= 1000000) {
                // 調試日誌已移除
                
                // 延遲執行跳轉，確保播放器已準備好
                setTimeout(() => {
                  if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
                    seekToWithFade(timestampValue, 300).then(() => {
                      // 操作完成（日誌已移除）
                      setSnackbarMessage(`已從筆記跳轉到 NO ${targetKeyNumber} (${Math.floor(timestampValue)} 秒)`);
                      setSnackbarOpen(true);
                    });
                  }
                }, 2000); // 延遲 2 秒確保影片載入完成
              }
            }
          }
        }
      }
      
      // 如果有 YouTube 詳細資訊，設定相關狀態
      if (state.youtubeInfo) {
        // 調試日誌已移除
        // 設定影片標題
        if (state.youtubeInfo.title) {
          setVideoTitle(state.youtubeInfo.title);
          // 調試日誌已移除
        }
      }
      
      // 🔧 修復：設置 documentId 到 currentFirestoreId（傳統路由模式）
      if (state?.documentId && currentFirestoreId !== state.documentId) {
        setCurrentFirestoreId(state.documentId);
        // 調試日誌已移除
      }
      
      // 自動切換到 YouTube Tab
      setTabValue(3);
      
      // 🔧 新增：調用 loadVideo 以觸發字幕緩存檢查（跳過重置數字鍵）
      loadVideo(state.videoUrl, true).then(() => {
        // 調試日誌已移除
      }).catch((error) => {
        console.error('🔧 從 OpenPage 導航：loadVideo 調用失敗:', error);
      });
      
      // 成功日誌已移除
    } else if (state?.fromManagePages && state?.firestoreId && state?.fileRelativePath) {
      // 🔧 修復：處理從 ManagePages 來的本機檔案
      // 調試日誌已移除
      
      // 設定檔案相關狀態
      setFileRelativePath(state.fileRelativePath);
      setOriginalFileName(state.fileRelativePath);
      
      // 設定為 VIDEO 模式
      setVideoMode('VIDEO');
      
      // 自動切換到 VIDEO Tab (index 2)
      setTabValue(2);
      
      // 🔧 修復：直接嘗試智能檔案匹配，成功則直接播放
      // 調試日誌已移除
      
      const trySmartMatchAndPlay = async () => {
        try {
          const fileName = state.fileRelativePath;
          const matchResult = await smartFileMatch(fileName);
          
          if (matchResult.success && matchResult.filePath) {
            // 成功日誌已移除
            
            // 🔧 修復：使用 loadVideo 函數設定影片 URL，確保字幕內容被正確重置
            loadVideo(matchResult.filePath, false);
            loadFirestoreData(state.firestoreId);
            
            // 成功日誌已移除
          } else {
            // 智能匹配日志已移除
            
            // 創建一個假的 File 對象來觸發檔案選擇對話框
            const mockFile = new File([''], state.fileRelativePath, { 
              type: 'video/mp4',
              lastModified: new Date().getTime()
            });
            setSelectedFile(mockFile);
            openDialog('fileSelection');
            
            // 成功日誌已移除
          }
        } catch (error) {
          console.error('❌ 智能檔案匹配過程出錯:', error);
          
          // 出錯時顯示檔案選擇對話框
          const mockFile = new File([''], state.fileRelativePath, { 
            type: 'video/mp4',
            lastModified: new Date().getTime()
          });
          setSelectedFile(mockFile);
          openDialog('fileSelection');
        }
      };
      
      // 執行智能檔案匹配
      trySmartMatchAndPlay();
    } else if (state?.localFile && state?.source === 'local') {
      // 調試日誌已移除
      
      // 設定選中的檔案
      setSelectedFile(state.localFile);
      
      // 創建本地播放 URL
      const url = URL.createObjectURL(state.localFile);
      setVideoUrl(url);
      setFileRelativePath(state.localFile.name);
      setOriginalFileName(state.localFile.name);
      
      // 設定為 VIDEO 模式
      setVideoMode('VIDEO');
      
      // 🔧 修復：自動切換到 VIDEO Tab (index 2)
      setTabValue(2);
      
      // 成功日誌已移除
    } else if (state?.googleDriveUrl && state?.fromCloudUpload) {
      // 🔧 新增：處理從雲端上傳來的檔案
      // 雲端上傳日誌已移除
      // 調試日誌已移除
      
      // 設定影片 URL（添加 ?alt=media 參數以確保可以訪問）
      const videoUrl = state.googleDriveUrl.includes('?alt=media') 
        ? state.googleDriveUrl 
        : `${state.googleDriveUrl}?alt=media`;
      setVideoUrl(videoUrl);
      setFileRelativePath(state.fileRelativePath || state.googleDrivePath || state.googleDriveTitle);
      setOriginalFileName(state.googleDriveTitle);
      
      // 🔧 修復：設置 firestoreId 以確保觀看記錄正確
      if (state.firestoreId) {
        // 調試日誌已移除
        setCurrentFirestoreId(state.firestoreId);
        
        // 🔧 關鍵修復：立即載入重點數據（繞過 loadVideoData）
        const loadCloudFileCheckpoints = async () => {
          try {
            // 調試日誌已移除
            
            // 導入必要的 Firebase 函數
            const { doc, getDoc } = await import('firebase/firestore');
            const { getDb } = await import('../services/firebase');
            
            const db = getDb();
            if (!db) {
              console.error('❌ Firestore 未初始化');
              return;
            }
            
            const docRef = doc(db, 'sharedVbids', state.firestoreId);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
              const data = docSnap.data();
              const vbids = data.vbids || [];
              
              // 調試日誌已移除
              
              // 🔧 調試：檢查 vbids 結構
              if (vbids.length > 0) {
                // 調試日誌已移除
                // 調試日誌已移除
              }
              
              // 轉換為 numberKeys 格式
              const vbidsToNumberKeys = (vbids: any[]) => {
                const keys: { [key: string]: any } = {};
                vbids.forEach((vbid, index) => {
                  // 調試日誌已移除
                  
                  // 🔧 修復：支援多種時間戳格式
                  const timestamp = vbid.timeStamp || vbid.timestamp || vbid.time;
                  // 🔧 關鍵修復：使用 keyNo 作為鍵，而不是時間戳
                  const keyNo = vbid.keyNo || (index + 1);
                  
                  if (vbid && timestamp !== undefined && keyNo) {
                    const key = keyNo.toString();
                    keys[key] = {
                      keyNo: keyNo,
                      time: timestamp,
                      timestamp: timestamp,
                      note: vbid.note || '',
                      isActive: true,  // 🔧 關鍵修復：設置為 true 讓按鈕變藍色
                      isPickUpState: false,
                      isABPoint: false,
                      flag: vbid.flag || 'on',
                      timecode: vbid.timecode || '00:00:00 00',
                      mediaMode: vbid.mediaMode || 'VIDEO',
                      playMode: vbid.playMode || null,
                      frame: vbid.frame || null,
                      screenshot: vbid.screenshot || '',
                      vbid: vbid
                    };
                    // 調試日誌已移除
                  } else {
                    console.warn(`⚠️ vbid[${index}] 缺少必要資料:`, { vbid, timestamp, keyNo });
                  }
                });
                return keys;
              };
              
              const numberKeysFromVbids = vbidsToNumberKeys(vbids);
              
              // 調試日誌已移除
              
              // 更新狀態
              setCurrentVbids(vbids);
              setNumberKeys(numberKeysFromVbids);
              setTabNumberKeys(prev => ({ ...prev, 2: numberKeysFromVbids })); // Tab 2 是 Video Tab
              
              // 按鈕顯示日誌已移除
            } else {
              // 重點記錄日誌已移除
            }
          } catch (error) {
            console.error('❌ 載入雲端檔案重點失敗:', error);
          }
        };
        
        // 延遲執行以確保狀態已設置
        setTimeout(() => {
          loadCloudFileCheckpoints();
        }, 500);
      } else {
        console.warn('⚠️ 雲端檔案缺少 firestoreId，觀看記錄可能無法正確記錄');
      }
      
      // 設定為 VIDEO 模式（使用 CheckPlay 播放器）
      setVideoMode('VIDEO');
      
      // 自動切換到 VIDEO Tab (index 2)
      setTabValue(2);
      
      // 設定來源頁面
      setSourcePage('cloudUpload');
      
      // 成功日誌已移除
    }
    
    // 🔧 新增：處理從 NotesPage 來的數據
    // 🔧 修復：只有在不是雲端上傳時才處理 NotesPage 數據，避免覆蓋 tabValue
    if (state?.isFromNotesPage && state?.vbids && state?.jumpToCheckpoint && !state?.fromCloudUpload) {
      // 調試日誌已移除
      
      // 🔧 新增：設置來自 NotesPage 的標記，禁用觀看進度對話框
      setIsFromNotesPage(true);
      setSourcePage('notes'); // 記錄來源頁面
      
      // 🔧 修復：使用正確的 vbidsToNumberKeys 轉換函數
      const numberKeysFromVbids = vbidsToNumberKeys(state.vbids);
      // 調試日誌已移除
      
      // 調試日誌已移除
      
      // 設定 numberKeys 狀態
      setNumberKeys(numberKeysFromVbids);
      
      // 設定目標重點編號為 pickup 狀態
      const targetKeyNumber = state.jumpToCheckpoint;
      setCurrentSubtitleKey(targetKeyNumber);
      
      if (numberKeysFromVbids[targetKeyNumber]) {
        const updatedNumberKeys = { ...numberKeysFromVbids };
        updatedNumberKeys[targetKeyNumber] = {
          ...updatedNumberKeys[targetKeyNumber],
          isPickUpState: true
        };
        // 設置狀態（日誌已移除）
        // 調試日誌已移除
        setNumberKeys(updatedNumberKeys);
        
        // 🔧 修復：同時更新 tabNumberKeys 確保 pickup 狀態在所有 tab 中正確顯示
        setTabNumberKeys(prev => {
          const newTabNumberKeys = { ...prev };
          // 確保 YouTube tab (tabValue = 3) 也有正確的 pickup 狀態
          if (!newTabNumberKeys[3]) {
            newTabNumberKeys[3] = {};
          }
          newTabNumberKeys[3] = { ...updatedNumberKeys };
          
          // 調試日誌已移除
          
          return newTabNumberKeys;
        });

        // 🔧 修復：設置全局 pickup 狀態標記，防止被 Firestore 數據覆蓋
        (window as any).__checkplay_pickup_state = {
          targetKeyNumber,
          timestamp: Date.now()
        };
        
        // 自動跳轉到對應的時間碼
        const targetKeyState = updatedNumberKeys[targetKeyNumber];
        if (targetKeyState && targetKeyState.timestamp !== null && targetKeyState.timestamp !== undefined) {
          const timestampValue = convertTimestampToNumber(targetKeyState.timestamp);
          // 🔧 修復：擴大時間範圍以支持長時間直播影片（最多支持約 278 小時）
          if (timestampValue >= 0 && timestampValue <= 1000000) {
            // 調試日誌已移除
            
            // 🔧 關鍵修復：區分 YouTube 和雲端檔案，使用不同的跳轉策略
            const isCloudFile = videoMode === 'VIDEO' || state.mode === 'VIDEO' || videoUrl?.includes('storage.googleapis.com');
            // 調試日誌已移除
            
            // 🔧 修復：增加延遲，確保播放器完全準備好（雲端檔案需要更長時間）
            const seekDelay = isCloudFile ? 1500 : 500;
            
            setTimeout(() => {
              if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
                // 直接跳轉
                playerRef.current.seekTo(timestampValue, 'seconds');
                // 調試日誌已移除
                
                // 🔧 修復：雲端檔案和 YouTube 使用不同的播放策略
                if (isCloudFile) {
                  // 雲端檔案：等待跳轉完成後自動播放
                  // 處理日誌已移除
                  setTimeout(() => {
                    setIsPlaying(true);
                    // 處理日誌已移除
                  }, 300);
                } else {
                  // YouTube 影片：立即設置播放狀態
                  setIsPlaying(true);
                  // 調用 playVideo 確保播放
                  setTimeout(() => {
                    const internalPlayer = playerRef.current?.getInternalPlayer?.();
                    if (internalPlayer && typeof internalPlayer.playVideo === 'function') {
                      try {
                        const playerState = internalPlayer.getPlayerState?.();
                        if (playerState !== undefined && playerState !== null) {
                          internalPlayer.playVideo();
                          // 開始處理（日誌已移除）
                        }
                      } catch (error) {
                        console.warn('⚠️ YouTube playVideo 失敗:', error);
                      }
                    }
                  }, 300);
                }
                
                setSnackbarMessage(`已從筆記跳轉到 NO ${targetKeyNumber} (${Math.floor(timestampValue)} 秒)`);
                setSnackbarOpen(true);
              } else {
                console.warn('⚠️ playerRef.current 不可用，等待播放器準備好');
                // 播放器未準備好，等待更長時間
                setTimeout(() => {
                  if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
                    playerRef.current.seekTo(timestampValue, 'seconds');
                    setIsPlaying(true);
                    // 調試日誌已移除
                  } else {
                    console.error('❌ 播放器始終未準備好，跳轉失敗');
                  }
                }, 2000); // 等待 2 秒
              }
            }, seekDelay); // 雲端檔案 1.5 秒，YouTube 0.5 秒
          } else {
            console.warn(`⚠️ timestamp 值不合理: ${timestampValue}，跳過跳轉`);
          }
        } else {
          console.warn(`⚠️ 目標數字鍵 ${targetKeyNumber} 沒有有效的 timestamp`);
        }
      } else {
        console.warn(`⚠️ 找不到目標數字鍵 ${targetKeyNumber}`);
      }
      
      // 自動切換到 YouTube Tab
      setTabValue(3);
      
      // 成功日誌已移除
    }
  }, [location.state, isEmbedded]); // 🔧 修復：移除 navigationState 依賴避免無限循環

  // 🔧 修改：從 Firestore 獲取影片標題
  useEffect(() => {
    const fetchVideoTitleFromFirestore = async () => {
      if (videoUrl && videoMode === 'YouTube') {
        try {
          const videoId = youtubeApi.extractVideoId(videoUrl);
          if (videoId) {
            // 從 Firestore sharedVbids 集合查詢影片標題
            const docRef = doc(db, 'sharedVbids', videoId);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
              const data = docSnap.data();
              // 優先使用 title 字段，其次使用 youtubeInfo.title，最後使用 youtubeMetadata.title
              const title = data.title || 
                           data.youtubeInfo?.title || 
                           data.youtubeMetadata?.title || 
                           '';
              
              if (title && title !== videoTitle) {
                setVideoTitle(title);
                // 調試日誌已移除
              }
            } else {
              // 記錄日誌已移除
              // 🔧 修復：不在此處重複調用 YouTube API，避免與 loadVideo 函數重複
              // loadVideo 函數已經會調用 YouTube API 獲取標題
            }
          }
        } catch (error) {
          console.warn('⚠️ 從 Firestore 獲取影片標題失敗:', error);
        }
      }
    };

    fetchVideoTitleFromFirestore();
  }, [videoUrl, videoMode, videoTitle]);

  // Firestore 影片自動載入 - 優化快取機制
  useEffect(() => {
    // 調試日誌已移除
    // 調試日誌已移除
    
    // 🔧 新增：檢查傳統導航模式的 location.state（優先處理）
    const locationState = location.state as any;
    if (locationState?.isFromNotesPage && locationState?.vbids && id) {
      // 調試日誌已移除
      
      // 🔧 關鍵修復：設置 videoUrl 和 mode
      if (locationState.videoUrl) {
        setVideoUrl(locationState.videoUrl);
        const targetMode = locationState.mode || 'VIDEO';
        setVideoMode(targetMode);
        setTabValue(targetMode === 'VIDEO' ? 2 : 3);
        // 設置狀態（日誌已移除）
      }
      
      // 🔧 設置 firestoreId
      if (locationState.firestoreId) {
        setCurrentFirestoreId(locationState.firestoreId);
        // 設置狀態（日誌已移除）
      }
      
      // 🔧 關鍵修復：記錄 jumpToCheckpoint 到 ref，等待播放器準備好後執行跳轉
      if (locationState.jumpToCheckpoint) {
        pendingJumpToCheckpointRef.current = locationState.jumpToCheckpoint;
        // 調試日誌已移除
      }
      
      // 使用 vbidsToNumberKeys 转换
      import('../utils/numberKeyUtils').then(({ vbidsToNumberKeys }) => {
        const numberKeysFromVbids = vbidsToNumberKeys(locationState.vbids);
        // 調試日誌已移除
        
        setNumberKeys(numberKeysFromVbids);
        
        // 🔧 同時更新 tabNumberKeys
        setTabNumberKeys(prev => {
          const newState = { ...prev };
          // 根据 mode 判断使用哪个 tab
          const tabIndex = locationState.mode === 'VIDEO' ? 2 : 3;
          newState[tabIndex] = { ...numberKeysFromVbids };
          // 調試日誌已移除
          return newState;
        });
        
        // 🔧 關鍵修復：設置目標重點為 pickup 狀態
        if (locationState.jumpToCheckpoint && numberKeysFromVbids[locationState.jumpToCheckpoint]) {
          const updatedNumberKeys = { ...numberKeysFromVbids };
          updatedNumberKeys[locationState.jumpToCheckpoint] = {
            ...updatedNumberKeys[locationState.jumpToCheckpoint],
            isPickUpState: true
          };
          setNumberKeys(updatedNumberKeys);
          setTabNumberKeys(prev => {
            const newState = { ...prev };
            const tabIndex = locationState.mode === 'VIDEO' ? 2 : 3;
            newState[tabIndex] = { ...updatedNumberKeys };
            return newState;
          });
          // 設置狀態（日誌已移除）
        }
        
        // 成功日誌已移除
      });
      
      // 不需要继续执行后续的 URL 构建逻辑
      // 但不 return，让其他初始化继续
    }
    
    // 🔧 修復：儲存所有 setTimeout ID，以便清理
    const timeoutIds: NodeJS.Timeout[] = [];
    
    // 🔧 修復：處理從 ManagePages 來的本機檔案
    if (pendingFirestoreLoad && !id) {
      // 調試日誌已移除
      
      // 使用 pendingFirestoreLoad 作為 id 來載入 Firestore 資料
      const loadId = pendingFirestoreLoad;
      setPendingFirestoreLoad(null); // 清除待載入狀態
      
      // 直接調用載入邏輯，但使用 loadId 而不是 id
      // 調試日誌已移除
      loadFirestoreData(loadId);
      return;
    }
    
    // 檢查是否為 Google Drive 影片
    if (id && id.startsWith('gdrive_')) {
      // 調試日誌已移除
      const googleDriveFileId = id.replace('gdrive_', '');
      // 調試日誌已移除
      
      const state = location.state as any;
      // 調試日誌已移除
      
      if (state?.googleDriveFile) {
        const { id: fileId, name } = state.googleDriveFile;
        // 調試日誌已移除
        
        // 設定影片 URL 和模式 - 優先使用 preview 模式（最穩定，無權限問題）
        const googleDriveUrls = [
          // 優先使用 preview 連結（最穩定，已確認可用，無權限問題）
          `https://drive.google.com/file/d/${fileId}/preview?usp=sharing`,
          `https://drive.google.com/file/d/${fileId}/preview?usp=drivesdk`,
          `https://drive.google.com/file/d/${fileId}/preview`,
          // 備用 preview 連結（resourcekey 參數）
          `https://drive.google.com/file/d/${fileId}/preview?usp=drivesdk&resourcekey=`,
          // 注意：完全移除 view 模式，因為：
          // 1. view 模式會導致 403 Forbidden 權限錯誤
          // 2. preview 模式最穩定，無權限問題
          // 3. 只使用 preview 相關的模式
        ];
        
        // 調試日誌已移除
        
        // 設定第一個連結作為預設
        setVideoUrl(googleDriveUrls[0]);
        
        // 儲存所有連結供錯誤處理使用
        if (typeof window !== 'undefined') {
          (window as any).googleDriveUrls = googleDriveUrls;
          (window as any).currentGoogleDriveUrlIndex = 0;
        }
        
        // 設定為 Google 模式，強制使用 Google Tab 的 iframe
        setVideoMode('Google');
        
        // 自動切換到 Google Tab
        if (typeof setTabValue === 'function') {
          setTabValue(1); // Google Tab 的索引 (index 1)
          // 狀態追蹤日誌已移除
        }
        
        // 成功日誌已移除
        
        // 檢查檔案權限設定
        console.warn('⚠️ Google Drive 影片無法播放，請檢查檔案權限設定（設為「任何有連結的人都能查看」）');
        
        return; // 跳過 Firestore 查詢
      } else {
        // 查找失败日志已移除
        // 調試日誌已移除
        
        // 備用方案：直接從 URL 構建 Google Drive URL（優先使用 preview 模式，無權限問題）
        const googleDriveUrls = [
          // 優先使用 preview 連結（最穩定，已確認可用，無權限問題）
          `https://drive.google.com/file/d/${googleDriveFileId}/preview?usp=sharing`,
          `https://drive.google.com/file/d/${googleDriveFileId}/preview?usp=drivesdk`,
          `https://drive.google.com/file/d/${googleDriveFileId}/preview`,
          // 備用 preview 連結（resourcekey 參數）
          `https://drive.google.com/file/d/${googleDriveFileId}/preview?usp=drivesdk&resourcekey=`,
          // 注意：完全移除 view 模式，因為：
          // 1. view 模式會導致 403 Forbidden 權限錯誤
          // 2. preview 模式最穩定，無權限問題
          // 3. 只使用 preview 相關的模式
        ];
        
        // 調試日誌已移除
        
        // 設定第一個連結作為預設
        setVideoUrl(googleDriveUrls[0]);
        
        // 設定為 Google 模式，強制使用 Google Tab 的 iframe
        setVideoMode('Google');
        
        // 自動切換到 Google Tab
        if (typeof setTabValue === 'function') {
          setTabValue(1); // Google Tab 的索引 (index 1)
          // 狀態追蹤日誌已移除
        }
        
        // 成功日誌已移除
        
        return; // 跳過 Firestore 查詢
      }
    }
    
    if (id) {
      let isMounted = true;
      
      // 檢查快取 - 刪除操作後禁用快取，確保即時性
      const cacheKey = `firestore_${id}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      const cacheTimestamp = sessionStorage.getItem(`${cacheKey}_timestamp`);
      const now = Date.now();
      const cacheAge = cacheTimestamp ? now - parseInt(cacheTimestamp) : 0;
      const CACHE_DURATION = 30 * 1000; // 縮短為30秒快取，減少數據不一致問題
      
      // 檢查是否有強制重新載入標記（刪除操作後設置）
      const forceReload = sessionStorage.getItem(`${cacheKey}_force_reload`);
      
      // 如果有強制重新載入標記，跳過快取直接從 Firestore 載入
      if (forceReload) {
        // 狀態追蹤日誌已移除
        sessionStorage.removeItem(`${cacheKey}_force_reload`);
      } else if (cachedData && cacheAge < CACHE_DURATION) {
        // 調試日誌已移除
        try {
          const data = JSON.parse(cachedData);
          
          // 檢查數據一致性：如果 vbids 為空但 checkpointCount 不為 0，強制從 Firestore 重新載入
          const vbidsLength = data.vbids ? data.vbids.length : 0;
          const checkpointCount = data.checkpointCount || 0;
          
          // 加強數據一致性檢查
          const hasInconsistentData = (
            (vbidsLength === 0 && checkpointCount > 0) ||
            (vbidsLength > 0 && checkpointCount === 0) ||
            (vbidsLength !== checkpointCount)
          );
          
          if (hasInconsistentData) {
            // 快取日誌已移除;
            // 清除快取並繼續執行 Firestore 查詢
            sessionStorage.removeItem(cacheKey);
            sessionStorage.removeItem(`${cacheKey}_timestamp`);
          } else if (isMounted) {
                    // 調試日誌已移除
        // 調試日誌已移除
        
        // 批量設定狀態，減少重複渲染
        const updates: any = {};
        if (data.path) {
          // 調試日誌已移除
          // 對於 YouTube URL，先進行過濾處理
          let processedUrl = data.path;
          if (data.path.includes('youtube.com') || data.path.includes('youtu.be')) {
            processedUrl = filterYoutubeUrl(data.path);
            // 調試日誌已移除
          }
          updates.videoUrl = processedUrl;
        }
        if (data.documentId && currentFirestoreId !== data.documentId) {
          // 調試日誌已移除
          setCurrentFirestoreId(data.documentId);
        }
        if (data.mediaMode) {
          // 調試日誌已移除
          updates.videoMode = data.mediaMode;
        }
        if (data.numberKeys) {
          // 調試日誌已移除
          updates.numberKeys = data.numberKeys;
        }
        if (data.vbids) {
          // 標記點日誌已移除;
          updates.vbids = data.vbids;
        }
            
            // 批量更新狀態
            if (updates.videoUrl) setVideoUrl(updates.videoUrl);
            if (updates.videoMode) setVideoMode(updates.videoMode);
            if (data.documentId && currentFirestoreId !== data.documentId) {
              // 調試日誌已移除
              setCurrentFirestoreId(data.documentId);
            }
            
            // 自動還原機制：優先使用 vbids 還原，確保 isActive 狀態正確
            if (updates.vbids && updates.vbids.length > 0) {
              // 調試日誌已移除
              const restoredNumberKeys = vbidsToNumberKeys(updates.vbids);
              // 調試日誌已移除
              
              // 檢查還原的 numberKeys 的 isActive 狀態
              let activeCount = 0;
              Object.entries(restoredNumberKeys).forEach(([key, state]) => {
                const keyState = state as any;
                if (keyState.isActive) {
                  activeCount++;
                }
              });
              // 調試日誌已移除
              
              // 🔧 修復：使用 setTimeout 確保狀態更新順序正確，並儲存 timeoutId 以便清理
              const timeoutId = setTimeout(() => {
                setNumberKeys(restoredNumberKeys);
                // 同步更新 tabNumberKeys
                setTabNumberKeys(prev => {
                  const newState = { ...prev };
                  newState[tabValue] = restoredNumberKeys;
                  // 調試日誌已移除
                  return newState;
                });
              }, 0);
              timeoutIds.push(timeoutId);
            } else if (updates.numberKeys && Object.keys(updates.numberKeys).length > 0) {
              // 調試日誌已移除
              
              // 清理無效的 numberKeys（只保留 isActive: true 且有 timestamp 的鍵）
              const cleanedNumberKeys: Record<string, NumberKeyState> = {};
              Object.entries(updates.numberKeys).forEach(([key, state]) => {
                const keyState = state as any;
                if (keyState.isActive && keyState.timestamp !== null && keyState.timestamp !== undefined) {
                  cleanedNumberKeys[key] = keyState;
                }
              });
              
              // 調試日誌已移除
              // 調試日誌已移除
              
              // 🔧 修復：使用 setTimeout 確保狀態更新順序正確，並儲存 timeoutId 以便清理
              const timeoutId2 = setTimeout(() => {
                setNumberKeys(cleanedNumberKeys);
                // 同步更新 tabNumberKeys
                setTabNumberKeys(prev => {
                  const newState = { ...prev };
                  newState[tabValue] = cleanedNumberKeys;
                  // 調試日誌已移除
                  return newState;
                });
              }, 0);
              timeoutIds.push(timeoutId2);
            }
            
            if (updates.vbids) setVbids(updates.vbids);
          }
        } catch (err) {
          console.error('快取資料解析失敗:', err);
        }
        return;
      }
      
      // 非阻塞查詢 Firestore
      (async () => {
        try {
          // 調試日誌已移除
          // 調試日誌已移除
          
          // 🔧 關鍵修復：使用 ref 檢查是否從 NotesPage 接收到數據（SPA 模式）
          if (isFromNotesPageRef.current) {
            // 成功日誌已移除
            // 成功日誌已移除
            isFromNotesPageRef.current = false; // 重置標記
            // 跳過下面的 URL 構建邏輯，直接返回
            return;
          }
          
          // 🔧 備用檢查 1：從 navigationState 檢查（SPA 模式備用）
          const hasNotesPageData = navigationState?.isFromNotesPage && navigationState?.videoUrl;
          
          if (hasNotesPageData) {
            // 成功日誌已移除
            // 調試日誌已移除
            // 成功日誌已移除
            // 跳過下面的 URL 構建邏輯，直接返回
            return;
          }
          
          // 🔧 備用檢查 2：從 location.state 檢查（傳統導航模式）
          const locationState = location.state as any;
          const hasLocationStateData = locationState?.isFromNotesPage && locationState?.vbids;
          
          if (hasLocationStateData) {
            // 成功日誌已移除
            // 調試日誌已移除
            // 成功日誌已移除
            // 跳過下面的 URL 構建邏輯，直接返回
            return;
          }

          // 修復：當沒有 videoUrl 但有 id 時，構建 YouTube URL
          let currentVideoUrl = videoUrl;
          if (!currentVideoUrl && id && !id.startsWith('gdrive_') && !id.startsWith('local_')) {
            // 🔧 修復：優先檢查是否是分享 URL（路徑包含 /share/）
            const isShareUrl = location.pathname.includes('/share/');
            // 🔧 修復：也檢查 ID 格式（長度 > 11 且不包含下劃線，不是 vid_ 開頭）
            const looksLikeShareId = id.length > 11 && !id.includes('_') && !id.startsWith('vid_');
            
            if (isShareUrl || looksLikeShareId) {
              console.log('🔍 檢測到分享 URL，開始載入分享資料，ID:', id);
              try {
                const { getSharedVbidsById } = await import('../services/firestoreApi');
                const shareData = await getSharedVbidsById(id);
                
                console.log('🔍 分享資料查詢結果:', {
                  hasData: !!shareData,
                  hasPath: !!shareData?.path,
                  hasVbids: !!shareData?.vbids,
                  vbidsCount: shareData?.vbids?.length || 0,
                  videoId: shareData?.videoId,
                  mode: shareData?.mode || shareData?.mediaMode
                });
                
                if (shareData) {
                  // 🔧 修復：優先使用 path，如果沒有則從 videoId 構建
                  if (shareData.path) {
                    currentVideoUrl = shareData.path;
                  } else if (shareData.videoId) {
                    // 檢查 videoId 是否已經是完整 URL
                    if (shareData.videoId.startsWith('http://') || shareData.videoId.startsWith('https://')) {
                      currentVideoUrl = shareData.videoId;
                    } else if (shareData.videoId.startsWith('file_') || shareData.videoId.startsWith('gdrive_')) {
                      currentVideoUrl = shareData.videoId;
                    } else {
                      // 假設是 YouTube ID
                      currentVideoUrl = `https://www.youtube.com/watch?v=${shareData.videoId}`;
                    }
                  }
                  
                  if (currentVideoUrl) {
                    console.log('✅ 設置分享影片 URL:', currentVideoUrl);
                    setVideoUrl(currentVideoUrl);
                    
                    // 🔧 修復：根據資料設置正確的 videoMode
                    const detectedMode = shareData.mode || shareData.mediaMode || 'YouTube';
                    setVideoMode(detectedMode);
                    
                    // 🔧 修復：根據模式設置正確的 Tab
                    switch (detectedMode) {
                      case 'VIDEO':
                      case 'Short':
                        setTabValue(2); // 影片 Tab
                        break;
                      case 'YouTube':
                        setTabValue(3); // YouTube Tab
                        break;
                      case 'Google':
                        setTabValue(1); // Google Drive Tab
                        break;
                      default:
                        setTabValue(3); // 預設 YouTube Tab
                    }
                    
                    // 🔧 關鍵修復：設置正確的 firestoreId
                    const originalFirestoreId = shareData.videoId || shareData.id || shareData._originalDocId || shareData._sharedLink?.videoId;
                    if (originalFirestoreId) {
                      console.log('✅ 設置 firestoreId:', originalFirestoreId);
                      setCurrentFirestoreId(originalFirestoreId);
                    } else {
                      console.warn('⚠️ 無法從分享資料中確定 firestoreId，使用分享 ID:', id);
                      setCurrentFirestoreId(id);
                    }
                    
                    // 🔧 修復：如果有重點資料，直接設置
                    if (shareData.vbids && shareData.vbids.length > 0) {
                      console.log('✅ 還原重點資料，數量:', shareData.vbids.length);
                      const restoredNumberKeys = vbidsToNumberKeys(shareData.vbids);
                      setNumberKeys(restoredNumberKeys);
                      setVbids(shareData.vbids);
                    }
                    
                    // 🔧 修復：如果有標題等資訊，也設置
                    if (shareData.title) {
                      // 可以在這裡設置標題（如果有相關狀態）
                      console.log('📝 分享標題:', shareData.title);
                    }
                    
                    console.log('✅ 分享資料載入完成');
                  } else {
                    console.error('❌ 分享資料中沒有找到有效的影片 URL');
                  }
                } else {
                  console.warn('⚠️ 分享 ID 查詢結果為空，ID:', id);
                }
              } catch (error) {
                console.error('❌ 分享 ID 查詢錯誤:', error);
                console.error('❌ 錯誤詳情:', {
                  message: error instanceof Error ? error.message : 'Unknown error',
                  stack: error instanceof Error ? error.stack : 'No stack trace',
                  id: id
                });
              }
            }
            
            // 如果分享查詢失敗或不是分享 ID，繼續原有邏輯
            if (!currentVideoUrl) {
              // 🔧 新增：處理標準化的文檔 ID 格式（以 vid_ 開頭）
              let youtubeVideoId = id;
              
              if (id.startsWith('vid_')) {
                // 調試日誌已移除
                // 格式：vid_{userId}_{videoId}_{version}
                // 例如：vid_uc9C6sAfsPT743cQApZ4MmZVoU72_j25W8wd2s2M_v1
                const parts = id.split('_');
                if (parts.length >= 3) {
                  // 取第三部分作為 YouTube 影片 ID
                  const extractedId = parts[2];
                  if (extractedId && /^[a-zA-Z0-9_-]{11}$/.test(extractedId)) {
                    youtubeVideoId = extractedId;
                    // 調試日誌已移除
                  }
                }
              }
              // 處理舊的複合格式的 ID，如 uc9C6sAfsPT743cQApZ4MmZVoU72_cHTHYzQ8ErU_v1
              else if (id.includes('_')) {
                const parts = id.split('_');
                // 調試日誌已移除
                
                // 🔧 修復：優先尋找第二部分（通常是 YouTube ID）
                // 對於格式：用戶ID_YouTubeID_版本，第二部分是 YouTube ID
                if (parts.length >= 2) {
                  const candidateId = parts[1];
                  if (candidateId && /^[a-zA-Z0-9_-]{11}$/.test(candidateId)) {
                    youtubeVideoId = candidateId;
                    // 調試日誌已移除
                  }
                  // 🔧 特殊處理：如果第二部分不是完整的 YouTube ID，嘗試合併第二部分和第三部分
                  else if (parts.length >= 3 && candidateId && parts[2]) {
                    const combinedId = `${candidateId}_${parts[2]}`;
                    if (/^[a-zA-Z0-9_-]{11}$/.test(combinedId)) {
                      youtubeVideoId = combinedId;
                      // 調試日誌已移除
                    }
                  }
                }
                
                // 如果第二部分不是有效的 YouTube ID，則遍歷所有部分
                if (!youtubeVideoId) {
                  for (let i = 0; i < parts.length; i++) {
                    const part = parts[i];
                    if (part && /^[a-zA-Z0-9_-]{11}$/.test(part)) {
                      // 驗證這個 ID 是否真的是 YouTube ID（不是用戶 ID）
                      // 用戶 ID 通常更長，YouTube ID 正好是 11 位
                      youtubeVideoId = part;
                      // 調試日誌已移除
                      break;
                    }
                  }
                }
              }
              
              // 構建 YouTube URL
              currentVideoUrl = `https://www.youtube.com/watch?v=${youtubeVideoId}`;
              // 調試日誌已移除
              setVideoUrl(currentVideoUrl);
              setVideoMode('YouTube');
              setTabValue(3); // 切換到 YouTube Tab
            }
          }

          // 使用 loadVideoData 函數載入 YouTube 影片的重點資料
          if (currentVideoUrl) {
            // 調試日誌已移除
            await loadVideoData(currentVideoUrl);
          } else {
            console.log('⚠️ 沒有 videoUrl，無法載入重點資料');
          }

          // 操作完成（日誌已移除）
        } catch (err) {
          console.error('💥 Firestore 載入錯誤:', err);
          console.error('💥 錯誤詳情:', {
            message: err instanceof Error ? err.message : 'Unknown error',
            stack: err instanceof Error ? err.stack : 'No stack trace',
            id: id,
            isMounted: isMounted
          });
          if (isMounted) {
            setVideoError('載入影片資料失敗');
          }
        }
      })();
      
      return () => {
        isMounted = false;
        // 🔧 修復：清理所有 setTimeout，防止記憶體洩漏
        timeoutIds.forEach(timeoutId => clearTimeout(timeoutId));
        // 調試日誌已移除
      };
    }
  }, [id, pendingFirestoreLoad, location.state]); // 🔧 新增：監聽 location.state 變化（傳統導航模式）

  // 監聽 videoUrl 變化 - 優化版本
  useEffect(() => {
    if (videoUrl) {
      // 調試日誌已移除
    }
  }, [videoUrl]);

  // Refs
  const playerRef = useRef<ReactPlayer>(null);
  const videoRef = useRef<ReactPlayerInstance>(null);
  const googleDriveRef = useRef<GoogleDriveIframeRef>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const progressAnimationRef = useRef<number | undefined>(undefined);
  const lastProgressUpdateRef = useRef<number>(0);
  const deletePressTimer = useRef<NodeJS.Timeout | null>(null);
  const abLoopIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pressTimer = useRef<NodeJS.Timeout | null>(null);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle bookmark toggle
  const handleBookmarkToggle = useCallback(() => {
    setIsBookmarkEnabled(!isBookmarkEnabled);
  }, [isBookmarkEnabled]);

  // Safe file path update
  const handleFilePathUpdate = useCallback((path: string) => {
    if (path) {
      setOriginalFileName(path);
      setFileRelativePath(path);
    }
  }, []);

  // 🔧 新增：檔案選擇處理函數
  const handleLocalPlay = () => {
    if (selectedFile) {
      // 調試日誌已移除
      
      // 🔧 修復：檢查是否來自 ManagePages，如果是則先載入 Firestore 資料
      const locationState = location.state as any;
      if (locationState?.fromManagePages && locationState?.firestoreId) {
        // 調試日誌已移除
        
        // 創建本地播放 URL
        const url = URL.createObjectURL(selectedFile);
        setVideoUrl(url);
        setFileRelativePath(selectedFile.name);
        setOriginalFileName(selectedFile.name);
        setVideoMode('VIDEO');
        
        // 載入 Firestore 資料
        // 調試日誌已移除
        loadFirestoreData(locationState.firestoreId);
        // 調試日誌已移除
        
        // 關閉對話框
        closeDialog('fileSelection');
        setSelectedFile(null);
        
        return;
      }
      
      // 🔧 新增：重置所有數字鍵狀態，避免使用前一筆資料的藍色鍵
      // 狀態變化（日誌已移除）
      
      // 重置 numberKeys 狀態
      setNumberKeys({});
      
      // 重置 tabNumberKeys 中 VIDEO Tab (index 2) 的狀態
      setTabNumberKeys(prev => {
        const newState = { ...prev };
        if (newState[2]) {
          // 清空 VIDEO Tab 的所有數字鍵狀態
          newState[2] = {};
        }
        return newState;
      });
      
      // 重置相關狀態
      setCurrentSubtitleKey(null);
      setSubtitleContent('');
      setVbids([]);
      setTabVbids(prev => ({
        ...prev,
        2: [] // 清空 VIDEO Tab 的 vbids
      }));
      
      // 重置播放相關狀態
      setCurrentTime(0);
      setCurrentTimecode('00:00:00 00');
      setIsPlaying(false);
      
      // 重置按鈕狀態
      resetAllButton();
      
      // 創建本地播放 URL
      const url = URL.createObjectURL(selectedFile);
      setVideoUrl(url);
      setFileRelativePath(selectedFile.name);
      setOriginalFileName(selectedFile.name);
      setVideoMode('VIDEO');
      
      // 輸入框會自動顯示，因為已綁定到 fileRelativePath 狀態
      
      // 關閉對話框
      closeDialog('fileSelection');
      setSelectedFile(null);
      
      // 操作完成（日誌已移除）
    }
  };

  const handleUploadToCloud = async () => {
    if (selectedFile) {
      // 调试日志已移除
      
      try {
        // 關閉檔案選擇對話框
        closeDialog('fileSelection');
        
        // 切換到「交片上傳」Tab
        setTabValue(0); // 「交片上傳」是 Tab 0
        
        // 設置上傳狀態
        setIsUploading(true);
        setUploadProgress(0);
        
        // 模擬上傳進度
        const uploadInterval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev >= 100) {
              clearInterval(uploadInterval);
              setIsUploading(false);
              
              // 上傳完成後，顯示成功訊息
              // 操作完成（日誌已移除）
              
              // 這裡可以添加實際的上傳邏輯
              // 例如調用 VideoUpload 組件的上傳功能
              
              return 100;
            }
            return prev + 10;
          });
        }, 200);
        
      } catch (error) {
        console.error('❌ 上傳失敗:', error);
        setIsUploading(false);
        setUploadProgress(0);
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // 💬 對話框管理模組
  // ═══════════════════════════════════════════════════════════════════════
  // 負責：管理所有對話框的開啟與關閉狀態
  // State: showWelcomeDialog, vbidDialogOpen, languageDialogOpen, authDialogOpen...
  // Handlers: handleWelcomeDialogClose, handleVbidDialogClose, handleOpenNoteRecords...
  
  // Dialog handlers
  const handleWelcomeDialogClose = () => {
    closeDialog('welcome');
    localStorage.setItem('hasSeenWelcome', 'true');
  };

  const handleVbidDialogClose = useCallback(() => {
    closeDialog('vbid');
  }, []);

  // Note Records Dialog 處理函數
  const handleOpenNoteRecords = useCallback(() => {
    // 操作日誌已移除
    
    // 🔧 新增：強制同步本地狀態，確保 NoteRecordsDialog 顯示最新數據
    // 狀態變化（日誌已移除）
    
    // 強制觸發一次狀態更新，確保 numberKeys 是最新的
    if (tabValue === 3) {
      setNumberKeys(prev => {
        // 狀態變化（日誌已移除）
        return { ...prev };
      });
    } else {
      setTabNumberKeys(prev => {
        // 狀態變化（日誌已移除）
        return { ...prev };
      });
    }
    
    // 使用延遲確保狀態更新完成後再開啟 Dialog
    setTimeout(() => {
      // 狀態追蹤日誌已移除
      openDialog('noteRecords');
    }, 200);
  }, [tabValue]);

  const handleCloseNoteRecords = useCallback(() => {
    // 操作日誌已移除
    closeDialog('noteRecords');
  }, []);

  // 知識管理 Dialog 處理函數
  const handleOpenKmDialog = useCallback(() => {
    // UI 操作日誌已移除;
    openDialog('km');
  }, []);

  const handleCloseKmDialog = useCallback(() => {
    // UI 操作日誌已移除;
    closeDialog('km');
  }, []);

  // 🆕 觀點管理處理函數
  const handleOpenViewpointManager = useCallback(() => {
    // 操作日誌已移除
    setViewpointManagerOpen(true);
  }, []);

  const handleCloseViewpointManager = useCallback(() => {
    // 操作日誌已移除
    setViewpointManagerOpen(false);
  }, []);


  const handleViewpointCreated = useCallback((newDocId: string) => {
    // 調試日誌已移除
    
    // 🔧 修復：立即切換到新創建的觀點
    setCurrentViewpointDocId(newDocId);
    
    // 重新載入數據
    if (user?.uid && videoUrl) {
      const videoId = videoUrl.includes('youtube.com') 
        ? new URL(videoUrl).searchParams.get('v') || ''
        : '';
      if (videoId) {
        loadVideoData(videoUrl).catch(error => {
          console.error('❌ 重新載入數據失敗:', error);
        });
      }
    }
  }, [user, videoUrl]);

  // 🔧 新增：知識管理變更處理函數
  const handleZoneChange = useCallback(async (zones: string[]) => {
    // Zone 變更日誌已移除;
    setVideoZones(zones);
    
    // 保存到 Firestore（同時保存到 videos 和 sharedVbids）
    try {
      if (videoUrl && user?.uid) {
        const videoId = extractYouTubeId(videoUrl);
        if (videoId) {
          // 1. 保存到 videos collection（用於知識管理）
          const videosDocRef = doc(db, 'videos', `${user.uid}_${videoId}`);
          await setDoc(videosDocRef, { 
            zones,
            videoId,
            videoUrl,
            userId: user.uid,
            updatedAt: new Date().toISOString()
          }, { merge: true });
          
          // 2. 保存到 sharedVbids collection（用於 ManagePages）
          const sharedVbidsDocRef = doc(db, 'sharedVbids', videoId);
          await setDoc(sharedVbidsDocRef, { 
            zones,
            updatedAt: new Date().toISOString()
          }, { merge: true });
          
          // 成功日誌已移除
        }
      }
    } catch (error) {
      console.error('❌ Zone 保存失敗:', error);
      throw error;
    }
  }, [videoUrl, user]);

  const handleTagChange = useCallback(async (tags: string[]) => {
    // 調試日誌已移除
    setVideoTags(tags);
    
    // 保存到 Firestore（同時保存到 videos 和 sharedVbids）
    try {
      if (videoUrl && user?.uid) {
        const videoId = extractYouTubeId(videoUrl);
        if (videoId) {
          // 1. 保存到 videos collection（用於知識管理）
          const videosDocRef = doc(db, 'videos', `${user.uid}_${videoId}`);
          await setDoc(videosDocRef, { 
            tags,
            videoId,
            videoUrl,
            userId: user.uid,
            updatedAt: new Date().toISOString()
          }, { merge: true });
          
          // 2. 保存到 sharedVbids collection（用於 ManagePages）
          const sharedVbidsDocRef = doc(db, 'sharedVbids', videoId);
          await setDoc(sharedVbidsDocRef, { 
            tags,
            updatedAt: new Date().toISOString()
          }, { merge: true });
          
          // 成功日誌已移除
        }
      }
    } catch (error) {
      console.error('❌ Tag 保存失敗:', error);
      throw error;
    }
  }, [videoUrl, user]);

  const handleGroupChange = useCallback(async (groupIds: string[]) => {
    // 调试日志已移除
    setVideoGroupIds(groupIds);
    
    // 保存到 Firestore（同時保存到 videos 和 sharedVbids）
    try {
      if (videoUrl && user?.uid) {
        const videoId = extractYouTubeId(videoUrl);
        if (videoId) {
          // 1. 保存到 videos collection（用於知識管理）
          const videosDocRef = doc(db, 'videos', `${user.uid}_${videoId}`);
          await setDoc(videosDocRef, { 
            groupIds,
            videoId,
            videoUrl,
            userId: user.uid,
            updatedAt: new Date().toISOString()
          }, { merge: true });
          
          // 2. 保存到 sharedVbids collection（用於 ManagePages）
          const sharedVbidsDocRef = doc(db, 'sharedVbids', videoId);
          await setDoc(sharedVbidsDocRef, { 
            groupIds,
            updatedAt: new Date().toISOString()
          }, { merge: true });
          
          // 成功日誌已移除
        }
      }
    } catch (error) {
      console.error('❌ 群組保存失敗:', error);
      throw error;
    }
  }, [videoUrl, user]);

  // 控制 Group UI 位置切換函數
  const handleControlGroupPositionToggle = useCallback(() => {
    setControlGroupPosition(prev => prev === 'right' ? 'left' : 'right');
  }, []);

  // 處理匯入 CheckPoint 數據
  const handleImportCheckpoints = useCallback((importedVbids: ShareableVBID[]) => {
    try {
      if (importedVbids.length > 0) {
        const firstVbid = importedVbids[0];
        
        // 設置影片模式
        const allowedModes = ["Short", "Google", "VIDEO", "YouTube", "PHOTO", "PDF", "Multiview", null];
        if (firstVbid.mediaMode && allowedModes.includes(firstVbid.mediaMode)) {
          setVideoMode(firstVbid.mediaMode as VideoMode);
        }
        
        // 根據模式設置對應的 Tab
        const tabIndex = (() => {
          switch (firstVbid.mediaMode) {
            case 'Short': return 0;
            case 'Google': return 1;
            case 'VIDEO': return 2;
            case 'YouTube': return 3;
            case 'PHOTO': return 4;
            case 'PDF': return 5;
        case 'Multiview': return 6;
            default: return 3; // 預設 YouTube
          }
        })();
        setTabValue(tabIndex);
        
        // 設置 URL 或檔案路徑
        const path = firstVbid.path || '';
        if (path) {
          setVideoUrl(path);
          // 更新 Tab 的 URL 記錄
          setTabVideoUrls(prev => ({
            ...prev,
            [tabIndex]: {
              url: path,
              fileName: path,
              relativePath: path
            }
          }));
        }

        // 更新當前 Tab 的 VBIDs
        setTabVbids(prev => {
          const newTabVbids = { ...prev };
          newTabVbids[tabIndex] = importedVbids;
          return newTabVbids;
        });

        // 更新當前顯示的 VBIDs
        setCurrentVbids(importedVbids);

        // 設置數字鍵狀態
        const newNumberKeys = getInitialNumberKeyState(maxNumberKeys);
        importedVbids.forEach((vbid) => {
          const [keyStr, timeStr] = vbid.timecode.split('_');
          const key = parseInt(keyStr);
          if (!isNaN(key)) {
            const hours = parseInt(timeStr.substring(0, 2));
            const minutes = parseInt(timeStr.substring(2, 4));
            const seconds = parseInt(timeStr.substring(4, 6));
            const frames = parseInt(timeStr.substring(6, 8));
            const timestamp = hours * 3600 + minutes * 60 + seconds;

            newNumberKeys[key] = {
              keyNo: key,
              timestamp,
              videoMode: vbid.mediaMode,
              isActive: true,
              isABPoint: false,
              isPickUpState: false,
              frame: frames
            };
          }
        });
        setNumberKeys(newNumberKeys);

        // 啟用書籤功能
        setIsBookmarkEnabled(true);

        // 關閉對話框
        closeDialog('vbid');

        // 顯示成功訊息
        setSnackbarMessage('成功匯入 Check Points');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error importing checkpoints:', error);
      setSnackbarMessage('匯入失敗，請確認檔案格式');
      setSnackbarOpen(true);
    }
  }, []);

  // Add missing state variables
  const {
    savedTimecodes,
    setSavedTimecodes,
    bookmarkEnabled,
    setBookmarkEnabled,
    isPlaying,
    setIsPlaying,
    progress,
    setProgress,
    duration,
    setDuration,
    volume,
    setVolume,
    isMuted,
    setIsMuted,
    playbackRate,
    setPlaybackRate,
    playedSeconds,
    setPlayedSeconds,
    embedUrl,
    setEmbedUrl,
    settingsOpen,
    setSettingsOpen,
    currentFrame,
    setCurrentFrame,
    isPlayingRef,
    toggleMute,
  } = useVideoPlayer({
    defaultVolume: 0.7,
    defaultPlaybackRate: 1,
  });

  const volumeRef = useRef<number>(volume);
  const mutedRef = useRef<boolean>(isMuted);

  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  useEffect(() => {
    mutedRef.current = isMuted;
  }, [isMuted]);

  // 🔧 重構：使用 useTimecodeDisplay Hook（替換原有的時間碼狀態）
  const {
    currentTimecode, setCurrentTimecode,
    showTimecode, setShowTimecode,
    showTimecodeBackground, setShowTimecodeBackground,
    timecodePosition, setTimecodePosition,
    timecodeScale, setTimecodeScale, // 🔧 新增：從 Hook 獲取 timecodeScale
    updateTimecode,
    resetTimecode,
    timecodeIntervalRef,
    timecodeRafIdRef,
    timecodeElRef,
    lastKnownTimeRef,
    lastTickRef,
  } = useTimecodeDisplay();
  
  // 🔧 重構：使用 useScreenshotManagement Hook（替換原有的截圖狀態）
  const {
    screenshotOpen, setScreenshotOpen,
    screenshotData, setScreenshotData,
    screenshotName, setScreenshotName,
    screenshotCounter, setScreenshotCounter,
    screenshotSize, setScreenshotSize,
    burstCount, setBurstCount,
    burstInterval, setBurstInterval,
    burstUnit, setBurstUnit,
    isBurstMode, setIsBurstMode,
    isScreenshotMode, setIsScreenshotMode,
    capturedImages, setCapturedImages,
    imageManagerOpen, setImageManagerOpen,
    incrementScreenshotCounter,
    clearCapturedImages,
    addCapturedImage,
    removeCapturedImage,
  } = useScreenshotManagement();

  // 🔧 重構：時間碼相關狀態已移至 useTimecodeDisplay Hook（上方）
  
  // 🔧 重構：使用 usePlaybackControl Hook（替換原有的播放控制狀態）
  const {
    fastForwardSeconds, setFastForwardSeconds,
    stepUnit, setStepUnit,
    isLongPressingFastForward, setIsLongPressingFastForward,
    isLongPressingRewind, setIsLongPressingRewind,
    isLongPressingStepBackward, setIsLongPressingStepBackward,
    isLongPressingStepForward, setIsLongPressingStepForward,
    originalPlaybackRate, setOriginalPlaybackRate,
    fastForwardLongPressTimerRef,
    rewindLongPressTimerRef,
    stepBackwardLongPressTimerRef,
    stepForwardLongPressTimerRef,
    resetLongPressStates,
    clearAllTimers,
  } = usePlaybackControl({
    defaultFastForwardSeconds: 5,
    defaultStepUnit: 'second'
  });
  
  // 🔧 重構：截圖相關狀態已移至 useScreenshotManagement Hook（上方）
  // 🔧 重構：時間碼相關狀態已移至 useTimecodeDisplay Hook（上方）
  // 🔧 重構：播放控制相關狀態已移至 usePlaybackControl Hook（上方）
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isVerticalVideo, setIsVerticalVideo] = useState(false);
  
  // 強制手機橫式檢測：結合 orientation 和螢幕寬度，添加寬度監聽
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  
  // 🔧 修復：使用穩定的 windowDimensions 狀態，避免響應式變化時 Hook 調用順序改變
  const aspectRatio = windowDimensions.width / windowDimensions.height;
  const isMobile = aspectRatio > 1.8; // 比例大於1.8:1視為手機版
  const isPortrait = windowDimensions.width <= windowDimensions.height; // 高度 >= 寬度即為直式
  const isLandscape = windowDimensions.width > windowDimensions.height && windowDimensions.height < 700; // 寬度 > 高度 且 高度 < 700 為橫式

  // 自動滾動到父容器頂部邊界與視窗邊界之間的位置
  const autoScrollToParentContainer = useCallback(() => {
    // 🔧 修復：使用穩定的 windowDimensions 狀態，避免響應式變化時 Hook 調用順序改變
    const aspectRatio = windowDimensions.width / windowDimensions.height;
    const isMobile = aspectRatio > 1.8; // 比例大於1.8:1視為手機版
    const isMobileLandscape = isMobile && windowDimensions.height < windowDimensions.width;
    const isDesktop = !isMobile;
    
    let scrollDistance;
    if (isMobileLandscape) {
      scrollDistance = 128; // 手機橫式：128px
    } else if (isDesktop) {
      scrollDistance = 75;  // 桌面模式：75px
    } else {
      scrollDistance = 158; // 手機直式：158px（與手機模式專用滾動保持一致）
    }
    
    // 執行滾動
    // 調試日誌已移除
    // 調試日誌已移除
    
    window.scrollTo({
      top: scrollDistance,
      behavior: 'smooth'
    });
    
    // 滾動後檢查實際位置
    setTimeout(() => {
      const actualPosition = window.pageYOffset || 0;
      // 調試日誌已移除
      // 調試日誌已移除
      // 調試日誌已移除
      // 調試日誌已移除
    }, 1000);
    
          // 調試日誌已移除
          // 調試日誌已移除
  }, []);

  // 調試信息狀態
  const [debugInfo, setDebugInfo] = useState({
    browserWidth: windowDimensions.width,
    browserHeight: windowDimensions.height,
    documentWidth: 0,
    documentHeight: 0,
    scrollTop: 0,
    parentHeight: '',
    actualParentHeight: 0,
    scrollTarget: 0
  });

  // 更新調試信息
  const updateDebugInfo = useCallback(() => {
    const browserHeight = windowDimensions.height;
    const browserWidth = windowDimensions.width;
    const documentHeight = document.documentElement.scrollHeight;
    const documentWidth = document.documentElement.scrollWidth;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const parentHeight = (windowDimensions.width > 800 || windowDimensions.height < 1200) ? 'calc(100vh - 48px - 48px)' : 'calc(100vh - 48px - 48px)';
    
    // 計算實際的父容器高度
    const actualParentHeight = (windowDimensions.width > 800 || windowDimensions.height < 1200) 
      ? (browserHeight - 96) // 100vh - 48px - 48px
      : (browserHeight - 96); // 100vh - 48px - 48px
    
    // 計算滾動目標：手機橫式時128px，桌面橫式時75px，直式佈局時58px
    const isDesktopHorizontal = browserWidth > 800;
    const isMobileLandscape = browserWidth <= 800 && browserWidth > browserHeight;
    
    let scrollTarget;
    if (isMobileLandscape) {
      scrollTarget = 128; // 手機橫式：128px
    } else if (isDesktopHorizontal) {
      scrollTarget = 75;  // 桌面橫式：75px
    } else {
      scrollTarget = 58;  // 直式佈局：58px
    }
    
    setDebugInfo({
      browserWidth,
      browserHeight,
      documentWidth,
      documentHeight,
      scrollTop,
      parentHeight,
      actualParentHeight,
      scrollTarget
    });
  }, []);

  // 🔧 修復：使用穩定的 windowDimensions 狀態，避免響應式變化時 Hook 調用順序改變
  const isMobileLandscape = isMobile && windowDimensions.height < windowDimensions.width;
  const isMobilePortrait = isMobile && windowDimensions.height >= windowDimensions.width;
  
  // 桌面模式判斷：不滿足手機模式的條件
  const isDesktop = !isMobile;
  
  // 保持向後兼容：原有的手機橫式檢測
  const isMobileLandscapeLegacy = windowDimensions.width <= 800;


  
  // 智能響應式縮放計算
  const [optimalScale, setOptimalScale] = useState(0.8);
  const [controlPanelWidth, setControlPanelWidth] = useState(200);
  
  // 實時計算最佳縮放比例和寬度
  useEffect(() => {
    const calculateOptimalDimensions = () => {
      // 🔧 优化：使用工具函数计算最佳缩放和面板宽度
      const scale = calculateOptimalScale(
        windowDimensions.width,
        windowDimensions.height,
        isVerticalVideo || isLandscape
      );
      const width = calculateControlPanelWidth(
        windowDimensions.width,
        isVerticalVideo || isLandscape
      );
      
      setOptimalScale(scale);
      setControlPanelWidth(width);
    };
    
    calculateOptimalDimensions();
    
    // 頁面載入時更新調試信息
    updateDebugInfo();
    
    // 視窗大小變動時自動滾動到父容器頂部邊界與視窗邊界之間58px的位置（暫時停用）
    const handleResize = () => {
      // 🔧 修復：從 window 對象讀取最新的視窗尺寸
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      
      setWindowDimensions({
        width: newWidth,
        height: newHeight
      });
      
      calculateOptimalDimensions();
      
      // 更新調試信息
      updateDebugInfo();
      
      // 暫時停用自動滾動功能
      // 自動滾動日誌已移除
      
      // 如果需要重新啟用，請取消註釋以下代碼
      /*
      // 延遲執行，確保DOM更新完成
      setTimeout(() => {
        // 計算當前佈局的滾動距離
        const isDesktopHorizontal = newWidth > 800;
        // 使用統一的 isMobileLandscape 變量
        
        let scrollDistance;
        if (isMobileLandscape) {
          scrollDistance = 123; // 手機橫式：123px
        } else if (isDesktopHorizontal) {
          scrollDistance = 75;  // 桌面橫式：75px
        } else {
          scrollDistance = 58;  // 直式佈局：58px
        }
        
        // 調試日誌已移除
        // 調試日誌已移除
        // 調試日誌已移除
        autoScrollToParentContainer();
        // 滾動後再次更新調試信息
        setTimeout(() => updateDebugInfo(), 50);
      }, 100);
      */
    };
    
    window.addEventListener('resize', handleResize, { passive: true });
    
    return () => window.removeEventListener('resize', handleResize);
  }, [isVerticalVideo, isLandscape, autoScrollToParentContainer, updateDebugInfo]);

  // 監聽橫式/直式切換，自動滾動（暫時停用）
  useEffect(() => {
    // 暫時停用橫式/直式切換自動滾動功能
    // 自動滾動日誌已移除
    
    // 如果需要重新啟用，請取消註釋以下代碼
    /*
    if (isLandscape) {
      // 狀態追蹤日誌已移除
      // 調試日誌已移除
      autoScrollToParentContainer();
      // 滾動後檢查實際位置
      setTimeout(() => {
        // 調試日誌已移除
        // 調試日誌已移除
      }, 500);
    }
    */
  }, [isLandscape, autoScrollToParentContainer]);

  // 監聽滾動事件，實時更新調試信息
  useEffect(() => {
    const handleScroll = () => {
      updateDebugInfo();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [updateDebugInfo]);

  // 防止瀏覽器下拉刷新功能 - 修復：允許正常滾動
  useEffect(() => {
    let startY = 0;
    let isScrolling = false;
    
    const preventPullRefresh = (e: TouchEvent) => {
      // 只有在頁面頂部且明確向下拖拽時才阻止（不是正常滾動）
      if (window.pageYOffset === 0 && e.touches[0].clientY > startY + 50 && !isScrolling) {
        e.preventDefault();
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
      isScrolling = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      // 如果移動距離超過10px，認為是正常滾動
      if (Math.abs(e.touches[0].clientY - startY) > 10) {
        isScrolling = true;
      }
    };

    // 添加觸控事件監聽器防止下拉刷新，但允許正常滾動
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchmove', preventPullRefresh, { passive: false });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchmove', preventPullRefresh);
    };
  }, []);
  // 🔧 保留：安全框和網格相關設定（非顯示控制）
  const [safetyFrameColor, setSafetyFrameColor] = useState<'magenta' | 'blue' | 'yellow' | 'green'>(
    'magenta'
  );
  const [safetyFrameSize, setSafetyFrameSize] = useState<number>(90);
  const [gridMode, setGridMode] = useState<'none' | '3x3' | '6x4' | '10x6'>(() => {
    const savedMode = localStorage.getItem('gridMode');
    return (savedMode as 'none' | '3x3' | '6x4' | '10x6') || 'none';
  });
  const [previewImage, setPreviewImage] = useState<ImageData | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState<number | null>(null);
  // 🔧 保留：編輯和模式狀態（非單純顯示控制）
  const [isEditing, setIsEditing] = useState(false);
  const [isShortMode, setIsShortMode] = useState(false);
  
  // 🔧 保留：認證相關狀態（非對話框開關）
  const [isLogin, setIsLogin] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  
  // 控制 Group UI 位置控制狀態
  const [controlGroupPosition, setControlGroupPosition] = useState<'left' | 'right'>('right');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [authTab, setAuthTab] = useState(0);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company: '',
    position: '',
    phone: ''
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  // 🔧 重構：移除 settingsAnchorEl 和 apiMonitorOpen，統一由 MainLayout 管理
  // 新增時間碼選單的錨點狀態
  const [timecodeMenuAnchorEl, setTimecodeMenuAnchorEl] = useState<null | HTMLElement>(null);
  // 在組件頂部添加快門聲音效
  const shutterSound = useMemo(() => {
    const audio = new Audio('/sounds/shutter.mp3');
    audio.preload = 'auto';
    
    audio.addEventListener('error', (e) => {
      console.error('音效載入失敗:', e);
    });

    return audio;
  }, []);

  // 添加音效播放的輔助函數
  const playShutterSound = () => {
    try {
      shutterSound.currentTime = 0;
      shutterSound.play().catch(error => {
        console.error('播放音效失敗:', error);
      });
    } catch (error) {
      console.error('音效播放錯誤:', error);
    }
  };

  // 創建一個通用的音效播放函數，支持不同的音效類型
  const playButtonSound = (soundType: 'click' | 'delete' | 'reset' | 'shutter' = 'click') => {
    // 如果音效被禁用且不是快門聲音，直接返回
    if (!isSoundEnabled && soundType !== 'shutter') {
      return;
    }
    
    try {
      let soundPath = '/sounds/MouseClick-SoundEffect.mp3'; // 預設的點擊音效
      
      // 根據音效類型選擇不同的音效文件
      switch (soundType) {
        case 'delete':
          soundPath = '/sounds/MouseClick-SoundEffect.mp3'; // 暫時使用點擊音效，後續可替換
          break;
        case 'reset':
          soundPath = '/sounds/MouseClick-SoundEffect.mp3'; // 暫時使用點擊音效，後續可替換
          break;
        case 'shutter':
          // 直接使用已存在的快門音效
          shutterSound.currentTime = 0;
          shutterSound.play().catch(error => {
            console.error('播放快門音效失敗:', error);
          });
          return; // 直接返回，不需要創建新的 Audio 對象
        default:
          soundPath = '/sounds/MouseClick-SoundEffect.mp3';
      }
      
      // 創建並播放指定的音效
      const audio = new Audio(soundPath);
      audio.play().catch(error => {
        console.error(`播放 ${soundType} 音效失敗:`, error);
      });
    } catch (error) {
      console.error('播放音效時發生錯誤:', error);
    }
  };

  // 即時保存函數
  const saveCurrentState = useCallback(async () => {
    // 這個函數會在所有狀態定義完成後重新定義
  }, []);

    // 在 MainPage component 內部頂部加上（靠近其他 useRef）
    const pbIsReadyRef = useRef(false);
    const pbStartTimeRef = useRef(0);
    const pbLastPlayIndexRef = useRef(-1);
    const pbJustStartedRef = useRef(false);
    const currentPlayIndexRef = useRef(0);
    
  useEffect(() => {
    // 檢查是否有存儲的用戶資料
    const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (storedUser) {
      setUserProfile(JSON.parse(storedUser));
    }
  }, []);

  // 監聽 localStorage 變化
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // 🔧 如果是同步播放模式（主機或參與者），忽略 localStorage 同步（保持獨立設定）
      if (isSyncParticipant || isHost) {
        // 同步播放日誌已移除;
        return;
      }
      
      if (e.key && e.newValue !== null) {
        switch (e.key) {
          case 'showTimecode':
            setShowTimecode(e.newValue !== 'false');
            break;
          case 'showTimecodeBackground':
            setShowTimecodeBackground(e.newValue !== 'false');
            break;
          case 'timecodePosition':
            setTimecodePosition(e.newValue as TimecodePosition);
            break;
          case 'uiDisplay.clock':
            setUiDisplay((prev) => ({ ...prev, clock: e.newValue !== 'false' }));
            break;
          case 'uiDisplay.currentTime':
            setUiDisplay((prev) => ({ ...prev, currentTime: e.newValue !== 'false' }));
            break;
          case 'uiDisplay.remainingTime':
            setUiDisplay((prev) => ({ ...prev, remainingTime: e.newValue !== 'false' }));
            break;
          case 'timeFormat':
            setTimeFormat(e.newValue as '24h' | '12h');
            break;
          case 'uiDisplay.safetyFrame':
            setUiDisplay((prev) => ({ ...prev, safetyFrame: e.newValue !== 'false' }));
            break;
          case 'safetyFrameSize':
            setSafetyFrameSize(Number(e.newValue));
            break;
          case 'uiDisplay.crosshair':
            setUiDisplay((prev) => ({ ...prev, crosshair: e.newValue !== 'false' }));
            break;
          case 'uiDisplay.gridLines':
            setUiDisplay((prev) => ({ ...prev, gridLines: e.newValue === 'true' }));
            break;
          case 'uiDisplay.movieMask':
            setUiDisplay((prev) => ({ ...prev, movieMask: e.newValue === 'true' }));
            break;
          case 'fastForwardSeconds':
            const newValue = Number(e.newValue);
            setFastForwardSeconds(newValue);
            // 同時更新 burstInterval
            setBurstInterval(newValue);
            localStorage.setItem('burstInterval', newValue.toString());
            break;
          case 'stepUnit':
            setStepUnit(e.newValue as StepUnit);
            break;
          case 'screenshotSize':
            // 🔧 重構：screenshotSize 已由 useScreenshotManagement Hook 管理
            setScreenshotSize(e.newValue as ScreenshotSize);
            break;
          case 'burstCount':
            // 🔧 重構：burstCount 已由 useScreenshotManagement Hook 管理
            setBurstCount(Number(e.newValue) as BurstCount);
            break;
          case 'burstInterval':
            // 🔧 重構：burstInterval 已由 useScreenshotManagement Hook 管理
            setBurstInterval(Number(e.newValue));
            break;
          case 'burstUnit':
            // 🔧 重構：burstUnit 已由 useScreenshotManagement Hook 管理
            setBurstUnit(e.newValue as BurstUnit);
            break;
          case 'gridMode':
            setGridMode(e.newValue as 'none' | '3x3' | '6x4' | '10x6');
            break;
        }
      }
    };

    window.addEventListener('storage', handleStorageChange, { passive: true });
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // 🔧 優化：自動保存所有用戶設定偏好
  useEffect(() => {
    localStorage.setItem('timecodePosition', timecodePosition);
    // 已保存時間碼位置
  }, [timecodePosition]);

  useEffect(() => {
    localStorage.setItem('fastForwardSeconds', String(fastForwardSeconds));
    // 已保存快進/倒退秒數
  }, [fastForwardSeconds]);

  useEffect(() => {
    localStorage.setItem('stepUnit', stepUnit);
    // 已保存步進單位
  }, [stepUnit]);

  useEffect(() => {
    localStorage.setItem('ppMode.playbackDuration', String(ppMode.playbackDuration));
    // 已保存回放秒數
  }, [ppMode.playbackDuration]);

  // 🔧 重構：截圖尺寸同步已由 useScreenshotManagement Hook 內部處理（包含 localStorage）
  // useEffect(() => {
  //   localStorage.setItem('screenshotSize', screenshotSize);
  // 调试日志已移除
  // }, [screenshotSize]);

  useEffect(() => {
    localStorage.setItem('uiDisplay.watermarkInOutput', String(uiDisplay.watermarkInOutput));
    // 已保存浮水印設定
  }, [uiDisplay.watermarkInOutput]);

  useEffect(() => {
    localStorage.setItem('isSoundEnabled', String(isSoundEnabled));
    // 已保存音效設定
  }, [isSoundEnabled]);

  useEffect(() => {
    localStorage.setItem('enableFadeEffect', String(enableFadeEffect));
    // 已保存淡入/淡出效果設定
  }, [enableFadeEffect]);



  // 添加 blob URL 清理機制
  useEffect(() => {
    return () => {
      // 組件卸載時清理 blob URL
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
        // 調試日誌已移除
      }
    };
  }, [currentBlobUrl]);

  // 添加一個新的函數來將所有數字鍵狀態轉換為 VBID
  const convertNumberKeysToVBIDs = (
    keys: { [key: number]: NumberKeyState },
    videoMode?: VideoMode,
    mButtonState?: MButtonState
  ): VBID[] => {
    const currentUserId = user?.uid || 'Guest';
    const vbids: VBID[] = [];

    Object.entries(keys).forEach(([key, state]) => {
      // MV 模式特殊處理：檢查是否有 mvUrl
      if (mButtonState === 'MV' && state.mvUrl && state.isActive) {
        const mode = getCurrentVideoMode() || videoMode || state.videoMode || 'Multiview';
        const currentTime = new Date();
        
        vbids.push({
          keyNo: Number(key),
          userId: currentUserId,
          privacy: 'Public',
          mediaMode: mode,
          path: state.mvUrl, // 使用儲存的 URL
          timecode: '00:00:00 00', // MV 模式不需要時間碼
          flag: state.isActive ? 'on' : 'off',
          note: state.note ?? '',
          screenshot: state.screenshot ?? '',
          timestamp: 0, // MV 模式不需要時間戳
          frame: 0,
          playMode: mButtonState ?? 'MV',
          mvUrl: state.mvUrl,
          mvTitle: state.mvTitle ?? '',
          mvDescription: state.mvDescription ?? '',
          // 新增MV模式專用欄位
          createdAt: currentTime.toISOString(),
          viewCount: 0,
          thumbnail: state.mvThumbnail || '/CheckPlay_Logo-100x100.png' // 預設使用CheckPlay Logo
        });
      }
      // 修改：處理活躍的重點，即使 timestamp 為 null
      else if (state.isActive) {
        // 如果 timestamp 為 null，使用預設值或當前時間
        const timestamp = state.timestamp !== null ? state.timestamp : 0;
        const frame = state.frame !== null ? state.frame : calculateFrameFromTime(timestamp);
        const timecode = state.timecode || formatTimecode(timestamp, frame);
        let path = videoUrl;
        if (["VIDEO", "Short", "PHOTO", "PDF", "Multiview"].includes(state.videoMode || '')) {
          path = originalFileName;
        }
        // 修正 mode 欄位
        const mode = getCurrentVideoMode() || videoMode || state.videoMode || 'Unknown';
        

        
        vbids.push({
          keyNo: Number(key),
          userId: currentUserId,
          privacy: 'Public',
          mediaMode: mode,
          path,
          timecode,
          flag: state.isActive ? 'on' : 'off',
          note: state.note ?? '',
          screenshot: state.screenshot ?? '',
          timestamp: timestamp, // 移除 Math.round 處理，保持原始精度
          frame,
          playMode: mButtonState ?? 'M'
        });
      }
    });
    return vbids;
  };

  // 🔧 getInitialNumberKeyState 已从 '../utils/numberKeyUtils' 导入

  // ═══════════════════════════════════════════════════════════════════════
  // 🔢 數字鍵管理模組 (CheckPlay 核心功能)
  // ═══════════════════════════════════════════════════════════════════════
  // 負責：CHECK 按鈕、數字鍵點擊、重點創建與管理、自動填充
  // State: numberKeys, tabNumberKeys, currentSubtitleKey, selectedKey, maxNumberKeys
  // Handlers: handleCheckClick (~600行), handleNumberKeyUp, handleNumberKeyPress
  // 依賴：影片播放器（時間）、字幕模組（自動填充）、Firestore（保存）
  
  // 初始化數字鍵狀態 - 改為空物件，避免覆蓋 Firestore 載入的資料
  const [numberKeys, setNumberKeys] = useState<Record<string, NumberKeyState>>({});

  // 🔧 新增：全局診斷函數，方便除錯
  useEffect(() => {
    // 將診斷函數掛載到 window 對象
    (window as any).__checkplay_debug = {
      // 導出當前字幕內容
      exportSRT: () => {
        if (srtContent && srtContent !== '_READY_') {
          // 調試日誌已移除
          // 調試日誌已移除
          // SRT 內容日誌已移除;
          // 分隔線日誌已移除;
          // 調試工具日誌已移除;
          return srtContent;
        } else {
          console.warn('⚠️ 目前沒有載入字幕內容');
          return null;
        }
      },
      // 獲取字幕內容（用於複製）
      getSRT: () => srtContent,
      // 獲取當前數字鍵狀態
      getNumberKeys: () => {
        // 調試日誌已移除
        return numberKeys;
      },
      // 獲取當前選中的重點
      getCurrentKey: () => {
        if (currentSubtitleKey !== null) {
          const keyState = numberKeys[currentSubtitleKey];
          // 調試日誌已移除
          return keyState;
        } else {
          console.warn('⚠️ 目前沒有選中的重點');
          return null;
        }
      },
      // 測試 SRT 解析
      testParseSRT: async () => {
        if (!srtContent || srtContent === '_READY_') {
          console.error('❌ 沒有字幕內容可供測試');
          return;
        }
        const { parseSRT } = await import('../utils/srtUtils');
        const entries = parseSRT(srtContent);
        // 調試日誌已移除
        if (entries.length > 0) {
          // 條目日誌已移除;
        }
        return entries;
      },
      // 打開 SRT 診斷工具
      openSRTDebugger: () => {
        window.open('http://localhost:5173/debug-srt-parser.html', '_blank');
        // 調試工具日誌已移除;
        // 調試日誌已移除
      },
      help: () => {
        // 調試日誌已移除
        // 調試工具日誌已移除;
        // 調試工具日誌已移除;
        // 調試工具日誌已移除;
        // 調試工具日誌已移除;
        // 調試工具日誌已移除;
        // 調試工具日誌已移除;
        // 調試工具日誌已移除;
      }
    };
    
    // 調試日誌已移除
  }, [srtContent, numberKeys, currentSubtitleKey]);

  // 🆕 權限錯誤處理函數
  const handlePermissionError = useCallback((error: any) => {
    if (error instanceof Error && error.message.startsWith('PERMISSION_DENIED')) {
      console.error('🚨 權限錯誤:', error.message);
      alert('❌ 權限不足：' + error.message);
      return true; // 表示已處理權限錯誤
    }
    return false; // 表示不是權限錯誤
  }, []);

  // 🆕 觀點管理處理函數
  const handleViewpointChange = useCallback(async (newDocId: string) => {
    // 調試日誌已移除
    
    // 🔧 修復：檢查當前觀點是否屬於當前用戶，只保存自己的觀點
    // 1. 保存當前觀點的數據（僅當觀點屬於當前用戶時）
    if (currentViewpointDocId && user?.uid) {
      // 檢查 currentViewpointDocId 是否屬於當前用戶
      const isOwnViewpoint = currentViewpointDocId.startsWith(`${user.uid}_`);
      
      if (isOwnViewpoint) {
        try {
          await saveVbidsToFirestore({
            vbids: [],
            user: user,
            videoUrl: videoUrl,
            currentMode: getCurrentVideoMode() || 'YouTube',
            numberKeys: numberKeys,
            mButtonState: mButtonState,
            youtubeInfo: null,
            checkpointCount: Object.keys(numberKeys).filter(k => numberKeys[parseInt(k)].isActive).length,
            fileName: '',
            documentId: currentViewpointDocId
          });
          // 調試日誌已移除
        } catch (error) {
          if (!handlePermissionError(error)) {
            console.error('❌ 保存當前觀點失敗:', error);
          }
        }
      } else {
        // 调试日志已移除
      }
    }
    
    // 2. 切換到新觀點
    setCurrentViewpointDocId(newDocId);
    
    // 3. 載入新觀點的數據
    if (user?.uid && newDocId) {
      try {
        const { getDb } = await import('../services/firebase');
        const db = await getDb();
        const docRef = doc(db, 'sharedVbids', newDocId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          
          // 載入重點數據
          setNumberKeys(data.numberKeys || {});
          setVideoZones(data.zones || []);
          setVideoTags(data.tags || []);
          setVideoGroupIds(data.groupIds || []);
          
          // 調試日誌已移除
        }
      } catch (error) {
        console.error('❌ 載入新觀點數據失敗:', error);
      }
    }
  }, [currentViewpointDocId, user, videoUrl, numberKeys, mButtonState]);

  // 🔧 新增：SPA 模式下頁面可見性檢測和視頻暫停/恢復邏輯
  useEffect(() => {
    if (!isEmbedded) return; // 只在 SPA 模式下啟用
    
    // 🔧 SPA 模式視頻狀態管理（日誌已移除）
    
    // 頁面可見性變化處理函數
    const handleVisibilityChange = () => {
      if (!playerRef.current) return;
      
      const player = playerRef.current.getInternalPlayer();
      if (!player) return;
      
      if (document.hidden) {
        // 頁面變為隱藏，保存當前狀態並暫停視頻
        // 處理日誌已移除
        
        // 暫停視頻
        if (isPlaying) {
          if (videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be')) {
            player.pauseVideo?.();
          } else {
            player.pause?.();
          }
          setIsPlaying(false);
        }
        
        // 保存到 sessionStorage 以便恢復
        sessionStorage.setItem('mainPageVideoState', JSON.stringify({
          wasPlaying: isPlaying,
          currentTime: currentTime,
          videoUrl: videoUrl,
          numberKeys: numberKeys,
          timestamp: Date.now()
        }));
        
      } else {
        // 頁面變為可見，檢查是否需要恢復視頻
        // 處理日誌已移除
        
        // 檢查是否有新的導航請求
        const hasNavigationRequest = navigationState?.shouldNavigateToMain;
        
        if (!hasNavigationRequest) {
          // 沒有新的導航請求，恢復之前的視頻狀態
          const savedState = sessionStorage.getItem('mainPageVideoState');
          
          if (savedState) {
            try {
              const state = JSON.parse(savedState);
              
              // 檢查保存的狀態是否還有效（5分鐘內）
              const isStateValid = Date.now() - state.timestamp < 5 * 60 * 1000;
              
              if (isStateValid && state.videoUrl === videoUrl) {
                // 處理日誌已移除
                
                // 恢復播放狀態
                if (state.wasPlaying) {
                  setTimeout(() => {
                    if (videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be')) {
                      player.playVideo?.();
                    } else {
                      player.play?.();
                    }
                    setIsPlaying(true);
                  }, 500); // 延遲一點確保頁面完全可見
                }
                
                // 恢復播放位置
                if (state.currentTime > 0) {
                  setTimeout(() => {
                    if (videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be')) {
                      player.seekTo?.(state.currentTime, true);
                    } else {
                      player.currentTime = state.currentTime;
                    }
                  }, 1000);
                }
              }
            } catch (error) {
              console.error('🎬 恢復視頻狀態失敗:', error);
            }
          }
        }
        
        // 清除保存的狀態
        sessionStorage.removeItem('mainPageVideoState');
      }
    };
    
    // 添加頁面可見性變化監聽器
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isEmbedded, isPlaying, currentTime, videoUrl]); // 🔧 修復：移除 navigationState 和 numberKeys 依賴避免無限循環

  // useEffect 來檢測是否需要保留狀態 - 優化重複初始化
  useEffect(() => {
    // 檢查多個來源以確定是否需要保留狀態
    const returnFromFavoritesLS = localStorage.getItem('returnFromFavorites');
    const returnFromFavoritesSS = sessionStorage.getItem('returnFromFavorites');
    const preserveMainPageStateLS = localStorage.getItem('preserveMainPageState') === 'true';
    const preserveMainPageStateSS = localStorage.getItem('preserveMainPageState') === 'true';
    // 如果任一標誌存在，就保留狀態
    const shouldPreserveState = !!(returnFromFavoritesLS || returnFromFavoritesSS || preserveMainPageStateLS || preserveMainPageStateSS);
    // 分隔線（日誌已移除）
    // 調試日誌已移除
    
    // 🔧 修復：只有在沒有保留狀態標誌且沒有從分享還原時才初始化
    if (!shouldPreserveState && !restoredFromShare.current && !initializedRef.current) {
      // 狀態變化（日誌已移除）
      // 🔧 修復：延遲初始化，確保不會覆蓋從 Firestore 載入的狀態
      setTimeout(() => {
        setNumberKeys(prev => {
          // 🔧 修復：檢查是否有活躍的數字鍵狀態（從 Firestore 載入），如果有則不覆蓋
          const hasActiveKeys = Object.values(prev).some(keyState => keyState.isActive);
          if (Object.keys(prev).length > 0 && hasActiveKeys) {
            // 狀態檢測日誌已移除
            return prev;
          }
          // 🔧 修復：如果沒有活躍的鍵，但有一些數據，也檢查是否有有效的時間碼
          const hasValidTimecodes = Object.values(prev).some(keyState => 
            keyState.timecode && 
            keyState.timecode.trim() !== ''
          );
          if (Object.keys(prev).length > 0 && hasValidTimecodes) {
            // 狀態檢測日誌已移除
            return prev;
          }
          // 狀態變化（日誌已移除）
          return getInitialNumberKeyState(maxNumberKeys);
        });
      }, 100);
      initializedRef.current = true;
    } else if (shouldPreserveState) {
      // 狀態檢測日誌已移除
      initializedRef.current = true;
    } else if (restoredFromShare.current) {
      // 狀態檢測日誌已移除
      initializedRef.current = true;
    }
  }, []); // 只在組件掛載時執行一次

  // 在現有的狀態宣告後添加新的狀態
  const [tabVideoUrls, setTabVideoUrls] = useState<{ [key: number]: { url: string; fileName: string; relativePath: string } }>({});

  // 在現有的狀態宣告後添加新的狀態
  const [tabPlaybackPositions, setTabPlaybackPositions] = useState<{ [key: number]: number }>({});
  const [tabMButtonStates, setTabMButtonStates] = useState<{ [key: number]: MButtonState }>({});

  // 添加一個函數來檢查和過濾重複的 VBID
  // 🔧 filterDuplicateVBIDs 已从 '../utils/numberKeyUtils' 导入

  // 在 handleTabChange 函數之前添加新的 useEffect
  useEffect(() => {
    if (videoMode) {
      const savedContent = tabVideoUrls[tabValue] || { url: '', fileName: '', relativePath: '' };
      switch (videoMode) {
        case 'VIDEO':
        case 'Short':
          const videoFileInput = document.getElementById('video-file-path') as HTMLInputElement;
          if (videoFileInput) {
            videoFileInput.value = savedContent.fileName || '';
          }
          break;
        case 'PHOTO':
          const photoButton = document.querySelector('button[data-mode="PHOTO"]') as HTMLButtonElement;
          if (photoButton) {
            photoButton.textContent = savedContent.fileName || '選擇照片（JPG、PNG、HEIC）';
          }
          break;
        case 'PDF':
          const pdfButton = document.querySelector('button[data-mode="PDF"]') as HTMLButtonElement;
          if (pdfButton) {
            pdfButton.textContent = savedContent.fileName || '選擇 PPT 或 PDF 檔案';
          }
          break;
      }
    }
  }, [videoMode, tabVideoUrls, tabValue]);

  // 修改 handleTabChange 函數，移除 setTimeout 部分
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (tabValue !== newValue) {
      // 保存當前 Tab 的數字鍵狀態
      setTabNumberKeys(prev => ({
        ...prev,
        [tabValue]: numberKeys
      }));
      
      // 保存當前 Tab 的所有數字鍵狀態到 VBID
      const currentTabVBIDs = convertNumberKeysToVBIDs(numberKeys, videoMode, mButtonState);
      const uniqueCurrentTabVBIDs = filterDuplicateVBIDs(currentTabVBIDs);
      
      setTabVbids(prev => ({
        ...prev,
        [tabValue]: uniqueCurrentTabVBIDs
      }));

      // 保存當前 Tab 的輸入欄位內容
      setTabVideoUrls(prev => ({
        ...prev,
        [tabValue]: {
          url: videoUrl,
          fileName: originalFileName,
          relativePath: fileRelativePath || ''
        }
      }));

      // 保存當前 Tab 的播放位置
      if (playerRef.current) {
        const currentTime = playerRef.current.getCurrentTime() || 0;
        setTabPlaybackPositions(prev => ({
          ...prev,
          [tabValue]: currentTime
        }));
      }

      // 保存當前 Tab 的 M 按鈕狀態
      setTabMButtonStates(prev => ({
        ...prev,
        [tabValue]: mButtonState
      }));

      // 設置新的 tabValue
      setTabValue(newValue);
      
      // 🔧 修復：切換 Tab 時清理字幕狀態，防止不同 Tab 間的字幕混淆
      setSrtContent('');
      setCurrentVideoId('');
      // 清理日誌已移除

      // 根據新 tab 設置對應的 videoMode
      const newMode = (() => {
        switch (newValue) {
          case 0: return 'Short';
          case 1: return 'Google';
          case 2: return 'VIDEO';
          case 3: return 'YouTube';
          case 4: return 'PHOTO';
          case 5: return 'PDF';
          case 6: return 'Multiview';
          default: return 'YouTube';
        }
      })();
      setVideoMode(newMode);

      // 恢復新 tab 的視頻 URL
      const savedUrl = tabVideoUrls[newValue];
      if (savedUrl) {
        setVideoUrl(savedUrl.url);
        setOriginalFileName(savedUrl.fileName);
        setFileRelativePath(savedUrl.relativePath);
      } else {
        // 如果沒有保存的 URL，清空當前 URL
        setVideoUrl('');
        setOriginalFileName('');
        setFileRelativePath('');
      }

      // 恢復新 Tab 的數字鍵狀態
      const savedNumberKeys = tabNumberKeys[newValue];
      if (savedNumberKeys) {
        setNumberKeys(savedNumberKeys);
      } else {
        // 如果沒有保存的狀態，且不是 YouTube Tab，使用初始狀態
        // YouTube Tab (tabValue = 3) 保持現有的 numberKeys 狀態
        if (newValue !== 3) {
          setNumberKeys(getInitialNumberKeyState(maxNumberKeys));
        }
        // 如果是 YouTube Tab，保持現有的 numberKeys 狀態
      }
      
      // 檢查新 Tab 是否有保存的 VBID
      const savedVbids = tabVbids[newValue] || [];
      const uniqueSavedVbids = filterDuplicateVBIDs(savedVbids);
      setCurrentVbids(uniqueSavedVbids);

      // 恢復新 tab 的播放位置
      const savedPosition = tabPlaybackPositions[newValue];
      if (savedPosition !== undefined && playerRef.current) {
        playerRef.current.seekTo(savedPosition);
      }

      // 恢復新 Tab 的 M 按鈕狀態
      const savedMButtonState = tabMButtonStates[newValue];
      if (savedMButtonState) {
        setMButtonState(savedMButtonState);
      } else {
        // 如果沒有保存的狀態，且切換到 Multiview Tab，設置為 MV 模式
        if (newValue === 6) {
          setMButtonState('MV');
        } else {
          // 其他 Tab 使用預設的 M 模式
          setMButtonState('M');
        }
      }
    }
  };

  const handleLogin = () => {
    // 調試日誌已移除
    openDialog('auth');
  };

  // 添加一個 effect 來監聽播放器的實際狀態
  useEffect(() => {
    const checkPlayerState = () => {
      if (playerRef.current) {
        const player = playerRef.current.getInternalPlayer();
        if (player) {
          if (videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be')) {
            // YouTube 播放器
            const state = player.getPlayerState?.();
            // YT.PlayerState.PLAYING = 1, YT.PlayerState.PAUSED = 2
            const isActuallyPlaying = state === 1;
            if (isActuallyPlaying !== isPlaying) {
              setIsPlaying(isActuallyPlaying);
            }
          } else {
            // HTML5 播放器
            const isActuallyPlaying = !player.paused;
            if (isActuallyPlaying !== isPlaying) {
              setIsPlaying(isActuallyPlaying);
            }
          }
        }
      }
    };

    // 每秒檢查一次播放狀態
    const intervalId = setInterval(checkPlayerState, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isPlaying, videoUrl]);

  useEffect(() => {
    let isActive = true;
    const updateFrame = (timestamp: number) => {
      if (!isActive) return;
      if (!lastTimeRef.current) {
        lastTimeRef.current = timestamp;
        animationFrameRef.current = requestAnimationFrame(updateFrame);
        return;
      }
      const deltaTime = timestamp - lastTimeRef.current;
      if (deltaTime >= 1000 / 30) {
        setCurrentFrame(prev => (prev + 1) % 30);
        lastTimeRef.current = timestamp;
      }
      animationFrameRef.current = requestAnimationFrame(updateFrame);
    };
    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(updateFrame);
    }
    return () => {
      isActive = false;
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isPlaying]);

  // 🎬 碼表式時間碼更新 - 使用 setInterval 持續更新，確保流暢
  useEffect(() => {
    // 僅在顯示時間碼且有有效影片時啟動 rAF 迴圈
    if (!showTimecode || !videoUrl) {
      // 調試日誌已移除
      // 停止任意既有 interval/rAF
      if (timecodeIntervalRef.current) {
        clearInterval(timecodeIntervalRef.current);
        timecodeIntervalRef.current = null;
      }
      if (timecodeRafIdRef.current !== null) {
        cancelAnimationFrame(timecodeRafIdRef.current);
        timecodeRafIdRef.current = null;
      }
      return;
    }

    // 啟動 rAF 之前，保險起見清掉舊的 interval/rAF
    if (timecodeIntervalRef.current) {
      clearInterval(timecodeIntervalRef.current);
      timecodeIntervalRef.current = null;
    }
    if (timecodeRafIdRef.current !== null) {
      cancelAnimationFrame(timecodeRafIdRef.current);
      timecodeRafIdRef.current = null;
    }

    // 啟動時間碼更新 rAF
    lastTickRef.current = performance.now();

    const loop = () => {
      const now = performance.now();
      let t: number | null = null;

      try {
        const p: any = playerRef.current;
        if (p && typeof p.getCurrentTime === 'function') {
          const ct = p.getCurrentTime();
          if (Number.isFinite(ct) && ct >= 0) {
            t = ct;
            lastKnownTimeRef.current = ct;
          }
        }
      } catch {}

      if (!Number.isFinite(t as number)) {
        // 以最後有效時間為基礎，在播放期間做外插，避免暫停/恢復的 null 空窗
        t = lastKnownTimeRef.current;
        if (isPlayingRef.current && Number.isFinite(t) && (t as number) >= 0 && lastTickRef.current) {
          const deltaSec = (now - lastTickRef.current) / 1000;
          const rate = Number.isFinite(playbackRate) && (playbackRate as number) > 0 ? (playbackRate as number) : 1;
          t = (t as number) + deltaSec * rate;
        }
      }

      // 格式化並直接寫入 DOM，避免不必要的重渲染
      const safeT = Number.isFinite(t as number) && (t as number) >= 0 ? (t as number) : 0;
      const hours = Math.floor(safeT / 3600);
      const minutes = Math.floor((safeT % 3600) / 60);
      const seconds = Math.floor(safeT % 60);
      const frame = Math.floor((safeT % 1) * 30);
      const text = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} ${String(frame).padStart(2, '0')}`;
      if (timecodeElRef.current) {
        timecodeElRef.current.textContent = text;
      }

      lastTickRef.current = now;
      timecodeRafIdRef.current = requestAnimationFrame(loop);
    };

    // 延遲少許，避開 IFrame 恢復時可能的 null 空窗
    const startId = window.setTimeout(() => {
      lastTickRef.current = performance.now();
      timecodeRafIdRef.current = requestAnimationFrame(loop);
    }, 200);

    return () => {
      window.clearTimeout(startId);
      if (timecodeRafIdRef.current !== null) {
        cancelAnimationFrame(timecodeRafIdRef.current);
        timecodeRafIdRef.current = null;
      }
    };
  }, [showTimecode, videoUrl, playbackRate]);

  const handleGoogleLoginClick = async (event: React.MouseEvent<HTMLElement>) => {
    try {
      // 調試日誌已移除
      setAuthLoading(true);
      
      // 調試日誌已移除
      await authHandleGoogleLogin();
      
      // 調試日誌已移除
      handleCloseAuthDialog();
    } catch (error: any) {
      console.error('Google 登入失敗:', error);
      // 不使用 setAuthError 以避免類型錯誤
      console.error(error.message || 'Google 登入失敗，請稍後再試');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleEmailLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setAuthLoading(true);
      await authHandleEmailLogin(email, password);
      handleCloseAuthDialog();
    } catch (error: any) {
      console.error('Email 登入失敗:', error);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setAuthLoading(true);
      await authHandleRegister(email, password, name);
      handleCloseAuthDialog();
    } catch (error: any) {
      console.error('註冊失敗:', error);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleFacebookLogin = async () => {
    try {
      setAuthLoading(true);
      await loginWithFacebook();
      closeDialog('auth');
    } catch (error: any) {
      console.error('Facebook login error:', error);
      setError(error.message || 'Facebook login failed');
    } finally {
      setAuthLoading(false);
    }
  };

  // 放在所有用到的地方之前
  const handleLogoutClick = async () => {
    try {
      setAuthLoading(true);
      await logout();
      
      // 清除 localStorage 和 sessionStorage 中的所有狀態標誌
      const cleanStorage = () => {
      // 清除 localStorage 暫存
      localStorage.removeItem('mainPageState');
      localStorage.removeItem('hasSeenWelcome');
        
        // 確保清除從收藏頁面返回的標誌
        localStorage.removeItem('returnFromFavorites');
        sessionStorage.removeItem('returnFromFavorites');
        localStorage.removeItem('preserveMainPageState');
        sessionStorage.removeItem('preserveMainPageState');
        
        // 清除 localStorage 中的記憶，但不播放聲音
        localStorage.removeItem('numberKeys');
        localStorage.removeItem('abPoints');
        
        // 調試日誌已移除
      };
      
      // 執行清理
      cleanStorage();
      
      // 重置所有數字鍵狀態
      const defaultState = getInitialNumberKeyState(maxNumberKeys);
      setNumberKeys(defaultState);
      
      // 重置所有 Tab 的資料
      setTabVbids({});
      setCurrentVbids([]);
      
      // 清空視頻 URL 和相關檔案資訊
      setVideoUrl('');
      setOriginalFileName('');
      setFileRelativePath('');
      
      // 重置 Tab 相關資料
      setTabVideoUrls({});
      setTabPlaybackPositions({});
      
      // 重置影片狀態
      setIsPlaying(false);
      if (playerRef.current) {
        const player = playerRef.current.getInternalPlayer();
        if (player) {
          if (videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be')) {
            player.pauseVideo?.();
            player.seekTo?.(0);
          } else {
            player.pause?.();
            if (player.currentTime !== undefined) {
              player.currentTime = 0;
            }
          }
        }
      }
      
      // 重置播放模式
      setMButtonState('M');
      setIsDeleteMode(false);
      
      // 重置 AB 點
      setABPoints({
        pointA: { key: null, timestamp: null },
        pointB: { key: null, timestamp: null }
      });
      
      // 清除 AB 循環
      if (abLoopIntervalRef.current) {
        clearInterval(abLoopIntervalRef.current);
        abLoopIntervalRef.current = null;
      }
      
      // 重置其他狀態
      if (typeof setProgress === 'function') {
        setProgress(0);
      }
      setPlayedSeconds(0);
      
      // 顯示成功訊息
      setSnackbarMessage('已登出並重置所有設定');
      setSnackbarOpen(true);
      
      handleCloseAuthDialog();
    } catch (error: any) {
      console.error('登出失敗:', error);
      setSnackbarMessage('登出失敗，請稍後再試');
      setSnackbarOpen(true);
    } finally {
      setAuthLoading(false);
    }
  };

  // PB 模式播放邏輯 - 修正版本
  useEffect(() => {
    // 只在 PB 模式且 ppMode.keysToPlay 準備好時啟動 interval
    if (mButtonState !== 'PB' || !ppMode.keysToPlay.length) {
      // 清除現有 interval
      if (ppMode.interval) {
        clearInterval(ppMode.interval);
        updatePPMode('interval', null);
      }
      return;
    }

    // 調試日誌已移除

    const player = playerRef.current;
    if (!player) return;

    // 清除現有 interval
    if (ppMode.interval) {
      clearInterval(ppMode.interval);
    }



    const checkProgress = setInterval(() => {
      if (!playerRef.current) return;
      
      const currentTime = playerRef.current.getCurrentTime();
      const currentKey = ppMode.keysToPlay[currentPlayIndexRef.current];
      const nextKey = ppMode.keysToPlay[currentPlayIndexRef.current + 1];

      // 防呆：確保 currentKey 存在
      if (!currentKey) {
        console.log('⚠️ PB interval: currentKey 不存在，停止 interval');
        clearInterval(checkProgress);
        updatePPMode('interval', null);
        return;
      }

      // 調試日誌已移除
      
      // 檢查播放狀態，如果播放中斷則嘗試恢復
      if (pbIsReadyRef.current && !isPlaying) {
        console.log('⚠️ PB 模式檢測到播放中斷，嘗試恢復播放');
        const internalPlayer = playerRef.current?.getInternalPlayer?.();
        if (videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be')) {
          try {
            internalPlayer?.playVideo?.();
            // 成功日誌已移除
          } catch (error) {
            console.error('❌ PB 模式恢復 YouTube 播放失敗:', error);
          }
        } else {
          try {
            internalPlayer?.play?.();
            // 成功日誌已移除
          } catch (error) {
            console.error('❌ PB 模式恢復一般播放器播放失敗:', error);
          }
        }
      }

      // 等待 currentTime 靠近目標才開始計時，增加容錯機制
      if (!pbIsReadyRef.current || pbLastPlayIndexRef.current !== currentPlayIndexRef.current) {
        // 增加容錯範圍，從 0.3 秒增加到 1 秒
        if (Math.abs(currentTime - currentKey.timestamp) < 1.0) {
          pbIsReadyRef.current = true;
          pbStartTimeRef.current = currentTime;
          pbLastPlayIndexRef.current = currentPlayIndexRef.current;
          // 調試日誌已移除
        } else {
            // 調試日誌已移除
          return;
        }
      }

      if (currentTime >= pbStartTimeRef.current + ppMode.playbackDuration) {
        if (!nextKey) {
          // 調試日誌已移除
          clearInterval(checkProgress);
          updatePPMode('interval', null);
          if (videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be')) {
            playerRef.current.getInternalPlayer()?.pauseVideo();
          } else {
            playerRef.current.getInternalPlayer()?.pause();
          }
          setIsPlaying(false);
          setMButtonState('M');
          
          // 修正：PB 模式完成後，將第一個重點設為綠色，方便下次重複播放
          const firstKey = ppMode.keysToPlay[0].keyNo;
          if (!isNaN(firstKey)) {
            setNumberKeys(prev => {
              const newState: Record<string, NumberKeyState> = { ...prev };
              // 先清除所有綠色狀態
              Object.keys(newState).forEach(k => {
                const numKey = parseInt(k);
                if (!isNaN(numKey) && newState[numKey]?.isPickUpState) {
                  newState[numKey] = {
                    ...newState[numKey],
                    isPickUpState: false
                  };
                }
              });
              
              // ✅ 重要修復：恢復所有重點的藍色狀態（isActive）
              ppMode.keysToPlay.forEach(key => {
                if (newState[key.keyNo]) {
                  newState[key.keyNo] = {
                    ...newState[key.keyNo],
                    isActive: true  // 恢復藍色狀態
                  };
                }
              });
              
              // 將第一個重點設為綠色，方便下次重複播放
              newState[String(firstKey)] = {
                ...newState[String(firstKey)],
                isPickUpState: true
              };
              return newState;
            });
          }
          
          // 清空 ppMode.keysToPlay，避免下次進入 PB 模式時使用舊的資料
          updatePPMode('keysToPlay', []);
          updatePPMode('currentPlayIndex', 0);
          pbJustStartedRef.current = false; // 離開PB模式時重置
          return;
        }
        
                // 跳到下一個重點
        // 調試日誌已移除
        updatePPMode('currentPlayIndex', idx => {
          currentPlayIndexRef.current = idx + 1;
          return idx + 1;
        });
        setNumberKeys(prev => {
          const newState: Record<string, NumberKeyState> = { ...prev };
          if (typeof currentKey?.keyNo === 'number') {
            newState[String(currentKey.keyNo)] = {
              ...newState[String(currentKey.keyNo)],
              isPickUpState: false
            };
          }
          if (typeof nextKey?.keyNo === 'number') {
            newState[String(nextKey.keyNo)] = {
              ...newState[String(nextKey.keyNo)],
              isPickUpState: true
            };
          }
          return newState;
        });
        const nextKeyNumber = nextKey.keyNo;
        const targetKeySetIndex = Math.floor((nextKeyNumber - 1) / 6);
        setKeySetIndex(targetKeySetIndex);
        
        // 使用 seekToWithFade 進行跳轉，增加播放狀態檢查
        (async () => {
          await seekToWithFade(nextKey.timestamp);
          setIsPlaying(true);
          const internalPlayer = playerRef.current?.getInternalPlayer?.();
          
          // 增加播放狀態檢查和重試機制
          const ensurePlayback = () => {
            if (videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be')) {
              try {
                internalPlayer?.playVideo?.();
                // 成功日誌已移除
              } catch (error) {
                console.error('❌ YouTube playVideo() 調用失敗:', error);
                // 重試機制
                setTimeout(() => {
                  internalPlayer?.playVideo?.();
                }, 500);
              }
            } else {
              try {
                internalPlayer?.play?.();
                // 成功日誌已移除
              } catch (error) {
                console.error('❌ 一般播放器 play() 調用失敗:', error);
                // 重試機制
                setTimeout(() => {
                  internalPlayer?.play?.();
                }, 500);
              }
            }
          };
          
          ensurePlayback();
        })();
        
        pbIsReadyRef.current = false; // 跳到新重點後，重設 ready 狀態
        pbLastPlayIndexRef.current = currentPlayIndexRef.current + 1;
        return;
      }
      
      // 維持綠色狀態與分頁
      setNumberKeys(prev => {
        const newState: Record<string, NumberKeyState> = { ...prev };
        Object.keys(newState).forEach(k => {
          const numKey = Number(k);
          if (newState[numKey].isPickUpState) {
            newState[numKey] = {
              ...newState[numKey],
              isPickUpState: false
            };
          }
        });
        newState[String(currentKey.keyNo)] = {
          ...newState[String(currentKey.keyNo)],
          isPickUpState: true
        };
        return newState;
      });
      const currentKeyNumber = currentKey.keyNo;
      if (currentKeyNumber >= 1 && currentKeyNumber <= 6) {
        setKeySetIndex(0);
      } else if (currentKeyNumber >= 7 && currentKeyNumber <= 12) {
        setKeySetIndex(1);
      } else if (currentKeyNumber >= 13 && currentKeyNumber <= 18) {
        setKeySetIndex(2);
      }
    }, 200); // 降低檢查頻率，從 100ms 改為 200ms，減少效能負擔
    
    updatePPMode('interval', checkProgress);
    return () => {
      if (checkProgress) clearInterval(checkProgress);
    };
  }, [mButtonState, ppMode.keysToPlay.length]); // 只依賴 mButtonState 和 ppMode.keysToPlay.length

  // 新增：ppMode.keysToPlay 準備好後自動 seekTo/play 第一個重點
  useEffect(() => {
    if (mButtonState === 'PB' && ppMode.keysToPlay.length > 0 && ppMode.currentPlayIndex === 0) {
      // 調試日誌已移除
      const firstKey = ppMode.keysToPlay[0];
      if (playerRef.current && firstKey.timestamp != null) {
                // 使用 seekToWithFade 進行跳轉，增加播放狀態檢查和重試機制
        (async () => {
          await seekToWithFade(firstKey.timestamp);
          // 操作完成（日誌已移除）
          setIsPlaying(true);
          const internalPlayer = playerRef.current?.getInternalPlayer?.();
          // 調試日誌已移除

          // 增加播放狀態檢查和重試機制
          const ensurePlayback = () => {
            if (videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be')) {
              // 處理日誌已移除
              try {
                internalPlayer?.playVideo?.();
                // 成功日誌已移除
              } catch (error) {
                console.error('❌ YouTube playVideo() 調用失敗:', error);
                // 重試機制
                setTimeout(() => {
                  internalPlayer?.playVideo?.();
                }, 500);
              }
            } else {
              // 處理日誌已移除
              try {
                internalPlayer?.play?.();
                // 成功日誌已移除
              } catch (error) {
                console.error('❌ 一般播放器 play() 調用失敗:', error);
                // 重試機制
                setTimeout(() => {
                  internalPlayer?.play?.();
                }, 500);
              }
            }
          };
          
          ensurePlayback();
        })();
        // 重置 PB 狀態
        pbIsReadyRef.current = false;
        pbLastPlayIndexRef.current = -1;
        currentPlayIndexRef.current = 0;
      }
    }
  }, [mButtonState, ppMode.keysToPlay, ppMode.currentPlayIndex]);

  // 修改 handlePlayPause 函數，確保開始播放時設置正確的按鈕狀態
const handlePlayPause = () => {
    // 🔧 修復：設置用戶主動播放標記，讓 VideoPlayer 知道這是用戶主動操作
    (window as any).__checkplay_user_initiated_play = true;
    // 檢查 Google Drive 模式
    if (videoMode === 'Google' && googleDriveRef.current) {
      // 處理日誌已移除
      if (isPlaying) {
        // 暫停 Google Drive 影片
        googleDriveRef.current.pause();
        setIsPlaying(false);
        return;
      } else {
        // 播放 Google Drive 影片
        googleDriveRef.current.play();
        setIsPlaying(true);
        return;
      }
    }
    
    // 檢查一般播放器模式
    if (playerRef.current) {
      const player = playerRef.current.getInternalPlayer();
      if (player) {
        if (isPlaying) {
          // 清除之前的間隔
          if (ppMode.interval) {
            clearInterval(ppMode.interval);
            updatePPMode('interval', null);
          }
          if (videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be')) {
            player.pauseVideo();
          } else {
            player.pause();
          }
          setIsPlaying(false);
          return;
        }

        // 注意：關注度增加已移至 VideoPlayer.tsx 的 onPlay 事件中
        // 避免重複累加問題

        if (mButtonState === 'PB') {
          // 操作日誌已移除
          // 調試日誌已移除
          
          // ✅ 修改：PB 模式下，播放按鈕直接開始播放，因為 Dialog 已經在模式切換時彈出過了
          // 裝置日誌已移除
          
          const allKeys = Object.entries(numberKeys)
            .filter(([_, keyState]) => keyState.timestamp !== null && !keyState.isABPoint)
            .map(([key, keyState]) => ({
              keyNo: Number(key),
              timestamp: keyState.timestamp!,
              isABPoint: keyState.isABPoint,
              isPickUpState: keyState.isPickUpState
            }))
            .sort((a, b) => a.keyNo - b.keyNo); // ✅ 按數字鍵順序排序，不按時間碼順序

          // 調試日誌已移除

          const firstGreenKey = allKeys.find(key => key.isPickUpState);
          // 狀態檢測日誌已移除
          
          if (allKeys.length === 0) {
            console.log('⚠️ 沒有重點，正常播放');
            if (videoMode === 'Google' && googleDriveRef.current) {
              googleDriveRef.current.play();
            } else if (videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be')) {
              player.playVideo();
            } else {
              player.play();
            }
            setIsPlaying(true);
            return;
          }

          // 修正：如果沒有綠色重點，使用所有重點；如果有綠色重點，從該重點開始
          let keysToPlay;
          if (firstGreenKey) {
            // 從綠色重點開始，但按數字鍵順序排列
            const filteredKeys = allKeys.filter(key => key.keyNo >= firstGreenKey.keyNo);
            keysToPlay = filteredKeys.sort((a, b) => a.keyNo - b.keyNo);
          } else {
            // 沒有綠色重點，直接使用按數字鍵順序排列的所有重點
            keysToPlay = allKeys;
          }

          // 調試日誌已移除

          if (keysToPlay.length === 0) {
            console.log('⚠️ 沒有要播放的重點，正常播放');
            if (videoMode === 'Google' && googleDriveRef.current) {
              googleDriveRef.current.play();
            } else if (videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be')) {
              player.playVideo();
            } else {
              player.play();
            }
            setIsPlaying(true);
            return;
          }

          // ✅ 修改：直接開始播放，使用預設的播放時長
          // 調試日誌已移除
          
          updatePPMode('currentPlayIndex', 0);
          updatePPMode('keysToPlay', keysToPlay);
          
          // 將所有按鈕變回藍色，然後將第一個重點設為綠色
          setNumberKeys(prev => {
            const newState = { ...prev };
            // 先清除所有綠色狀態
            Object.keys(newState).forEach(k => {
              const numKey = Number(k);
              if (!isNaN(numKey) && newState[numKey]?.isPickUpState) {
                newState[numKey] = {
                  ...newState[numKey],
                  isPickUpState: false
                };
              }
            });
            
            // 將第一個重點設為綠色
            const firstKey = ppMode.keysToPlay[0].keyNo;
            if (!isNaN(firstKey)) {
              newState[firstKey] = {
                ...newState[firstKey],
                isPickUpState: true
              };
            }
            
            return newState;
          });
          
          // 注意：不在此處 seekTo/play，讓新增的 useEffect 處理
        } else {
          try {
            if (videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be')) {
              if (typeof player.playVideo === 'function') {
                player.playVideo();
              } else {
                console.warn('YouTube player not ready, using fallback play method');
                player.play?.();
              }
            } else {
              player.play?.();
            }
            setIsPlaying(true);
          } catch (error) {
            console.error('播放器錯誤:', error);
            // 嘗試使用 ReactPlayer 的 ref 方法
            if (playerRef.current) {
              try {
                playerRef.current.getInternalPlayer()?.play?.();
                setIsPlaying(true);
              } catch (fallbackError) {
                console.error('備用播放方法也失敗:', fallbackError);
              }
            }
          }
        }
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // ⏯️ 播放控制模組
  // ═══════════════════════════════════════════════════════════════════════
  // 負責：快進快退、單步控制、播放速度調整
  // Handlers: handleFastForward, handleRewind, handleStepForward, handleStepBackward
  // 包含：長按處理、滑鼠事件、播放暫停控制

  const handleFastForward = () => {
    // 調試日誌已移除
    
    if (!playerRef.current) {
      console.error('❌ [快進] playerRef.current 不存在');
      return;
    }
    
    try {
      const currentTime = playerRef.current.getCurrentTime();
      // 快進快退詳細日誌已移除
      
      // 🔧 修復：如果 getCurrentTime 返回無效值，使用 playedSeconds 作為備用
      const actualCurrentTime = (currentTime !== null && currentTime !== undefined && Number.isFinite(currentTime) && currentTime >= 0) 
        ? currentTime 
        : playedSeconds;
      
      // 快進快退詳細日誌已移除
      
      // 🔧 關鍵修復：如果時間仍然無效或為 0，且不是影片開始，則返回，避免跳回 00:00
      if (actualCurrentTime <= 0 && playedSeconds > 1) {
        console.warn('⚠️ [快進] 檢測到異常：當前時間為 0，但 playedSeconds 不是 0，可能導致跳回 00:00，中止快進操作');
        return;
      }
      
      const validDuration = Number.isFinite(duration) && duration > 0 ? duration : Infinity;
      const validForwardSeconds = Number.isFinite(fastForwardSeconds) ? fastForwardSeconds : 10;
      
      const newTime = Math.min(actualCurrentTime + validForwardSeconds, validDuration);
      
      // 快進快退詳細日誌已移除
      
      // 🔧 關鍵修復：確保新時間有效且大於當前時間（除非影片剛開始）
      if (Number.isFinite(newTime) && newTime >= 0 && newTime > actualCurrentTime) {
        // 調試日誌已移除
        playerRef.current.seekTo(newTime);
        setPlayedSeconds(newTime);
        setProgress(duration > 0 ? (newTime / duration) : 0);
      } else if (newTime === 0 && validDuration === 0) {
        console.error('❌ [快進] duration 為 0，無法快進，可能影片尚未載入完成');
        return;
      } else {
        console.error('❌ [快進] 計算的新時間無效:', newTime);
      }
    } catch (error) {
      console.error('❌ [快進] 發生錯誤:', error);
    }
  };

  // 長按 +5s 快進按鈕處理函數
  const handleFastForwardLongPress = () => {
    // 保存當前播放速度
    setOriginalPlaybackRate(playbackRate);
    // 設置為長按狀態
    setIsLongPressingFastForward(true);
    // 啟動 2x 播放
    if (playerRef.current) {
      playerRef.current.setPlaybackRate(2);
    }
  };

  const handleFastForwardMouseDown = () => {
    fastForwardLongPressTimerRef.current = setTimeout(() => {
      handleFastForwardLongPress();
    }, 500); // 500ms 長按觸發
  };

  const handleFastForwardMouseUp = () => {
    if (fastForwardLongPressTimerRef.current) {
      clearTimeout(fastForwardLongPressTimerRef.current);
      fastForwardLongPressTimerRef.current = null;
    }
    
    // 如果正在長按狀態，恢復原始播放速度
    if (isLongPressingFastForward) {
      setIsLongPressingFastForward(false);
      if (playerRef.current) {
        playerRef.current.setPlaybackRate(originalPlaybackRate);
      }
    }
  };

  const handleFastForwardMouseLeave = () => {
    if (fastForwardLongPressTimerRef.current) {
      clearTimeout(fastForwardLongPressTimerRef.current);
      fastForwardLongPressTimerRef.current = null;
    }
    
    // 如果正在長按狀態，恢復原始播放速度
    if (isLongPressingFastForward) {
      setIsLongPressingFastForward(false);
      if (playerRef.current) {
        playerRef.current.setPlaybackRate(originalPlaybackRate);
      }
    }
  };

  // n秒退按鈕長按處理函數（2x倒退）
  const handleRewindLongPress = () => {
    setOriginalPlaybackRate(playbackRate);
    setIsLongPressingRewind(true);
    if (playerRef.current) {
      playerRef.current.setPlaybackRate(-2); // 2x倒退
    }
  };

  const handleRewindMouseDown = () => {
    rewindLongPressTimerRef.current = setTimeout(() => {
      handleRewindLongPress();
    }, 500);
  };

  const handleRewindMouseUp = () => {
    if (rewindLongPressTimerRef.current) {
      clearTimeout(rewindLongPressTimerRef.current);
      rewindLongPressTimerRef.current = null;
    }
    
    if (isLongPressingRewind) {
      setIsLongPressingRewind(false);
      if (playerRef.current) {
        playerRef.current.setPlaybackRate(originalPlaybackRate);
      }
    }
  };

  const handleRewindMouseLeave = () => {
    if (rewindLongPressTimerRef.current) {
      clearTimeout(rewindLongPressTimerRef.current);
      rewindLongPressTimerRef.current = null;
    }
    
    if (isLongPressingRewind) {
      setIsLongPressingRewind(false);
      if (playerRef.current) {
        playerRef.current.setPlaybackRate(originalPlaybackRate);
      }
    }
  };

  // 秒退按鈕長按處理函數（1x倒退）
  const handleStepBackwardLongPress = () => {
    setOriginalPlaybackRate(playbackRate);
    setIsLongPressingStepBackward(true);
    if (playerRef.current) {
      playerRef.current.setPlaybackRate(-1); // 1x倒退
    }
  };

  const handleStepBackwardMouseDown = () => {
    stepBackwardLongPressTimerRef.current = setTimeout(() => {
      handleStepBackwardLongPress();
    }, 500);
  };

  const handleStepBackwardMouseUp = () => {
    if (stepBackwardLongPressTimerRef.current) {
      clearTimeout(stepBackwardLongPressTimerRef.current);
      stepBackwardLongPressTimerRef.current = null;
    }
    
    if (isLongPressingStepBackward) {
      setIsLongPressingStepBackward(false);
      if (playerRef.current) {
        playerRef.current.setPlaybackRate(originalPlaybackRate);
      }
    }
  };

  const handleStepBackwardMouseLeave = () => {
    if (stepBackwardLongPressTimerRef.current) {
      clearTimeout(stepBackwardLongPressTimerRef.current);
      stepBackwardLongPressTimerRef.current = null;
    }
    
    if (isLongPressingStepBackward) {
      setIsLongPressingStepBackward(false);
      if (playerRef.current) {
        playerRef.current.setPlaybackRate(originalPlaybackRate);
      }
    }
  };

  // 秒進按鈕長按處理函數（1x播放）
  const handleStepForwardLongPress = () => {
    setOriginalPlaybackRate(playbackRate);
    setIsLongPressingStepForward(true);
    if (playerRef.current) {
      playerRef.current.setPlaybackRate(1); // 1x播放
    }
  };

  const handleStepForwardMouseDown = () => {
    stepForwardLongPressTimerRef.current = setTimeout(() => {
      handleStepForwardLongPress();
    }, 500);
  };

  const handleStepForwardMouseUp = () => {
    if (stepForwardLongPressTimerRef.current) {
      clearTimeout(stepForwardLongPressTimerRef.current);
      stepForwardLongPressTimerRef.current = null;
    }
    
    if (isLongPressingStepForward) {
      setIsLongPressingStepForward(false);
      if (playerRef.current) {
        playerRef.current.setPlaybackRate(originalPlaybackRate);
      }
    }
  };

  const handleStepForwardMouseLeave = () => {
    if (stepForwardLongPressTimerRef.current) {
      clearTimeout(stepForwardLongPressTimerRef.current);
      stepForwardLongPressTimerRef.current = null;
    }
    
    if (isLongPressingStepForward) {
      setIsLongPressingStepForward(false);
      if (playerRef.current) {
        playerRef.current.setPlaybackRate(originalPlaybackRate);
      }
    }
  };

  const handleRewind = () => {
    // 調試日誌已移除
    
    if (!playerRef.current) {
      console.error('❌ [快退] playerRef.current 不存在');
      return;
    }
    
    try {
      const currentTime = playerRef.current.getCurrentTime();
      // 快進快退詳細日誌已移除
      
      // 🔧 修復：如果 getCurrentTime 返回無效值，使用 playedSeconds 作為備用
      const actualCurrentTime = (currentTime !== null && currentTime !== undefined && Number.isFinite(currentTime) && currentTime >= 0) 
        ? currentTime 
        : playedSeconds;
      
      // 快進快退詳細日誌已移除
      
      // 🔧 關鍵修復：如果時間仍然無效或為 0，且不是影片開始，則返回，避免跳回 00:00
      if (actualCurrentTime <= 0 && playedSeconds > 1) {
        console.warn('⚠️ [快退] 檢測到異常：當前時間為 0，但 playedSeconds 不是 0，可能導致跳回 00:00，中止快退操作');
        return;
      }
      
      const validDuration = Number.isFinite(duration) && duration > 0 ? duration : Infinity;
      const validForwardSeconds = Number.isFinite(fastForwardSeconds) ? fastForwardSeconds : 10;
      
      const newTime = Math.max(actualCurrentTime - validForwardSeconds, 0);
      
      // 快進快退詳細日誌已移除
      
      // 🔧 關鍵修復：確保新時間有效且小於當前時間
      if (Number.isFinite(newTime) && newTime >= 0 && newTime < actualCurrentTime) {
        // 調試日誌已移除
        playerRef.current.seekTo(newTime);
        setPlayedSeconds(newTime);
        setProgress(duration > 0 ? (newTime / duration) : 0);
      } else if (newTime === actualCurrentTime) {
        console.warn('⚠️ [快退] 已在影片開頭，無法繼續後退');
        return;
      } else {
        console.error('❌ [快退] 計算的新時間無效:', newTime);
      }
    } catch (error) {
      console.error('❌ [快退] 發生錯誤:', error);
    }
  };

  const handleStepForward = () => {
    // 調試日誌已移除
    
    if (!playerRef.current) {
      console.error('❌ [微進] playerRef.current 不存在');
      return;
    }
    
    try {
      const currentTime = playerRef.current.getCurrentTime();
      // 快進快退詳細日誌已移除
      
      // 🔧 修復：如果 getCurrentTime 返回無效值，使用 playedSeconds 作為備用
      const actualCurrentTime = (currentTime !== null && currentTime !== undefined && Number.isFinite(currentTime) && currentTime >= 0) 
        ? currentTime 
        : playedSeconds;
      
      // 快進快退詳細日誌已移除
      
      // 🔧 關鍵修復：如果時間仍然無效或為 0，且不是影片開始，則返回，避免跳回 00:00
      if (actualCurrentTime <= 0 && playedSeconds > 1) {
        console.warn('⚠️ [微進] 檢測到異常：當前時間為 0，但 playedSeconds 不是 0，可能導致跳回 00:00，中止微進操作');
        return;
      }
      
      const validDuration = Number.isFinite(duration) && duration > 0 ? duration : Infinity;
      
      let newTime;
      if (stepUnit === 'second') {
        newTime = Math.min(actualCurrentTime + 1, validDuration);
      } else {
        // 以幀為單位（假設 30fps）
        newTime = Math.min(actualCurrentTime + (1/30), validDuration);
        // 更新幀數
        setCurrentFrame(prev => (prev + 1) % 30);
      }
      
      // 快進快退詳細日誌已移除
      
      // 🔧 關鍵修復：確保新時間有效且大於當前時間
      if (Number.isFinite(newTime) && newTime >= 0 && newTime > actualCurrentTime) {
        // 調試日誌已移除
        playerRef.current.seekTo(newTime);
        setPlayedSeconds(newTime);
        setProgress(duration > 0 ? (newTime / duration) : 0);
      } else if (newTime === 0 && validDuration === 0) {
        console.error('❌ [微進] duration 為 0，無法微進，可能影片尚未載入完成');
        return;
      } else {
        console.error('❌ [微進] 計算的新時間無效:', newTime);
      }
    } catch (error) {
      console.error('❌ [微進] 發生錯誤:', error);
    }
  };

  const handleStepBackward = () => {
    // 調試日誌已移除
    
    if (!playerRef.current) {
      console.error('❌ [微退] playerRef.current 不存在');
      return;
    }
    
    try {
      const currentTime = playerRef.current.getCurrentTime();
      // 快進快退詳細日誌已移除
      
      // 🔧 修復：如果 getCurrentTime 返回無效值，使用 playedSeconds 作為備用
      const actualCurrentTime = (currentTime !== null && currentTime !== undefined && Number.isFinite(currentTime) && currentTime >= 0) 
        ? currentTime 
        : playedSeconds;
      
      // 快進快退詳細日誌已移除
      
      // 🔧 關鍵修復：如果時間仍然無效或為 0，且不是影片開始，則返回，避免跳回 00:00
      if (actualCurrentTime <= 0 && playedSeconds > 1) {
        console.warn('⚠️ [微退] 檢測到異常：當前時間為 0，但 playedSeconds 不是 0，可能導致跳回 00:00，中止微退操作');
        return;
      }
      
      let newTime;
      if (stepUnit === 'second') {
        newTime = Math.max(actualCurrentTime - 1, 0);
      } else {
        // 以幀為單位（假設 30fps）
        newTime = Math.max(actualCurrentTime - (1/30), 0);
        // 更新幀數
        setCurrentFrame(prev => (prev - 1 + 30) % 30);
      }
      
      // 快進快退詳細日誌已移除
      
      // 🔧 關鍵修復：確保新時間有效且小於當前時間
      if (Number.isFinite(newTime) && newTime >= 0 && newTime < actualCurrentTime) {
        // 調試日誌已移除
        playerRef.current.seekTo(newTime);
        setPlayedSeconds(newTime);
        setProgress(duration > 0 ? (newTime / duration) : 0);
      } else if (newTime === actualCurrentTime) {
        console.warn('⚠️ [微退] 已在影片開頭，無法繼續後退');
        return;
      } else {
        console.error('❌ [微退] 計算的新時間無效:', newTime);
      }
    } catch (error) {
      console.error('❌ [微退] 發生錯誤:', error);
    }
  };

  const handleProgress = (state: { played: number; playedSeconds: number; loaded: number; loadedSeconds: number }) => {
    if (!state) return;
    
    const validPlayedSeconds = Number.isFinite(state.playedSeconds) ? state.playedSeconds : 0;
    const validPlayed = Number.isFinite(state.played) ? state.played : 0;
    // 同步更新最後有效時間，供 rAF loop 於暫停/恢復空窗時平滑外插
    lastKnownTimeRef.current = validPlayedSeconds;
    
    // 🔧 修復：移除頻繁的進度監聽日誌，避免性能問題
    // if (Math.abs(validPlayedSeconds - playedSeconds) > 1) {
    //   // 分隔線（日誌已移除）
    // 调试日志已移除
    // 调试日志已移除
    // 调试日志已移除
    // 调试日志已移除
    // 调试日志已移除
    //   // 分隔線（日誌已移除）
    // }
    
    // 🔧 修復：移除過於敏感的進度檢查，避免誤報
    // if (validPlayedSeconds === playedSeconds) {
    // 调试日志已移除
    // }
    
    setPlayedSeconds(validPlayedSeconds);
    setProgress(validPlayed);
    setCurrentTime(validPlayedSeconds); // 新增：更新 currentTime 狀態
    
    const frame = calculateFrameFromTime(validPlayedSeconds);
    setCurrentFrame(frame);
    setCurrentTimecode(formatTimecode(validPlayedSeconds, frame));
  };

  // 增加播放狀態監控
  useEffect(() => {
    if (!isPlaying || !videoUrl) return;
    
    let lastProgressTime = Date.now();
    let lastPlayedSeconds = playedSeconds;
    
    const checkProgressHealth = setInterval(() => {
      const now = Date.now();
      const timeSinceLastProgress = now - lastProgressTime;
      
      // 如果超過 10 秒沒有進度更新，且播放狀態仍然是 true，可能是串流卡住了
      if (timeSinceLastProgress > 10000 && isPlaying && playedSeconds === lastPlayedSeconds) {
        console.log('⚠️ 檢測到播放進度停滯，可能是串流卡住了');
        console.log('最後進度更新時間:', new Date(lastProgressTime).toLocaleTimeString());
        console.log('當前播放進度:', playedSeconds);
        // 調試日誌已移除
        
        // 如果是 Google Drive 影片，嘗試重新載入
        if (videoUrl.includes('drive.google.com') || videoUrl.includes('docs.google.com')) {
          // 狀態追蹤日誌已移除
          // 這裡可以觸發重新載入邏輯
        }
      }
      
      // 更新最後進度時間
      if (playedSeconds !== lastPlayedSeconds) {
        lastProgressTime = now;
        lastPlayedSeconds = playedSeconds;
      }
    }, 5000); // 每 5 秒檢查一次
    
    return () => {
      clearInterval(checkProgressHealth);
    };
  }, [isPlaying, videoUrl, playedSeconds]);

  const handleDuration = (duration: number) => {
    const validDuration = Number.isFinite(duration) ? duration : 0;
    setDuration(validDuration);
  };

  const handleVolumeChange = (event: Event, newValue: number | number[]) => {
    if (!Number.isFinite(newValue as number)) return;
    
    const volumeValue = Math.max(0, Math.min(1, newValue as number));
    setVolume(volumeValue);
    setIsMuted(false);
  };

  const handleMuteToggle = () => {
    toggleMute();
  };

  const handleSeekChange = (event: Event, newValue: number | number[]) => {
    if (!Number.isFinite(newValue as number)) return;
    
    const validDuration = Number.isFinite(duration) ? duration : 0;
    const seekTime = (newValue as number) * validDuration;
    
    if (Number.isFinite(seekTime)) {
      setPlayedSeconds(seekTime);
      setProgress(newValue as number);
      if (playerRef.current) {
        playerRef.current.seekTo(seekTime);
      }
    }
  };

  // 處理 Google Drive 連結
  const handleGoogleDriveUrl = (url: string) => {
    try {
      // 處理 YouTube URL
      if (url.includes('youtu.be') || url.includes('youtube.com')) {
        let videoId = '';
        
        // 處理 youtu.be 短網址
        if (url.includes('youtu.be')) {
          const urlParts = url.split('?')[0]; // 移除所有參數
          videoId = urlParts.split('youtu.be/')[1];
        } 
        // 處理 youtube.com 網址
        else if (url.includes('youtube.com')) {
          const urlObj = new URL(url);
          videoId = urlObj.searchParams.get('v') || '';
        }

        if (!videoId) {
          throw new Error('無法解析 YouTube 影片 ID');
        }

        // 檢查是否為播放清單
        if (url.includes('list=')) {
          console.warn('不支援播放清單功能，將只播放單一影片');
        }

        // 轉換為標準 YouTube 觀看網址
        const standardUrl = `https://www.youtube.com/watch?v=${videoId}`;
        setVideoMode('YouTube');
        setTabValue(3);
        setVideoUrl(standardUrl);
        return;
      }

      // Remove @ symbol if present
      const cleanUrl = url.startsWith('@') ? url.substring(1) : url;

      // Extract file ID
        let fileId = '';
      let resourceKey = '';
      
      // 處理不同格式的 Google Drive 連結
      if (cleanUrl.includes('/file/d/')) {
        const match = cleanUrl.match(/\/file\/d\/([^/]+)/);
        if (match) fileId = match[1];
        
        // 提取 resourceKey（如果存在）
        const resourceKeyMatch = cleanUrl.match(/resourcekey=([^&]+)/);
        if (resourceKeyMatch) resourceKey = resourceKeyMatch[1];
      } else if (cleanUrl.includes('id=')) {
        const match = cleanUrl.match(/id=([^&]+)/);
        if (match) fileId = match[1];
      } else if (cleanUrl.includes('drive.google.com/open')) {
        const match = cleanUrl.match(/id=([^&]+)/);
        if (match) fileId = match[1];
        }
        
        if (!fileId) {
        console.error('無法解析 Google Drive 檔案 ID');
        setError('無法解析 Google Drive 檔案 ID，請確認連結格式是否正確');
        return '';
      }
      
      // 使用 iframe 嵌入格式，加入必要的參數
      const embedUrl = `https://drive.google.com/file/d/${fileId}/preview`;
      if (resourceKey) {
        return `${embedUrl}?resourcekey=${resourceKey}`;
      }
      return embedUrl;
    } catch (error) {
      console.error('處理 Google Drive URL 時發生錯誤:', error);
      setError('處理 Google Drive 連結時發生錯誤');
      return '';
    }
  };
  // 處理 Google Drive 影片準備完成事件
  const handleGoogleDriveReady = (videoUrl: string) => {
    // 調試日誌已移除
    
    // 自動儲存 Google Drive 影片到 Firestore
    if (videoUrl && (videoUrl.includes('drive.google.com') || videoUrl.includes('docs.google.com'))) {
      try {
        // 提取檔案 ID
        const fileId = videoUrl.match(/\/file\/d\/([^/]+)/)?.[1] || 
                      videoUrl.match(/id=([^&]+)/)?.[1];
        
        if (fileId) {
          // 準備影片資料
          const videoData = {
            id: fileId,
            provider: 'Google Drive',
            path: videoUrl,
            title: `Google Drive Video - ${fileId}`,
            thumbnail: '', // Google Drive 可能沒有縮圖
            userId: user?.uid || '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          
          // 儲存到 Firestore
          saveDocument('videos', videoData)
            .then(() => {
              // 調試日誌已移除
              setSnackbarMessage('Google Drive 影片已自動儲存');
              setSnackbarSeverity('success');
              setSnackbarOpen(true);
            })
            .catch((error) => {
              console.error('❌ 儲存 Google Drive 影片失敗:', error);
              setSnackbarMessage('儲存失敗，請稍後再試');
              setSnackbarSeverity('error');
              setSnackbarOpen(true);
            });
        }
      } catch (error) {
        console.error('處理 Google Drive 影片時發生錯誤:', error);
      }
    }
  };

  // 更新 loadVideo 函數
  const loadVideo = async (url: string, skipReset = false, firestoreIdOverride?: string) => {
    // 🔧 修復：只有在不是用戶主動播放時才設置自動載入標記
    if (!(window as any).__checkplay_user_initiated_play) {
      (window as any).__checkplay_user_initiated_play = false;
    }
    try {
      // 清理之前的 blob URL
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
        setCurrentBlobUrl(null);
        // 調試日誌已移除
      }
      
      // 如果是 blob URL，保存它以便後續清理
      if (url.startsWith('blob:')) {
        setCurrentBlobUrl(url);
        console.log('📎 New blob URL created:', url);
      }
      
      setVideoUrl(url);
      setVideoError(null);
      setCurrentTime(0);
      setDuration(0);
      setProgress(0);
      setIsPlaying(false);
      setCurrentTimecode('00:00:00 00');
      setShowTimecode(true);
      setIsTimecodeDisabled(false);
      
      // 🔧 修復：立即清空影片標題，防止顯示舊影片標題
      setVideoTitle('');
      // 清理日誌已移除
      
      // 🔧 修復：只在真正切換影片時清理字幕狀態（skipReset=false）
      if (!skipReset) {
        setSrtContent('');
        setCurrentVideoId('');
        setHasSRTCache(false);  // 清除字幕緩存狀態
        setCachedSRTLanguage('');  // 清除語言狀態
        setIsAutoFillMode(false);  // 關閉 AI 按鈕
        // 清理日誌已移除
      } else {
        // 快速操作日誌已移除
      }
      
      // 🔧 新增：清理 Note 文字區內容，防止不同影片間的筆記混淆
      setSubtitleContent('');
      setCurrentSubtitleKey(null);
      setSubtitleContentWithSource({ text: '', isFromSRT: false });
      // 清理日誌已移除
      
      // 🔧 新增：檢查是否有字幕緩存和獲取影片標題
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        const extractYouTubeId = (url: string): string | null => {
          const match = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/|live\/)([a-zA-Z0-9_-]{11})/);
          return match ? match[1] : null;
        };
        const youtubeId = extractYouTubeId(url);
        if (youtubeId) {
          // 開始處理（日誌已移除）
          // 異步檢查字幕緩存
          checkSRTCacheAvailability(youtubeId);
          
          // 🔧 修復：異步獲取影片標題（添加競態條件保護）
          const currentUrl = url; // 保存當前 URL 用於驗證
          // 調試日誌已移除
          youtubeApi.getVideoDetails(youtubeId)
            .then(youtubeInfo => {
              // 調試日誌已移除
              // 🔧 修復：檢查 URL 是否仍然是當前的，避免競態條件
              if (youtubeInfo && youtubeInfo.title && videoUrl === currentUrl) {
                setVideoTitle(youtubeInfo.title);
                // 調試日誌已移除
              } else if (videoUrl !== currentUrl) {
                // 影片切換日誌已移除
              } else {
                console.log('⚠️ YouTube API 回應無效:', { youtubeInfo, videoUrl, currentUrl });
              }
            })
            .catch(error => {
              console.error('❌ 獲取 YouTube 影片標題失敗:', error);
            });
        } else {
          console.log('⚠️ 無法從 URL 中提取 YouTube ID:', url);
        }
      } else {
        // 非 YouTube 影片，清空標題
        setVideoTitle('');
      }
      
      // 重置當前模式的按鈕狀態（MV模式下不重置數字鍵，SPA模式下不重置數字鍵）
      if (mButtonState !== 'MV' && !skipReset) {
        resetAllButton();
      }
      
      // 🔧 關鍵修復：始終從 Firestore 載入重點，不受 skipReset 影響
      // skipReset 只控制 resetAllButton，不應該跳過載入 Firestore 重點
      if (mButtonState !== 'MV') {
        try {
          // 提取 YouTube ID 或使用完整 URL
          let videoId = url;
          if (url.includes('youtube.com') || url.includes('youtu.be')) {
            const extractYouTubeId = (url: string): string | null => {
              const match = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/|live\/)([a-zA-Z0-9_-]{11})/);
              return match ? match[1] : null;
            };
            const extractedId = extractYouTubeId(url);
            if (extractedId) {
              videoId = extractedId;
            }
          }
          
          // 清理 videoId 以符合 Firestore 路徑要求
          const cleanVideoId = videoId.replace(/[\/\\]/g, '_');
          
          // 🔧 關鍵修復：只使用傳入的 firestoreIdOverride，完全忽略 currentFirestoreId
          // 避免在 loadVideo 執行過程中 currentFirestoreId 被意外更新
          const effectiveFirestoreId = firestoreIdOverride;
          // 調試日誌已移除
          
          let querySnapshot = null;
          let loadedDoc = null;
          
          // 🔧 修復：優先使用 effectiveFirestoreId 精確查詢（避免觀點污染）
          if (effectiveFirestoreId) {
            try {
              const specificDocRef = doc(db, "sharedVbids", effectiveFirestoreId);
              const specificDocSnap = await getDoc(specificDocRef);
              
              if (specificDocSnap.exists()) {
                // 調試日誌已移除
                loadedDoc = specificDocSnap;
              } else {
                console.log('⚠️ firestoreId 文檔不存在，將創建新文檔:', effectiveFirestoreId);
              }
            } catch (error) {
              console.warn('⚠️ 使用 firestoreId 查詢失敗:', error);
            }
          }
          
          // 🔧 關鍵修復：移除 fallback 邏輯，確保只使用精確的 firestoreId 查詢
          // 避免觀點污染：不使用 path 查詢，因為它不區分觀點版本
          if (!loadedDoc && effectiveFirestoreId) {
            console.log('⚠️ 指定的觀點文檔不存在，使用初始狀態:', effectiveFirestoreId);
          } else if (!loadedDoc && !effectiveFirestoreId) {
            console.log('⚠️ 沒有提供 firestoreId，使用初始狀態');
          }
          
          if (loadedDoc) {
            const data = loadedDoc.data();
            
            // 設置當前文檔ID
            setCurrentDocumentId(loadedDoc.id);
            
            // 🔧 設置當前觀點名稱（從 Firestore 讀取）
            if (data.viewpointName) {
              setCurrentViewpointName(data.viewpointName);
              // 調試日誌已移除
            } else {
              setCurrentViewpointName(''); // 清空觀點名稱
            }
            
            if (data.numberKeys && Object.keys(data.numberKeys).length > 0) {
              // 調試日誌已移除
              setNumberKeys(data.numberKeys);
              
              // 同步更新 tabNumberKeys
              setTabNumberKeys(prev => {
                const newState = { ...prev };
                newState[tabValue] = data.numberKeys;
                return newState;
              });
            } else if (data.vbids && data.vbids.length > 0) {
              // 調試日誌已移除
              
              const restoredNumberKeys = vbidsToNumberKeys(data.vbids);
              // 調試日誌已移除
              
              // 檢查還原的 numberKeys 的 isActive 狀態
              let activeCount = 0;
              Object.entries(restoredNumberKeys).forEach(([key, state]) => {
                if (state.isActive) {
                  activeCount++;
    
                }
              });
              // 調試日誌已移除
              
              setNumberKeys(restoredNumberKeys);
              
              // 同步更新 tabNumberKeys
              setTabNumberKeys(prev => {
                const newState = { ...prev };
                newState[tabValue] = restoredNumberKeys;
                return newState;
              });
            } else {
              // 如果 sharedVbids 中沒有找到重點數，使用初始狀態
              // 調試日誌已移除
              setNumberKeys(getInitialNumberKeyState(maxNumberKeys));
            }
          } else {
            console.log('⚠️ Firestore 中沒有找到影片記錄，使用初始狀態');
            setNumberKeys(getInitialNumberKeyState(maxNumberKeys));
          }
        } catch (error) {
          console.error('❌ 載入重點數失敗:', error);
          setNumberKeys(getInitialNumberKeyState(maxNumberKeys));
        }
      }
      
      // Video loaded
    } catch (error) {
      console.error('Error loading video:', error);
      setVideoError('Failed to load video');
    }
  };

  const [lastRestartClick, setLastRestartClick] = useState<number>(0);
  const DOUBLE_CLICK_DELAY = 300; // 300ms for double click detection

  const handleRestart = () => {
    const now = Date.now();
    
    // Always perform basic reset
    if (playerRef.current) {
      playerRef.current.seekTo(0);
      setPlayedSeconds(0);
      setProgress(0);
      setCurrentFrame(0);
      setCurrentTimecode(formatTimecode(0, 0));
    }

    // Check for double click
    if (now - lastRestartClick <= DOUBLE_CLICK_DELAY) {
      // Double click detected - reset all number keys
      resetAllButton();
      // Play reset sound effect
      const audio = new Audio('/reset-sound.mp3');
      audio.play().catch(console.error);
      // Show success message
      setSnackbarMessage('已重置所有按鈕');
      setSnackbarOpen(true);
    }

    setLastRestartClick(now);
  };

  // 🔧 已从 vbidUtils.ts 导入 formatTimecode
  // 🔧 calculateFrameFromTime 已移至 timestampUtils.ts
  const calculateFrameFromTime = (time: number): number => {
    return Math.floor((time % 1) * 30);
  };

  const formatScreenshotName = (actualTime?: number, frameOverride?: number) => {
    // 使用傳入的實際時間或當前播放時間
    const timeToUse = actualTime !== undefined ? actualTime : playedSeconds;
    
    // 從實際時間取得時間碼
    const totalSeconds = Math.floor(timeToUse);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    
    // 使用傳入的幀數或當前幀數
    const frame = frameOverride !== undefined ? frameOverride : currentFrame;
    
    // 格式化時間碼：CheckPlay_00h30m08s_14
    const timeCode = `${String(hours).padStart(2, '0')}h${String(minutes).padStart(2, '0')}${String(seconds).padStart(2, '0')}s_${String(frame).padStart(2, '0')}`;
    
    return `CheckPlay_${timeCode}`;
  };

  // 🔧 移除：時間碼縮放狀態已移至 useTimecodeDisplay Hook
  // const [timecodeScale, setTimecodeScale] = useState<number>(0.85);
  const [isTimecodeDisabled, setIsTimecodeDisabled] = useState<boolean>(false);
  
  // 🔧 保留：時鐘格式狀態（非顯示控制）
  const [timeFormat, setTimeFormat] = useState<'24h' | '12h'>((localStorage.getItem('timeFormat') as '24h' | '12h') || '24h');
  const [clockTime, setClockTime] = useState<Date>(new Date());

  // 時鐘更新邏輯
  useEffect(() => {
    if (!uiDisplay.clock) return;
    
    const timer = setInterval(() => {
      setClockTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, [uiDisplay.clock]);

  // 監聽時鐘設定變化並保存到 localStorage
  useEffect(() => {
    localStorage.setItem('uiDisplay.clock', uiDisplay.clock.toString());
  }, [uiDisplay.clock]);

  useEffect(() => {
    localStorage.setItem('uiDisplay.currentTime', uiDisplay.currentTime.toString());
  }, [uiDisplay.currentTime]);

  useEffect(() => {
    localStorage.setItem('uiDisplay.remainingTime', uiDisplay.remainingTime.toString());
  }, [uiDisplay.remainingTime]);

  useEffect(() => {
    localStorage.setItem('timeFormat', timeFormat);
  }, [timeFormat]);

  // 時鐘功能輔助函數
  const formatClockTime = (date: Date) => {
    if (timeFormat === '12h') {
      return date.toLocaleTimeString('zh-TW', { 
        hour12: true, 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
      });
    }
    return date.toLocaleTimeString('zh-TW', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  const calculateRemainingTime = () => {
    if (!playerRef.current) return 0;
    const currentPlayTime = playerRef.current.getCurrentTime();
    const duration = playerRef.current.getDuration();
    return Math.max(0, duration - currentPlayTime);
  };

  // 🔧 formatDuration 已从 '../utils/formatUtils' 导入

  // 格式化播放時間（包含影片總長度）
  const formatCurrentTimeWithDuration = () => {
    if (!playerRef.current) return '00:00/00:00';
    
    const currentPlayTime = playerRef.current.getCurrentTime();
    const duration = playerRef.current.getDuration();
    
    // 格式化當前播放時間
    const currentHours = Math.floor(currentPlayTime / 3600);
    const currentMinutes = Math.floor((currentPlayTime % 3600) / 60);
    const currentSecs = Math.floor(currentPlayTime % 60);
    
    // 格式化總時長
    const totalHours = Math.floor(duration / 3600);
    const totalMinutes = Math.floor((duration % 3600) / 60);
    const totalSecs = Math.floor(duration % 60);
    
    // 根據是否有小時來決定格式
    if (totalHours > 0) {
      const currentStr = `${String(currentHours).padStart(2, '0')}:${String(currentMinutes).padStart(2, '0')}:${String(currentSecs).padStart(2, '0')}`;
      const totalStr = `${String(totalHours).padStart(2, '0')}:${String(totalMinutes).padStart(2, '0')}:${String(totalSecs).padStart(2, '0')}`;
      return `${currentStr}/${totalStr}`;
    } else {
      const currentStr = `${String(currentMinutes).padStart(2, '0')}:${String(currentSecs).padStart(2, '0')}`;
      const totalStr = `${String(totalMinutes).padStart(2, '0')}:${String(totalSecs).padStart(2, '0')}`;
      return `${currentStr}/${totalStr}`;
    }
  };

  // 格式化剩餘時間
  const formatRemainingTime = () => {
    if (!playerRef.current) return '00:00';
    
    const currentPlayTime = playerRef.current.getCurrentTime();
    const duration = playerRef.current.getDuration();
    const remainingTime = Math.max(0, duration - currentPlayTime);
    
    const remainingHours = Math.floor(remainingTime / 3600);
    const remainingMinutes = Math.floor((remainingTime % 3600) / 60);
    const remainingSecs = Math.floor(remainingTime % 60);
    
    if (remainingHours > 0) {
      return `${String(remainingHours).padStart(2, '0')}:${String(remainingMinutes).padStart(2, '0')}:${String(remainingSecs).padStart(2, '0')}`;
    } else {
      return `${String(remainingMinutes).padStart(2, '0')}:${String(remainingSecs).padStart(2, '0')}`;
    }
  };

  // 添加時間碼點擊處理函數
  const handleTimecodeClick = (event?: React.MouseEvent<HTMLElement>) => {
    // 播放按鈕點擊音效
    playButtonSound('click');
    
    // 暫停影片播放
    if (isPlaying) {
      pauseVideo();
    }
    
    // 暫停時間碼更新
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    // 🔧 总是创建临时锚点，避免删除真实的按钮元素
    const anchor = document.createElement('div');
    anchor.style.position = 'fixed';
    anchor.style.top = '50%';
    anchor.style.left = '50%';
    anchor.style.transform = 'translate(-50%, -50%)';
    anchor.style.width = '1px';
    anchor.style.height = '1px';
    anchor.style.pointerEvents = 'none';
    anchor.style.visibility = 'hidden';
    anchor.setAttribute('data-temp-anchor', 'true'); // 标记为临时锚点
    document.body.appendChild(anchor);
    setTimecodeMenuAnchorEl(anchor);
  };

  // 時間碼選單關閉處理函數
  const handleTimecodeMenuClose = () => {
    // 只清理临时创建的锚点元素，不清理真实的按钮元素
    if (timecodeMenuAnchorEl && timecodeMenuAnchorEl.getAttribute('data-temp-anchor') === 'true') {
      if (timecodeMenuAnchorEl.parentNode) {
        timecodeMenuAnchorEl.parentNode.removeChild(timecodeMenuAnchorEl);
      }
    }
    
    setTimecodeMenuAnchorEl(null);
  };

  // 修改 TimecodeButton 組件
  const TimecodeButton = () => (
    <IconButton
      onClick={handleTimecodeClick}
      disabled={false}
      sx={{
        minWidth: 'auto',
        padding: '6px',
        marginRight: 1,
        transform: `scale(${timecodeScale})`,
        transition: 'transform 0.3s ease',
        backgroundColor: showTimecode ? 'primary.main' : 'grey.400',
        '&:hover': {
          backgroundColor: showTimecode ? 'primary.dark' : 'grey.500',
        }
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          color: 'white'
        }}
      >
        00:00:00
      </Typography>
    </IconButton>
  );

  // 修改 drawWatermark 函數
  const drawWatermark = (ctx: CanvasRenderingContext2D, width: number, height: number, textOverride?: string) => {
    if (!showTimecode || isTimecodeDisabled) return;

    const text = textOverride || formatTimecode(playedSeconds);
    
    // 根據播放器尺寸計算基礎字體大小（寬度的 2.5%）
    let fontSize = Math.round(width * 0.025);
    
    // 應用時間碼縮放
    if (timecodeScale === 0.85) {
      fontSize *= 0.7; // 縮小2級
    } else if (timecodeScale === 0.7) {
      fontSize *= 0.5; // 縮小4級
    }
    
    // 設定字體和樣式
    ctx.font = `bold ${fontSize}px monospace`;
    ctx.textBaseline = 'middle'; // 設定文字基線為中間，以便更準確的垂直置中
    ctx.textAlign = 'center'; // 設定文字對齊方式為置中，以便更準確的水平置中
    
    // 計算文字尺寸
    const metrics = ctx.measureText(text);
    const textWidth = metrics.width;
    const textHeight = fontSize;
    
    // 計算邊距（使用播放器尺寸的百分比）
    const margin = Math.round(width * 0.03); // 3% 的邊距
    const padding = Math.round(fontSize * 0.5);
    
    // 根據位置計算座標（使用播放器尺寸的百分比）
    let x, y;
    switch (timecodePosition) {
      case 'topLeft':
        x = margin + (textWidth / 2);
        y = margin + (textHeight / 2);
        ctx.textAlign = 'left';
        break;
      case 'topCenter':
        x = width / 2;
        y = margin + (textHeight / 2);
        break;
      case 'topRight':
        x = width - margin - (textWidth / 2);
        y = margin + (textHeight / 2);
        ctx.textAlign = 'right';
        break;
      case 'bottomLeft':
        x = margin + (textWidth / 2);
        y = height - margin - (textHeight / 2);
        ctx.textAlign = 'left';
        break;
      case 'bottomCenter':
        x = width / 2;
        y = height - margin - (textHeight / 2);
        break;
      case 'bottomRight':
        x = width - margin - (textWidth / 2);
        y = height - margin - (textHeight / 2);
        ctx.textAlign = 'right';
        break;
      default:
        x = width / 2;
        y = height - margin - (textHeight / 2);
    }
    
    // 繪製背景
    if (showTimecodeBackground) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      // 根據文字對齊方式調整背景位置
      let bgX = x;
      if (ctx.textAlign === 'center') {
        bgX = x - (textWidth / 2);
      } else if (ctx.textAlign === 'right') {
        bgX = x - textWidth;
      }
      ctx.fillRect(bgX - padding, y - (textHeight / 2) - padding, textWidth + padding * 2, textHeight + padding * 2);
      
      // 添加邊框
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 1;
      ctx.strokeRect(bgX - padding, y - (textHeight / 2) - padding, textWidth + padding * 2, textHeight + padding * 2);
    }
    
    // 繪製文字
    ctx.fillStyle = 'white';
    ctx.fillText(text, x, y);
  };

  const getScreenshotScale = (size: ScreenshotSize): number => {
    switch (size) {
      case 'original':
        return 1;
      case '70':
        return 0.7;
      case '50':
      default:
        return 0.5;
    }
  };

  const pauseVideo = () => {
    const player = playerRef.current?.getInternalPlayer?.();
    if (!player) return;
    // YouTube
    if (typeof player.pauseVideo === 'function') {
      player.pauseVideo();
    }
    // HTML5 video
    else if (typeof player.pause === 'function') {
      player.pause();
    }
  };

  const [crosshairColor, setCrosshairColor] = useState<'blue' | 'yellow' | 'green' | 'none'>('none');

  const drawGuideLines = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    if (crosshairColor === 'none') return;
    
    ctx.save();
    ctx.strokeStyle = crosshairColor === 'blue' ? '#0000FF' : 
                      crosshairColor === 'yellow' ? '#FFFF00' : '#00FF00';
    ctx.lineWidth = 2;
    ctx.globalAlpha = 0.8;

    // 繪製十字線
    ctx.beginPath();
    ctx.moveTo(width / 2, 0);
    ctx.lineTo(width / 2, height);
    ctx.moveTo(0, height / 2);
    ctx.lineTo(width, height / 2);
    ctx.stroke();

    ctx.restore();
  };

  const handleCrosshairClick = () => {
    setCrosshairColor(prev => {
      const nextColor = (() => {
        switch (prev) {
          case 'none':
            return 'yellow';
          case 'yellow':
            return 'green';
          case 'green':
            return 'blue';
          case 'blue':
            return 'none';
          default:
            return 'yellow';
        }
      })();
      
      // 更新顯示狀態
      setUiDisplay((prev) => ({ ...prev, crosshair: nextColor !== 'none' }));
      
      return nextColor;
    });
  };

  // 在 drawGuideLines 函數後添加繪製遮罩的函數
  const drawMovieMask = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    if (!uiDisplay.movieMask) return;
    
    const maskHeight = height * 0.15; // 15% 的高度
    
    ctx.fillStyle = 'black';
    // 繪製上方遮罩
    ctx.fillRect(0, 0, width, maskHeight);
    // 繪製下方遮罩
    ctx.fillRect(0, height - maskHeight, width, maskHeight);
  };

  // ═══════════════════════════════════════════════════════════════════════
  // 📸 截圖功能模組
  // ═══════════════════════════════════════════════════════════════════════
  // 負責：單張截圖、連拍截圖、截圖管理、浮水印設定
  // State: screenshotSize, screenshots, watermarkSettings
  // Handlers: handleScreenshot, handleBurstScreenshot, handleContinuousScreenshot
  
  // 在截圖函數中加入遮罩繪製
  const handleScreenshot = async () => {
    // 檢查是否為 YouTube 模式
    if (tabValue === 3) {
      setSnackbarMessage('YouTube模式下不開放截圖功能，避免違反影片著作權。僅Video模式(本機檔案)下能截圖');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    try {
      // 播放快門聲
      playShutterSound();

      // 創建 canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      let width = 0;
      let height = 0;
      let sourceElement: HTMLVideoElement | HTMLImageElement | null = null;

      if (tabValue === 3) {
        // PHOTOS 模式
        if (!imageUrl) return;
        const img = new Image();
        img.src = imageUrl;
        sourceElement = img;
        await new Promise((resolve) => {
          img.onload = () => {
            width = img.width;
            height = img.height;
            resolve(null);
          };
        });
      } else {
        // 影片模式
        if (!playerRef.current || !videoUrl) return;
        const videoElement = playerRef.current.getInternalPlayer() as HTMLVideoElement;
        sourceElement = videoElement;
        width = videoElement.videoWidth;
        height = videoElement.videoHeight;
      }

      if (!sourceElement || width === 0 || height === 0) return;

      // 根據截圖尺寸設定計算新的尺寸
      let targetWidth = width;
      let targetHeight = height;
      if (screenshotSize === '1/2') {
        targetWidth = Math.round(width * 0.5);
        targetHeight = Math.round(height * 0.5);
      } else if (screenshotSize === '1/4') {
        targetWidth = Math.round(width * 0.25);
        targetHeight = Math.round(height * 0.25);
      }

      // 設置 canvas 尺寸為目標尺寸
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      // 使用縮放繪製來源圖像
      ctx.drawImage(sourceElement, 0, 0, width, height, 0, 0, targetWidth, targetHeight);

      // 繪製輔助線
      // 1. 繪製安全框
      if (uiDisplay.safetyFrame) {
        const frameSize = (safetyFrameSize / 100) * Math.min(targetWidth, targetHeight);
        const x = (targetWidth - frameSize) / 2;
        const y = (targetHeight - frameSize) / 2;
        
        ctx.strokeStyle = safetyFrameColor === 'magenta' ? '#FF00FF' :
                         safetyFrameColor === 'blue' ? '#0000FF' :
                         safetyFrameColor === 'yellow' ? '#FFFF00' :
                         '#00FF00';
        ctx.lineWidth = 1.5;
        ctx.strokeRect(x, y, frameSize, frameSize);
      }

      // 2. 繪製十字線
      if (uiDisplay.crosshair && crosshairColor !== 'none') {
        const color = crosshairColor === 'blue' ? '#0000FF' : 
                     crosshairColor === 'yellow' ? '#FFFF00' : '#00FF00';
        
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.globalAlpha = 0.8;

        // 垂直線
        ctx.beginPath();
        ctx.moveTo(targetWidth / 2, 0);
        ctx.lineTo(targetWidth / 2, targetHeight);
        ctx.stroke();

        // 水平線
        ctx.beginPath();
        ctx.moveTo(0, targetHeight / 2);
        ctx.lineTo(targetWidth, targetHeight / 2);
        ctx.stroke();

        ctx.globalAlpha = 1;
      }

      // 3. 繪製井字線
      if (gridMode !== 'none') {
    ctx.strokeStyle = '#808080';
    ctx.lineWidth = 1.5;
    ctx.globalAlpha = 0.8;

        if (gridMode === '3x3') {
        // 垂直線
        ctx.beginPath();
          ctx.moveTo(targetWidth / 3, 0);
          ctx.lineTo(targetWidth / 3, targetHeight);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(2 * targetWidth / 3, 0);
          ctx.lineTo(2 * targetWidth / 3, targetHeight);
          ctx.stroke();

        // 水平線
          ctx.beginPath();
          ctx.moveTo(0, targetHeight / 3);
          ctx.lineTo(targetWidth, targetHeight / 3);
        ctx.stroke();
        ctx.beginPath();
          ctx.moveTo(0, 2 * targetHeight / 3);
          ctx.lineTo(targetWidth, 2 * targetHeight / 3);
          ctx.stroke();
        } else if (gridMode === '6x4') {
          // 垂直線
        for (let i = 1; i < 6; i++) {
            ctx.beginPath();
            ctx.moveTo(i * targetWidth / 6, 0);
            ctx.lineTo(i * targetWidth / 6, targetHeight);
            ctx.stroke();
        }

        // 水平線
        for (let i = 1; i < 4; i++) {
            ctx.beginPath();
            ctx.moveTo(0, targetHeight * i / 4);
            ctx.lineTo(targetWidth, targetHeight * i / 4);
        ctx.stroke();
          }
        } else if (gridMode === '10x6') {
        // 垂直線
        for (let i = 1; i < 10; i++) {
            ctx.beginPath();
            ctx.moveTo(i * targetWidth / 10, 0);
            ctx.lineTo(i * targetWidth / 10, targetHeight);
            ctx.stroke();
        }

        // 水平線
        for (let i = 1; i < 6; i++) {
            ctx.beginPath();
            ctx.moveTo(0, targetHeight * i / 6);
            ctx.lineTo(targetWidth, targetHeight * i / 6);
        ctx.stroke();
          }
    }

    ctx.globalAlpha = 1;
      }

      // 繪製時間碼
      if (showTimecode) {
        const currentTime = tabValue === 3 ? 0 : (playerRef.current?.getCurrentTime() || 0);
        const currentFrame = calculateFrameFromTime(currentTime);
        const timecode = formatTimecode(currentTime, currentFrame);
        ctx.font = '24px Arial';
        ctx.fillStyle = '#FFFFFF';
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 3;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.strokeText(timecode, 10, 10);
        ctx.fillText(timecode, 10, 10);
      }

      // 繪製浮水印
      if (uiDisplay.watermarkInOutput && showTimecode) {
        drawWatermark(ctx, targetWidth, targetHeight);
      }

      // 繪製遮罩
      if (uiDisplay.movieMask) {
        drawMovieMask(ctx, targetWidth, targetHeight);
      }

      // 生成圖片數據
      const imageData = canvas.toDataURL('image/jpeg', 0.95);
      const fileName = formatScreenshotName();

      // 添加到已捕獲的圖片列表
      setCapturedImages(prev => [...prev, {
        id: Date.now().toString(),
        name: fileName,
        url: imageData,
        timestamp: tabValue === 3 ? 0 : (videoMode === 'Google' && googleDriveRef.current ? googleDriveRef.current.getCurrentTime() : playerRef.current?.getCurrentTime() || 0)
      }]);

      // 直接開啟圖片管理器
      setImageManagerOpen(true);
    } catch (error) {
      console.error('截圖失敗:', error);
      setError('截圖失敗');
    }
  };

  const handleBurstScreenshot = async () => {
    // 檢查是否為 YouTube 模式
    if (tabValue === 3) {
      setSnackbarMessage('YouTube模式下不開放截圖功能，避免違反影片著作權。僅Video模式(本機檔案)下能截圖');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    if (!playerRef.current) return;
    const videoElement = playerRef.current.getInternalPlayer();
    if (!(videoElement instanceof HTMLVideoElement)) return;

    setIsBurstMode(true);
    setImageManagerOpen(true);

    for (let i = 0; i < burstCount; i++) {
      // 播放快門聲
      playShutterSound();

        const canvas = document.createElement('canvas');
      const width = videoElement.videoWidth;
      const height = videoElement.videoHeight;

      // 根據截圖尺寸設定計算新的尺寸
      let targetWidth = width;
      let targetHeight = height;
      if (screenshotSize === '1/2') {
        targetWidth = Math.round(width * 0.5);
        targetHeight = Math.round(height * 0.5);
      } else if (screenshotSize === '1/4') {
        targetWidth = Math.round(width * 0.25);
        targetHeight = Math.round(height * 0.25);
      }

      // 設置 canvas 尺寸為目標尺寸
      canvas.width = targetWidth;
      canvas.height = targetHeight;

            const ctx = canvas.getContext('2d');
      if (!ctx) continue;

      // 清除 canvas
      ctx.clearRect(0, 0, targetWidth, targetHeight);
      
      // 確保影片已經載入
      if (videoElement.readyState >= videoElement.HAVE_CURRENT_DATA) {
        // 使用縮放繪製影片幀
        ctx.drawImage(videoElement, 0, 0, width, height, 0, 0, targetWidth, targetHeight);
        
        // 繪製輔助線
        drawGuideLines(ctx, targetWidth, targetHeight);
        
        // 只有在啟用輸出浮水印時才繪製浮水印
        if (uiDisplay.watermarkInOutput && showTimecode && !isTimecodeDisabled) {
          drawWatermark(ctx, targetWidth, targetHeight);
        }
              
        const imageData = canvas.toDataURL('image/jpeg', 0.95);
        const fileName = formatScreenshotName();
        
        setCapturedImages(prev => [...prev, {
          id: Date.now().toString(),
          name: fileName,
          url: imageData,
          timestamp: videoMode === 'Google' && googleDriveRef.current ? googleDriveRef.current.getCurrentTime() : playerRef.current?.getCurrentTime() || 0
        }]);
      }

      // 等待指定的間隔時間
      await new Promise(resolve => setTimeout(resolve, burstInterval));
    }

    setIsBurstMode(false);
  };

  // 🔧 重構：移除原來的 handleSettings，創建一個觸發事件的版本
  // 設定功能統一由 MainLayout 管理
  const handleSettings = useCallback(() => {
    console.log('🔧 MainPage: 觸發打開設定事件');
    // 觸發事件，由 MainLayout 處理
    window.dispatchEvent(new CustomEvent('openSettings'));
  }, []);

  // 🔧 新增：處理刪除NO重點
  const handleDeleteNoteFromSRT = (keyNo: number | string) => {
    // 調試日誌已移除
    
    const keyNumber = typeof keyNo === 'number' ? keyNo : parseInt(keyNo);
    if (isNaN(keyNumber)) {
      console.error('無效的NO編號:', keyNo);
      return;
    }
    
    // 檢查該NO是否存在且為活躍狀態
    const currentTabNumberKeys = tabValue === 3 ? numberKeys : tabNumberKeys[tabValue];
    const keyState = currentTabNumberKeys[keyNumber];
    
    if (!keyState || !keyState.isActive) {
      console.warn(`NO ${keyNumber} 不存在或未啟動`);
      setSnackbarMessage(`NO ${keyNumber} 不存在或未啟動`);
      setSnackbarOpen(true);
      return;
    }
    
    // 執行刪除邏輯
    if (tabValue === 3) {
      setNumberKeys(prev => ({
        ...prev,
        [keyNumber]: {
          keyNo: keyNumber,
          timestamp: null,
          timecode: '00:00:00 00',
          note: '',
          screenshot: '',
          flag: 'off',
          privacy: 'Private' as const,
          isActive: false,
          frame: null
        }
      }));
    } else {
      setTabNumberKeys(prev => ({
        ...prev,
        [tabValue]: {
          ...prev[tabValue],
          [keyNumber]: {
            keyNo: keyNumber,
            timestamp: null,
            timecode: '00:00:00 00',
            note: '',
            screenshot: '',
            flag: 'off',
            privacy: 'Private' as const,
            isActive: false,
            frame: null
          }
        }
      }));
    }
    
    // 同步到Firestore
    const syncDeleteToFirestore = async () => {
      try {
        // 調試日誌已移除
        
        if (user && videoUrl) {
          const videoId = extractYouTubeId(videoUrl);
          if (videoId) {
            const userDocRef = doc(db, 'users', user.uid);
            const videoDocRef = doc(userDocRef, 'videos', videoId);
            
            // 🔧 修復：使用 setDoc 並啟用 merge，確保文檔不存在時也能創建
            await setDoc(videoDocRef, {
              numberKeys: {
                [keyNumber]: {
                  keyNo: keyNumber,
                  timestamp: null,
                  timecode: '00:00:00 00',
                  note: '',
                  screenshot: '',
                  flag: 'off',
                  privacy: 'Private',
                  isActive: false,
                  frame: null
                }
              }
            }, { merge: true }); // 🔧 關鍵：merge 模式，不會覆蓋其他欄位
            
            // 成功日誌已移除
          }
        }
      } catch (error) {
        console.error('❌ 同步刪除NO到Firestore失敗:', error);
      }
    };
    
    // 延遲同步到Firestore
    setTimeout(syncDeleteToFirestore, 100);
    
    setSnackbarMessage(`已刪除NO ${keyNumber}重點`);
    setSnackbarOpen(true);
    
    // 操作完成（日誌已移除）
  };

  // 🔧 新增：處理字幕下載
  const handleDownloadCaptions = async () => {
    try {
      if (!videoUrl) {
        alert('請先載入影片');
        return;
      }

      // 提取 YouTube 影片 ID
      const videoId = extractYouTubeId(videoUrl);
      
      if (!videoId) {
        alert('這不是 YouTube 影片，無法下載字幕');
        return;
      }

      console.log('[Caption Download] 開始下載字幕 - Video ID:', videoId);
      setCurrentVideoId(videoId);
      
      // 🔧 修復：避免重複請求，直接使用 fetchSRTContent 獲取內容並下載
      // 調試日誌已移除
      await fetchSRTContent(videoId);
      
      // 如果成功獲取內容，創建下載
      if (srtContent) {
        const blob = new Blob([srtContent], { type: 'text/plain; charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${videoId}_zh-TW.srt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        console.log('[Caption Download] 字幕下載成功');
      }
    } catch (error) {
      console.error('[Caption Download] 下載字幕時發生錯誤:', error);
      alert(`下載字幕失敗：${error instanceof Error ? error.message : '未知錯誤'}`);
    }
  };

  // 🆕 新增：處理 SRT 文字校正後的更新
  const handleSRTCorrected = async (correctedSRT: string) => {
    try {
      console.log('[SRT Correction] 收到校正後的 SRT，準備更新...');
      // 調試日誌已移除
      // 調試日誌已移除

      if (!currentVideoId) {
        console.error('[SRT Correction] 沒有影片 ID，無法保存');
        alert('錯誤：無法保存校正後的字幕（缺少影片 ID）');
        return;
      }

      // 保存到 Firestore 緩存（覆蓋原有內容）
      const { saveSRTToCache } = await import('../services/srtCacheService');
      const language = cachedSRTLanguage || 'zh-TW'; // 使用當前的語言
      
      await saveSRTToCache(currentVideoId, language, correctedSRT);
      // 調試日誌已移除
      console.log('[SRT Correction] 🌐 所有用戶現在都可以使用校正後的字幕！');

      // 更新本地狀態
      setSrtContent(correctedSRT);
      // 調試日誌已移除

      // 🔧 重要：不影響已標記的重點
      // 重點是基於時間碼的，文字改變不會影響 numberKeys 中的映射關係
      console.log('[SRT Correction] ℹ️ 已標記的重點不受影響（基於時間碼映射）');

    } catch (error) {
      console.error('[SRT Correction] 保存校正後的 SRT 失敗:', error);
      alert(`保存校正後的字幕失敗：${error instanceof Error ? error.message : '未知錯誤'}`);
    }
  };

  // 🔧 修改：獲取 SRT 內容用於顯示（支援語言選擇 + Firestore 緩存）
  const fetchSRTContent = async (videoId: string, language: string = 'zh-TW'): Promise<string | null> => {
    setIsDownloadingSubtitles(true); // 🔧 新增：設置下載狀態為 true
    
    try {
      // 調試日誌已移除
      
      // 🔧 新增：首先嘗試從 Firestore 緩存獲取
      const { getSRTFromCache, saveSRTToCache } = await import('../services/srtCacheService');
      
      // 🔧 新增：顯示下載中的提示（如果緩存存在但內容還沒加載到狀態中）
      // 調試日誌已移除
      
      const cachedContent = await getSRTFromCache(videoId, language);
      if (cachedContent) {
        // 調試日誌已移除
        setSrtContent(cachedContent);
        
        // 🔧 修復：更新字幕緩存狀態，讓 AI 按鈕變藍色
        setHasSRTCache(true);
        setCachedSRTLanguage(language);
        setCurrentVideoId(videoId);
        // 狀態變化（日誌已移除）
        
        // 🔧 新增：字幕下載完成提示和 ToolTip 觸發
        alert('字幕下載完成！');
        if (subtitleFillTooltipTimerRef.current) {
          clearTimeout(subtitleFillTooltipTimerRef.current);
        }
        showUI('subtitleFillTooltip');
        subtitleFillTooltipTimerRef.current = setTimeout(() => {
          hideUI('subtitleFillTooltip');
          subtitleFillTooltipTimerRef.current = null;
        }, 3000); // ToolTip 顯示 3 秒
        
        // 🔧 新增：自動啟動全自動填充模式
        // 操作日誌已移除
        setIsAutoFillMode(true);
        
        return cachedContent; // 🔧 修復：返回字幕內容
      }
      
      // 調試日誌已移除
      
      // 緩存未命中，從 API 下載
      const apiBaseUrl = (window as any).ENV?.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL || 
        (import.meta.env.PROD 
          ? 'https://checkplay-230168256043.asia-east1.run.app' 
          : 'http://localhost:8080');
      console.log('[SRT Content] 使用 API URL:', apiBaseUrl);
      const response = await fetch(`${apiBaseUrl}/api/youtube-captions/${videoId}/download?lang=${language}`);
      
      if (response.ok) {
        const content = await response.text();
        setSrtContent(content);
        // 調試日誌已移除
        
        // 🔧 新增：保存到 Firestore 緩存
        try {
          await saveSRTToCache(videoId, language, content);
          // 調試日誌已移除
          
          // 🔧 修復：更新字幕緩存狀態，讓 AI 按鈕變藍色
          setHasSRTCache(true);
          setCachedSRTLanguage(language);
          setCurrentVideoId(videoId);
          // 狀態變化（日誌已移除）
          
        } catch (cacheError) {
          console.error('[SRT Content] 保存到緩存失敗:', cacheError);
          // 不影響主流程，繼續執行
        }
        
        // 🔧 新增：字幕下載完成提示和 ToolTip 觸發
        alert('字幕下載完成！');
        if (subtitleFillTooltipTimerRef.current) {
          clearTimeout(subtitleFillTooltipTimerRef.current);
        }
        showUI('subtitleFillTooltip');
        subtitleFillTooltipTimerRef.current = setTimeout(() => {
          hideUI('subtitleFillTooltip');
          subtitleFillTooltipTimerRef.current = null;
        }, 3000); // ToolTip 顯示 3 秒
        
        // 🔧 新增：自動啟動全自動填充模式
        // 操作日誌已移除
        setIsAutoFillMode(true);
        
        // 🔧 修復：返回下載的字幕內容
        return content;
      } else {
        console.log(`[SRT Content] ❌ 獲取字幕內容失敗: ${response.status}`);
        alert(`無法獲取 ${language} 字幕，請嘗試其他語言`);
        return null;
      }
    } catch (error) {
      console.error('[SRT Content] 獲取字幕內容時發生錯誤:', error);
      alert(`字幕下載失敗：${error instanceof Error ? error.message : '未知錯誤'}`);
      return null;
    } finally {
      setIsDownloadingSubtitles(false); // 🔧 新增：設置下載狀態為 false
    }
  };

  // 🔧 新增：處理語言選擇確認
  const handleLanguageConfirm = async (language: string) => {
    // 調試日誌已移除
    setSelectedLanguage(language);
    
    // 關閉語言選擇對話框
    closeDialog('language');
    
    // 🔧 新增：處理自動生成 SRT 選項
    if (language === 'auto-generate') {
      await handleAutoGenerateSRT();
      return;
    }
    
    // 🔧 修改：檢查是否為字幕填充模式，如果是則繼續處理字幕填充
    if (videoUrl && currentSubtitleKey !== null) {
      const videoId = extractYouTubeId(videoUrl);
      if (videoId) {
        // 調試日誌已移除
        try {
          // 開始下載字幕
          setCurrentVideoId(videoId);
          await fetchSRTContent(videoId, language);
          
          // 獲取當前重點的時間碼
          const isYouTubeTab = tabValue === 3;
          const currentTabNumberKeys = isYouTubeTab ? numberKeys : tabNumberKeys[tabValue];
          const keyState = currentTabNumberKeys[currentSubtitleKey];
          
          if (keyState && keyState.timecode) {
            await fillNoteWithSRT(videoId, language, keyState);
          }
        } catch (error) {
          console.error('[Language Selection] 字幕填充失敗:', error);
          alert('字幕下載失敗，請稍後再試');
        }
        return;
      }
    }
    
    // 否則顯示 SRT 字幕內容（使用選擇的語言）
    await handleShowSRTContentWithLanguage(language);
  };

  // 🔧 新增：處理自動生成 SRT 功能（使用進度對話框）
  const handleAutoGenerateSRT = async () => {
    try {
      if (!videoUrl) {
        alert('請先載入影片');
        return;
      }

      // 🔧 關鍵修復：區分 YouTube 影片和雲端檔案
      const youtubeVideoId = extractYouTubeId(videoUrl);
      const isCloudFile = videoUrl.includes('storage.googleapis.com') || 
                          videoUrl.includes('blob:') || 
                          videoUrl.startsWith('/') || 
                          videoMode === 'VIDEO';
      
      let identifierForLog = '';
      let requestPayload: any = {
        language: 'zh', // 預設中文（Whisper 支援的語言代碼）
        useWhisper: true
      };
      
      if (youtubeVideoId && !isCloudFile) {
        // YouTube 影片
        identifierForLog = youtubeVideoId;
        requestPayload.videoId = youtubeVideoId;
        // 調試日誌已移除
      } else if (isCloudFile) {
        // 雲端檔案
        identifierForLog = videoUrl.substring(0, 50) + '...';
        requestPayload.videoUrl = videoUrl;
        requestPayload.isCloudFile = true;
        
        // 🔧 添加 firestoreId 以便後端緩存（如果有）
        if (currentFirestoreId) {
          requestPayload.firestoreId = currentFirestoreId;
        }
        
        // 調試日誌已移除
        // 調試日誌已移除
      } else {
        alert('無法識別影片來源，請確認影片已正確載入');
        return;
      }

      // 調試日誌已移除

      // 初始化進度對話框
      setSrtGenerationStatus('downloading');
      setSrtGenerationProgress(0);
      setSrtGenerationMessage('開始生成字幕...');
      setSrtGenerationError(undefined);
      openDialog('srtGeneration');

      // 調用 Whisper 專用服務生成 SRT（使用 Server-Sent Events）
      // 🔧 使用統一的後端服務 URL（Whisper API 已整合到主後端）
      const apiBaseUrl = (window as any).ENV?.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL || 
        (import.meta.env.PROD 
          ? 'https://checkplay-230168256043.asia-east1.run.app' 
          : 'http://localhost:8080');
      
      console.log('[Auto Generate SRT] 使用 API URL:', apiBaseUrl);
      
      // 🔧 修復：API endpoint 支持兩種模式
      const apiEndpoint = youtubeVideoId && !isCloudFile
        ? `${apiBaseUrl}/api/generate-srt-stream/${youtubeVideoId}`
        : `${apiBaseUrl}/api/generate-srt-stream`;
      
      console.log('[Auto Generate SRT] API Endpoint:', apiEndpoint);
      console.log('[Auto Generate SRT] Request Payload:', requestPayload);
      
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // 處理 Server-Sent Events
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('無法建立數據流');
      }

      let buffer = '';
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log('[Auto Generate SRT] 收到進度更新:', data);
              
              setSrtGenerationStatus(data.status);
              setSrtGenerationProgress(data.progress || 0);
              setSrtGenerationMessage(data.message || '');
              
              if (data.status === 'completed' && data.srtContent) {
                // 調試日誌已移除
                
                // 設置字幕內容
                setSrtContent(data.srtContent);
                
                // 🔧 修復：為雲端檔案設置正確的 videoId
                const cacheKey = isCloudFile 
                  ? (currentFirestoreId || videoUrl.substring(videoUrl.lastIndexOf('/') + 1, videoUrl.indexOf('?') > 0 ? videoUrl.indexOf('?') : undefined))
                  : youtubeVideoId;
                
                setCurrentVideoId(cacheKey || '');
                // 調試日誌已移除
                
                // 🔧 修復：保存到 Firestore 緩存（支持雲端檔案）
                try {
                  const { saveSRTToCache } = await import('../services/srtCacheService');
                  if (cacheKey) {
                    await saveSRTToCache(cacheKey, 'zh', data.srtContent);
                    // 調試日誌已移除
                  }
                } catch (cacheError) {
                  console.error('[Auto Generate SRT] 保存到緩存失敗:', cacheError);
                }
                
                // 🔧 修復：設置字幕緩存狀態，讓 AI 按鈕變藍色
                setHasSRTCache(true);
                setCachedSRTLanguage('zh'); // Whisper 生成的語言
                // 狀態變化（日誌已移除）
                
                // 🔧 新增：自動啟動全自動填充模式
                setIsAutoFillMode(true);
                // 操作日誌已移除
                
                // 如果有當前重點，自動填充
                if (currentSubtitleKey !== null) {
                  const isYouTubeTab = tabValue === 3;
                  const currentTabNumberKeys = isYouTubeTab ? numberKeys : tabNumberKeys[tabValue];
                  const keyState = currentTabNumberKeys[currentSubtitleKey];
                  
                  if (keyState && keyState.timecode) {
                    await fillNoteWithSRTContent(data.srtContent, keyState);
                  }
                }
                
                // 3秒後自動關閉對話框
                setTimeout(() => {
                  closeDialog('srtGeneration');
                }, 3000);
              }
              
              if (data.status === 'error') {
                setSrtGenerationError(data.error || '生成字幕失敗');
              }
              
            } catch (parseError) {
              console.warn('[Auto Generate SRT] 解析進度數據失敗:', parseError);
            }
          }
        }
      }
      
    } catch (error) {
      console.error('[Auto Generate SRT] 生成字幕失敗:', error);
      setSrtGenerationStatus('error');
      setSrtGenerationError(error instanceof Error ? error.message : '未知錯誤');
    }
  };

  // 🔧 修改：顯示 SRT 字幕對話框（支援語言選擇）
  const handleShowSRTContentWithLanguage = async (language?: string) => {
    const lang = language || selectedLanguage;
    
    if (!videoUrl) {
      console.log('[SRT Display] 沒有影片 URL');
      return;
    }

    const videoId = extractYouTubeId(videoUrl);
    if (!videoId) {
      console.log('[SRT Display] 無法提取 YouTube 影片 ID');
      return;
    }

    // 調試日誌已移除
    
    // 獲取新的字幕內容（使用指定語言）
    setCurrentVideoId(videoId);
    await fetchSRTContent(videoId, lang);
    openDialog('srt');
  };

  // 🔧 保留原有函數以保持向後兼容
  const handleShowSRTContent = async () => {
    await handleShowSRTContentWithLanguage();
  };

  // 🔧 新增：檢查是否有字幕緩存（兩階段優化版）
  const checkSRTCacheAvailability = async (videoId: string) => {
    // 🔥 防抖：使用 ref 立即檢查，避免重複調用
    if (srtCheckInProgressRef.current === videoId) {
      // SRT Cache: 正在檢查，跳過重複調用
      return;
    }
    
    if (hasSRTCache && currentVideoId === videoId) {
      // SRT Cache: 已檢查過，跳過
      return;
    }
    
    // 🔥 立即標記為檢查中
    srtCheckInProgressRef.current = videoId;
    
    try {
      // SRT Cache: 快速檢測字幕緩存
      
      // 🔥 顯示載入提示
      setSnackbarMessage('正在檢查 AI 字幕...');
      setSnackbarOpen(true);
      
      // 動態導入 srtCacheService
      const srtCacheService = await import('../services/srtCacheService');
      
      // 🔥 第一階段：快速檢測（只查一次，不管語言）
      const hasAnyCache = await srtCacheService.hasAnySRTCache(videoId);
      
      if (hasAnyCache) {
        // SRT Cache: 檢測到字幕緩存
        
        // 🔥 立即啟動 AI 按鈕（不等 useEffect）
        setHasSRTCache(true);
        setCachedSRTLanguage('zh');  // 預設中文
        setIsAutoFillMode(true);  // ⚡ 立即變紅！不等 useEffect
        
        // 🔥 顯示成功訊息，1 秒後關閉
        setSnackbarMessage('✅ AI 字幕已就緒');
        setTimeout(() => {
          setSnackbarOpen(false);
        }, 1000);
        
        // SRT Cache: AI 按鈕已啟動
        
        // 🔥 300ms 後顯示 Tooltip
        if (!hasShownTooltipRef.current) {
          hasShownTooltipRef.current = true;
          setTimeout(() => {
            showUI('aiTooltip');
            // 3 秒後自動隱藏
            setTimeout(() => {
              hideUI('aiTooltip');
            }, 3000);
          }, 300);
        }
        
        // ✅ 清除檢查標記，允許後續重新載入字幕內容
        srtCheckInProgressRef.current = null;
        
        return;
      }
      
      // SRT Cache: 沒有找到字幕緩存
      
      // 🔥 確認沒有字幕，關閉 AI 按鈕
      setHasSRTCache(false);
      setCachedSRTLanguage('');
      setIsAutoFillMode(false);
      setSrtContent('');
      
      // 關閉提示（沒有字幕緩存，靜默關閉）
      setSnackbarOpen(false);
      
      // ✅ 清除檢查標記
      srtCheckInProgressRef.current = null;
    } catch (error) {
      console.error('[SRT Cache Check] 檢查字幕緩存失敗:', error);
      setHasSRTCache(false);
      setCachedSRTLanguage('');
      
      // 🔥 關閉提示
      setSnackbarOpen(false);
      
      // ✅ 清除檢查標記
      srtCheckInProgressRef.current = null;
    }
  };

  // 🔧 新增：處理字幕填充按鈕雙擊
  const handleSubtitleButtonClick = () => {
    subtitleButtonClickCount.current += 1;
    
    if (subtitleButtonClickCount.current === 1) {
      // 第一次點擊，設置計時器
      subtitleButtonClickTimer.current = setTimeout(() => {
        // 單擊：執行正常的字幕填充
        // 操作日誌已移除
        handleAutoFillNoteFromSRT();
        subtitleButtonClickCount.current = 0;
      }, 300); // 300ms 內沒有第二次點擊視為單擊
    } else if (subtitleButtonClickCount.current === 2) {
      // 雙擊：切換自動填充模式
      // 操作日誌已移除
      
      // 清除單擊計時器
      if (subtitleButtonClickTimer.current) {
        clearTimeout(subtitleButtonClickTimer.current);
        subtitleButtonClickTimer.current = null;
      }
      
      // 切換自動填充模式
      setIsAutoFillMode(prev => {
        const newMode = !prev;
        // AI 模式切換日誌已移除
        return newMode;
      });
      
      subtitleButtonClickCount.current = 0;
    }
  };

  // 🔧 重新設計：自動填充 Note 內容（處理三種情況）
  const handleAutoFillNoteFromSRT = async (language?: string) => {
    try {
      // 🔧 新增：調試語言參數類型和當前狀態
      // 調試日誌已移除
      // 調試日誌已移除
      
      if (!videoUrl) {
        alert('請先載入影片');
        return;
      }

      // 🔧 檢查是否為 YouTube 影片
      const videoId = extractYouTubeId(videoUrl);
      if (!videoId) {
        alert('非 YouTube 影片，無字幕檔');
        return;
      }

      // 獲取當前重點編號
      if (currentSubtitleKey === null) {
        alert('請先選擇一個重點');
        return;
      }

      // 獲取當前重點的時間碼
      const isYouTubeTab = tabValue === 3;
      const currentTabNumberKeys = isYouTubeTab ? numberKeys : tabNumberKeys[tabValue];
      const keyState = currentTabNumberKeys[currentSubtitleKey];

      // 🔧 修復：檢查 timecode 或 timestamp，允許兩種格式
      if (!keyState) {
        alert('請先選擇一個重點');
        return;
      }

      // 🔧 修復：如果沒有 timecode 但有 timestamp，從 timestamp 生成 timecode
      let timecodeToUse = keyState.timecode;
      if (!timecodeToUse && keyState.timestamp !== undefined) {
        // 調試日誌已移除
        // 從 timestamp 生成 timecode (HH:MM:SS,mmm 格式)
        const totalSeconds = keyState.timestamp;
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);
        const milliseconds = Math.floor((totalSeconds % 1) * 1000);
        const pad = (num: number) => String(num).padStart(2, '0');
        timecodeToUse = `${pad(hours)}:${pad(minutes)}:${pad(seconds)},${milliseconds.toString().padStart(3, '0')}`;
        // 調試日誌已移除
      }

      if (!timecodeToUse && keyState.timestamp === undefined) {
        alert('當前重點沒有時間碼或時間戳記');
        return;
      }

      // 調試日誌已移除

      // 使用指定語言或預設語言，確保是字符串類型
      const lang = (typeof language === 'string' ? language : selectedLanguage) || 'zh-TW';
      // 調試日誌已移除

      // 🔧 重新設計：處理三種情況
      // 調試日誌已移除

      // 情況 1：如果已有字幕內容且是同一影片，直接使用
      if (srtContent && srtContent !== '_READY_' && currentVideoId === videoId) {
        console.log('[Auto Fill Note] 使用已載入的字幕內容（無需重新讀取快取）');
        // 🔧 關鍵修復：直接使用記憶體中的 srtContent，避免重複讀取 Firestore
        await fillNoteWithSRTContent(srtContent, { ...keyState, timecode: timecodeToUse });
        return; // 🚨 重要：直接返回，不再繼續執行
      }

      // 情況 2：檢查 Firestore 緩存是否存在
      console.log('[Auto Fill Note] 記憶體中沒有字幕，檢查 Firestore 緩存...');
      const { hasSRTCache } = await import('../services/srtCacheService');
      const hasCache = await hasSRTCache(videoId, lang);
      
      if (hasCache) {
        // 調試日誌已移除
        // ✅ 修復：不顯示 alert，改用 Snackbar 提示
        setSnackbarMessage('正在從緩存載入字幕...');
        setSnackbarSeverity('warning'); // 🔧 修復：使用 'warning' 替代 'info'
        setSnackbarOpen(true);
        
        setCurrentVideoId(videoId);
        
        try {
          const content = await fetchSRTContent(videoId, lang);
          
          // 🔧 修復：檢查 content 是否為有效字串
          if (content && typeof content === 'string' && content.trim().length > 0) {
            // ✅ 顯示成功訊息
            setSnackbarMessage('✅ 字幕已載入');
            setSnackbarSeverity('success');
            
            // 🔧 修復：直接使用獲取的字幕內容
            await fillNoteWithSRTContent(content, { ...keyState, timecode: timecodeToUse });
            
            // 1 秒後關閉提示
            setTimeout(() => {
              setSnackbarOpen(false);
            }, 1000);
          } else {
            console.error('[Auto Fill Note] 字幕內容無效:', content);
            setSnackbarMessage('❌ 字幕載入失敗');
            setSnackbarSeverity('error');
            setTimeout(() => {
              setSnackbarOpen(false);
            }, 2000);
          }
        } catch (error) {
          console.error('[Auto Fill Note] 載入字幕時發生錯誤:', error);
          setSnackbarMessage('❌ 字幕載入失敗');
          setSnackbarSeverity('error');
          setTimeout(() => {
            setSnackbarOpen(false);
          }, 2000);
        }
        
        return;
      }

      // 情況 3：緩存不存在，首次嘗試下載
      // 調試日誌已移除
      // 🔧 新增：打開語言選擇對話框
      openDialog('language');
      
    } catch (error) {
      console.error('[Auto Fill Note] 處理字幕填充時發生錯誤:', error);
      alert(`處理字幕填充時發生錯誤：${error instanceof Error ? error.message : '未知錯誤'}`);
    }
  };

  // 🔧 新增：使用指定字幕內容填充 Note 的函數
  const fillNoteWithSRTContent = async (srtContentToUse: string, keyState: any) => {
    const isYouTubeTab = tabValue === 3;
    const currentTabValue = tabValue;
    
    // 🔧 修復：使用 keyState.keyNo 而不是 currentSubtitleKey
    const targetKeyNo = keyState?.keyNo || currentSubtitleKey;
    // 調試日誌已移除
    
    try {
      // 🔧 修改：使用 srtUtils 來查找最接近的字幕（前1句 + 中1句 + 後3句 = 共5句）
      const { findClosestSRTEntries, formatSRTEntriesSimple } = await import('../utils/srtUtils');
      
      // 🔧 修復：優先使用 timestamp 來查找字幕，因為 timecode 可能都是 00:00:00 00
      let searchTimecode = keyState.timecode;
      if (keyState.timestamp !== null && keyState.timestamp !== undefined && keyState.timestamp >= 0) {
        // 將 timestamp 轉換為 timecode 格式 (HH:MM:SS,mmm)
        const seconds = keyState.timestamp;
        const totalSeconds = Math.floor(seconds);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;
        const milliseconds = Math.floor((seconds % 1) * 1000);
        
        const pad = (num: number) => num.toString().padStart(2, '0');
        searchTimecode = `${pad(hours)}:${pad(minutes)}:${pad(secs)},${milliseconds.toString().padStart(3, '0')}`;
        // 調試日誌已移除
      } else {
        // 調試日誌已移除
      }
      
      // 🔧 使用可配置的行數：前1行 + 當前行 + 後n行
      const beforeLines = 1;
      const afterLines = subtitleFillLines - 2; // 總行數減去前1行和當前行
      const closestEntries = findClosestSRTEntries(srtContentToUse, searchTimecode, beforeLines, afterLines);
      console.log(`[Auto Fill Note] 使用設定行數: 總共${subtitleFillLines}行 (前${beforeLines}行 + 當前行 + 後${afterLines}行)`);

      if (closestEntries.length === 0) {
        alert('沒有找到匹配的字幕內容');
        return;
      }

      // 格式化為簡潔文字
      const newNoteText = formatSRTEntriesSimple(closestEntries);
      console.log(`[Auto Fill Note] 找到 ${closestEntries.length} 句字幕:`, newNoteText);

      // 🔧 修復：只使用 keyState.note，不使用全局的 subtitleContent，避免狀態污染
      const existingNote = keyState.note || '';
      const finalNoteText = existingNote.trim() 
        ? `${existingNote}\n${newNoteText}` 
        : newNoteText;
      
      // 調試日誌已移除

      // 調試日誌已移除
      // 調試日誌已移除

      // 🔥 修復：更新顯示區域的字幕內容（淺黃色/淺粉紅色區域）
      setSubtitleContent(finalNoteText);
      setSubtitleContentWithSource({
        text: finalNoteText,
        isFromSRT: true
      });
      // 調試日誌已移除

      // 同時更新 numberKeys 狀態
      if (isYouTubeTab) {
        setNumberKeys(prev => ({
          ...prev,
          [targetKeyNo]: {
            ...prev[targetKeyNo],
            note: finalNoteText,
            hasNote: true
          }
        }));
      } else {
        setTabNumberKeys(prev => {
          const newState = { ...prev };
          newState[currentTabValue] = { ...newState[currentTabValue] };
          newState[currentTabValue][targetKeyNo] = {
            ...newState[currentTabValue][targetKeyNo],
            note: finalNoteText,
            hasNote: true
          };
          return newState;
        });
      }

      // 保存到 Firestore
      saveNoteToFirestore(targetKeyNo, finalNoteText, currentTabValue);

      // 調試日誌已移除
    } catch (error) {
      console.error('填充字幕時發生錯誤:', error);
      alert(`填充字幕失敗：${error instanceof Error ? error.message : '未知錯誤'}`);
    }
  };

  // 🔧 新增：填充 Note 內容的獨立函數
  const fillNoteWithSRT = async (videoId: string, lang: string, keyState: any) => {
    // 🔧 新增：獲取當前 tab 狀態
    const isYouTubeTab = tabValue === 3;
    const currentTabValue = tabValue; // 確保 tabValue 在函數作用域內可用
    try {
      // 確保有字幕內容
      if (!srtContent) {
        // 🔧 修改：先檢查 Firestore 緩存，避免不必要的 API 調用
        const { getSRTContent } = await import('../services/srtCacheService');
        try {
          const cachedContent = await getSRTContent(videoId, lang);
          if (cachedContent && cachedContent.trim() !== '') {
            // 調試日誌已移除
            setSrtContent(cachedContent);
            // 🔧 修復：直接使用緩存內容，不依賴狀態更新
            return await fillNoteWithSRTContent(cachedContent, keyState);
          } else {
            // 如果緩存中沒有，嘗試下載
            console.log('[Auto Fill Note] 緩存中沒有字幕，嘗試下載');
            const apiBaseUrl = (window as any).ENV?.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL || 
              (import.meta.env.PROD 
                ? 'https://checkplay-230168256043.asia-east1.run.app' 
                : 'http://localhost:8080');
            const response = await fetch(`${apiBaseUrl}/api/youtube-captions/${videoId}/download?lang=${lang}`);
            if (!response.ok) {
              if (response.status === 404) {
                alert('影片未提供字幕檔');
              } else {
                alert('無法獲取字幕內容，請稍後再試');
              }
              return;
            }
            
            const content = await response.text();
            if (!content || content.trim() === '') {
              alert('影片未提供字幕檔');
              return;
            }
            
            setSrtContent(content);
            // 🔧 修復：直接使用下載的內容，不依賴狀態更新
            return await fillNoteWithSRTContent(content, keyState);
          }
        } catch (cacheError) {
          console.warn('[Auto Fill Note] 緩存檢查失敗，嘗試直接下載:', cacheError);
          // 緩存檢查失敗時，仍然嘗試下載
          const apiBaseUrl = (window as any).ENV?.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL || 
            (import.meta.env.PROD 
              ? 'https://checkplay-230168256043.asia-east1.run.app' 
              : 'http://localhost:8080');
          const response = await fetch(`${apiBaseUrl}/api/youtube-captions/${videoId}/download?lang=${lang}`);
          if (!response.ok) {
            if (response.status === 404) {
              alert('影片未提供字幕檔');
            } else {
              alert('無法獲取字幕內容，請稍後再試');
            }
            return;
          }
          
          const content = await response.text();
          if (!content || content.trim() === '') {
            alert('影片未提供字幕檔');
            return;
          }
          
          setSrtContent(content);
          // 🔧 修復：直接使用下載的內容，不依賴狀態更新
          return await fillNoteWithSRTContent(content, keyState);
        }
      }

      // 🔧 修改：如果 srtContent 已經存在，直接使用
      if (srtContent) {
        return await fillNoteWithSRTContent(srtContent, keyState);
      }
      
      // 調試日誌已移除
    } catch (error) {
      console.error('[Auto Fill Note] 自動填充 Note 時發生錯誤:', error);
      alert(`自動填充失敗：${error instanceof Error ? error.message : '未知錯誤'}`);
    }
  };

  // 🔧 調試：檢查傳遞給 SwipeableControlPanel 的參數（已移除以避免過多日誌）

  const handleCleanupDuplicateVbids = async () => {
    if (!videoUrl || !user?.uid) {
      console.log('❌ 無法清理：缺少 videoUrl 或用戶資訊');
      return;
    }
    
    try {
      // 調試日誌已移除
      await cleanupDuplicateVbids(videoUrl, user.email);
      
      // 顯示成功訊息
      setSnackbarMessage('重複資料清理完成');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // 重新載入影片資料
      window.location.reload();
    } catch (error) {
      console.error('❌ 清理重複 vbids 資料失敗:', error);
      setSnackbarMessage('清理失敗：' + (error instanceof Error ? error.message : '未知錯誤'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleDeleteImages = (id: string) => {
    setCapturedImages(prev => prev.filter(img => img.id !== id));
  };

  const handleSaveImage = (id: string) => {
    const image = capturedImages.find(img => img.id === id);
    if (image) {
      const link = document.createElement('a');
      link.href = image.url;
      link.download = image.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // 更新播放器配置
  const playerConfig = {
    youtube: {
      playerVars: {
        modestbranding: 1,
        showinfo: 1,
        rel: 0,
        iv_load_policy: 3,
        origin: window.location.origin,
        enablejsapi: 1,
        playsinline: 1,
        controls: 1,
        autoplay: 0
      }
    },
    file: {
      attributes: {
        style: { width: '100%', height: '100%' },
        controlsList: 'nodownload',
        playsInline: true,
        crossOrigin: 'anonymous',
        allow: 'autoplay; encrypted-media',
        sandbox: 'allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation'
      },
      forceVideo: true,
      forceHLS: false,
      forceDASH: false,
      hlsOptions: {
        enableWorker: true,
        lowLatencyMode: true,
        debug: false
      },
      forceDisableTrack: true
    }
  };

  // 更新錯誤處理函數
  const handlePlayerError = (error: any) => {
    console.error('Player error:', error);
    
    // YouTube 特定錯誤處理
    if (videoMode === 'YouTube') {
      let errorMessage = '播放器發生錯誤，請重新載入頁面';
      let showRetryButton = false;
      let isEmbeddingError = false;
      
      // YouTube 錯誤碼處理
      if (typeof error === 'number') {
        switch (error) {
          case 150:
            errorMessage = '❌ 此影片無法播放\n\n可能原因：\n• 影片不存在或已被刪除\n• 影片是私人的或需要年齡驗證\n• 影片的嵌入權限被限制\n• 地區限制或版權問題\n\n💡 建議：\n• 直接在 YouTube 上觀看\n• 嘗試其他影片';
            showRetryButton = true;
            isEmbeddingError = true;
            break;
          case 101:
            errorMessage = '❌ 此影片不允許嵌入播放\n\n💡 建議：直接在 YouTube 上觀看';
            showRetryButton = true;
            isEmbeddingError = true;
            break;
          case 103:
            errorMessage = '❌ 此影片不存在或已被刪除\n\n💡 建議：檢查影片連結是否正確';
            showRetryButton = true;
            break;
          case 151:
            errorMessage = '❌ 此影片需要年齡驗證\n\n💡 建議：直接在 YouTube 上觀看';
            showRetryButton = true;
            isEmbeddingError = true;
            break;
          default:
            errorMessage = `❌ YouTube 播放錯誤 (錯誤碼: ${error})\n\n💡 建議：重新載入頁面或嘗試其他影片`;
            showRetryButton = true;
        }
      }
      
      setError(errorMessage);
      
      // 如果是嵌入相關錯誤，提供更詳細的解決方案
      if (isEmbeddingError) {
        // 調試日誌已移除
        
        // 添加直接 YouTube 連結按鈕
        setTimeout(() => {
          const youtubeUrl = `https://www.youtube.com/watch?v=${youtubeApi.extractVideoId(videoUrl)}`;
          console.log('🔗 建議直接觀看:', youtubeUrl);
          
          // 可以在這裡添加一個按鈕讓用戶直接跳轉到 YouTube
          if (window.confirm('影片無法在此播放，是否要直接在 YouTube 上觀看？')) {
            window.open(youtubeUrl, '_blank');
          }
        }, 3000);
      }
      
      // 如果是可重試的錯誤，提供重試選項
      if (showRetryButton && !isEmbeddingError) {
        setTimeout(() => {
          // 狀態追蹤日誌已移除
          // 嘗試重新載入播放器
          if (playerRef.current) {
            // 狀態追蹤日誌已移除
            // 重新設置 URL 來觸發重新載入
            const currentUrl = videoUrl;
            setVideoUrl('');
            setTimeout(() => {
              setVideoUrl(currentUrl);
              // 狀態追蹤日誌已移除
            }, 1000);
          }
        }, 5000);
      }
      
      return;
    }
    
    // 其他影片模式的錯誤處理
    setError('❌ 播放器發生錯誤，請重新載入頁面');
  };
  
  // 🔧 重構：重命名為 updateTimecodeDisplay 避免與 Hook 中的 updateTimecode 衝突
  const updateTimecodeDisplay = () => {
    // 🔧 修復：使用 playedSeconds 狀態而非 getCurrentTime() 避免無效值
    if (playedSeconds > 0) {
      const fps = 30; // 默认使用30fps
      const frame = Math.floor((playedSeconds * fps) % fps);
      
      const timecode = formatTimecode(playedSeconds, frame);
      setCurrentTimecode(timecode);
      
      // 使用 requestAnimationFrame 来确保平滑更新
      if (isPlaying) {
        animationFrameRef.current = requestAnimationFrame(updateTimecode);
      }
    } else {
      // 如果 playedSeconds 為 0，跳過更新
      // 调试日志已移除
      if (isPlaying) {
        animationFrameRef.current = requestAnimationFrame(updateTimecode);
      }
    }
  };

  // 添加 useEffect 来处理播放状态变化
  useEffect(() => {
    if (isPlaying) {
      updateTimecodeDisplay();
    } else if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying]);

  const handlePlayerReady = () => {
    // 處理日誌已移除
    // 調試日誌已移除
    
    setIsVideoLoading(false);
    setError(null);
    updateTimecode();
    
    // 🔧 新增：處理傳統模式下的延遲跳轉
    if (pendingJumpToCheckpointRef.current !== null && numberKeys[pendingJumpToCheckpointRef.current]) {
      const targetKeyNumber = pendingJumpToCheckpointRef.current;
      const targetKey = numberKeys[targetKeyNumber];
      const timestampValue = targetKey?.timestamp;
      
      if (timestampValue !== null && timestampValue !== undefined) {
        // 調試日誌已移除
        
        // 延遲執行跳轉，確保播放器完全準備好
        setTimeout(() => {
          if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
            playerRef.current.seekTo(timestampValue, 'seconds');
            // 調試日誌已移除
            
            // 自動播放
            setTimeout(() => {
              setIsPlaying(true);
              // 處理日誌已移除
            }, 300);
          }
        }, 500); // 等待 500ms 確保播放器完全準備好
        
        // 清除 ref，避免重複跳轉
        pendingJumpToCheckpointRef.current = null;
      }
    }
    
    // 🔧 修復：如果是參與者模式且應該播放，則自動開始播放
    if (isSyncParticipant && lastIsPlayingRef.current && playerRef.current) {
      // 操作完成（日誌已移除）
      setTimeout(() => {
        if (lastIsPlayingRef.current && playerRef.current) {
          // 調試日誌已移除
          try {
            playerRef.current.getInternalPlayer()?.playVideo?.();
            // 成功日誌已移除
          } catch (error) {
            console.error('❌ 自動播放命令執行失敗:', error);
          }
        } else {
          console.log('⚠️ 自動播放條件已不滿足:', {
            lastIsPlayingRef: lastIsPlayingRef.current,
            hasPlayerRef: !!playerRef.current
          });
        }
      }, 500); // 延遲500ms確保播放器完全準備好
    } else {
        // 调试日志已移除（对象字面量已移除）
    }
  };

  const handleImageManager = () => {
    // 播放按鈕點擊音效
    playButtonSound('click');
    
    // 暫停影片播放
    if (isPlaying) {
      pauseVideo();
    }
    
    // 暫停時間碼更新
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    setImageManagerOpen(true);
  };

  const handleImageManagerClose = () => {
    // 確保在關閉對話框時清除焦點
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setImageManagerOpen(false);
    
    // 如果視頻正在播放，恢復時間碼更新
    if (isPlaying) {
      requestAnimationFrame(updateTimecode);
    }
  };

  const handlePreviewClick = (image: ImageData) => {
    // 暫停影片播放
    pauseVideo();
    setPreviewImage(image);
    setCurrentImageIndex(Number(image.id));
    setIsEditing(true);
  };

  const handlePreviewClose = () => {
    setPreviewImage(null);
  };

  const handleFileSelect = async (file: File, isShortMode: boolean = false) => {
    try {
      if (!file) {
        console.error('No file selected');
        return;
      }

      const fileType = file.type;
      const isVideo = fileType.startsWith('video/');
      const isImage = fileType.startsWith('image/');
      const isPDF = fileType === 'application/pdf';

      if (!isVideo && !isImage && !isPDF) {
        setVideoError('不支援的檔案格式');
          return;
      }

      const objectUrl = URL.createObjectURL(file);
      
      // 根據檔案類型設置模式
      if (isVideo) {
        setVideoMode(isShortMode ? 'Short' : 'VIDEO');
      } else if (isImage) {
        setVideoMode('PHOTO');
      } else if (isPDF) {
        setVideoMode('PDF');
      }

      // 重置所有按鈕狀態
      resetAllButton();
      
      loadVideo(objectUrl);
      setSelectedFile(file);
      setVideoError(null);
      
      // 調試日誌已移除
    } catch (error) {
      console.error('Error handling file:', error);
      setVideoError('檔案處理失敗');
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setAuthLoading(true);
      if (isLogin) {
        await authHandleEmailLogin(formData.email, formData.password);
      } else {
        await authHandleRegister(formData.email, formData.password, formData.name);
      }
      handleCloseAuthDialog();
    } catch (error: any) {
      console.error(isLogin ? '登入失敗:' : '註冊失敗:', error);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleCloseAuthDialog = () => {
    closeDialog('auth');
  };

  const UserSection = () => (
    <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
      {userProfile ? (
        <Button
          variant="outlined"
          onClick={() => {/* TODO: 實現個人資料功能 */}}
        >
          {userProfile.name}
        </Button>
      ) : (
        <Button
          variant="contained"
          onClick={() => openDialog('auth')}
        >
          登入/註冊
        </Button>
      )}
    </Box>
  );

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // 修改處理函數
  const handleScreenshotSizeChange = (size: ScreenshotSize) => {
    setScreenshotSize(size);
    localStorage.setItem('screenshotSize', size);
  };

  // 修改 CheckPoint 截圖函數
  const handleContinuousScreenshot = async () => {
    // 檢查是否為 YouTube 模式
    if (tabValue === 3) {
      setSnackbarMessage('YouTube模式下不開放截圖功能，避免違反影片著作權。僅Video模式(本機檔案)下能截圖');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsBurstMode(true);
      pauseVideo();

      // 根據當前 Tab 選擇正確的數字鍵狀態
      const isYouTubeTab = tabValue === 3;
      const currentNumberKeys = isYouTubeTab ? numberKeys : tabNumberKeys[tabValue];

      // 獲取所有藍色數字鍵的時間點
      const blueKeys = Object.entries(currentNumberKeys)
        .filter(([_, keyState]) => keyState.timestamp !== null && !keyState.isPickUpState)
        .map(([key, keyState]) => ({
          key: key,
          timestamp: keyState.timestamp!
        }))
        .sort((a, b) => a.timestamp - b.timestamp);

      if (blueKeys.length === 0) {
        setError('沒有可用的 CheckPoint');
        setIsBurstMode(false);
        return;
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('無法創建畫布上下文');

      const video = playerRef.current?.getInternalPlayer() as HTMLVideoElement;
      if (!video) throw new Error('無法獲取視頻元素');

      // 根據截圖尺寸設定計算新的尺寸
      let targetWidth = video.videoWidth;
      let targetHeight = video.videoHeight;
      if (screenshotSize === '1/2') {
        targetWidth = Math.round(video.videoWidth * 0.5);
        targetHeight = Math.round(video.videoHeight * 0.5);
      } else if (screenshotSize === '1/4') {
        targetWidth = Math.round(video.videoWidth * 0.25);
        targetHeight = Math.round(video.videoHeight * 0.25);
      }

      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const newImages: ImageData[] = [];

      // 遍歷所有藍色數字鍵的時間點
      for (let i = 0; i < blueKeys.length; i++) {
        const { key, timestamp } = blueKeys[i];
        
        // 設置視頻時間
        const timestampValue = convertTimestampToNumber(timestamp);
        playerRef.current?.seekTo(timestampValue);
        
        // 等待視頻更新到目標時間
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // 繪製視頻幀，使用縮放繪製
        ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight, 0, 0, targetWidth, targetHeight);
        
        // 繪製浮水印
        if (uiDisplay.watermarkInOutput && showTimecode) {
          drawWatermark(ctx, targetWidth, targetHeight);
        }
        
        // 繪製輔助線
        if (uiDisplay.crosshair) {
          drawGuideLines(ctx, targetWidth, targetHeight);
        }
        
        // 繪製網格線
        if (uiDisplay.gridLines) {
          drawGridLines(ctx, targetWidth, targetHeight);
        }
        
        // 繪製電影遮罩
        if (uiDisplay.movieMask) {
          drawMovieMask(ctx, targetWidth, targetHeight);
        }

        // 播放快門音效
        playShutterSound();

        // 將畫布轉換為圖片
        const imageData = canvas.toDataURL('image/jpeg', 0.95);
        const newImage: ImageData = {
          id: `${Date.now()}_${i}`,
          url: imageData,
          name: `CheckPoint_${key}_${formatTime(timestamp)}`,
          timestamp: timestamp
        };
        newImages.push(newImage);

        // 短暫延遲以確保音效播放
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      // 更新圖片列表
      setCapturedImages(prevImages => [...prevImages, ...newImages]);
      setSnackbarMessage(`已成功截取 ${blueKeys.length} 個 CheckPoint`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setIsBurstMode(false);
    } catch (error) {
      console.error('CheckPoint截圖失敗:', error);
      setSnackbarMessage('CheckPoint截圖失敗');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setIsBurstMode(false);
    }
  };

  // 添加必要的狀態
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>('success');

  // 添加狀態
  const [watermarkOpen, setWatermarkOpen] = useState(false);
  const [watermarkAnchorEl, setWatermarkAnchorEl] = useState<null | HTMLElement>(null);

  // 處理浮水印視窗
  const handleWatermarkClick = (event: React.MouseEvent<HTMLElement>) => {
    setWatermarkAnchorEl(event.currentTarget);
    setWatermarkOpen(true);
  };

  const handleWatermarkClose = () => {
    setWatermarkAnchorEl(null);
    setWatermarkOpen(false);
  };

  // 在 useEffect 中保存設定 - 使用 useCallback 優化
  const saveSettings = useCallback(() => {
    localStorage.setItem('showTimecode', showTimecode.toString());
    localStorage.setItem('timecodeScale', timecodeScale.toString());
  }, [showTimecode, timecodeScale]);

  useEffect(() => {
    saveSettings();
  }, [saveSettings]);

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setError('請選擇照片');
      return;
    }

    try {
      setError(null);
      setIsLoading(true);

      // 檢查檔案類型
      const validTypes = ['image/jpeg', 'image/png', 'image/heic'];
      if (!validTypes.includes(file.type.toLowerCase())) {
        throw new Error('請上傳 JPG、PNG 或 HEIC 格式的照片');
      }

      // 如果是 HEIC 格式，進行轉換
      if (file.type.toLowerCase() === 'image/heic') {
        try {
          // 🔧 优化：动态导入 heic2any（只在需要时加载）
          const heic2any = await heic2anyPromise;
          const blob = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.9
          });
          const url = URL.createObjectURL(blob as Blob);
          setImageUrl(url);
          setVideoUrl(url);
        } catch (error) {
          throw new Error('HEIC 圖片轉換失敗，請確認檔案是否正確');
        }
      } else {
        // 直接創建 URL
        const url = URL.createObjectURL(file);
        setImageUrl(url);
        setVideoUrl(url);
      }

      // 設置檔案名稱
      setOriginalFileName(file.name);
      setFileRelativePath(file.name);

      // 保存當前 Tab 的輸入欄位內容
      setTabVideoUrls(prev => ({
        ...prev,
        [tabValue]: {
          url: videoUrl,
          fileName: file.name,
          relativePath: file.name
        }
      }));

    } catch (error) {
      console.error('照片上傳錯誤:', error);
      setError(error instanceof Error ? error.message : '上傳照片時發生錯誤');
    } finally {
      setIsLoading(false);
    }
  };

  // 添加新的狀態
  const [imageUrl, setImageUrl] = useState<string>('');

  // 新增 PHOTOS 模式下的圖片控制
  useEffect(() => {
    if (tabValue === 3) {
      setIsScreenshotMode(false);
      setIsBurstMode(false);
    }
  }, [tabValue]);

  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  // 🔧 优化：懒加载 Lightbox 组件（只在打开时加载）
  const LazyLightbox: React.FC<{
    open: boolean;
    close: () => void;
    slides: Array<{ src: string }>;
  }> = ({ open, close, slides }) => {
    const [LightboxComponent, setLightboxComponent] = useState<any>(null);
    const [ZoomPlugin, setZoomPlugin] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      if (open) {
        Promise.all([LightboxPromise, ZoomPromise]).then(([LightboxModule, ZoomModule]) => {
          setLightboxComponent(() => LightboxModule.default);
          setZoomPlugin(() => ZoomModule.default);
          setLoading(false);
        });
      }
    }, [open]);

    if (loading || !LightboxComponent) {
      return null; // 或返回一个加载中的占位符
    }

    return (
      <LightboxComponent
        open={open}
        close={close}
        slides={slides}
        plugins={[ZoomPlugin]}
        animation={{ zoom: 500 }}
        zoom={{
          maxZoomPixelRatio: 5,
          zoomInMultiplier: 2,
          doubleTapDelay: 300,
          doubleClickDelay: 300,
          doubleClickMaxStops: 2,
          keyboardMoveDistance: 50,
          wheelZoomDistanceFactor: 100,
          pinchZoomDistanceFactor: 100,
          scrollToZoom: true
        }}
      />
    );
  };

  const handleSafetyFrameClick = () => {
    // 調試日誌已移除
    if (!uiDisplay.safetyFrame) {
      // 如果安全框是關閉的，開啟它並設置為洋紅色
      showUI('safetyFrame');
      setSafetyFrameColor('magenta');
      setSafetyFrameSize(90);
      localStorage.setItem('uiDisplay.safetyFrame', 'true');
      // 調試日誌已移除
      return;
    }

    setSafetyFrameColor(prev => {
      let nextColor: 'magenta' | 'blue' | 'yellow' | 'green';
      let nextSize: number;
      
      switch (prev) {
        case 'magenta':
          nextColor = 'blue';
          nextSize = 85;
          break;
        case 'blue':
          nextColor = 'yellow';
          nextSize = 80;
          break;
        case 'yellow':
          nextColor = 'green';
          nextSize = 75;
          break;
        case 'green':
          // 當是綠色時，關閉安全框並重置顏色為洋紅色（為下一次做準備）
          hideUI('safetyFrame');
          setSafetyFrameSize(90);
          localStorage.setItem('uiDisplay.safetyFrame', 'false');
          return 'magenta';
        default:
          nextColor = 'magenta';
          nextSize = 90;
      }
      
      setSafetyFrameSize(nextSize);
      localStorage.setItem('safetyFrameSize', nextSize.toString());
      return nextColor;
    });
  };

  // 修改 handleGridLinesClick 函數
  const handleGridLinesClick = () => {
    // 調試日誌已移除
    setGridMode(prev => {
      const nextMode = (() => {
      switch (prev) {
        case 'none':
          return '3x3';
        case '3x3':
          return '6x4';
        case '6x4':
          return '10x6';
        case '10x6':
          return 'none';
        default:
          return 'none';
      }
      })();
      // 調試日誌已移除
      localStorage.setItem('gridMode', nextMode);
      setUiDisplay((prev) => ({ ...prev, gridLines: nextMode !== 'none' }));  // 根据gridMode更新uiDisplay.gridLines
      return nextMode;
    });
  };

  // 添加缺失的處理函數
  const handleScreenshotClick = () => {
    // 調試日誌已移除
    // 這裡可以添加截圖邏輯
  };


  const handleFullScreenClick = () => {
    // 調試日誌已移除
    // 這裡可以添加全螢幕邏輯
  };

  // 修改繪製井字線的函數
  const drawGridLines = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    if (gridMode === 'none') return;
    
    ctx.strokeStyle = '#808080';
    ctx.lineWidth = 1.5;
    ctx.globalAlpha = 0.8;

    switch (gridMode) {
      case '3x3':
        // 垂直線
        ctx.beginPath();
        ctx.moveTo(width / 3, 0);
        ctx.lineTo(width / 3, height);
        ctx.moveTo(width * 2 / 3, 0);
        ctx.lineTo(width * 2 / 3, height);

        // 水平線
        ctx.moveTo(0, height / 3);
        ctx.lineTo(width, height / 3);
        ctx.moveTo(0, height * 2 / 3);
        ctx.lineTo(width, height * 2 / 3);
        ctx.stroke();
        break;

      case '6x4':
        // 垂直線
        ctx.beginPath();
        for (let i = 1; i < 6; i++) {
          ctx.moveTo(width * i / 6, 0);
          ctx.lineTo(width * i / 6, height);
        }

        // 水平線
        for (let i = 1; i < 4; i++) {
          ctx.moveTo(0, height * i / 4);
          ctx.lineTo(width, height * i / 4);
        }
        ctx.stroke();
        break;

      case '10x6':
        // 垂直線
        ctx.beginPath();
        for (let i = 1; i < 10; i++) {
          ctx.moveTo(width * i / 10, 0);
          ctx.lineTo(width * i / 10, height);
        }

        // 水平線
        for (let i = 1; i < 6; i++) {
          ctx.moveTo(0, height * i / 6);
          ctx.lineTo(width, height * i / 6);
        }
        ctx.stroke();
        break;
    }

    ctx.globalAlpha = 1;
  };

  const [isMButtonActive, setIsMButtonActive] = useState(false);
  const [isRightButtonActive, setIsRightButtonActive] = useState(false);

  // 初始化時設定為 M 模式
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const resetTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // 添加 useEffect 來監控數字鍵狀態
  useEffect(() => {
    // 檢查是否有任何數字鍵被啟動
    const hasActiveKeys = Object.values(numberKeys).some(key => key.isActive);
    
    // 如果沒有數字鍵被啟動，且當前不是 M 模式，且不是 Multiview Tab
    if (!hasActiveKeys && mButtonState !== 'M' && tabValue !== 6) {
      // 清除現有的計時器
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
      
      // 設置新的計時器
      resetTimerRef.current = setTimeout(() => {
        setMButtonState('M');
        setIsDeleteMode(false);
      }, 3000);
    } else if (hasActiveKeys || tabValue === 6) {
      // 如果有數字鍵被啟動，或是 Multiview Tab，清除計時器
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
    }

    // 清理函數
    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
    };
  }, [numberKeys, mButtonState, tabValue]);

  // 用於追蹤雙擊事件
  const [lastClickTime, setLastClickTime] = useState<{ [key: number]: number }>({});
  // 使用已存在的 DOUBLE_CLICK_DELAY 常量

  // 獲取當前影片模式
  const getCurrentVideoMode = (): 'VIDEO' | 'YouTube' | 'Google' | 'Short' | 'PHOTO' | 'PDF' | 'Multiview' | null => {
    switch (tabValue) {
      case 0: return 'Short';
      case 1: return 'Google';
      case 2: return 'VIDEO';
      case 3: return 'YouTube';
              case 4: return 'PHOTO';
        case 5: return 'PDF';
        case 6: return 'Multiview';
        default: return null;
    }
  };

  // AB 模式相關狀態
  const [abPoints, setABPoints] = useState<ABPoints>({
    pointA: { key: null, timestamp: null },
    pointB: { key: null, timestamp: null }
  });

  // 處理 AB 循環播放
  useEffect(() => {
    if (mButtonState === 'AB' && 
        abPoints.pointA.timestamp !== null && 
        abPoints.pointB.timestamp !== null) {
      // 清除現有的 interval
      if (abLoopIntervalRef.current) {
        clearInterval(abLoopIntervalRef.current);
      }

      // 設定新的循環播放
      const startTime = Math.min(abPoints.pointA.timestamp, abPoints.pointB.timestamp);
      const endTime = Math.max(abPoints.pointA.timestamp, abPoints.pointB.timestamp);
      
      if (playerRef.current) {
        playerRef.current.seekTo(startTime);
        setIsPlaying(true);
      }

      abLoopIntervalRef.current = setInterval(() => {
        if (playerRef.current) {
          const currentTime = playerRef.current.getCurrentTime();
          if (currentTime >= endTime) {
            playerRef.current.seekTo(startTime);
          }
        }
      }, 100);
    }

    return () => {
      if (abLoopIntervalRef.current) {
        clearInterval(abLoopIntervalRef.current);
      }
    };
  }, [mButtonState, abPoints]);

  // 重置計時器的函數
  const resetDeleteTimer = () => {
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }
  };

  // 開始計時器的函數
  const startDeleteTimer = () => {
    resetDeleteTimer();
    deleteTimeoutRef.current = setTimeout(() => {
      // 重置所有狀態
      setIsDeleteMode(false);
      setMButtonState('M');
      // 顯示提示訊息
      setSnackbarMessage('已自動回到初始狀態');
      setSnackbarOpen(true);
    }, 3000); // 3秒
  };

  // 🔧 新增：只切換 Note 顯示，不觸發跳轉（用於放大動畫時的自動 Note 切換）
  const handleNoteDisplayChange = useCallback((key: number) => {
    // 調試日誌已移除
    
    // 獲取當前 Tab 的數字鍵狀態
    const isYouTubeTab = tabValue === 3;
    const currentTabNumberKeys = isYouTubeTab ? numberKeys : tabNumberKeys[tabValue];
    const keyState = currentTabNumberKeys[key];
    
    // 只在 M 模式下處理
    if (mButtonState === 'M' && keyState) {
      // 如果有之前選擇的數字鍵，先保存其內容（不調用 saveNoteToFirestore，避免依賴問題）
      if (currentSubtitleKey !== null && currentSubtitleKey !== key) {
        const previousTabNumberKeys = tabValue === 3 ? numberKeys : tabNumberKeys[tabValue];
        const previousKeyState = previousTabNumberKeys[currentSubtitleKey];
        
        if (previousKeyState) {
          const updatedPreviousKeyState = {
            ...previousKeyState,
            note: subtitleContent
          };
          
          if (tabValue === 3) {
            setNumberKeys(prev => ({
              ...prev,
              [String(currentSubtitleKey)]: updatedPreviousKeyState
            }));
          } else {
            setTabNumberKeys(prev => {
              const newState = { ...prev };
              newState[tabValue] = { ...newState[tabValue] };
              newState[tabValue][String(currentSubtitleKey)] = updatedPreviousKeyState;
              return newState;
            });
          }
          
          // 調試日誌已移除
        }
      }
      
      // 載入新選擇數字鍵的字幕內容
      setCurrentSubtitleKey(key);
      const noteContent = keyState.note || '';
      setSubtitleContent(noteContent);
      
      // 調試日誌已移除
      
      // 如果 keyState 沒有 timecode，嘗試從 timestamp 計算
      if (!keyState.timecode && keyState.timestamp) {
        const calculatedTimecode = formatTimecode(keyState.timestamp, keyState.frame || 0);
        // 調試日誌已移除
        
        // 更新 numberKeys 中的 timecode
        if (tabValue === 3) {
          setNumberKeys(prev => ({
            ...prev,
            [String(key)]: {
              ...prev[String(key)],
              timecode: calculatedTimecode
            }
          }));
        }
      }
    }
  }, [tabValue, numberKeys, tabNumberKeys, mButtonState, currentSubtitleKey, subtitleContent, formatTimecode]);
  
  // 修改 handleNumberKeyUp 函數，使用 tabNumberKeys[tabValue] 而不是 numberKeys
  const handleNumberKeyUp = useCallback(async (key: number) => {
    // 調試日誌已移除
    
    // 獲取當前 Tab 的數字鍵狀態
    // YouTube Tab (tabValue = 3) 直接使用 numberKeys，其他 Tab 使用 tabNumberKeys
    const isYouTubeTab = tabValue === 3;
    const currentTabNumberKeys = isYouTubeTab ? numberKeys : tabNumberKeys[tabValue];
    const keyState = currentTabNumberKeys[key];
    
    // 調試日誌已移除
    // 調試日誌已移除
    // 調試日誌已移除
    
    // M 模式下，自動處理字幕白板的數字鍵切換
    if (mButtonState === 'M') {
      if (keyState) {
        // 如果有之前選擇的數字鍵，先保存其內容
        if (currentSubtitleKey !== null && currentSubtitleKey !== key) {
          const previousTabNumberKeys = tabValue === 3 ? numberKeys : tabNumberKeys[tabValue];
          const previousKeyState = previousTabNumberKeys[currentSubtitleKey];
          
          if (previousKeyState) {
            const updatedPreviousKeyState = {
              ...previousKeyState,
              note: subtitleContent
            };
            
            if (tabValue === 3) {
              setNumberKeys(prev => ({
                ...prev,
                [String(currentSubtitleKey)]: updatedPreviousKeyState
              }));
            } else {
              setTabNumberKeys(prev => {
                const newState = { ...prev };
                newState[tabValue] = { ...newState[tabValue] };
                newState[tabValue][String(currentSubtitleKey)] = updatedPreviousKeyState;
                return newState;
              });
            }
            
            // 保存到 Firestore
            saveNoteToFirestore(currentSubtitleKey, subtitleContent, tabValue);
            // 調試日誌已移除
          }
        }
        
        // 載入新選擇數字鍵的字幕內容
        setCurrentSubtitleKey(key);
        const noteContent = keyState.note || '';
        setSubtitleContent(noteContent);
        
        // 🔧 新增：詳細日誌，檢查 keyState 中的 note 字段
        // 調試日誌已移除
        // 調試日誌已移除
        // 調試日誌已移除
        // 調試日誌已移除
        
        // 如果 keyState 沒有 timecode，嘗試從 timestamp 計算
        if (!keyState.timecode && keyState.timestamp) {
          const calculatedTimecode = formatTimecode(keyState.timestamp, keyState.frame || 0);
          // 調試日誌已移除
          
          // 更新 numberKeys 中的 timecode
          if (tabValue === 3) {
            setNumberKeys(prev => ({
              ...prev,
              [String(key)]: {
                ...prev[String(key)],
                timecode: calculatedTimecode
              }
            }));
          }
        }
        
        // 🔧 新增：全自動填充模式 - 選擇數字鍵時也自動填充（如果 Note 為空）
        if (isAutoFillMode && 
            hasSRTCache && 
            srtContent && 
            srtContent !== '_READY_' && 
            (!noteContent || noteContent.trim() === '')) {  // Note 為空時才自動填充
          
          console.log(`🤖 [數字鍵自動填充] 為數字鍵 ${key} 自動填充字幕（Note 為空）`);
          
          // 延遲一點時間確保 UI 更新完成
          setTimeout(() => {
            handleAutoFillNoteFromSRT().catch(error => {
              console.error('🤖 [數字鍵自動填充] 失敗:', error);
            });
          }, 100);
        }
        
        // 調試日誌已移除
      }
    }
    
    // 調試：檢查是否有其他鍵被意外設置為 pickup 狀態
    if (isYouTubeTab) {
      Object.entries(numberKeys).forEach(([k, state]) => {
        if (state.isPickUpState && Number(k) !== key) {
          // 狀態變化（日誌已移除）
          // 清理之前的 pickup 狀態
          setNumberKeys(prev => ({
            ...prev,
            [k]: { ...prev[k], isPickUpState: false }
          }));
        }
      });
    } else {
      Object.entries(tabNumberKeys[tabValue] || {}).forEach(([k, state]) => {
        if (state.isPickUpState && Number(k) !== key) {
          // 狀態變化（日誌已移除）
          // 清理之前的 pickup 狀態
          setTabNumberKeys(prev => ({
            ...prev,
            [tabValue]: {
              ...prev[tabValue],
              [k]: { ...prev[tabValue][k], isPickUpState: false }
            }
          }));
        }
      });
    }
    
    // === 0. 刪除模式優先判斷 ===
    if (isDeleteMode) {
      // 模式切換日誌已移除;
      startDeleteTimer();
      
      // 在刪除模式下，直接刪除該數字鍵的時間碼記憶（不論是否激活）
      if (isYouTubeTab) {
        setNumberKeys(prev => {
          const newState = { ...prev };
          delete newState[String(key)];
          return newState;
        });
      } else {
        setTabNumberKeys(prev => {
          const newState = { ...prev };
          newState[tabValue] = { ...(newState[tabValue] || {}) };
          delete newState[tabValue][String(key)];
          return newState;
        });
      }

      // 如果是 AB 點，也要清除
      if (abPoints.pointA.key === key) {
        setABPoints(prev => ({
          ...prev,
          pointA: { key: null, timestamp: null }
        }));
      }
      if (abPoints.pointB.key === key) {
        setABPoints(prev => ({
          ...prev,
          pointB: { key: null, timestamp: null }
        }));
      }

      // 播放刪除音效
      const audio = new Audio('/delete-sound.mp3');
      audio.play();

      // 顯示成功訊息
      setSnackbarMessage('已刪除時間碼記憶');
      setSnackbarOpen(true);
      
      // 調試日誌已移除
      // 調試日誌已移除
      
      // 同步刪除操作到 Firestore
      const syncDeleteToFirestore = async () => {
        try {
          // 調試日誌已移除
          
          // 直接使用函數式更新來確保獲取最新狀態
          const getUpdatedNumberKeys = () => {
            if (isYouTubeTab) {
              // 創建一個新的狀態對象，完全移除該鍵
              const updatedKeys = { ...numberKeys };
              delete updatedKeys[key];
              return updatedKeys;
            } else {
              const updatedKeys = { ...(tabNumberKeys[tabValue] || {}) };
              delete updatedKeys[key];
              return updatedKeys;
            }
          };
          
          // 確保 tabNumberKeys 同步更新
          if (isYouTubeTab) {
            setTabNumberKeys(prev => {
              const newState = { ...prev };
              newState[tabValue] = getUpdatedNumberKeys();
              // 調試日誌已移除
              return newState;
            });
          }
          
          const updatedNumberKeys = getUpdatedNumberKeys();
          // 調試日誌已移除
          
          const vbids = convertNumberKeysToVBIDs(updatedNumberKeys, getCurrentVideoMode() || 'YouTube', mButtonState);
          
          // 調試日誌已移除
          // 調試日誌已移除
          
          if (vbids.length >= 0) {
            // 調試日誌已移除
            
            await saveVbidsToFirestore({
              vbids,
              user,
              videoUrl,
              currentMode: getCurrentVideoMode() || 'YouTube',
              numberKeys: updatedNumberKeys,
              mButtonState,
              checkpointCount: vbids.length,
              fileName: originalFileName,
              fileSize: selectedFile?.size
            });
            // 調試日誌已移除
            
            // 🔧 新增：通知 ManagePages 清除快取，確保統計數據同步
            sessionStorage.setItem('clearManagePagesCache', Date.now().toString());
            // 調試日誌已移除
            
            // 🔧 新增：同時刪除 userNotes 中的對應筆記
            try {
              if (user?.uid && id) {
                const noteRef = doc(db, 'userNotes', `${user.uid}_${id}_${tabValue}_${key}`);
                await deleteDoc(noteRef);
                // 調試日誌已移除
              }
            } catch (error) {
              console.warn(`⚠️ 刪除 userNotes 失敗 (可能不存在): 數字鍵 ${key}`, error);
            }
            
            // 徹底清除快取，確保刪除操作後立即從 Firestore 重新載入
            const cacheKey = `firestore_${id}`;
            sessionStorage.removeItem(cacheKey);
            sessionStorage.removeItem(`${cacheKey}_timestamp`);
            sessionStorage.removeItem(`${cacheKey}_force_reload`);
            // 設置強制重新載入標記，確保下次載入時跳過快取
            sessionStorage.setItem(`${cacheKey}_force_reload`, 'true');
            // 清理日誌已移除
          } else {
            console.log('⚠️ vbids.length < 0，跳過 Firestore 同步');
          }
        } catch (error) {
          console.error('❌ 同步刪除操作到 Firestore 失敗:', error);
          console.error('❌ 錯誤詳情:', {
            message: error instanceof Error ? error.message : error,
            stack: error instanceof Error ? error.stack : undefined
          });
        }
      };
      
      // 立即同步，確保狀態更新完成
      syncDeleteToFirestore();
      
      // 直接 return，避免後續 pickup 跳轉
      return;
    }
    
    // === 1. AB模式優先判斷 ===
    if (mButtonState === 'AB' && keyState && keyState.timestamp !== null && !keyState.isABPoint) {
      // 如果已經有 A 點和 B 點，進行輪換
      if (abPoints.pointA.key !== null && abPoints.pointB.key !== null) {
        // 先將所有 isABPoint 設為 false，確保不會有三個黃色
        if (isYouTubeTab) {
          setNumberKeys(prev => {
            const newState = { ...prev };
            Object.keys(newState).forEach(k => {
              newState[Number(k)] = { ...newState[Number(k)], isABPoint: false };
            });
            return newState;
          });
        } else {
          setTabNumberKeys(prev => {
            const newState = { ...prev };
            newState[tabValue] = { ...newState[tabValue] };
            Object.keys(newState[tabValue]).forEach(k => {
              newState[tabValue][Number(k)] = { ...newState[tabValue][Number(k)], isABPoint: false };
            });
            return newState;
          });
        }

        // 將原 B 點設為新 A 點，新點設為新 B 點，並設 isABPoint 為 true
        if (isYouTubeTab) {
          if (abPoints.pointB.key !== null) {
            setNumberKeys(prev => ({
              ...prev,
              [abPoints.pointB.key!]: {
                ...prev[abPoints.pointB.key!],
                isABPoint: true
              },
              [key]: {
                ...prev[key],
                isABPoint: true
              }
            }));
          }
        } else {
          setTabNumberKeys(prev => {
            const newState = { ...prev };
            newState[tabValue] = { ...newState[tabValue] };
            if (abPoints.pointB.key !== null) {
              newState[tabValue][abPoints.pointB.key] = {
                ...newState[tabValue][abPoints.pointB.key],
                isABPoint: true
              };
            }
            newState[tabValue][key] = {
              ...newState[tabValue][key],
              isABPoint: true
            };
            return newState;
          });
        }

        // 更新 AB 點狀態
        setABPoints({
          pointA: { key: abPoints.pointB.key, timestamp: abPoints.pointB.timestamp },
          pointB: { key, timestamp: keyState.timestamp }
        });

        // 顯示設定 B 點訊息
        setSnackbarMessage(`已更新 AB 點: ${formatTimecode(keyState.timestamp!, keyState.frame)}`);
        setSnackbarOpen(true);
      } else if (abPoints.pointA.key === null) {
        // 設定 A 點
        setABPoints(prev => ({
          ...prev,
          pointA: { key, timestamp: keyState.timestamp }
        }));
        if (isYouTubeTab) {
          setNumberKeys(prev => ({
            ...prev,
            [key]: { ...prev[key], isABPoint: true }
          }));
        } else {
          setTabNumberKeys(prev => {
            const newState = { ...prev };
            newState[tabValue] = { ...newState[tabValue] };
            newState[tabValue][key] = { ...newState[tabValue][key], isABPoint: true };
            return newState;
          });
        }
        setSnackbarMessage(`已設定 A 點: ${formatTimecode(keyState.timestamp!, keyState.frame)}`);
        setSnackbarOpen(true);
      } else if (abPoints.pointB.key === null) {
        // 設定 B 點
        setABPoints(prev => ({
          ...prev,
          pointB: { key, timestamp: keyState.timestamp }
        }));
        if (isYouTubeTab) {
          setNumberKeys(prev => ({
            ...prev,
            [key]: { ...prev[key], isABPoint: true }
          }));
        } else {
          setTabNumberKeys(prev => {
            const newState = { ...prev };
            newState[tabValue] = { ...newState[tabValue] };
            newState[tabValue][key] = { ...newState[tabValue][key], isABPoint: true };
            return newState;
          });
        }
        setSnackbarMessage(`已設定 B 點: ${formatTimecode(keyState.timestamp!, keyState.frame)}`);
        setSnackbarOpen(true);
      }
      lastClickTime[key] = Date.now();
      return;
    }
    
    // 修正：正確的單擊pickup邏輯
    // 檢查點擊時間差來區分單擊和雙擊
    const currentTime = Date.now();
    const lastClick = lastClickTime[key] || 0;
    const timeDiff = currentTime - lastClick;
    
    // 如果時間差太小，說明是雙擊，執行check標記功能
    if (timeDiff > 0 && timeDiff < DOUBLE_CLICK_DELAY) {
      // 調試日誌已移除
      
      // 🔧 修復：使用 playedSeconds 狀態而非 getCurrentTime() 避免返回 0 的問題
      let currentTimestamp: number;
      let currentFrame: number = 0;
      let currentTimecode: string;
      
      if (videoMode === 'Google' && googleDriveRef.current) {
        // Google Drive 模式使用專用的 getCurrentTime
        try {
          currentTimestamp = googleDriveRef.current.getCurrentTime();
          if (currentTimestamp === undefined || currentTimestamp === null || isNaN(currentTimestamp)) {
            console.warn('⚠️ Google Drive getCurrentTime 返回無效值，無法創建重點');
            setSnackbarMessage('無法獲取當前播放時間，請稍後再試');
            setSnackbarOpen(true);
            return;
          }
          currentTimecode = formatTimecode(currentTimestamp, currentFrame);
          // 調試日誌已移除
        } catch (error) {
          console.error('❌ Google Drive 獲取時間失敗:', error);
          setSnackbarMessage('獲取播放時間時發生錯誤，請稍後再試');
          setSnackbarOpen(true);
          return;
        }
      } else if (playerRef.current) {
        // 🔧 修復：優先從播放器獲取當前時間（更準確，尤其是直播）
        try {
          currentTimestamp = playerRef.current.getCurrentTime();
          
          // 🔧 修復直播問題：如果 getCurrentTime() 回傳 0 或無效值，使用 playedSeconds 備用
          if (currentTimestamp === undefined || currentTimestamp === null || isNaN(currentTimestamp)) {
            console.warn('⚠️ getCurrentTime() 無效，使用 playedSeconds 備用');
            currentTimestamp = playedSeconds;
          }
          
          // 🔧 修復直播問題：如果兩者都是 0，可能是直播剛開始，詢問用戶
          if (currentTimestamp === 0 && playedSeconds === 0) {
            console.warn('⚠️ 時間戳為 0，可能是直播影片，請確認影片已開始播放');
            // 仍然允許記錄，但給予警告
            setSnackbarMessage('⚠️ 當前時間為 00:00:00，請確認這是否為正確的起始點');
            setSnackbarOpen(true);
          }
          
          currentTimecode = formatTimecode(currentTimestamp, currentFrame);
          // 調試日誌已移除
          
        } catch (error) {
          console.error('❌ ReactPlayer 獲取時間失敗:', error);
          setSnackbarMessage('無法獲取播放時間，請等待影片載入完成');
          setSnackbarOpen(true);
          return;
        }
      } else {
        console.log(`⚠️ 無法獲取當前時間，無法設置Check標記`);
        setSnackbarMessage(`無法獲取當前時間，無法設置Check標記`);
        setSnackbarOpen(true);
        return;
      }
      
      // 🔧 新增：檢測時間倒退（回播檢測）
      const lastActiveKey = Object.entries(isYouTubeTab ? numberKeys : (tabNumberKeys[tabValue] || {}))
        .filter(([_, state]) => state.isActive && !state.isABPoint)
        .map(([k, state]) => ({ keyNo: parseInt(k), timestamp: state.timestamp }))
        .sort((a, b) => b.keyNo - a.keyNo)[0]; // 取最後一個活躍的重點
      
      if (lastActiveKey && currentTimestamp < lastActiveKey.timestamp - 5) {
        // 🚨 檢測到時間倒退超過 5 秒
        const timeDiff = lastActiveKey.timestamp - currentTimestamp;
        console.warn('🚨 檢測到時間倒退！', {
          上一個重點: `NO${lastActiveKey.keyNo} (${formatTimecode(lastActiveKey.timestamp, 0)})`,
          當前位置: currentTimecode,
          時間差: `${timeDiff.toFixed(0)}秒`
        });
        
        // 顯示警告給用戶
        setSnackbarMessage(`⚠️ 檢測到回播！當前 ${currentTimecode}，上個重點 NO${lastActiveKey.keyNo}。建議：觀看直播時避免回播以保持時間順序正確。`);
        setSnackbarOpen(true);
        
        // 🎯 策略：仍然記錄當前位置，但在 note 中標記為「回顧」
        // 用戶可以之後手動調整
      }
      
      // 設置Check標記（藍色數字鍵）
      if (isYouTubeTab) {
        setNumberKeys(prev => ({
          ...prev,
          [key]: {
            keyNo: key,
            isActive: true,
            isABPoint: false,
            isPickUpState: false, // Check標記不是Pickup狀態
            timestamp: currentTimestamp,
            videoMode: getCurrentVideoMode(),
            frame: currentFrame,
            timecode: currentTimecode,
            note: '',
            screenshot: '',
            flag: 'on',
            playMode: mButtonState || 'M'
          }
        }));
      } else {
        setTabNumberKeys(prev => {
          const newState = { ...prev };
          newState[tabValue] = { ...newState[tabValue] };
          newState[tabValue][key] = {
            keyNo: key,
            isActive: true,
            isABPoint: false,
            isPickUpState: false, // Check標記不是Pickup狀態
            timestamp: currentTimestamp,
            videoMode: getCurrentVideoMode(),
            frame: currentFrame,
            timecode: currentTimecode,
            note: '',
            screenshot: '',
            flag: 'on',
            playMode: mButtonState || 'M'
          };
          return newState;
        });
      }
      
      // 顯示成功訊息
      setSnackbarMessage(`已設置Check標記: ${currentTimecode}`);
      setSnackbarOpen(true);
      
      // 更新最後點擊時間
      lastClickTime[key] = currentTime;
      return;
    }
    
    // 單擊pickup邏輯（時間差大於雙擊延遲，或是第一次點擊）
    if (timeDiff >= DOUBLE_CLICK_DELAY || timeDiff === 0) {
      // 調試日誌已移除
      // 調試日誌已移除
      
      // 檢查當前鍵是否有時間碼，只有有時間碼的鍵才能進入 pickup 邏輯
      // 允許任意按鈕選擇，但智能處理
      // 調試日誌已移除
      
      // 重置所有其他按鈕的綠色狀態，並 pickup 當前 key
      if (isYouTubeTab) {
        setNumberKeys(prev => {
          const newState = { ...prev };
          Object.keys(newState).forEach(k => {
            newState[k] = { ...newState[k], isPickUpState: false };
          });
          newState[key] = { ...newState[key], isPickUpState: true };
          return newState;
        });
      } else {
        setTabNumberKeys(prev => {
          const newState = { ...prev };
          newState[tabValue] = { ...newState[tabValue] };
          Object.keys(newState[tabValue]).forEach(k => {
            newState[tabValue][k] = { ...newState[tabValue][k], isPickUpState: false };
          });
          newState[tabValue][key] = { ...newState[tabValue][key], isPickUpState: true };
          return newState;
        });
      }
      
      // 同步設置 currentSubtitleKey，讓控制面板上半部顯示正確的數字鍵
      // 設置狀態（日誌已移除）
      setCurrentSubtitleKey(key);
      
      // 🔧 新增：觸發實時同步更新（如果正在同步會話中）
      if (syncManagerOpen || isSyncParticipant) {
        // 調試日誌已移除
        // SyncPlayManager會自動監聽numberKeys變化並發送同步更新
      }
      
      // 載入該數字鍵的 Note 內容到字幕白板
      if (keyState && keyState.note) {
        // 調試日誌已移除
        setSubtitleContent(keyState.note);
        
        // 🔧 新增：檢查內容是否來自 SRT（這裡假設現有內容是手動輸入的）
        setSubtitleContentWithSource({
          text: keyState.note,
          isFromSRT: false // 現有內容預設為手動輸入
        });
      } else {
        // 調試日誌已移除
        setSubtitleContent('');
        setSubtitleContentWithSource({ text: '', isFromSRT: false });
      }
      
      // 自動翻頁：計算 pickup 鍵所在的頁面並切換到該頁面
      const pickupKeyPage = Math.floor((key - 1) / 6);
      if (pickupKeyPage !== keySetIndex) {
        // 調試日誌已移除
        setKeySetIndex(pickupKeyPage);
      }
      
      // Pickup 邏輯：選取並跳轉到對應時間位置
      if (keyState && (keyState.timestamp !== null || keyState.timecode)) {
        // 🔧 修復：優先使用 timestamp 計算正確的視頻播放時間（因為 timecode 可能都是 00:00:00 00）
        let videoTimeSeconds = 0;
        
        // 優先使用 timestamp（因為 VBID 中的 timecode 可能都是錯誤的 00:00:00 00）
        if (keyState.timestamp !== null && keyState.timestamp !== undefined) {
          const timestampValue = convertTimestampToNumber(keyState.timestamp);
          // 🔧 修復：擴大時間範圍以支持長時間直播影片（最多支持約 278 小時）
          // 同時排除過大的數值（可能是錯誤的 Unix timestamp）
          if (timestampValue >= 0 && timestampValue <= 1000000) {
            videoTimeSeconds = timestampValue;
            // 調試日誌已移除
          } else {
            console.warn(`⚠️ timestamp 不合理 (${timestampValue} 秒)，跳過跳轉`);
            return;
          }
        } else if (keyState.timecode && keyState.timecode.trim() !== '' && keyState.timecode !== '00:00:00 00') {
          // 只有在 timestamp 無效且 timecode 不是 00:00:00 00 時，才使用 timecode
          const timecodeMatch = keyState.timecode.match(/^(\d{2}):(\d{2}):(\d{2})\s*[: ]\s*(\d{2})$/);
          if (timecodeMatch) {
            const hours = parseInt(timecodeMatch[1], 10);
            const minutes = parseInt(timecodeMatch[2], 10);
            const seconds = parseInt(timecodeMatch[3], 10);
            const frames = parseInt(timecodeMatch[4], 10);
            videoTimeSeconds = hours * 3600 + minutes * 60 + seconds + frames / 30;
            // 調試日誌已移除
          }
        }
        
        if (videoTimeSeconds > 0) {
          // 調試日誌已移除
          setSnackbarMessage(`已Pickup按鈕 ${key}，跳轉到 ${Math.floor(videoTimeSeconds)} 秒`);
          setSnackbarOpen(true);
        } else {
          console.warn(`⚠️ 鍵 ${key} 沒有有效的時間信息，無法跳轉`);
          return;
        }
        
        if (videoMode === 'Google' && googleDriveRef.current) {
          // Google Drive 模式下的跳轉
          // 調試日誌已移除
          googleDriveRef.current.seekTo(videoTimeSeconds);
          
          // 在 M 模式下，跳轉後確保播放狀態
          if (mButtonState === 'M') {
            setTimeout(() => {
              googleDriveRef.current?.play();
            }, 100);
          }
        } else if (playerRef.current && videoTimeSeconds) {
          // 一般播放器模式下的跳轉
          // 調試日誌已移除
          
          // 🔧 立即更新 playedSeconds，確保同步更新發送正確的時間
          setPlayedSeconds(videoTimeSeconds);
          
          seekToWithFade(videoTimeSeconds, 300).then(() => {
            // 操作完成（日誌已移除）
            
            // 🔧 再次確保時間狀態正確
            setPlayedSeconds(videoTimeSeconds);
            
            // 在 M 模式下，跳轉後確保播放狀態
            if (mButtonState === 'M') {
              setTimeout(() => {
                setIsPlaying(true);
                const internalPlayer = playerRef.current?.getInternalPlayer?.();
                if (videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be')) {
                  internalPlayer?.playVideo?.();
                } else {
                  // 對於非 YouTube 播放器，使用 getInternalPlayer
                  if (internalPlayer && typeof internalPlayer.play === 'function') {
                    internalPlayer.play();
                  }
                }
              }, 100);
            }
          });
        }
      } else {
        console.log(`⚠️ 鍵 ${key} 沒有時間碼，但允許pickup選取`);
        setSnackbarMessage(`已Pickup按鈕 ${key}（無時間碼）`);
        setSnackbarOpen(true);
      }
      
      // 調試：檢查是否有其他鍵被意外設置為 pickup 狀態
      if (isYouTubeTab) {
        Object.entries(numberKeys).forEach(([k, state]) => {
          if (state.isPickUpState && Number(k) !== key) {
            // 狀態變化（日誌已移除）
            // 調試日誌已移除
            // 清理之前的 pickup 狀態
            setNumberKeys(prev => ({
              ...prev,
              [k]: { ...prev[k], isPickUpState: false }
            }));
          }
        });
      } else {
        Object.entries(tabNumberKeys[tabValue] || {}).forEach(([k, state]) => {
          if (state.isPickUpState && Number(k) !== key) {
            // 狀態變化（日誌已移除）
            // 調試日誌已移除
            // 清理之前的 pickup 狀態
            setTabNumberKeys(prev => ({
              ...prev,
              [tabValue]: {
                ...prev[tabValue],
                [k]: { ...prev[tabValue][k], isPickUpState: false }
              }
            }));
          }
        });
      }
      
      // 檢查當前點擊的鍵是否已經有時間碼
      if (keyState && keyState.timestamp !== null) {
        // 調試日誌已移除
      } else {
        console.log(`⚠️ 當前點擊的鍵 ${key} 沒有時間碼，但進入了 pickup 邏輯`);
      }
      
      // 讀取藍色鍵的時間碼並跳轉播放（像控制鍵一樣）
      
      // 新增：pickup 紅色鍵會停留在最後一個藍色鍵上的邏輯
      // 找到最後一個藍色鍵（isActive = true 且 isPickUpState = false）
      const findLastBlueKey = () => {
        if (isYouTubeTab) {
          const blueKeys = Object.entries(numberKeys)
            .filter(([_, state]) => state.isActive && !state.isPickUpState && !state.isABPoint)
            .map(([k, _]) => Number(k))
            .sort((a, b) => a - b);
          return blueKeys.length > 0 ? blueKeys[blueKeys.length - 1] : null;
        } else {
          const blueKeys = Object.entries(tabNumberKeys[tabValue] || {})
            .filter(([_, state]) => state.isActive && !state.isPickUpState && !state.isABPoint)
            .map(([k, _]) => Number(k))
            .sort((a, b) => a - b);
          return blueKeys.length > 0 ? blueKeys[blueKeys.length - 1] : null;
        }
      };
      
      const lastBlueKey = findLastBlueKey();
      if (lastBlueKey !== null) {
        // 調試日誌已移除
        
        // 自動翻頁到最後一個藍色鍵所在的頁面
        const lastBlueKeyPage = Math.floor((lastBlueKey - 1) / 6);
        if (lastBlueKeyPage !== keySetIndex) {
          // 調試日誌已移除
          setKeySetIndex(lastBlueKeyPage);
        }
        
        // 修改：允許用戶手動選擇 pickup 狀態，但預設會轉移到最後一個藍色鍵
        // 如果用戶點擊的鍵沒有時間碼，則自動轉移到最後一個藍色鍵
        if (!keyState?.timestamp) {
          // 調試日誌已移除
          
          if (isYouTubeTab) {
            setNumberKeys(prev => {
              const newState = { ...prev };
              // 清除當前鍵的 pickup 狀態
              if (newState[key]) {
                newState[key] = { ...newState[key], isPickUpState: false };
              }
              // 設置最後一個藍色鍵的 pickup 狀態
              if (newState[lastBlueKey]) {
                newState[lastBlueKey] = { ...newState[lastBlueKey], isPickUpState: true };
              }
              return newState;
            });
          } else {
            setTabNumberKeys(prev => {
              const newState = { ...prev };
              newState[tabValue] = { ...newState[tabValue] };
              // 清除當前鍵的 pickup 狀態
              if (newState[tabValue][key]) {
                newState[tabValue][key] = { ...newState[tabValue][key], isPickUpState: false };
              }
              // 設置最後一個藍色鍵的 pickup 狀態
              if (newState[tabValue][lastBlueKey]) {
                newState[tabValue][lastBlueKey] = { ...newState[tabValue][lastBlueKey], isPickUpState: true };
              }
              return newState;
            });
          }
        } else {
          // 如果用戶點擊的鍵有時間碼，允許手動設置 pickup 狀態
          // 設置狀態（日誌已移除）
          
          if (isYouTubeTab) {
            setNumberKeys(prev => {
              const newState = { ...prev };
              // 清除所有其他鍵的 pickup 狀態
              Object.keys(newState).forEach(k => {
                newState[k] = { ...newState[k], isPickUpState: false };
              });
              // 設置當前點擊的鍵為 pickup 狀態
              if (newState[key]) {
                newState[key] = { ...newState[key], isPickUpState: true };
              }
              return newState;
            });
          } else {
            setTabNumberKeys(prev => {
              const newState = { ...prev };
              newState[tabValue] = { ...newState[tabValue] };
              // 清除所有其他鍵的 pickup 狀態
              Object.keys(newState[tabValue]).forEach(k => {
                newState[tabValue][k] = { ...newState[tabValue][k], isPickUpState: false };
              });
              // 設置當前點擊的鍵為 pickup 狀態
              if (newState[tabValue][key]) {
                newState[tabValue][key] = { ...newState[tabValue][key], isPickUpState: true };
              }
              return newState;
            });
          }
        }
      }

      // MV 模式特殊處理：切換到儲存的URL
      if (mButtonState === 'MV' && keyState?.isActive && keyState.mvUrl) {
        // 調試日誌已移除
        
        // 切換到儲存的URL
        setVideoUrl(keyState.mvUrl);
        loadVideo(keyState.mvUrl);
        
        // 顯示切換訊息
        setSnackbarMessage(`已切換至第 ${key} 支影片`);
        setSnackbarOpen(true);
      } else if (keyState?.isActive && keyState.timestamp !== null) {
        // 🔧 修復：移除重複的跳轉邏輯，因為 pickup 邏輯已經處理了跳轉
        // 調試日誌已移除
        
        // 顯示跳轉訊息
        setSnackbarMessage(`已跳轉至時間碼 ${formatTimecode(keyState.timestamp, keyState.frame)}`);
        setSnackbarOpen(true);
      }
      
      // 更新最後點擊時間
      lastClickTime[key] = currentTime;
      return;
    }

    
    // 如果是 PB 模式，使用 handleNumberKeyClick 的邏輯
    if (mButtonState === 'PB') {
      // 調試日誌已移除
      
      // 先清除現有的 interval，避免衝突
      if (ppMode.interval) {
        clearInterval(ppMode.interval);
        updatePPMode('interval', null);
      }
      
      if (isYouTubeTab) {
        setNumberKeys(prev => {
          const newState = { ...prev };
          Object.keys(newState).forEach(k => {
            newState[k] = { ...newState[k], isPickUpState: false };
          });
          newState[key] = { ...newState[key], isPickUpState: true };

          // 修正：PB 模式下可以點擊任何重點，包括之前播過的重點
          const allKeys = Object.entries(newState)
            .filter(([_, keyState]) => keyState.timestamp !== null && !keyState.isABPoint)
            .map(([k, keyState]) => ({ keyNo: Number(k), timestamp: keyState.timestamp!, isABPoint: keyState.isABPoint, isPickUpState: keyState.isPickUpState }))
            .sort((a, b) => a.timestamp - b.timestamp);
          
          // 找到點擊的重點在 allKeys 中的索引
          const clickedKeyIndex = allKeys.findIndex(k => k.keyNo === key);
          if (clickedKeyIndex !== -1) {
            // 從點擊的重點開始，包含該重點及之後的所有重點
            const keysToPlay = allKeys.slice(clickedKeyIndex);
            
            // 調試日誌已移除
            
            updatePPMode('keysToPlay', keysToPlay);
            // 🔧 修復：不要設置 ppMode.currentPlayIndex 為 0，避免觸發 useEffect 的自動跳轉
            // updatePPMode('currentPlayIndex', 0);
            
            // 重置 PB 狀態，確保從新重點開始計時
            pbIsReadyRef.current = false;
            pbLastPlayIndexRef.current = -1;
            currentPlayIndexRef.current = 0;
            
            // 立即跳轉到用戶點擊的重點並開始播放
            const clickedKey = allKeys[clickedKeyIndex];
            if (playerRef.current && clickedKey.timestamp != null) {
              // 調試日誌已移除
              const timestampValue = convertTimestampToNumber(clickedKey.timestamp);
              playerRef.current.seekTo(timestampValue);
              setIsPlaying(true);
              const internalPlayer = playerRef.current.getInternalPlayer?.();
              if (videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be')) {
                internalPlayer?.playVideo?.();
              } else {
                internalPlayer?.play?.();
              }
            }
          }

          return newState;
        });
      } else {
        // 處理其他 Tab 的邏輯
        setTabNumberKeys(prev => {
          const newState = { ...prev };
          newState[tabValue] = { ...newState[tabValue] };
          Object.keys(newState[tabValue]).forEach(k => {
            newState[tabValue][k] = { ...newState[tabValue][k], isPickUpState: false };
          });
          newState[tabValue][key] = { ...newState[tabValue][key], isPickUpState: true };

          // 修正：PB 模式下可以點擊任何重點，包括之前播過的重點
          const allKeys = Object.entries(newState[tabValue])
            .filter(([_, keyState]) => keyState.timestamp !== null && !keyState.isABPoint)
            .map(([k, keyState]) => ({ keyNo: Number(k), timestamp: keyState.timestamp!, isABPoint: keyState.isABPoint, isPickUpState: keyState.isPickUpState }))
            .sort((a, b) => a.timestamp - b.timestamp);
          
          // 找到點擊的重點在 allKeys 中的索引
          const clickedKeyIndex = allKeys.findIndex(k => k.keyNo === key);
          if (clickedKeyIndex !== -1) {
            // 從點擊的重點開始，包含該重點及之後的所有重點
            const keysToPlay = allKeys.slice(clickedKeyIndex);
            
            // 調試日誌已移除
            
            updatePPMode('keysToPlay', keysToPlay);
            // 🔧 修復：不要設置 ppMode.currentPlayIndex 為 0，避免觸發 useEffect 的自動跳轉
            // updatePPMode('currentPlayIndex', 0);
            
            // 重置 PB 狀態，確保從新重點開始計時
            pbIsReadyRef.current = false;
            pbLastPlayIndexRef.current = -1;
            currentPlayIndexRef.current = 0;
            
            // 立即跳轉到用戶點擊的重點並開始播放
            const clickedKey = allKeys[clickedKeyIndex];
            if (playerRef.current && clickedKey.timestamp != null) {
              // 調試日誌已移除
              const timestampValue = convertTimestampToNumber(clickedKey.timestamp);
              playerRef.current.seekTo(timestampValue);
              setIsPlaying(true);
              const internalPlayer = playerRef.current.getInternalPlayer?.();
              if (videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be')) {
                internalPlayer?.playVideo?.();
              } else {
                internalPlayer?.play?.();
              }
            }
          }

          return newState;
        });
      }
      return;
    }
    
    // 自動擴增數字鍵數量
    if (key === maxNumberKeys) {
      handleMaxNumberKeysChange(maxNumberKeys + 6);
    }
    
    // 重置所有其他按鈕的綠色狀態
    if (isYouTubeTab) {
      setNumberKeys(prev => {
        const newState = { ...prev };
        Object.keys(newState).forEach(k => {
          const numKey = Number(k);
          if (numKey !== key && newState[numKey].isPickUpState) {
            newState[numKey] = {
              ...newState[numKey],
              isPickUpState: false
            };
          }
        });
        return newState;
      });
    } else {
      setTabNumberKeys(prev => {
        const newState = { ...prev };
        newState[tabValue] = { ...newState[tabValue] };
        Object.keys(newState[tabValue]).forEach(k => {
          const numKey = Number(k);
          if (numKey !== key && newState[tabValue][numKey].isPickUpState) {
            newState[tabValue][numKey] = {
              ...newState[tabValue][numKey],
              isPickUpState: false
            };
          }
        });
        return newState;
      });
    }

    // 獲取當前時間和播放器時間（重命名避免衝突）
    const clickCurrentTime = Date.now();
    const clickLastClick = lastClickTime[key] || 0;
    const clickTimeDiff = clickCurrentTime - clickLastClick;
    const currentVideoMode = tabValue === 0 ? 'Short' :
                           tabValue === 1 ? 'Google' :
                           tabValue === 2 ? 'VIDEO' :
                           tabValue === 3 ? 'YouTube' :
                           tabValue === 4 ? 'PHOTO' :
                           tabValue === 5 ? 'PDF' : tabValue === 6 ? 'Multiview' : null;

    if (clickTimeDiff < DOUBLE_CLICK_DELAY) {
      // 雙擊處理
      if (!currentTabNumberKeys[key]?.isActive) {
        // 第一次雙擊：記憶當前時間碼
        console.log(`Setting timestamp for key ${key}: ${playedSeconds}, frame: ${currentFrame}, timecode: ${currentTimecode}`);
        
        // 從 currentTimecode 解析時間和幀數
        const [timeStr, frameStr] = currentTimecode.split(' ');
        const [hours, minutes, seconds] = timeStr.split(':').map(Number);
        const frame = parseInt(frameStr);
        const timestamp = hours * 3600 + minutes * 60 + seconds;
        
        if (isYouTubeTab) {
          setNumberKeys(prev => ({
            ...prev,
            [key]: { 
              keyNo: key,
              isActive: true,
              isABPoint: false,
              isPickUpState: true,
              timestamp: timestamp,
              videoMode: currentVideoMode,
              frame: frame,
              timecode: currentTimecode,
              note: '',
              screenshot: '',
              flag: 'on',
              playMode: mButtonState || 'M'
            }
          }));
        } else {
          setTabNumberKeys(prev => {
            const newState = { ...prev };
            newState[tabValue] = { ...newState[tabValue] };
            newState[tabValue][key] = { 
              keyNo: key,
              isActive: true,
              isABPoint: false,
              isPickUpState: true,
              timestamp: timestamp,
              videoMode: currentVideoMode,
              frame: frame,
              timecode: currentTimecode,
              note: '',
              screenshot: '',
              flag: 'on',
              playMode: mButtonState || 'M'
            };
            return newState;
          });
        }
        
        // 🔧 修復：雙擊創建重點時也要設置 currentSubtitleKey
        // 設置狀態（日誌已移除）
        setCurrentSubtitleKey(key);
        
        // 即時保存重點數到 Firestore
        const saveCheckpointToFirestore = async () => {
          try {
            // 🔧 修復：延遲獲取最新的 numberKeys 狀態
            setTimeout(async () => {
              const currentNumberKeys = isYouTubeTab ? numberKeys : tabNumberKeys[tabValue];
              const vbids = convertNumberKeysToVBIDs(currentNumberKeys, currentVideoMode || 'YouTube', mButtonState);
              
              if (vbids.length > 0) {
                await saveVbidsToFirestore({
                  vbids,
                  user,
                  videoUrl,
                  currentMode: currentVideoMode || 'YouTube',
                  numberKeys: currentNumberKeys,
                  mButtonState,
                  checkpointCount: vbids.length,
                  fileName: originalFileName,
                  fileSize: selectedFile?.size
                });
                // 調試日誌已移除
              }
            }, 100); // 延遲 100ms 確保狀態更新完成
          } catch (error) {
            console.error('❌ 保存重點數失敗:', error);
          }
        };
        
        // 異步保存，不阻塞 UI
        saveCheckpointToFirestore();
        
        // 暫停影片
        if (isPlaying) {
          setIsPlaying(false);
        }
        // 顯示成功訊息
        setSnackbarMessage(`已記憶時間碼 ${currentTimecode}`);
        setSnackbarOpen(true);
      } else {
        // 第二次雙擊：清除記憶
        console.log(`Clearing timestamp for key ${key}`);
        if (isYouTubeTab) {
          setNumberKeys(prev => ({
            ...prev,
            [key]: { 
              keyNo: key,
              isActive: false,
              isABPoint: false,
              isPickUpState: false,
              timestamp: null,
              videoMode: null,
              frame: null,
              timecode: '00:00:00 00',
              note: '',
              screenshot: '',
              flag: 'off',
              playMode: 'M'
            }
          }));
        } else {
          setTabNumberKeys(prev => {
            const newState = { ...prev };
            newState[tabValue] = { ...newState[tabValue] };
            newState[tabValue][key] = { 
              keyNo: key,
              isActive: false,
              isABPoint: false,
              isPickUpState: false,
              timestamp: null,
              videoMode: null,
              frame: null,
              timecode: '00:00:00 00',
              note: '',
              screenshot: '',
              flag: 'off',
              playMode: 'M'
            };
            return newState;
          });
        }
        // 如果是 AB 點，也要清除對應的 AB 點
        if (abPoints.pointA.key === key) {
          setABPoints(prev => ({
            ...prev,
            pointA: { key: null, timestamp: null }
          }));
        }
        if (abPoints.pointB.key === key) {
          setABPoints(prev => ({
            ...prev,
            pointB: { key: null, timestamp: null }
          }));
        }
        
        // 即時保存更新後的重點數到 Firestore
        const saveUpdatedCheckpointsToFirestore = async () => {
          try {
            const currentNumberKeys = isYouTubeTab ? numberKeys : tabNumberKeys[tabValue];
            const vbids = convertNumberKeysToVBIDs(currentNumberKeys, currentVideoMode || 'YouTube', mButtonState);
            
            await saveVbidsToFirestore({
              vbids,
              user,
              videoUrl,
              currentMode: currentVideoMode || 'YouTube',
              numberKeys: currentNumberKeys,
              mButtonState,
              checkpointCount: vbids.length,
              fileName: originalFileName,
              fileSize: selectedFile?.size
            });
            // 調試日誌已移除
          } catch (error) {
            console.error('❌ 保存更新後的重點數失敗:', error);
          }
        };
        
        // 異步保存，不阻塞 UI
        saveUpdatedCheckpointsToFirestore();
        
        // 顯示成功訊息
        setSnackbarMessage('已清除時間碼記憶');
        setSnackbarOpen(true);
      }
    } else {
      // 單擊處理
      if (mButtonState === 'M' || mButtonState === 'MS' || mButtonState === 'MV') {
        // M 模式、MS 模式或 MV 模式：跳轉到記憶的時間碼或切換URL
        const keyState = currentTabNumberKeys[key];
        
        // MV 模式特殊處理：切換到儲存的URL
        if (mButtonState === 'MV' && keyState?.isActive && keyState.mvUrl) {
          console.log(`MV mode - switching to URL for key ${key}: ${keyState.mvUrl}`);
          console.log('Current keyState:', keyState);
          
          // 如果按鈕已經是藍色狀態，點擊後變為綠色
          if (!keyState.isPickUpState) {
            if (isYouTubeTab) {
              setNumberKeys(prev => ({
                ...prev,
                [key]: { ...prev[key], isPickUpState: true }
              }));
            } else {
              setTabNumberKeys(prev => {
                const newState = { ...prev };
                newState[tabValue] = { ...newState[tabValue] };
                newState[tabValue][key] = { ...newState[tabValue][key], isPickUpState: true };
                return newState;
              });
            }
          }
          
          // 切換到儲存的URL
          setVideoUrl(keyState.mvUrl);
          loadVideo(keyState.mvUrl);
          
          // 顯示切換訊息
          setSnackbarMessage(`已切換至第 ${key} 支影片`);
          setSnackbarOpen(true);
          return;
        }
        
        // M 模式或 MS 模式：跳轉到記憶的時間碼
        if (keyState?.isActive && 
            keyState.timestamp !== null && 
            keyState.videoMode === currentVideoMode) {
          // 如果按鈕已經是藍色狀態，點擊後變為綠色
          if (!keyState.isPickUpState) {
            if (isYouTubeTab) {
              setNumberKeys(prev => ({
                ...prev,
                [key]: { ...prev[key], isPickUpState: true }
              }));
            } else {
              setTabNumberKeys(prev => {
                const newState = { ...prev };
                newState[tabValue] = { ...newState[tabValue] };
                newState[tabValue][key] = { ...newState[tabValue][key], isPickUpState: true };
                return newState;
              });
            }
          }
          console.log(`Seeking to timestamp for key ${key}: ${keyState.timestamp}, frame: ${keyState.frame}`);
          const timestampValue = convertTimestampToNumber(keyState.timestamp);
          playerRef.current?.seekTo(timestampValue);
          
          // 在M模式下，跳轉後確保播放狀態，使用更長的延遲避免YouTube載入問題
          if (mButtonState === 'M') {
            // 使用更長的延遲確保YouTube完全載入
            setTimeout(() => {
              setIsPlaying(true);
              
              // 再次延遲確認播放狀態
              setTimeout(() => {
                if (!isPlaying) {
                  setIsPlaying(true);
                }
              }, 800);
            }, 1000); // 1秒延遲確保YouTube載入完成
          } else {
            // 和MS模式保持原來的暫停行為
            if (isPlaying) {
              setIsPlaying(false);
            }
          }
          
          // 顯示跳轉訊息
          setSnackbarMessage(`已跳轉至時間碼 ${formatTimecode(keyState.timestamp, keyState.frame)}`);
          setSnackbarOpen(true);
        }
      }
      
      // 新增：確保 pickup 狀態正確設置
      // 如果當前點擊的鍵沒有時間碼，但我們找到了最後一個藍色鍵，則將 pickup 狀態設置到最後一個藍色鍵上
      const findLastBlueKey = () => {
        if (isYouTubeTab) {
          const blueKeys = Object.entries(numberKeys)
            .filter(([_, state]) => state.isActive && !state.isPickUpState && !state.isABPoint)
            .map(([k, _]) => Number(k))
            .sort((a, b) => a - b);
          return blueKeys.length > 0 ? blueKeys[blueKeys.length - 1] : null;
        } else {
          const blueKeys = Object.entries(tabNumberKeys[tabValue] || {})
            .filter(([_, state]) => state.isActive && !state.isPickUpState && !state.isABPoint)
            .map(([k, _]) => Number(k))
            .sort((a, b) => a - b);
          return blueKeys.length > 0 ? blueKeys[blueKeys.length - 1] : null;
        }
      };
      
      const lastBlueKey = findLastBlueKey();
      if (!keyState?.timestamp && lastBlueKey !== null && lastBlueKey !== key) {
        // 狀態變化（日誌已移除）
        
        if (isYouTubeTab) {
          setNumberKeys(prev => {
            const newState = { ...prev };
            // 清除當前鍵的 pickup 狀態
            if (newState[key]) {
              newState[key] = { ...newState[key], isPickUpState: false };
            }
            // 設置最後一個藍色鍵的 pickup 狀態
            if (newState[lastBlueKey]) {
              newState[lastBlueKey] = { ...newState[lastBlueKey], isPickUpState: true };
            }
            return newState;
          });
        } else {
          setTabNumberKeys(prev => {
            const newState = { ...prev };
            newState[tabValue] = { ...newState[tabValue] };
            // 清除當前鍵的 pickup 狀態
            if (newState[tabValue][key]) {
              newState[tabValue][key] = { ...newState[tabValue][key], isPickUpState: false };
            }
            // 設置最後一個藍色鍵的 pickup 狀態
            if (newState[tabValue][lastBlueKey]) {
              newState[tabValue][lastBlueKey] = { ...newState[tabValue][lastBlueKey], isPickUpState: true };
            }
            return newState;
          });
        }
      }
      
      // 更新最後點擊時間
      lastClickTime[key] = Date.now();
    }
  }, [tabValue, numberKeys, tabNumberKeys, mButtonState, currentSubtitleKey, subtitleContent, setSubtitleContent, setCurrentSubtitleKey, setSubtitleContentWithSource, handleAutoFillNoteFromSRT, lastClickTime]);

  // 鍵盤事件處理 - 擴展功能
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // 调试日志已移除
      
      // 檢查是否在輸入框中，如果是則不處理鍵盤事件
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true') {
        // 调试日志已移除
        return;
      }

      const key = event.key;
      const isShift = event.shiftKey;
      const isCtrl = event.ctrlKey;
      const isAlt = event.altKey;

      // 1. 空白鍵 - 播放/暫停
      if (key === ' ') {
        event.preventDefault();
        console.log('⏯️ 空白鍵 - 播放/暫停');
        handlePlayPause();
        return;
      }

      // 2. Shift 鍵 - 已取消 M 鍵功能
      // if (key === 'Shift') {
      //   event.preventDefault();
      //   // 狀態追蹤日誌已移除
      //   handleMButtonClick();
      //   return;
      // }

      // 3. Delete 鍵、Backspace 鍵或 D 鍵 - 直接刪除所選的重點數字
      if (key === 'Delete' || key === 'Backspace' || key === 'd' || key === 'D') {
        event.preventDefault();
        console.log('🗑️ Delete/Backspace/D 鍵 - 直接刪除所選的重點數字');
        
        // 檢查是否有任何數字鍵被啟動
        const currentTabNumberKeys = tabValue === 3 ? numberKeys : tabNumberKeys[tabValue];
        const hasActiveKeys = Object.values(currentTabNumberKeys).some(key => key.isActive);
        
        // 刪除模式日誌已移除;
        
        if (!hasActiveKeys) {
          // 如果沒有數字鍵被啟動，顯示提示訊息
          setSnackbarMessage('請先按下數字記憶鍵');
          setSnackbarOpen(true);
          return;
        }
        
        // 找到當前選中的紅色鍵（isPickUpState = true）
        let keyToDelete = Object.entries(currentTabNumberKeys)
          .find(([_, keyState]) => keyState.isPickUpState)?.[0];
        console.log('🗑️ 找到紅色鍵:', keyToDelete);
        
        // 如果沒有紅色鍵，找第一個藍色鍵（有 timestamp 的 isActive 鍵）
        if (!keyToDelete) {
          keyToDelete = Object.entries(currentTabNumberKeys)
            .find(([_, keyState]) => keyState.isActive && keyState.timestamp !== null)?.[0];
          console.log('🗑️ 找到藍色鍵:', keyToDelete);
        }
        
        if (keyToDelete) {
          console.log(`🗑️ 準備刪除重點數字: ${keyToDelete}`);
          
                  // 直接執行刪除邏輯，不依賴 isDeleteMode 狀態
        const keyToDeleteNum = Number(keyToDelete);
          
          // 刪除該按鈕的時間碼記憶
          if (tabValue === 3) {
            setNumberKeys(prev => ({
              ...prev,
              [String(keyToDeleteNum)]: {
                keyNo: keyToDeleteNum,
                isActive: false,
                isABPoint: false,
                isPickUpState: false,
                timestamp: null,
                timecode: '00:00:00 00',
                videoMode: null,
                frame: null,
                note: '',
                screenshot: '',
                flag: 'off',
                playMode: 'M'
              }
            }));
          } else {
            setTabNumberKeys(prev => {
              const newState = { ...prev };
              newState[tabValue] = { ...newState[tabValue] };
              newState[tabValue][String(keyToDeleteNum)] = {
                keyNo: keyToDeleteNum,
                isActive: false,
                isABPoint: false,
                isPickUpState: false,
                timestamp: null,
                timecode: '00:00:00 00',
                videoMode: null,
                frame: null,
                note: '',
                screenshot: '',
                flag: 'off',
                playMode: 'M'
              };
              return newState;
            });
          }

          // 如果是 AB 點，也要清除
          if (abPoints.pointA.key === keyToDeleteNum) {
            setABPoints(prev => ({
              ...prev,
              pointA: { key: null, timestamp: null }
            }));
          }
          if (abPoints.pointB.key === keyToDeleteNum) {
            setABPoints(prev => ({
              ...prev,
              pointB: { key: null, timestamp: null }
            }));
          }

          // 播放刪除音效
          const audio = new Audio('/delete-sound.mp3');
          audio.play();

          // 顯示成功訊息
          setSnackbarMessage('已刪除時間碼記憶');
          setSnackbarOpen(true);
          
          // 🔧 修復：同步刪除到 Firestore
          const syncDeleteToFirestore = async () => {
            try {
              // 調試日誌已移除
              
              // 獲取更新後的 numberKeys
              const getUpdatedNumberKeys = () => {
                if (tabValue === 3) {
                  const updatedKeys = { ...numberKeys };
                  delete updatedKeys[String(keyToDeleteNum)];
                  return updatedKeys;
                } else {
                  const updatedKeys = { ...(tabNumberKeys[tabValue] || {}) };
                  delete updatedKeys[String(keyToDeleteNum)];
                  return updatedKeys;
                }
              };
              
              const updatedNumberKeys = getUpdatedNumberKeys();
              const vbids = convertNumberKeysToVBIDs(updatedNumberKeys);
              
              // 調試日誌已移除
              // 調試日誌已移除
              
              // 同步到 Firestore
              await saveVbidsToFirestore({
                vbids,
                user,
                videoUrl,
                currentMode: getCurrentVideoMode() || 'YouTube',
                numberKeys: updatedNumberKeys,
                mButtonState,
                checkpointCount: vbids.length,
                fileName: originalFileName,
                fileSize: selectedFile?.size
              });
              
              // 成功日誌已移除
            } catch (error) {
              console.error('❌ 同步刪除到 Firestore 失敗:', error);
            }
          };
          
          // 延遲同步到 Firestore，確保 state 已更新
          setTimeout(syncDeleteToFirestore, 100);
          
          // 調試日誌已移除
        } else {
          console.log('⚠️ 沒有可刪除的重點數字鍵');
        }
        return;
      } 
            // 4. 左右鍵 - 移動 pickup 數字鍵
      if (key === 'ArrowLeft' || key === 'ArrowRight') {
        event.preventDefault();
        
        // 檢查是否同時按下 Shift 鍵
        if (isShift) {
          // Shift + 左右鍵 = 5秒前進/倒退
          if (key === 'ArrowLeft') {
            console.log('⏪ Shift + 左鍵 - 倒退5秒');
            const newTime = Math.max(0, currentTime - 5);
            handleSeek(newTime);
          } else {
            console.log('⏩ Shift + 右鍵 - 前進5秒');
            const newTime = Math.min(duration, currentTime + 5);
            handleSeek(newTime);
          }
        } else {
          // 普通左右鍵 = 移動 pickup 數字鍵
          console.log(`⬅️➡️ 方向鍵 ${key} - 移動 pickup 數字鍵`);
          
          // 找到當前選中的紅色鍵（isPickUpState = true）
          const currentTabNumberKeys = tabValue === 3 ? numberKeys : tabNumberKeys[tabValue];
          const activeKeys = Object.entries(currentTabNumberKeys)
            .filter(([_, keyState]) => keyState.isPickUpState)
            .map(([key, _]) => parseInt(key))
            .sort((a, b) => a - b);
          
          // 🔧 修復：計算實際的最大鍵號，而不是使用 maxNumberKeys
          const allKeys = Object.entries(currentTabNumberKeys)
            .map(([key, _]) => parseInt(key))
            .filter(key => !isNaN(key))
            .sort((a, b) => a - b);
          const actualMaxKey = allKeys.length > 0 ? Math.max(...allKeys) : maxNumberKeys;
          
          // 🔧 修復：智能左右鍵移動，跳過無法 pickup 的鍵
          let nextKey;
          if (activeKeys.length > 0) {
            // 如果有數字被 pickup，按左右鍵改變選取的數字
            const currentActiveKey = activeKeys[0]; // 取第一個紅色鍵
            
            if (key === 'ArrowLeft') {
              // 左箭頭：往左移動（數字變小），智能跳過
              nextKey = currentActiveKey - 1;
              while (nextKey >= 1 && !currentTabNumberKeys[nextKey]) {
                nextKey--;
              }
              if (nextKey < 1) {
                // 如果左邊沒有可用鍵，從最右邊開始找
                nextKey = actualMaxKey;
                while (nextKey > currentActiveKey && !currentTabNumberKeys[nextKey]) {
                  nextKey--;
                }
              }
            } else {
              // 右箭頭：往右移動（數字變大），智能跳過
              nextKey = currentActiveKey + 1;
              while (nextKey <= actualMaxKey && !currentTabNumberKeys[nextKey]) {
                nextKey++;
              }
              if (nextKey > actualMaxKey) {
                // 如果右邊沒有可用鍵，從最左邊開始找
                nextKey = 1;
                while (nextKey < currentActiveKey && !currentTabNumberKeys[nextKey]) {
                  nextKey++;
                }
              }
            }
          } else {
            // 如果沒有任何數字被 pickup，從數字1開始
            nextKey = 1;
            // 如果數字1不可用，找第一個可用的鍵
            while (nextKey <= actualMaxKey && !currentTabNumberKeys[nextKey]) {
              nextKey++;
            }
            if (nextKey > actualMaxKey) nextKey = 1; // 如果都沒有，回到1
          }
          
          // 調試日誌已移除
          handleNumberKeyUp(nextKey);
        }
        return;
      }

      // 5. 上下鍵 - 控制數字鍵 pickup
      if (key === 'ArrowUp' || key === 'ArrowDown') {
        event.preventDefault();
        console.log(`⬆️⬇️ 方向鍵 ${key} - 控制數字鍵 pickup`);
        
        // 找到當前選中的紅色鍵（isPickUpState = true）
        const currentTabNumberKeys = tabValue === 3 ? numberKeys : tabNumberKeys[tabValue];
        const activeKeys = Object.entries(currentTabNumberKeys)
          .filter(([_, keyState]) => keyState.isPickUpState)
          .map(([key, _]) => parseInt(key))
          .sort((a, b) => a - b);
        
        // 🔧 修復：計算實際的最大鍵號，而不是使用 maxNumberKeys
        const allKeys = Object.entries(currentTabNumberKeys)
          .map(([key, _]) => parseInt(key))
          .filter(key => !isNaN(key))
          .sort((a, b) => a - b);
        const actualMaxKey = allKeys.length > 0 ? Math.max(...allKeys) : maxNumberKeys;
        
        // 🔧 修復：智能上下鍵移動，跳過無法 pickup 的鍵
        let nextKey;
        if (activeKeys.length > 0) {
          // 如果有數字被 pickup，按上下鍵改變選取的數字
          const currentActiveKey = activeKeys[0]; // 取第一個紅色鍵
          
          if (key === 'ArrowUp') {
            // 上箭頭：數字變大，智能跳過
            nextKey = currentActiveKey + 1;
            while (nextKey <= actualMaxKey && !currentTabNumberKeys[nextKey]) {
              nextKey++;
            }
            if (nextKey > actualMaxKey) {
              // 如果上邊沒有可用鍵，從最下邊開始找
              nextKey = 1;
              while (nextKey < currentActiveKey && !currentTabNumberKeys[nextKey]) {
                nextKey++;
              }
            }
          } else {
            // 下箭頭：數字變小，智能跳過
            nextKey = currentActiveKey - 1;
            while (nextKey >= 1 && !currentTabNumberKeys[nextKey]) {
              nextKey--;
            }
            if (nextKey < 1) {
              // 如果下邊沒有可用鍵，從最上邊開始找
              nextKey = actualMaxKey;
              while (nextKey > currentActiveKey && !currentTabNumberKeys[nextKey]) {
                nextKey--;
              }
            }
          }
        } else {
          // 如果沒有任何數字被 pickup，從數字1開始
          nextKey = 1;
          // 如果數字1不可用，找第一個可用的鍵
          while (nextKey <= actualMaxKey && !currentTabNumberKeys[nextKey]) {
            nextKey++;
          }
          if (nextKey > actualMaxKey) nextKey = 1; // 如果都沒有，回到1
        }
        
        // 調試日誌已移除
        handleNumberKeyUp(nextKey);
        return;
      }

      // 6. \ 鍵 - 啟動截圖
      if (key === '\\') {
        event.preventDefault();
        // 調試日誌已移除
        handleScreenshot();
        return;
      }

      // 7. 數字鍵 1-9 - 選擇重點
      if (/^[1-9]$/.test(key)) {
        event.preventDefault();
        const numberKey = parseInt(key);
        console.log('⌨️ 鍵盤數字鍵按下:', numberKey);
        handleNumberKeyUp(numberKey);
        return;
      }

      // 8. 額外功能：F 鍵 - 全螢幕
      if (key === 'f' || key === 'F') {
        event.preventDefault();
        // 快捷鍵日誌已移除;
        handleFullScreen();
        return;
      }

      // 9. Enter 鍵 - 標記重點（等同 Check 鍵）
      if (key === 'Enter') {
        event.preventDefault();
        // 成功日誌已移除
        handleCheckClick();
        return;
      }

      // 10. / 鍵和 S 鍵 - 改變播放速度（等同點擊「速度」鍵）
      if (key === '/' || key === 's' || key === 'S') {
        event.preventDefault();
        // 快速操作日誌已移除
        
        // 定義播放速度循環順序
        const speedSequence = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
        const currentIndex = speedSequence.indexOf(playbackRate);
        const nextIndex = (currentIndex + 1) % speedSequence.length;
        const newSpeed = speedSequence[nextIndex];
        
        handleSpeedChange(newSpeed);
        return;
      }

      // 11. 額外功能：R 鍵 - 播放回到 00:00
      if (key === 'r' || key === 'R') {
        event.preventDefault();
        // 狀態追蹤日誌已移除
        
        // 跳轉到 00:00
        if (playerRef.current) {
          playerRef.current.seekTo(0);
        }
        
        // 確保播放狀態
        if (!isPlaying) {
          setIsPlaying(true);
        }
        
        return;
      }

      // 12. Esc 鍵 - 取消數字鍵選取（unPickup）
      if (key === 'Escape') {
        event.preventDefault();
        console.log('❌ Esc 鍵 - 取消數字鍵選取');
        
        // 找到當前選中的紅色鍵（isPickUpState = true）
        const currentTabNumberKeys = tabValue === 3 ? numberKeys : tabNumberKeys[tabValue];
        const activeKeys = Object.entries(currentTabNumberKeys)
          .filter(([_, keyState]) => keyState.isPickUpState)
          .map(([key, _]) => parseInt(key))
          .sort((a, b) => a - b);
        
        if (activeKeys.length > 0) {
          console.log(`❌ 取消選取重點數字: ${activeKeys.join(', ')}`);
          
          // 取消所有紅色鍵的選取狀態
          if (tabValue === 3) {
            // YouTube Tab
            setNumberKeys(prev => {
              const newState = { ...prev };
              activeKeys.forEach(key => {
                if (newState[String(key)]) {
                  newState[String(key)] = {
                    ...newState[String(key)],
                    isPickUpState: false
                  };
                }
              });
              return newState;
            });
          } else {
            // 其他 Tab
            setTabNumberKeys(prev => {
              const newState = { ...prev };
              newState[tabValue] = { ...newState[tabValue] };
              activeKeys.forEach(key => {
                if (newState[tabValue][String(key)]) {
                  newState[tabValue][String(key)] = {
                    ...newState[tabValue][String(key)],
                    isPickUpState: false
                  };
                }
              });
              return newState;
            });
          }
          
          // 顯示成功訊息
          setSnackbarMessage('已取消數字鍵選取');
          setSnackbarOpen(true);
        } else {
          console.log('⚠️ 沒有選中的數字鍵可取消');
        }
        return;
      }

      // 13. N 鍵 - 開啟 Note 記錄管理視窗
      if (key === 'n' || key === 'N') {
        event.preventDefault();
        // 記錄日誌已移除
        
        // 檢查是否在直式佈局（使用與主定義一致的判斷邏輯）
        const aspectRatio = windowDimensions.width / windowDimensions.height;
        // 🔧 修復：使用與主定義一致的直式模式判斷邏輯
        const isPortrait = windowDimensions.width <= windowDimensions.height;
        
        if (isPortrait) {
          // 直式佈局：直接開啟 Note 記錄管理視窗
          // 裝置日誌已移除
          openDialog('noteRecords');
        } else {
          // 橫式佈局：根據 M 模式決定行為
          if (mButtonState === 'M') {
            if (user && videoUrl && user?.uid) {
              // 已登入用戶：開啟版本管理
              // 操作日誌已移除
              handleVersionManagement();
            } else {
              // 未登入用戶：開啟 Note 記錄視窗
              // 操作日誌已移除
              openDialog('noteRecords');
            }
          } else {
            // 非 M 模式：開啟 Note 記錄視窗
            // 操作日誌已移除
            openDialog('noteRecords');
          }
        }
        return;
      }

      // 14. P 鍵 - 依順序選擇藍色鍵（略過空白鍵）
      if (key === 'p' || key === 'P') {
        event.preventDefault();
        // 調試日誌已移除
        
        // 找到當前選中的紅色鍵
        const currentTabNumberKeys = tabValue === 3 ? numberKeys : tabNumberKeys[tabValue];
        const activeKeys = Object.entries(currentTabNumberKeys)
          .filter(([_, keyState]) => keyState.isPickUpState)
          .map(([key, _]) => parseInt(key))
          .sort((a, b) => a - b);
        
        // 找到所有藍色鍵（有 timestamp 的鍵）
        const blueKeys = Object.entries(currentTabNumberKeys)
          .filter(([_, keyState]) => keyState.timestamp !== null)
          .map(([key, _]) => parseInt(key))
          .sort((a, b) => a - b);
        
        if (blueKeys.length === 0) {
          console.log('⚠️ 沒有藍色鍵可選擇');
          return;
        }
        
        let nextKey: number;
        
        if (activeKeys.length > 0) {
          // 有當前選中的紅色鍵，找下一個藍色鍵
          const currentActiveKey = activeKeys[0];
          const currentIndex = blueKeys.indexOf(currentActiveKey);
          
          if (currentIndex !== -1) {
            // 當前鍵是藍色鍵，找下一個
            nextKey = blueKeys[(currentIndex + 1) % blueKeys.length];
          } else {
            // 當前鍵不是藍色鍵，找第一個藍色鍵
            nextKey = blueKeys[0];
          }
        } else {
          // 沒有選中的紅色鍵，從第一個藍色鍵開始
          nextKey = blueKeys[0];
        }
        
        // 調試日誌已移除
        
        // 檢查是否需要自動翻頁
        const currentActiveKey = activeKeys.length > 0 ? activeKeys[0] : 0;
        const currentPage = Math.floor((currentActiveKey - 1) / 6) + 1;
        const nextPage = Math.floor((nextKey - 1) / 6) + 1;
        
        if (nextPage !== currentPage) {
          // 調試日誌已移除
          
          // 計算需要翻頁的次數和方向
          const pageDiff = nextPage - currentPage;
          if (pageDiff > 0) {
            // 向右翻頁（等同點擊 S 鍵）
            for (let i = 0; i < pageDiff; i++) {
              handleSwitchClick();
            }
          } else {
            // 向左翻頁（等同點擊 B 鍵）
            for (let i = 0; i < Math.abs(pageDiff); i++) {
              handleBButtonClick();
            }
          }
        }
        
        handleNumberKeyUp(nextKey);
        return;
      }
    };

    // 添加事件監聽器
    document.addEventListener('keydown', handleKeyDown, { passive: false }); // keydown 需要 passive: false 以便 preventDefault

    // 清理函數
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleNumberKeyUp]); // 添加 handleNumberKeyUp 作為依賴


  // 在其他函數定義附近添加
  const handleReplay = () => {
    if (playerRef.current) {
      playerRef.current.seekTo(0);
    }
  };

  const handleSeek = (seconds: number) => {
    // 檢查 Google Drive 模式
    if (videoMode === 'Google' && googleDriveRef.current) {
      const currentTime = googleDriveRef.current.getCurrentTime();
      const targetTime = currentTime + seconds;
      console.log('[Checkpoint] handleSeek (Google)', {
        offset: seconds,
        targetTime,
        currentVolume: volumeRef.current,
        isMuted: mutedRef.current
      });
      googleDriveRef.current.seekTo(targetTime);
      // 調試日誌已移除
      return;
    }
    
    // 檢查一般播放器模式
    if (playerRef.current) {
      const currentTime = playerRef.current.getCurrentTime();
      console.log('[Checkpoint] handleSeek (Generic)', {
        offset: seconds,
        currentTime,
        targetTime: currentTime + seconds,
        currentVolume: volumeRef.current,
        isMuted: mutedRef.current
      });
      playerRef.current.seekTo(currentTime + seconds);
    }
  };

  const handleFullScreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      const videoContainer = document.getElementById('video-container');
      if (videoContainer) {
        videoContainer.requestFullscreen();
      }
    }
  };

  const handleMButtonClick = () => {
    // 操作日誌已移除
    // 調試日誌已移除
    // 調試日誌已移除
    // 調試日誌已移除
    
    // 播放按鈕點擊音效
    playButtonSound('click');
    
    // 🔧 修改：在 M 模式下點擊 AI 按鈕時的行為
    if (mButtonState === 'M') {
      // 🔥 如果字幕狀態是 '_READY_'，表示有字幕但尚未載入，先載入字幕
      if (srtContent === '_READY_' && hasSRTCache) {
        // AI 按鈕日誌已移除;
        const videoId = extractYouTubeId(videoUrl);
        if (videoId) {
          // ✅ 修復：直接載入字幕內容，而不是調用檢測函數
          (async () => {
            try {
              const { getSRTFromCache } = await import('../services/srtCacheService');
              const language = cachedSRTLanguage || 'zh-TW';
              // 調試日誌已移除
              
              const cachedContent = await getSRTFromCache(videoId, language);
              if (cachedContent) {
                // 調試日誌已移除
                setSrtContent(cachedContent);
                setCurrentVideoId(videoId); // ✅ 設置 currentVideoId，避免後續重複載入
                // 立即打開對話框
                openDialog('srt');
              } else {
                console.warn('⚠️ 字幕載入失敗，緩存中未找到內容');
                setSnackbarMessage('字幕載入失敗');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
              }
            } catch (error) {
              console.error('❌ 載入字幕時發生錯誤:', error);
              setSnackbarMessage('字幕載入失敗');
              setSnackbarSeverity('error');
              setSnackbarOpen(true);
            }
          })();
        }
        return;
      }
      
      // 如果沒有字幕緩存，打開語言選擇對話框
      if (!hasSRTCache) {
        // AI 按鈕日誌已移除;
        openDialog('language');
        return;
      }
      
      // 🔧 新增：有字幕緩存時，使用雙擊檢測
      aiButtonClickCount.current += 1;
      
      if (aiButtonClickCount.current === 1) {
        // 第一次點擊，設置計時器
        aiButtonClickTimer.current = setTimeout(async () => {
          // 單擊：顯示完整字幕 SRT 視窗
          // AI 按鈕日誌已移除;
          
          // 🔥 修復：如果字幕是 '_READY_' 或空的，先載入字幕
          if (!srtContent || srtContent === '_READY_') {
            // AI 字幕日誌已移除;
            const videoId = extractYouTubeId(videoUrl);
            if (videoId) {
              await checkSRTCacheAvailability(videoId);
            }
            // 等待一下讓字幕載入
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          // 顯示字幕對話框
          if (srtContent && srtContent !== '_READY_') {
            // AI 字幕日誌已移除;
            openDialog('srt');
          } else {
            console.warn('⚠️ 字幕載入失敗，無法顯示對話框');
          }
          
          aiButtonClickCount.current = 0;
        }, 300); // 300ms 內沒有第二次點擊視為單擊
      } else if (aiButtonClickCount.current === 2) {
        // 雙擊：切換自動填充模式
        // AI 按鈕日誌已移除;
        
        // 清除單擊計時器
        if (aiButtonClickTimer.current) {
          clearTimeout(aiButtonClickTimer.current);
          aiButtonClickTimer.current = null;
        }
        
        // 切換自動填充模式
        setIsAutoFillMode(prev => {
          const newMode = !prev;
          // 調試日誌已移除
          return newMode;
        });
        
        aiButtonClickCount.current = 0;
      }
      
      return;
    }
    
    setMButtonState(prev => {
      const nextState = prev === 'M' ? 'AB' : 
                       prev === 'AB' ? 'MS' : 
                       prev === 'MS' ? 'PB' : 'M';
      
      // Reset AB points when exiting AB mode
      if (prev === 'AB') {
        // Reset all AB points to their original blue state
        setNumberKeys(prevKeys => {
          const updatedKeys: Record<string, NumberKeyState> = { ...prevKeys };
          Object.keys(updatedKeys).forEach(key => {
            if (updatedKeys[String(key)].isABPoint) {
              updatedKeys[String(key)] = {
                ...updatedKeys[String(key)],
                isABPoint: false
              };
            }
          });
          return updatedKeys;
        });
        
        // Clear AB points state
        setABPoints({
          pointA: { key: null, timestamp: null },
          pointB: { key: null, timestamp: null }
        });
      }

      // 當切換到 PB 模式時，重置所有按鈕的 isPickUpState
      if (nextState === 'PB') {
        // 操作日誌已移除
        
        setNumberKeys(prevKeys => {
          const updatedKeys: Record<string, NumberKeyState> = { ...prevKeys };
          Object.keys(updatedKeys).forEach(key => {
            updatedKeys[String(key)] = {
              ...updatedKeys[String(key)],
              isPickUpState: false
            };
          });
          return updatedKeys;
        });
        
        // ✅ 修改：切換到 PB 模式後，立即彈出播放時長選擇 Dialog
        // 操作日誌已移除
        
        // 檢查是否有可播放的重點
        // 調試日誌已移除
        // 調試日誌已移除
        // 調試日誌已移除
        // 調試日誌已移除
        
        const allKeys = Object.entries(numberKeys)
          .filter(([_, keyState]) => keyState.timestamp !== null && !keyState.isABPoint)
          .map(([key, keyState]) => ({
            keyNo: Number(key),
            timestamp: keyState.timestamp!,
            isABPoint: keyState.isABPoint,
            isPickUpState: keyState.isPickUpState
          }))
          .sort((a, b) => a.keyNo - b.keyNo);

        // 調試日誌已移除
        // 調試日誌已移除

        if (allKeys.length > 0) {
          // 調試日誌已移除
          // 調試日誌已移除
          
          // 使用 setTimeout 確保狀態更新在正確的時機執行
          setTimeout(() => {
            // 調試日誌已移除
            openDialog('playbackDuration');
            // 調試日誌已移除
          }, 100);
        } else {
          console.log('⚠️ 沒有找到重點，不彈出 Dialog');
        }
      }
      
      return nextState;
    });
  };

  // 🔧 validateTimecode 已从 '../utils/timestampUtils' 导入

  // 🔧 新增：安全的時間碼獲取函數
  const getSafeTimecode = (keyNumber: number, tabIndex: number): string => {
    const timecode = tabIndex === 3 ? 
      (numberKeys[keyNumber]?.timecode) : 
      (tabNumberKeys[tabIndex]?.[keyNumber]?.timecode);
    
    if (timecode && validateTimecode(timecode)) {
      return timecode;
    }
    
    // 如果時間碼無效，使用當前播放時間
    const currentTime = playedSeconds || 0;
    const safeTimecode = formatTimecode(currentTime);
    // 調試日誌已移除
    return safeTimecode;
  };

  // 🔧 新增：記錄時間碼異常的函數
  const logTimecodeIssue = (keyNumber: number, tabIndex: number, timecode: string) => {
    console.error('🚨 時間碼異常檢測:', {
      keyNumber,
      tabIndex,
      timecode,
      numberKeys: numberKeys[keyNumber],
      tabNumberKeys: tabNumberKeys[tabIndex]?.[keyNumber],
      playedSeconds,
      currentFrame,
      timestamp: new Date().toISOString()
    });
  };

  // 🔧 新增：修復異常時間碼的函數
  const repairAbnormalTimecodes = async () => {
    if (!user) {
      console.log('⚠️ 用戶未登入，無法執行時間碼修復');
      return;
    }

    try {
      // 調試日誌已移除
      
      // 檢查本地狀態中的異常時間碼
      const currentTabNumberKeys = tabValue === 3 ? numberKeys : tabNumberKeys[tabValue];
      
      for (const [keyNumber, keyState] of Object.entries(currentTabNumberKeys)) {
        if (keyState?.timecode && !validateTimecode(keyState.timecode)) {
          // 調試日誌已移除
          
          // 使用當前播放時間修復
          const safeTimecode = formatTimecode(playedSeconds || 0);
          
          if (validateTimecode(safeTimecode)) {
            const updatedKeyState = {
              ...keyState,
              timecode: safeTimecode
            };
            
            // 更新本地狀態
            if (tabValue === 3) {
              setNumberKeys(prev => ({
                ...prev,
                [keyNumber]: updatedKeyState
              }));
            } else {
              setTabNumberKeys(prev => ({
                ...prev,
                [tabValue]: {
                  ...prev[tabValue],
                  [keyNumber]: updatedKeyState
                }
              }));
            }
            
            // 調試日誌已移除
          }
        }
      }
      
      // 檢查 Firestore 中的異常時間碼 - 已遷移到 sharedVbids
      // 調試日誌已移除
      const notesSnapshot = { forEach: () => {} }; // 空的操作
      let repairedCount = 0;
      
      // 跳過 userNotes 處理
      // 成功日誌已移除
      
    } catch (error) {
      console.error('❌ 時間碼修復過程中發生錯誤:', error);
    }
  };

  // 🔧 修復：保存 Note 到 sharedVbids 集合（與 NotesPage 同步）
  const saveNoteToFirestore = async (keyNumber: number, noteContent: string, tabIndex: number) => {
    // 調試日誌已移除
    
    if (!user) {
      console.log('⚠️ 用戶未登入，無法保存到 Firestore');
      return;
    }

    try {
      // 獲取正確的 videoId - 優先從 videoUrl 提取，如果沒有則使用 URL 參數
      let correctVideoId = id || 'unknown';
      
      // 🔧 修復：優先使用 fileRelativePath，與 vbidIO.ts 保持一致
      // 調試日誌已移除
      
      if (fileRelativePath && fileRelativePath.trim() !== '') {
        // 🔧 修復：生成統一格式的 videoId（與新架構一致）
        const cleanFilename = fileRelativePath.replace(/[^a-zA-Z0-9]/g, '_');
        correctVideoId = `file_${cleanFilename}`;
        // 調試日誌已移除
      } else if (videoUrl) {
        // 備用方案：從 videoUrl 提取 videoId
        // 從 YouTube URL 提取 videoId
        const youtubeMatch = videoUrl.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
        if (youtubeMatch) {
          correctVideoId = youtubeMatch[1];
          // 調試日誌已移除
        } else if (videoUrl.includes('drive.google.com')) {
          // Google Drive 文件 ID
          const driveMatch = videoUrl.match(/\/file\/d\/([^/]+)/);
          if (driveMatch) {
            correctVideoId = driveMatch[1];
            // 調試日誌已移除
          }
        } else if (videoUrl.includes('storage.googleapis.com/checkplay-videos/')) {
          // 🔧 處理 GCS (Google Cloud Storage) URL
          const urlParts = videoUrl.split('/');
          const fileName = urlParts[urlParts.length - 1];
          // 移除查詢參數
          const cleanFileName = fileName.split('?')[0];
          // 🔧 修復：生成統一格式的 videoId
          correctVideoId = `file_${cleanFileName.replace(/[^a-zA-Z0-9]/g, '_')}`;
          // 調試日誌已移除
        } else if (videoUrl.includes('storage.googleapis.com') || videoUrl.includes('checkplay-videos')) {
          // 🔧 備用：處理其他 GCS URL 格式
          correctVideoId = `file_${videoUrl.replace(/[^a-zA-Z0-9]/g, '_')}`;
          // 調試日誌已移除
        } else {
          // 調試日誌已移除
        }
      } else {
        // 調試日誌已移除
      }

      // 🔧 修復：優先使用 firestoreId 直接查詢文檔（新格式）
      // 新格式：[userId]_file_[filename]_v1
      // 如果沒有 firestoreId，則使用舊的查詢方式（相容舊資料）
      let docRef;
      
      if (currentFirestoreId) {
        // 新格式：直接使用文檔 ID 查詢
        // 調試日誌已移除
        docRef = doc(db, 'sharedVbids', currentFirestoreId);
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) {
          console.error('❌ 找不到對應的 sharedVbids 文檔 (firestoreId):', currentFirestoreId);
          return;
        }
      } else {
        // 舊格式：使用 where 查詢
        // 調試日誌已移除
        const sharedVbidsQuery = query(
          collection(db, 'sharedVbids'),
          where('userId', '==', user.uid),
          where('videoId', '==', correctVideoId)
        );

        const querySnapshot = await getDocs(sharedVbidsQuery);
        
        if (querySnapshot.empty) {
          console.error('❌ 找不到對應的 sharedVbids 文檔');
          return;
        }

        docRef = querySnapshot.docs[0].ref;
      }
      
      // 獲取文檔數據
      const docSnap = await getDoc(docRef);
      const docData = docSnap.data();
      
      if (!docData) {
        console.error('❌ 文檔數據為空');
        return;
      }
      
      const vbids = docData.vbids || [];

      // 🔧 關鍵修復：防止空內容覆蓋原有的 Note
      // 找到並更新對應的 vbid
      const updatedVbids = vbids.map((vbid: any) => {
        if (vbid.keyNo === keyNumber) {
          const trimmedNote = noteContent.trim();
          
          // 🔧 重要：如果新內容是空的，保留原有的 note，不要覆蓋
          if (trimmedNote === '' && vbid.note && vbid.note.trim() !== '') {
            console.log(`⚠️ [Note 保護] 跳過保存空內容，保留原有 Note: 鍵 ${keyNumber}, 原內容: "${vbid.note}"`);
            return vbid; // 保持不變
          }
          
          // 只有在有新內容時才更新
          return { ...vbid, note: trimmedNote };
        }
        return vbid;
      });

      // 更新文檔
      await updateDoc(docRef, {
        vbids: updatedVbids
      });

      // 成功日誌已移除
      
    } catch (error) {
      console.error('❌ 保存 Note 到 Firestore 失敗:', error);
    }
  };

  // 🔧 修復：自動保存 Note 的 useEffect（使用防抖避免頻繁觸發）
  useEffect(() => {
    // 只在用戶停止輸入後才觸發自動保存，避免頻繁保存
    if (user && id && currentSubtitleKey !== null && subtitleContent.trim() !== '') {
      // 🔧 新增：驗證時間碼有效性
      const timecode = tabValue === 3 ? 
        (numberKeys[currentSubtitleKey]?.timecode) : 
        (tabNumberKeys[tabValue]?.[currentSubtitleKey]?.timecode);
      
      if (timecode && !validateTimecode(timecode)) {
        console.warn('⚠️ 時間碼無效，跳過自動保存:', { 
          currentSubtitleKey, 
          timecode, 
          tabValue 
        });
        logTimecodeIssue(currentSubtitleKey, tabValue, timecode);
        return;
      }
      
      // 🔧 修復：使用更長的延遲，減少觸發頻率
      const autoSaveTimer = setTimeout(() => {
        // 靜默保存，不輸出日誌，避免控制台干擾
        saveNoteToFirestore(currentSubtitleKey, subtitleContent, tabValue);
      }, 15000); // 增加到 15 秒，大幅減少頻率

      return () => clearTimeout(autoSaveTimer);
    }
  }, [subtitleContent, currentSubtitleKey, user, id, tabValue, numberKeys, tabNumberKeys]); // 添加必要的依賴

  // 🔧 新增：組件載入時執行時間碼修復
  useEffect(() => {
    if (user && id) {
      // 延遲 3 秒後執行修復，確保組件完全載入
      const repairTimer = setTimeout(() => {
        repairAbnormalTimecodes();
      }, 3000);
      
      return () => clearTimeout(repairTimer);
    }
  }, [user, id]);

  // 🔧 新增：監聽數字鍵狀態變化，自動同步 pickup 狀態到 currentSubtitleKey
  useEffect(() => {
    // 獲取當前 Tab 的數字鍵狀態
    const currentTabNumberKeys = tabValue === 3 ? numberKeys : tabNumberKeys[tabValue];
    
    // 找到第一個有 pickup 狀態的數字鍵
    const firstPickUpKey = Object.entries(currentTabNumberKeys).find(([_, keyState]) => keyState.isPickUpState);
    
    if (firstPickUpKey) {
      const pickUpKey = Number(firstPickUpKey[0]);
      const keyState = firstPickUpKey[1];
      
      // 如果 currentSubtitleKey 與 pickup 狀態不一致，則同步更新
      if (currentSubtitleKey !== pickUpKey) {
        // 狀態變化（日誌已移除）
        setCurrentSubtitleKey(pickUpKey);
        setSubtitleContent(keyState.note || '');
      }
    }
  }, [tabValue, numberKeys, tabNumberKeys, currentSubtitleKey]);

  // 🔧 移除重複的跳轉邏輯 - handleNumberKeyUp 已經處理了跳轉
  // useEffect(() => {
  //   // 跳轉邏輯已移至 handleNumberKeyUp 函數中，避免重複執行
  // }, [currentSubtitleKey, tabValue, numberKeys, tabNumberKeys]);

  // ═══════════════════════════════════════════════════════════════════════
  // 💾 資料載入與保存模組
  // ═══════════════════════════════════════════════════════════════════════
  // 負責：Firestore 資料讀寫、觀點管理、版本控制、影片資料載入
  // State: currentFirestoreId, currentDocumentId, videoData, videoZones, videoTags
  // Handlers: loadVideoData, loadNotesFromFirestore, saveNoteToFirestore, saveCurrentState
  
  // 載入影片資料（包括重點和筆記）
  const loadVideoData = async (videoUrl: string) => {
    // 🔧 修復：只有在不是用戶主動播放時才設置自動載入標記
    if (!(window as any).__checkplay_user_initiated_play) {
      (window as any).__checkplay_user_initiated_play = false;
    }
    if (!user || !videoUrl) {
      console.log('⚠️ 用戶未登入或沒有影片 URL，無法載入資料');
      return;
    }

    // 🚨 關鍵修復：載入新影片前，先清空舊影片的數據，防止數據污染
    console.log(`🔄 開始載入新影片數據: ${videoUrl}`);
    console.log(`🧹 清空舊的 numberKeys，防止數據污染...`);
    setNumberKeys({});
    setTabNumberKeys(prev => ({ ...prev, [tabValue]: {} }));
    setCurrentVbids([]);

    try {
      // 調試日誌已移除
      
      // 🔧 修復：檢查是否為雲端檔案
      const isCloudFile = videoUrl.includes('storage.googleapis.com') || 
                         videoUrl.includes('checkplay-videos');
      
      if (isCloudFile) {
        // 调试日志已移除
        
        // 優先使用 currentFirestoreId，如果沒有則無法載入
        if (!currentFirestoreId) {
          console.warn('⚠️ 雲端檔案缺少 firestoreId，無法載入重點數');
          return;
        }
        
        // 調試日誌已移除
        
        // 直接查詢 sharedVbids 文檔
        const docRef = doc(db, 'sharedVbids', currentFirestoreId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const vbids = data.vbids || [];
          const numberKeysFromVbids = vbidsToNumberKeys(vbids);
          
          // 調試日誌已移除
          
          // 更新狀態
          setCurrentVbids(vbids);
          setNumberKeys(numberKeysFromVbids);
          setTabNumberKeys(prev => ({ ...prev, [tabValue]: numberKeysFromVbids }));
        } else {
          // 调试日志已移除
        }
        
        return; // 雲端檔案處理完成
      }
      
      // YouTube 影片處理
      const extractYouTubeId = (url: string): string | null => {
        const match = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/|live\/)([a-zA-Z0-9_-]{11})/);
        return match ? match[1] : null;
      };
      
      const videoId = extractYouTubeId(videoUrl);
      if (!videoId) {
        console.log('❌ 無法提取 YouTube ID');
        return;
      }
      
      // 調試日誌已移除

      // 🚀 新增：自動更新 YouTube 影片資訊
      const updateYouTubeInfo = async (videoId: string) => {
        try {
          // 調試日誌已移除
          
          // 獲取最新的 YouTube 影片資訊
          const youtubeInfo = await youtubeApi.getVideoDetails(videoId);
          // 調試日誌已移除
          
          // 🔧 修復：設置影片標題到狀態（添加競態條件保護）
          if (youtubeInfo.title && videoUrl === videoUrl) { // 這裡 videoUrl 是函數參數
            setVideoTitle(youtubeInfo.title);
            // 調試日誌已移除
          }
          
          // 查找對應的 sharedVbids 文檔並更新
          const sharedVbidsQuery = query(
            collection(db, 'sharedVbids'),
            where('videoId', '==', videoId),
            where('userId', '==', user.uid)
          );
          
          const sharedVbidsSnapshot = await getDocs(sharedVbidsQuery);
          
          if (!sharedVbidsSnapshot.empty) {
            const docRef = sharedVbidsSnapshot.docs[0].ref;
            await updateDoc(docRef, {
              title: youtubeInfo.title,
              description: youtubeInfo.description,
              channelTitle: youtubeInfo.channelTitle,
              publishedAt: youtubeInfo.publishedAt,
              thumbnails: youtubeInfo.thumbnails,
              viewCount: youtubeInfo.viewCount,
              duration: youtubeInfo.duration,
              tags: youtubeInfo.tags || [],
              lastTitleUpdate: serverTimestamp()
            });
            
            // 成功日誌已移除
            
            // 更新 UI 狀態（如果當前正在播放這個影片）
            if (videoUrl === videoUrl) {
              // 可以在這裡觸發 UI 更新，例如顯示通知
              // 調試日誌已移除
            }
          } else {
            console.log('⚠️ 未找到對應的 sharedVbids 文檔，無法更新');
          }
          
        } catch (error) {
          console.warn('⚠️ 更新 YouTube 影片資訊失敗:', error);
          // 不阻止主要流程繼續執行
        }
      };

      // 在後台更新 YouTube 資訊（不阻塞主要流程）
      updateYouTubeInfo(videoId);
      
      // 🚀 優化：跳過 userNotes 查詢，因為資料已遷移到 sharedVbids
      // 調試日誌已移除
      
      // 查詢 sharedVbids 集合
      const sharedVbidsQuery = query(
        collection(db, 'sharedVbids'),
        where('videoId', '==', videoId),
        where('userId', '==', user.uid)
      );
      
      const sharedVbidsSnapshot = await getDocs(sharedVbidsQuery);
      // 調試日誌已移除
      
      // 🔧 修復：在函數開始時定義 updatedNumberKeys，確保作用域正確
      const updatedNumberKeys = { ...numberKeys };
      
      // 🚀 優化：跳過 userNotes 處理，因為資料已遷移到 sharedVbids
      // 成功日誌已移除
      
      // 🆕 載入觀點數據
      if (user?.uid && videoId) {
        try {
          // 狀態追蹤日誌已移除
          const { ViewpointService } = await import('../services/viewpointService');
          const viewpoints = await ViewpointService.getViewpoints(user.uid, videoId);
          // 調試日誌已移除
          
          if (viewpoints.length > 0) {
            // 🔧 修復：根據 currentFirestoreId 選擇正確的觀點，而不是使用第一個觀點
            let targetViewpoint = viewpoints[0]; // 預設使用第一個
            
            // 如果有 currentFirestoreId，嘗試找到對應的觀點
            if (currentFirestoreId) {
              const matchedViewpoint = viewpoints.find(vp => vp.id === currentFirestoreId);
              if (matchedViewpoint) {
                targetViewpoint = matchedViewpoint;
                // 調試日誌已移除
              } else {
                console.log('⚠️ 未找到匹配的觀點，使用第一個觀點:', currentFirestoreId);
              }
            }
            
            setCurrentViewpointDocId(targetViewpoint.id || '');
            // 調試日誌已移除
            
            // 載入當前觀點的數據
            if (targetViewpoint.numberKeys) {
              setNumberKeys(targetViewpoint.numberKeys);
              // 調試日誌已移除
            }
            if (targetViewpoint.zones) {
              setVideoZones(targetViewpoint.zones);
              // 調試日誌已移除
            }
            if (targetViewpoint.tags) {
              setVideoTags(targetViewpoint.tags);
              // 調試日誌已移除
            }
            if (targetViewpoint.groupIds) {
              setVideoGroupIds(targetViewpoint.groupIds);
              // 調試日誌已移除
            }
          } else {
            // 调试日志已移除
          }
        } catch (error) {
          console.error('❌ 載入觀點數據失敗:', error);
        }
      }

      // 處理 sharedVbids 資料
      if (!sharedVbidsSnapshot.empty) {
        // 成功日誌已移除
        const sharedVbidsData = sharedVbidsSnapshot.docs[0].data();
        
        if (sharedVbidsData.vbids && Array.isArray(sharedVbidsData.vbids)) {
          // 調試日誌已移除
          setVbids(sharedVbidsData.vbids);
          setCurrentVbids(sharedVbidsData.vbids);
          
          // 🔧 修復：將 sharedVbids 轉換為 numberKeys 狀態，合併到 userNotes 的數據中
          const restoredNumberKeys = vbidsToNumberKeys(sharedVbidsData.vbids);
          // 調試日誌已移除
          
          // 🔧 關鍵修復：合併 sharedVbids 數據到 userNotes 的 updatedNumberKeys 中
          Object.entries(restoredNumberKeys).forEach(([keyNo, keyState]) => {
            if (!updatedNumberKeys[keyNo]) {
              updatedNumberKeys[keyNo] = keyState;
              // 調試日誌已移除
            } else {
              // 🔧 修復：合併 sharedVbids 的 timestamp 到 userNotes 中，但保留 userNotes 的 note 內容
              updatedNumberKeys[keyNo] = {
                ...updatedNumberKeys[keyNo],
                timestamp: keyState.timestamp, // 使用 sharedVbids 的 timestamp
                timecode: keyState.timecode,   // 使用 sharedVbids 的 timecode
                // 保留 userNotes 的 note 內容，但更新 isActive 狀態
                isActive: (updatedNumberKeys[keyNo].note && updatedNumberKeys[keyNo].note.trim() !== '') || 
                         (keyState.timestamp !== null && keyState.timestamp !== undefined)
              };
              // 調試日誌已移除
            }
          });
          
          // 成功日誌已移除
        }
      }
      
      // 🔧 一次性設置最終合併的 numberKeys 狀態
      // 調試日誌已移除
      
      // 🔧 新增：狀態更新前的驗證
      const activeKeys = Object.values(updatedNumberKeys).filter(key => key.isActive);
      const keysWithValidTimestamp = activeKeys.filter(key => key.timestamp && key.timestamp > 0);
      const keysWithValidTimecode = activeKeys.filter(key => key.timecode && key.timecode !== '00:00:00 00');
      
      // 調試日誌已移除
      
      if (activeKeys.length > 0 && keysWithValidTimestamp.length === 0) {
        console.warn('⚠️ 警告：所有活躍鍵都缺少有效的 timestamp，這可能導致時間碼顯示問題');
      }
      
      setNumberKeys(updatedNumberKeys);
      
      // 🔧 新增：調試特定影片的最終狀態
      if (videoId === 'aWZMTjnzG3U') {
        // 調試日誌已移除
        // 調試日誌已移除
        Object.entries(updatedNumberKeys).forEach(([key, value]) => {
          if (value.isActive || value.note) {
            // 調試日誌已移除
          }
        });
      }
      
      // 成功日誌已移除
      
      // 🔧 修復：重新應用 pickup 狀態（防止被 Firestore 數據覆蓋）
      if ((window as any).__checkplay_pickup_state) {
        const pickupState = (window as any).__checkplay_pickup_state;
        const targetKeyNumber = pickupState.targetKeyNumber;
        
        // 調試日誌已移除
        
        // 重新設置 pickup 狀態
        setNumberKeys(prev => {
          const newState = { ...prev };
          Object.keys(newState).forEach(k => {
            newState[k] = { ...newState[k], isPickUpState: false };
          });
          if (newState[targetKeyNumber]) {
            newState[targetKeyNumber] = { ...newState[targetKeyNumber], isPickUpState: true };
          }
          return newState;
        });
        
        // 同時更新 tabNumberKeys
        setTabNumberKeys(prev => {
          const newTabNumberKeys = { ...prev };
          if (!newTabNumberKeys[3]) {
            newTabNumberKeys[3] = {};
          }
          Object.keys(newTabNumberKeys[3]).forEach(k => {
            newTabNumberKeys[3][k] = { ...newTabNumberKeys[3][k], isPickUpState: false };
          });
          if (newTabNumberKeys[3][targetKeyNumber]) {
            newTabNumberKeys[3][targetKeyNumber] = { ...newTabNumberKeys[3][targetKeyNumber], isPickUpState: true };
          }
          return newTabNumberKeys;
        });
        
        // 清除標記
        delete (window as any).__checkplay_pickup_state;
      }
      
      // 🔧 修復：載入 YouTube Tab 的 Note 數據
      if (user && id) {
        // 調試日誌已移除
        const youtubeNotesQuery = query(
          collection(db, 'userNotes'),
          where('userId', '==', user.uid),
          where('videoId', '==', id),
          where('tabIndex', '==', 3)
        );
        const youtubeNotesSnapshot = await getDocs(youtubeNotesQuery);
        
        if (!youtubeNotesSnapshot.empty) {
          // 調試日誌已移除
          
          // 合併 YouTube Tab 的 Note 數據到現有狀態
          setNumberKeys(prevNumberKeys => {
            const mergedNumberKeys = { ...prevNumberKeys };
            
            youtubeNotesSnapshot.forEach((doc) => {
              const data = doc.data();
              const keyNumber = data.keyNumber;
              
              if (mergedNumberKeys[keyNumber]) {
                // 合併 Note 內容到現有狀態
                mergedNumberKeys[keyNumber] = {
                  ...mergedNumberKeys[keyNumber],
                  note: data.note || '',
                  // 🔧 修復：如果有 Note 內容或有有效時間碼，設置為活躍狀態
                  isActive: mergedNumberKeys[keyNumber].isActive || 
                           (data.note && data.note.trim() !== '') ||
                           (data.timecode && data.timecode.trim() !== '')
                };
                // 調試日誌已移除
              }
            });
            
            return mergedNumberKeys;
          });
        }
      }
      
      if (sharedVbidsSnapshot.empty) {
        // 调试日志已移除
      }
      
      // 🆕 載入觀點數據
      if (user?.uid && videoId) {
        try {
          // 狀態追蹤日誌已移除
          const { ViewpointService } = await import('../services/viewpointService');
          const viewpoints = await ViewpointService.getViewpoints(user.uid, videoId);
          // 調試日誌已移除
          
          if (viewpoints.length > 0) {
            // 🔧 修復：根據 currentFirestoreId 選擇正確的觀點，而不是使用第一個觀點
            let targetViewpoint = viewpoints[0]; // 預設使用第一個
            
            // 如果有 currentFirestoreId，嘗試找到對應的觀點
            if (currentFirestoreId) {
              const matchedViewpoint = viewpoints.find(vp => vp.id === currentFirestoreId);
              if (matchedViewpoint) {
                targetViewpoint = matchedViewpoint;
                // 調試日誌已移除
              } else {
                console.log('⚠️ 未找到匹配的觀點，使用第一個觀點:', currentFirestoreId);
              }
            }
            
            setCurrentViewpointDocId(targetViewpoint.id || '');
            // 調試日誌已移除
            
            // 載入當前觀點的數據
            if (targetViewpoint.numberKeys) {
              setNumberKeys(targetViewpoint.numberKeys);
              // 調試日誌已移除
            }
            if (targetViewpoint.zones) {
              setVideoZones(targetViewpoint.zones);
              // 調試日誌已移除
            }
            if (targetViewpoint.tags) {
              setVideoTags(targetViewpoint.tags);
              // 調試日誌已移除
            }
            if (targetViewpoint.groupIds) {
              setVideoGroupIds(targetViewpoint.groupIds);
              // 調試日誌已移除
            }
          } else {
            // 调试日志已移除
          }
        } catch (error) {
          console.error('❌ 載入觀點數據失敗:', error);
        }
      }
      
    } catch (error) {
      console.error('💥 載入影片資料失敗:', error);
    }
  };

  // 從 Firestore 載入 Note 和數字鍵狀態
  const loadNotesFromFirestore = async () => {
    if (!user || !id) {
      console.log('⚠️ 用戶未登入或沒有影片 ID，無法載入 Note');
      return;
    }

    try {
      // 狀態變化（日誌已移除）
      
      // 載入 YouTube Tab 的 Note 和狀態 - 已遷移到 sharedVbids
      // 調試日誌已移除
      const youtubeNotesSnapshot = { forEach: () => {} }; // 空的操作
      
      // 調試日誌已移除
      
      const updatedNumberKeys = { ...numberKeys };
      let loadedCount = 0;
      
      // 跳過 YouTube Tab 的 userNotes 處理
      // 調試日誌已移除
      
      if (loadedCount > 0) {
        // 🔧 修復：合併現有的 numberKeys 和更新的 Note 內容，而不是完全替換
        setNumberKeys(prevNumberKeys => {
          const mergedNumberKeys = { ...prevNumberKeys };
          
          // 只更新有 Note 或時間碼的數字鍵，保留其他所有狀態
          Object.keys(updatedNumberKeys).forEach(key => {
            const keyNum = Number(key);
            if (updatedNumberKeys[keyNum] && (updatedNumberKeys[keyNum].note || updatedNumberKeys[keyNum].timecode.trim() !== '')) {
              if (mergedNumberKeys[keyNum]) {
                // 如果數字鍵已存在，只更新 Note 相關欄位
                mergedNumberKeys[keyNum] = {
                  ...mergedNumberKeys[keyNum],
                  note: updatedNumberKeys[keyNum].note,
                  timecode: updatedNumberKeys[keyNum].timecode,
                  timestamp: updatedNumberKeys[keyNum].timestamp, // 🔧 修復：同時更新 timestamp
                  isActive: updatedNumberKeys[keyNum].isActive  // 🔧 修復：確保 isActive 狀態正確傳遞
                };
              } else {
                // 如果數字鍵不存在，創建完整的狀態
                mergedNumberKeys[keyNum] = updatedNumberKeys[keyNum];
              }
            }
          });
          
          // 調試日誌已移除
          return mergedNumberKeys;
        });
        
        // 🔧 修復：自動選擇第一個有 Note 的數字鍵，並更新字幕白板
        const firstKeyWithNote = Object.keys(updatedNumberKeys).find(key => {
          const keyState = updatedNumberKeys[Number(key)];
          return keyState && keyState.note && keyState.note.trim() !== '';
        });
        
        if (firstKeyWithNote) {
          const selectedKey = Number(firstKeyWithNote);
          const keyState = updatedNumberKeys[selectedKey];
          setCurrentSubtitleKey(selectedKey);
          setSubtitleContent(keyState.note || '');
          // 調試日誌已移除
        }
        
        // 🔧 新增：自動設置第一個有 pickup 狀態的數字鍵為 currentSubtitleKey
        const firstPickUpKey = Object.keys(updatedNumberKeys).find(key => {
          const keyState = updatedNumberKeys[Number(key)];
          return keyState && keyState.isPickUpState;
        });
        
        if (firstPickUpKey) {
          const pickUpKey = Number(firstPickUpKey);
          const keyState = updatedNumberKeys[pickUpKey];
          setCurrentSubtitleKey(pickUpKey);
          setSubtitleContent(keyState.note || '');
          // 設置狀態（日誌已移除）
        }
      }

      // 載入其他 Tab 的 Note 和狀態 - 已遷移到 sharedVbids
      // 調試日誌已移除
      const otherTabsNotesSnapshot = { forEach: () => {} }; // 空的操作
      
      const updatedTabNumberKeys = { ...tabNumberKeys };
      let otherTabsLoadedCount = 0;
      
      // 跳過其他 Tab 的 userNotes 處理
      // 調試日誌已移除
      
      if (otherTabsLoadedCount > 0) {
        setTabNumberKeys(updatedTabNumberKeys);
        // 調試日誌已移除
        
        // 🔧 新增：為當前 Tab 自動設置第一個有 pickup 狀態的數字鍵為 currentSubtitleKey
        if (updatedTabNumberKeys[tabValue]) {
          const currentTabKeys = updatedTabNumberKeys[tabValue];
          const firstPickUpKey = Object.keys(currentTabKeys).find(key => {
            const keyState = currentTabKeys[Number(key)];
            return keyState && keyState.isPickUpState;
          });
          
          if (firstPickUpKey) {
            const pickUpKey = Number(firstPickUpKey);
            const keyState = currentTabKeys[pickUpKey];
            setCurrentSubtitleKey(pickUpKey);
            setSubtitleContent(keyState.note || '');
            // 設置狀態（日誌已移除）
          }
        }
      }

      const totalLoaded = loadedCount + otherTabsLoadedCount;
      if (totalLoaded > 0) {
        // 調試日誌已移除
      } else {
        // 调试日志已移除
      }
      
    } catch (error) {
      console.error('❌ 從 Firestore 載入數字鍵狀態失敗:', error);
      
      // 錯誤處理：顯示用戶友好的錯誤信息
      if (error && typeof error === 'object' && 'code' in error) {
        if (error.code === 'failed-precondition') {
          console.log('⚠️ Firestore 索引尚未創建，請稍後再試');
          // 可以在這裡添加用戶提示
        } else if (error.code === 'permission-denied') {
          console.log('⚠️ 沒有權限訪問 Firestore');
        } else {
          console.log('⚠️ 網絡或服務器錯誤，數字鍵狀態功能暫時不可用');
        }
      } else {
        console.log('⚠️ 未知錯誤，數字鍵狀態功能暫時不可用');
      }
      
      // 錯誤時不重置本地狀態，保持現有數據
      // 狀態追蹤日誌已移除
    }
  };

  // 處理 FA 鍵（N=Note）在 M 模式下不再控制字幕白板狀態
  const handleNoteToggle = () => {
    if (mButtonState === 'M') {
      // M 模式下，N 鍵不再控制字幕白板顯示狀態
      // 字幕白板在 M 模式下始終顯示
      // 操作日誌已移除
      
      // 檢查是否有數字鍵已經被選中，自動設置並載入
      const currentTabNumberKeys = tabValue === 3 ? numberKeys : tabNumberKeys[tabValue];
      const activePickUpKey = Object.entries(currentTabNumberKeys).find(([_, keyState]) => keyState.isPickUpState);
      
      if (activePickUpKey) {
        // 有數字鍵已經被選中，自動設置並載入
        const [key, keyState] = activePickUpKey;
        const keyNumber = Number(key);
        
        setCurrentSubtitleKey(keyNumber);
        setSubtitleContent(keyState.note || '');
        // 調試日誌已移除
      } else if (currentSubtitleKey !== null) {
        // 沒有選中的數字鍵，但有之前記錄的數字鍵
        // 調試日誌已移除
      } else {
        // 完全沒有選擇的數字鍵
        // 操作日誌已移除
      }
    }
  };

  // 處理直接模式切換（用於選單功能）
  const handleModeChange = (newMode: MButtonState) => {
    // 播放按鈕點擊音效
    playButtonSound('click');
    
    // 如果選擇的模式與當前模式相同，不做任何操作
    if (newMode === mButtonState) {
      return;
    }

    // Reset AB points when exiting AB mode
    if (mButtonState === 'AB') {
      // Reset all AB points to their original blue state
      setNumberKeys(prevKeys => {
        const updatedKeys: Record<string, NumberKeyState> = { ...prevKeys };
        Object.keys(updatedKeys).forEach(key => {
          if (updatedKeys[String(key)].isABPoint) {
            updatedKeys[String(key)] = {
              ...updatedKeys[String(key)],
              isABPoint: false
            };
          }
        });
        return updatedKeys;
      });
      
      // Clear AB points state
      setABPoints({
        pointA: { key: null, timestamp: null },
        pointB: { key: null, timestamp: null }
      });
    }

    // 當切換到 PB 模式時，重置所有按鈕的 isPickUpState
    if (newMode === 'PB') {
      setNumberKeys(prevKeys => {
        const updatedKeys: Record<string, NumberKeyState> = { ...prevKeys };
        Object.keys(updatedKeys).forEach(key => {
          updatedKeys[String(key)] = {
            ...updatedKeys[String(key)],
            isPickUpState: false
          };
        });
        return updatedKeys;
      });
      
      // 切換到 PB 模式後，彈出播放時長選擇 Dialog
      // 檢查是否有可播放的重點
      const allKeys = Object.entries(numberKeys)
        .filter(([_, keyState]) => keyState.timestamp !== null && !keyState.isABPoint)
        .map(([key, keyState]) => ({
          keyNo: Number(key),
          timestamp: keyState.timestamp!,
          isABPoint: keyState.isABPoint,
          isPickUpState: keyState.isPickUpState
        }))
        .sort((a, b) => a.keyNo - b.keyNo);

      if (allKeys.length > 0) {
        openDialog('playbackDuration');
      }
    }

    // 當切換到 AB 模式時，彈出循環次數選擇 Dialog
    if (newMode === 'AB') {
      openDialog('abLoopCount');
    }
    
    // 切換到新模式
    setMButtonState(newMode);
  };

  const handleRightButtonClick = () => {
    setIsRightButtonActive(!isRightButtonActive);
    // 執行快進功能
    handleFastForward();
    console.log(`向右鍵被點擊，當前狀態：${!isRightButtonActive ? '激活' : '未激活'}`);
  };

  // Volume Control Button
  <Box sx={{ 
    position: 'fixed',
    right: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
          zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    gap: 1
  }}>
    <Tooltip title={isMuted ? "取消靜音" : "靜音"}>
      <IconButton 
        onClick={() => setIsMuted(!isMuted)}
        onMouseEnter={() => showUI('volumeSlider')}
        onMouseLeave={() => hideUI('volumeSlider')}
        sx={{ 
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          color: 'white',
          '&:hover': {
            bgcolor: 'rgba(0, 0, 0, 0.7)'
          }
        }}
      >
        {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
      </IconButton>
    </Tooltip>
    {uiDisplay.volumeSlider && (
      <Box sx={{ 
        position: 'absolute',
        right: '100%',
        top: '50%',
        transform: 'translateY(-50%)',
        width: 100,
        height: 24,
        mr: 1,
        bgcolor: 'rgba(0, 0, 0, 0.5)',
        borderRadius: 1,
        p: 1
      }}>
        <Slider
          value={volume}
          onChange={handleVolumeChange}
          min={0}
          max={1}
          step={0.1}
          sx={{
            color: 'white',
            '& .MuiSlider-thumb': {
              width: 16,
              height: 16
            }
          }}
        />
      </Box>
    )}
  </Box>

  const [isSecondKeySet, setIsSecondKeySet] = useState<boolean>(false);
  const [keySetIndex, setKeySetIndex] = useState<number>(0);
  const [sButtonClickCount, setSButtonClickCount] = useState<number>(0);

  // 追蹤上次按鈕時間
  const lastBButtonClickTime = useRef<number>(0);
  const lastSButtonClickTime = useRef<number>(0);

  const handleSwitchClick = () => {
    playButtonSound('click');
    const now = Date.now();
    // 找到紅色鍵
    let currentKey: number | null = null;
    for (let i = 1; i <= maxNumberKeys; i++) {
      if (numberKeys[i] && numberKeys[i].isPickUpState) {
        currentKey = i;
        break;
      }
    }
    // 雙擊才翻頁
    if (now - lastSButtonClickTime.current < 500) {
      // 直接切到下一組，紅色鍵不動
      const totalGroups = Math.ceil(maxNumberKeys / 6);
      setKeySetIndex((keySetIndex + 1) % totalGroups);
      lastSButtonClickTime.current = 0;
      return;
    }
    lastSButtonClickTime.current = now;
    // 單擊：紅色鍵往大一格
    if (currentKey && currentKey < maxNumberKeys) {
      const nextKey = currentKey + 1;
      setNumberKeys(prev => {
        const newState = { ...prev };
        if (currentKey !== null) {
          newState[currentKey] = { ...newState[currentKey], isPickUpState: false };
        }
        newState[nextKey] = { ...newState[nextKey], isPickUpState: true };
        return newState;
      });
      // 若跨頁自動切換顯示組
      const nextGroup = Math.floor((nextKey - 1) / 6);
      if (nextGroup !== keySetIndex) {
        setKeySetIndex(nextGroup);
      }
      // 跳轉到新紅色鍵的時間點（使用 fade 效果）
      const nextKeyState = numberKeys[nextKey];
      if (nextKeyState && nextKeyState.timestamp !== null && playerRef.current) {
        seekToWithFade(nextKeyState.timestamp, 300);
      }
    }
  };

  // B鈕處理函數 - 反向數字切換
  const handleBButtonClick = () => {
    playButtonSound('click');
    const now = Date.now();
    const isFirstGroup = keySetIndex === 0;
    // 找到紅色鍵
    let currentKey: number | null = null;
    for (let i = 1; i <= maxNumberKeys; i++) {
      if (numberKeys[i] && numberKeys[i].isPickUpState) {
        currentKey = i;
        break;
      }
    }
    // 雙擊才翻頁
    if (now - lastBButtonClickTime.current < 500) {
      if (isFirstGroup && currentKey && currentKey >= 1 && currentKey <= 6) {
        if (playerRef.current) playerRef.current.seekTo(0);
        setSnackbarMessage('影片已回到開始位置');
        setSnackbarOpen(true);
      } else {
        setKeySetIndex(0); // 換頁到1~6，紅色鍵不動
      }
      lastBButtonClickTime.current = 0;
      return;
    }
    lastBButtonClickTime.current = now;
    // 單擊：紅色鍵往小一格
    if (currentKey && currentKey > 1) {
      const prevKey = currentKey - 1;
      setNumberKeys(prev => {
        const newState = { ...prev };
        if (currentKey !== null) {
          newState[currentKey] = { ...newState[currentKey], isPickUpState: false };
        }
        newState[prevKey] = { ...newState[prevKey], isPickUpState: true };
        return newState;
      });
      // 若跨頁自動切換顯示組
      const prevGroup = Math.floor((prevKey - 1) / 6);
      if (prevGroup !== keySetIndex) {
        setKeySetIndex(prevGroup);
      }
      // 跳轉到新紅色鍵的時間點（使用 fade 效果）
      const prevKeyState = numberKeys[prevKey];
      if (prevKeyState && prevKeyState.timestamp !== null && playerRef.current) {
        seekToWithFade(prevKeyState.timestamp, 300);
      }
    }
  };

  // Speed鈕處理函數 - 調整播放速度
  const handleSpeedChange = (newSpeed: number) => {
    // 播放按鈕點擊音效
    playButtonSound('click');
    
    setPlaybackRate(newSpeed);
    
    // 顯示提示訊息
    setSnackbarMessage(`播放速度已調整為 ${newSpeed}x`);
    setSnackbarOpen(true);
  };

  // 調整最大按鈕數量的函數
  const handleMaxNumberKeysChange = (newMaxKeys: number) => {
    // 確保新值至少為6（一組的最小數量）
    const validMaxKeys = Math.max(6, newMaxKeys);
    
    // 調試日誌已移除
    
    setMaxNumberKeys(validMaxKeys);
    
    // 保留現有的數字鍵狀態，只為新增的數字鍵添加初始狀態
    setNumberKeys(prev => {
      const newNumberKeys: Record<string, NumberKeyState> = { ...prev };
      
      // 為新增的數字鍵添加初始狀態
      for (let i = 1; i <= validMaxKeys; i++) {
        const keyStr = i.toString();
        if (!newNumberKeys[keyStr]) {
          newNumberKeys[keyStr] = {
            keyNo: i,
            isActive: false,
            isABPoint: false,
            isPickUpState: false,
            timestamp: null,
            timecode: '00:00:00 00',
            videoMode: null,
            frame: null,
            note: '',
            screenshot: '',
            flag: 'off',
            playMode: 'M'
          };
        }
      }
      
      // 調試日誌已移除
      
      return newNumberKeys;
    });
    
    // 重置組索引到第一組
    setKeySetIndex(0);
    setSButtonClickCount(0);
    
    // 顯示提示訊息
    setSnackbarMessage(`數字按鈕數量已調整為 ${validMaxKeys} 個`);
    setSnackbarOpen(true);
  };

  const getSButtonStyle = () => {
    switch (sButtonClickCount) {
      case 1:
        return {
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          color: '#1976d2',
          hoverBackground: 'transparent',
          hoverBorder: 'transparent'
        };
      case 2:
        return {
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          color: '#1976d2',
          hoverBackground: 'transparent',
          hoverBorder: 'transparent'
        };
      default:
        return {
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          color: 'rgba(0, 0, 0, 0.54)',
          hoverBackground: 'transparent',
          hoverBorder: 'transparent'
        };
    }
  };

  // 清除所有時間碼記憶的函數
  const clearAllMemory = () => {
    // 重置所有數字鍵狀態
    const defaultState: Record<string, NumberKeyState> = {};
    for (let i = 1; i <= maxNumberKeys; i++) {
      defaultState[i.toString()] = {
        keyNo: i,
        isActive: false,
        isABPoint: false,
        isPickUpState: false,
        timestamp: null,
        timecode: '00:00:00 00',
        videoMode: null,
        frame: null,
        note: '',
        screenshot: '',
        flag: 'off',
        playMode: 'M'
      };
    }
    // MV模式下不重置數字鍵狀態
    if (mButtonState !== 'MV') {
      setNumberKeys(defaultState);
    }

    // 清除 AB 點
    setABPoints({
      pointA: { key: null, timestamp: null },
      pointB: { key: null, timestamp: null }
    });

    // 重置 checkpointCount
    setCheckpointCount(0);

    // 如果在播放中，暫停影片
    if (isPlaying) {
      setIsPlaying(false);
    }

    // 清除 localStorage 中的記憶
    localStorage.removeItem('numberKeys');
    localStorage.removeItem('abPoints');

    // 播放清除音效
    playButtonSound('reset');

    // 如果在 AB 循環播放中，清除循環
    if (abLoopIntervalRef.current) {
      clearInterval(abLoopIntervalRef.current);
      abLoopIntervalRef.current = null;
    }
    // 4. 在清除所有記憶時重置 checkpointCount（2025/6/17, 不知跟上面 清除AB點有什麼差別？ ）
    // MV模式下不重置數字鍵狀態
    if (mButtonState !== 'MV') {
      setNumberKeys(getInitialNumberKeyState());
    }
    setABPoints({
      pointA: { key: null, timestamp: null },
      pointB: { key: null, timestamp: null }
    });
    setCheckpointCount(0);
    playButtonSound('reset');
    setSnackbarMessage('已清除所有時間碼記憶');
    setSnackbarOpen(true);

    // 切換回 M 模式（MV模式下不切換）
    if (mButtonState !== 'MV') {
      setMButtonState('M');
    }
  };

  

  // 添加音量控制相關狀態
  const [volumeAnchorEl, setVolumeAnchorEl] = useState<null | HTMLElement>(null);
  const isVolumeOpen = Boolean(volumeAnchorEl);

  // 處理音量按鈕點擊
  const handleVolumeClick = (event: React.MouseEvent<HTMLElement>) => {
    setVolumeAnchorEl(event.currentTarget);
  };

  // 處理音量控制關閉
  const handleVolumeClose = () => {
    setVolumeAnchorEl(null);
  };

  // ═══════════════════════════════════════════════════════════════════════
  // 🎨 UI 控制與渲染模組
  // ═══════════════════════════════════════════════════════════════════════
  // 負責：Tab 切換、模式切換、UI 渲染函數
  // State: tabValue, mButtonState, activePanel
  // Functions: renderControlGroupUI, renderButtons, renderToolbar, renderVolumeControl
  
  // 渲染控制 Group UI 組件
  const renderControlGroupUI = () => (
    <Box sx={{
      width: '100%',
      // 控制面板高度填滿父容器
      height: '100%',
      // 強制手機模式下與父容器高度一致
      ...(aspectRatio > 1.8 ? {
        height: '100% !important',
        minHeight: 'auto !important',
        maxHeight: '100% !important',
        // 手機橫式時特別處理高度
        ...(aspectRatio > 1.8 && windowDimensions.height > windowDimensions.width ? {
          // 手機直式：保持原有設定
        } : {
          // 手機橫式：調整高度計算，避免淺藍色區超出
          '& .MuiTextField-root': {
            maxHeight: 'calc(100% - 5px) !important', // 底部間距5px
          },
          '& .MuiInputBase-root': {
            // 移除強制高度限制，讓手機橫式模式的70%設定生效
            minHeight: '60px !important',
          }
        })
      } : {}),
      display: 'flex',
      flexDirection: 'column',
      // 控制面板內容靠上對齊，實現往上平移效果
      justifyContent: { xs: 'flex-start', sm: 'flex-start', md: 'flex-start', lg: 'flex-start', xl: 'flex-start' },
      alignItems: 'center',
      gap: '0px !important',  // 🔧 修改：強制設定為零間距，完全消除行間距
      p: 0,     // 🔧 修改：移除所有padding，讓70%:30%比例更明顯
      position: 'relative',  // 添加相對定位，支持絕對定位的版權文字
      backgroundColor: (isVerticalVideo || isLandscape || isMobileLandscape) ? '#fafafa' : '#e3f2fd', // 直式佈局時使用淺藍色背景
      backdropFilter: 'blur(10px)',
      // 主容器邊框設定 - 灰色細框
      border: '1px solid #cccccc', // 灰色細框
      borderRadius: (isVerticalVideo || isLandscape || isMobileLandscape) ? 0 : 2, // 橫式佈局時移除圓角
      overflow: 'auto',
      // 移除縮放，直接調整實際尺寸
      // transform: (isVerticalVideo || isLandscape) ? {
      //   xs: `scale(${Math.min(optimalScale, 0.7)})`, // 手機版額外縮小到70%，確保按鈕不被切到
      //   sm: 'scale(1)'
      // } : 'scale(1)',
      transformOrigin: 'center center',
              // 手機版本時添加視覺反饋
        ...((isVerticalVideo || isLandscape || isMobileLandscape) ? {
        // 手機直式佈局時縮小控制面板按鈕90%，但播放功能列按鈕放大到120%
        ...(aspectRatio > 1.8 && windowDimensions.height > windowDimensions.width ? {
            '& .MuiButton-root:not(.playback-control-btn), & .MuiIconButton-root:not(.playback-control-btn), & .MuiFormControl-root, & .MuiSwitch-root, & .MuiSlider-root, & .MuiFormControlLabel-root': {
              transform: 'scale(0.9)',
              transformOrigin: 'center center',
            },
            // 手機橫式時不縮放，保持100%大小
            '& .MuiTextField-root': {
              transform: 'scale(1)', // 手機橫式時保持100%大小
              transformOrigin: 'center center',
            },
            // 播放功能列按鈕放大到120%
            '& .playback-control-btn': {
              transform: 'scale(1.2)',
              transformOrigin: 'center center',
            }
          } : {}),
        xs: {
          // 移除邊框覆蓋，保持主要紅色邊框
          // border: '3px solid #ff6b6b',  // 註解掉，避免覆蓋主要邊框
          backgroundColor: '#fff5f5',
          boxShadow: '0 0 10px rgba(255, 107, 107, 0.3)',
          position: 'relative',
          '&::before': {
            content: '"📱 手機模式"',
            position: 'absolute',
            top: '-25px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#ff6b6b',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '0.7rem',
            fontWeight: 'bold',
            zIndex: 10
          }
        }
      } : {})
    }}>
      {/* 移除獨立的位置切換開關，改為放在文字框內部 */}

      {/* 空白區域 - 手機版減少，其他版本保持 */}
      <Box sx={{ 
        flex: 1, 
        minHeight: { xs: '0px', sm: '0px', md: '0px' },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        width: '100%',              // 確保父容器佔滿寬度
        padding: '0 8px',           // 父容器左右各8px padding，縮短與邊界距離
        boxSizing: 'border-box'     // 確保 padding 包含在寬度計算中
      }}>
        {/* 新增：淺灰色方框，帶圓角，深灰色細框 */}
        <Box sx={{
          width: '100%',                // 方框佔滿父容器的100%寬度
          height: '98%',                // 高度98%，加大面積
          backgroundColor: '#f5f5f5',   // 淺灰色背景
          border: '1px solid #666666',  // 深灰色細框
          borderRadius: '12px',         // 圓角
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          padding: '4px',               // 🔧 修復：減少內部padding，讓淡黃色區域更接近底部
          boxSizing: 'border-box',      // 確保padding包含在寬度計算中
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', // 輕微陰影效果
          // 強制寬度設定
          flexShrink: 0,                // 防止被壓縮
          flexGrow: 1,                  // 🔧 修改：允許增長以填滿可用空間
          // 響應式高度調整
          minHeight: {
            xs: '200px', // 手機版最小高度
            sm: '250px', // 平板版最小高度
            md: '300px', // 桌面版最小高度
          },
          // 手機橫式時縮小到90%，讓淺黃色輸入區更大
          ...(aspectRatio > 1.8 && windowDimensions.width > windowDimensions.height ? {
            transform: 'scale(0.9)',
            transformOrigin: 'center center',
            minHeight: '180px', // 手機橫式時的最小高度
          } : {}),
          // 響應式調整 - 使用 MUI 的響應式斷點
          '@media (max-width: 800px)': {
            width: '100%',              // 平板版佔滿父容器寬度
            height: '98%',              // 修改：平板版也改為98%
            borderRadius: '8px',
            padding: '12px'             // 新增：平板版減少padding
          },
          '@media (max-width: 768px)': {
            width: '100%',              // 手機版佔滿父容器寬度
            height: '98%',              // 修改：手機版也改為98%
            borderRadius: '6px',
            padding: '8px'              // 新增：手機版減少padding
          },
          // 🔧 修復：手機橫式模式下底部padding為0
          ...(aspectRatio > 1.8 && windowDimensions.width > windowDimensions.height ? {
            padding: '8px 8px 0px 8px !important'  // 手機橫式：底部padding為0，使用!important覆蓋媒體查詢
          } : {}),
          // 確保方框在各種螢幕尺寸下都能正確顯示
          minWidth: '120px',            // 最小寬度
          maxWidth: 'none',             // 移除最大寬度限制
          maxHeight: 'none'             // 🔧 修改：移除最大高度限制，允許95%高度
        }}>
          {/* 字幕白板內容區域 - M 模式下始終顯示完整界面 */}
          {mButtonState === 'M' ? (
            // M 模式：始終顯示完整的字幕白板界面
            (() => {
              // 檢查是否有數字鍵處於 pickup 狀態
              const currentTabNumberKeys = tabValue === 3 ? numberKeys : tabNumberKeys[tabValue];
              const hasPickUpKey = Object.values(currentTabNumberKeys).some(keyState => keyState.isPickUpState);
              
              if (hasPickUpKey && currentSubtitleKey !== null) {
                // 已選擇數字鍵：顯示完整的編輯界面
                return (
                  <Box sx={{ 
                    width: '100%', 
                    height: '100%', // 高度100%，但會響應父容器
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    overflow: 'visible', // 新增：防止內容溢出
                    boxSizing: 'border-box' // 新增：確保padding和border包含在高度計算中
                  }}>
                    {/* 數字標題和時間碼 - 固定在頂部，與細圓角方框保持5px距離 */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1, 
                      mb: 1,
                      flexDirection: { xs: 'row', sm: 'row' }, // 手機橫式時強制保持同一行
                      flexWrap: 'nowrap', // 強制不換行
                      justifyContent: 'center', // 置中對齊
                      width: '100%', // 確保佔滿寬度
                      flexShrink: 0, // 新增：防止被壓縮
                      position: 'sticky', // 新增：粘性定位
                      top: '-8px', // 修改：縮減與上方邊界的間距
                      zIndex: 10, // 新增：確保在最上層
                      backgroundColor: 'transparent', // 新增：透明背景
                      padding: '4px 0', // 修改：縮減上下padding
                      borderBottom: '1px solid #e0e0e0' // 新增：底部細線分隔
                    }}>
                      {/* NO 標題 */}
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: '#1976d2',
                          fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                          fontWeight: 600,
                          textAlign: 'center'
                        }}
                      >
                        {`NO ${currentSubtitleKey}`}
                      </Typography>
                      
                      {/* 時間碼 */}
                      {(() => {
                        // 🔧 修復：優先使用 numberKeys（YouTube Tab 的主要數據源），然後才使用 tabNumberKeys
                        const currentTabNumberKeys = tabValue === 3 ? numberKeys : (tabNumberKeys[tabValue] || tabNumberKeys[3] || {});
                        const keyState = currentTabNumberKeys?.[String(currentSubtitleKey)];
                        
                        // 檢查 keyState 是否存在
                        if (!keyState) {
                          return null;
                        }
                        
                        let timeOnly = '--:--:--';
                        
                        // 🔧 修復：優先使用 timestamp 生成時間碼（因為 timecode 可能都是 00:00:00 00）
                        if (keyState.timestamp !== null && keyState.timestamp !== undefined && keyState.timestamp >= 0 && keyState.timestamp <= 1000000) {
                          // 使用與 NoteRecordsDialog 相同的格式化邏輯
                          const seconds = keyState.timestamp;
                          const totalSeconds = Math.floor(seconds);
                          const hours = Math.floor(totalSeconds / 3600);
                          const minutes = Math.floor((totalSeconds % 3600) / 60);
                          const secs = totalSeconds % 60;
                          
                          const pad = (num: number) => num.toString().padStart(2, '0');
                          timeOnly = `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
                        } else if (keyState.timecode && keyState.timecode !== '00:00:00 00' && keyState.timecode !== '00:00:00') {
                          // 只有在 timestamp 無效且 timecode 不是 00:00:00 時，才使用 timecode
                          const timeParts = keyState.timecode.split(' ');
                          timeOnly = timeParts[0]; // 取 "00:00:00" 部分
                        }
                        
                        return (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#000000', // 🔧 修改：改為全黑色，更容易辨識
                              fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                              fontWeight: 600, // 🔧 增加：字重改為 600，更清晰
                              textAlign: 'center',
                              opacity: 1, // 🔧 修改：不透明度改為 1，完全不透明
                              backgroundColor: '#f0f0f0',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              border: '1px solid #e0e0e0'
                            }}
                          >
                            {timeOnly}
                          </Typography>
                        );
                      })()}

                      {/* 🔧 新增：紫色字幕功能按鈕 */}
                      {videoUrl && (
                        <Tooltip
                          title={uiDisplay.subtitleFillTooltip ? "字幕已下載完成，點擊填充字幕！" : (isAutoFillMode ? "全自動填充模式已開啟（雙擊關閉）" : "單擊填充字幕，雙擊開啟全自動填充")}
                          open={uiDisplay.subtitleFillTooltip || undefined}
                          arrow
                        >
                          <IconButton
                            size="small"
                            onClick={handleSubtitleButtonClick}
                            disabled={isDownloadingSubtitles || !hasSRTCache}
                            sx={{
                              color: isAutoFillMode ? '#f44336' : '#9c27b0',
                              backgroundColor: isAutoFillMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(156, 39, 176, 0.1)',
                              border: `2px solid ${isAutoFillMode ? '#f44336' : '#9c27b0'}`,
                              opacity: (isDownloadingSubtitles || !hasSRTCache) ? 0.5 : 1,
                              '&:hover': {
                                backgroundColor: isAutoFillMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(156, 39, 176, 0.2)'
                              },
                              marginLeft: 1,
                              marginRight: 1
                            }}
                          >
                            <SubtitlesIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* 🔧 新增：右側控制開關 - 與 NO 數字/時間碼同一行 */}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={controlGroupPosition === 'right'}
                            onChange={handleControlGroupPositionToggle}
                            color="primary"
                            size="small"
                          />
                        }
                        label={controlGroupPosition === 'right' ? '右側控制' : '左側控制'}
                        labelPlacement="start"
                        sx={{
                          margin: 0,
                          '& .MuiFormControlLabel-label': {
                            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' },
                            color: '#666666',
                            fontWeight: 400
                          }
                        }}
                      />
                    </Box>
                    
                    {/* 🔧 觀點標題 - 顯示 Firestore 中的實際觀點名稱 */}
                    {currentViewpointName && (
                      <Typography 
                        sx={{ 
                          color: '#666',
                          fontSize: '0.875rem',
                          fontWeight: 400,
                          textAlign: 'left',
                          m: 0,
                          p: 0
                        }}
                      >
                        {currentViewpointName}
                      </Typography>
                    )}
                    
                    {/* 文字輸入框 - 簡化版，移除淡黃色區域包裝 */}
                    <TextField
                      multiline
                      value={subtitleContent}
                      onChange={(e) => {
                        // 🔧 修改：檢查用戶登入狀態，但保持正常外觀
                        if (!user?.uid) {
                          // 調試日誌已移除
                          openDialog('auth');
                          return;
                        }
                        setSubtitleContent(e.target.value);
                        
                        // 🔧 新增：標記為用戶手動輸入（直接設置，避免 useEffect 覆蓋）
                        // 調試日誌已移除
                        setSubtitleContentWithSource({
                          text: e.target.value,
                          isFromSRT: false
                        });
                      }}
                      placeholder="在此輸入字幕內容..."
                      variant="outlined"
                      size="small"
                      sx={{
                        width: '100%',
                        flex: '1 1 auto',
                        // 🔧 修正：手機橫式模式優先，其他情況使用相對計算
                        ...(aspectRatio > 1.8 && windowDimensions.width > windowDimensions.height ? {
                          // 手機橫式模式：直接設為灰色圓角框大小的15%
                          height: '15%',
                          minHeight: '80px', // 降低 minHeight 讓 15% 設定生效
                          maxHeight: '15%',
                        } : {
                          // 其他情況：視窗高度<500px時維持與>=500px時一樣的計算模式
                          ...(windowDimensions.height < 500 ? {
                            height: 'calc(100% - 10px)',
                            minHeight: '250px',
                            maxHeight: 'calc(100% - 10px)',
                          } : {
                            height: 'calc(100% - 10px)',
                            minHeight: '100px',
                            maxHeight: 'calc(100% - 10px)',
                          }),
                        }),
                        marginBottom: aspectRatio > 1.8 && windowDimensions.width > windowDimensions.height ? '0px' : '5px', // 🔧 修復：手機橫式時底部間距0px
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffe8f0', // 淡粉紅色背景
                          borderRadius: '8px',
                          fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                          color: '#333333',
                          height: '100%', // 🔧 修復：高度100%填充父容器
                          minHeight: '100%',
                          maxHeight: '100%',
                          overflow: 'auto', // 🔧 修復：添加滾動功能，防止內容溢出
                          '& fieldset': {
                            borderColor: '#e0e0e0', // 淡灰色邊框
                            borderWidth: '1px',
                          },
                          '&:hover fieldset': {
                            borderColor: '#ffd1e0', // hover 時邊框變為深粉紅色
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#9c27b0', // 聚焦時紫色邊框
                            borderWidth: '2px',
                          },
                        },
                        '& .MuiInputBase-input': {
                          // 🔧 新增：根據文字來源設置顏色
                          color: subtitleContentWithSource.isFromSRT ? '#1976d2' : 'inherit', // 藍色表示 SRT 字幕，黑色表示手動輸入
                          // 調試：添加背景色來確認狀態
                          backgroundColor: subtitleContentWithSource.isFromSRT ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                          padding: '0px 12px', // 🔧 修復：上下0px，左右12px，讓文字靠齊頂底部
                          textAlign: 'left',
                          lineHeight: '1.4',
                          height: '100%', // 🔧 修復：輸入區域高度100%
                          overflow: 'auto', // 🔧 修復：在實際文字容器上添加滾動功能
                          maxHeight: '100%', // 🔧 修復：限制最大高度
                          '&::placeholder': {
                            color: '#999999',
                            opacity: 0.7,
                          },
                        },
                      }}
                    />
                  </Box>
                );
              } else {
                // 未選擇數字鍵：顯示預設的完整界面
                return (
                  <Box sx={{ 
                    width: '100%', 
                    height: '100%', // 高度100%，但會響應父容器
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    overflow: 'visible', // 新增：防止內容溢出
                    boxSizing: 'border-box' // 新增：確保padding和border包含在高度計算中
                  }}>
                    {/* 預設的 NO 標題 - 固定在頂部，與細圓角方框保持5px距離 */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1, 
                      mb: 1,
                      flexDirection: { xs: 'row', sm: 'row' }, // 手機橫式時強制保持同一行
                      flexWrap: 'nowrap', // 強制不換行
                      justifyContent: 'center', // 置中對齊
                      width: '100%', // 確保佔滿寬度
                      flexShrink: 0, // 新增：防止被壓縮
                      position: 'sticky', // 新增：粘性定位
                      top: '-8px', // 修改：縮減與上方邊界的間距
                      zIndex: 10, // 新增：確保在最上層
                      backgroundColor: 'transparent', // 新增：透明背景
                      padding: '4px 0', // 修改：縮減上下padding
                      borderBottom: '1px solid #e0e0e0' // 新增：底部細線分隔
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: '#cccccc',
                          fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                          fontWeight: 600,
                          textAlign: 'center'
                        }}
                      >
                        {currentSubtitleKey !== null ? `NO ${currentSubtitleKey}` : 'NO --'}
                      </Typography>
                      
                      {/* 動態時間碼顯示區域 */}
                      {(() => {
                        // 獲取當前選中的數字鍵狀態
                        // 🔧 修復：優先使用 numberKeys（YouTube Tab 的主要數據源），然後才使用 tabNumberKeys
                        const currentTabNumberKeys = tabValue === 3 ? numberKeys : (tabNumberKeys[tabValue] || tabNumberKeys[3] || {});
                        const keyState = currentSubtitleKey !== null ? currentTabNumberKeys?.[currentSubtitleKey] : null;
                        
                        let displayText = '--:--:--';
                        
                        // 如果有選中的數字鍵且有效，優先使用 timestamp 生成正確的時間碼
                        if (currentSubtitleKey !== null && keyState) {
                          // 優先使用 timestamp 生成時間碼（因為 timecode 可能都是 00:00:00 00）
                          if (keyState.timestamp !== null && keyState.timestamp !== undefined && keyState.timestamp >= 0 && keyState.timestamp <= 1000000) {
                            // 使用與 NoteRecordsDialog 相同的格式化邏輯
                            const seconds = keyState.timestamp;
                            const totalSeconds = Math.floor(seconds);
                            const hours = Math.floor(totalSeconds / 3600);
                            const minutes = Math.floor((totalSeconds % 3600) / 60);
                            const secs = totalSeconds % 60;
                            const frames = Math.floor((seconds % 1) * 30);
                            
                            const pad = (num: number) => num.toString().padStart(2, '0');
                            displayText = `${pad(hours)}:${pad(minutes)}:${pad(secs)} ${pad(frames)}`;
                            // 调试日志已移除
                          } else if (keyState.timecode && keyState.timecode !== '00:00:00 00' && keyState.timecode !== '00:00:00') {
                            // 只有在 timestamp 無效且 timecode 不是 00:00:00 時，才使用 timecode
                            displayText = keyState.timecode;
                            // 调试日志已移除
                          }
                        }
                        
                        // 调试日志已移除
                        
                        return (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#cccccc',
                              fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                              fontWeight: 500,
                              textAlign: 'center',
                              opacity: 0.5,
                              backgroundColor: '#f0f0f0',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              border: '1px solid #e0e0e0'
                            }}
                          >
                            {displayText}
                          </Typography>
                        );
                      })()}

                      {/* 🔧 新增：紫色字幕按鈕 - 與預設 NO 標題/時間碼同一行 */}
                      {videoUrl && (
                        <Tooltip
                          title={uiDisplay.subtitleFillTooltip ? "字幕已下載完成，點擊填充字幕！" : (isAutoFillMode ? "全自動填充模式已開啟（雙擊關閉）" : "單擊填充字幕，雙擊開啟全自動填充")}
                          open={uiDisplay.subtitleFillTooltip || undefined}
                          arrow
                        >
                          <IconButton
                            size="small"
                            onClick={handleSubtitleButtonClick}
                            disabled={isDownloadingSubtitles || !hasSRTCache}
                            sx={{
                              color: isAutoFillMode ? '#f44336' : '#9c27b0',
                              backgroundColor: isAutoFillMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(156, 39, 176, 0.1)',
                              border: `2px solid ${isAutoFillMode ? '#f44336' : '#9c27b0'}`,
                              opacity: (isDownloadingSubtitles || !hasSRTCache) ? 0.5 : 1,
                              '&:hover': {
                                backgroundColor: isAutoFillMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(156, 39, 176, 0.2)'
                              },
                              marginLeft: 1,
                              marginRight: 1
                            }}
                          >
                            <SubtitlesIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* 🔧 新增：右側控制開關 - 與預設 NO 標題/時間碼同一行 */}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={controlGroupPosition === 'right'}
                            onChange={handleControlGroupPositionToggle}
                            color="primary"
                            size="small"
                          />
                        }
                        label={controlGroupPosition === 'right' ? '右側控制' : '左側控制'}
                        labelPlacement="start"
                        sx={{
                          margin: 0,
                          '& .MuiFormControlLabel-label': {
                            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' },
                            color: '#cccccc',
                            fontWeight: 400
                          }
                        }}
                      />
                    </Box>
                    
                    {/* 預設的文字輸入框 - 統一使用新樣式 */}
                    <TextField
                      multiline
                      value=""
                      placeholder="請先選擇數字鍵，然後在此輸入字幕內容..."
                      variant="outlined"
                      size="small"
                      disabled
                      sx={{
                        width: '100%',
                        flex: '1 1 auto',
                        // 🔧 修正：手機橫式模式優先，其他情況使用相對計算
                        ...(aspectRatio > 1.8 && windowDimensions.width > windowDimensions.height ? {
                          // 手機橫式模式：直接設為灰色圓角框大小的15%
                          height: '15%',
                          minHeight: '80px', // 降低 minHeight 讓 15% 設定生效
                          maxHeight: '15%',
                        } : {
                          // 其他情況：視窗高度<500px時維持與>=500px時一樣的計算模式
                          ...(windowDimensions.height < 500 ? {
                            height: 'calc(100% - 10px)',
                            minHeight: '250px',
                            maxHeight: 'calc(100% - 10px)',
                          } : {
                            height: 'calc(100% - 10px)',
                            minHeight: '100px',
                            maxHeight: 'calc(100% - 10px)',
                          }),
                        }),
                        marginBottom: aspectRatio > 1.8 && windowDimensions.width > windowDimensions.height ? '0px' : '5px', // 🔧 修復：手機橫式時底部間距0px
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffe8f0', // 統一使用淡粉紅色背景
                          borderRadius: '8px',
                          fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                          color: '#cccccc',
                          height: '100%', // 🔧 修復：高度100%填充父容器
                          minHeight: '100%',
                          maxHeight: '100%',
                          overflow: 'auto', // 🔧 修復：添加滾動功能，防止內容溢出
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                            borderWidth: '1px',
                          },
                          '&:hover fieldset': {
                            borderColor: '#ffd1e0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#9c27b0',
                            borderWidth: '2px',
                          },
                        },
                        '& .MuiInputBase-input': {
                          padding: '0px 12px', // 🔧 修復：上下0px，左右12px，讓文字靠齊頂底部
                          textAlign: 'left',
                          lineHeight: '1.4',
                          height: '100%', // 🔧 修復：輸入區域高度100%
                          overflow: 'auto', // 🔧 修復：在實際文字容器上添加滾動功能
                          maxHeight: '100%', // 🔧 修復：限制最大高度
                          '&::placeholder': {
                            color: '#cccccc',
                            opacity: 0.7,
                          },
                        },
                      }}
                    />
                  </Box>
                );
              }
            })()
          ) : (
            // 非 M 模式：顯示當前數字鍵的字幕內容
            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666666',
                  fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' },
                  textAlign: 'center',
                  opacity: 0.7,
                  mb: 0.5
                }}
              >
                字幕白板
              </Typography>
              {currentSubtitleKey && subtitleContent ? (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#1976d2',
                    fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.8rem' },
                    textAlign: 'center',
                    opacity: 0.9,
                    maxWidth: '100%',
                    wordBreak: 'break-word',
                    lineHeight: 1.2
                  }}
                >
                  {`數字鍵 ${currentSubtitleKey}: ${subtitleContent}`}
                </Typography>
              ) : (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#999999',
                    fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.8rem' },
                    textAlign: 'center',
                    opacity: 0.6,
                    fontStyle: 'italic'
                  }}
                >
                  切換到 M 模式使用字幕白板
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* 播放控制按鈕 - 移動到文字Note圓角方框下方 */}
      <Stack 
        direction="row" 
        spacing={3}
        alignItems="center" 
        justifyContent="center"
        sx={{ 
          width: '100%',
          py: '0px !important',
          pb: 0,
          mt: '1 !important', // 修改：從0.625改為1，與上方大外框保持8px間距
          mb: { xs: 0, sm: 0, md: 0 }, // 修改：設為0，讓數字列完全貼近下方
          opacity: tabValue === 4 || tabValue === 5 ? 0.5 : 1,
          pointerEvents: tabValue === 4 || tabValue === 5 ? 'none' : 'auto',
          // 整體縮小85%
          transform: 'scale(0.85)',
          transformOrigin: 'center center',
        // 手機橫式佈局模式下的統一設定
        ...(aspectRatio > 1.8 && windowDimensions.width > windowDimensions.height ? {
            transform: 'scale(0.81)', // 0.9 * 0.9 = 0.81
            transformOrigin: 'center center',
            mb: '0px !important',
            py: '2px !important',
          } : {})
        }}
      >
        <IconButton 
          onClick={handleRewind} 
          disabled={tabValue === 4 || tabValue === 5}
          size="small"
          className="playback-control-btn"
          sx={{ 
            transform: 'scale(1.2)',
            border: '2px solid #e0e0e0',
            borderRadius: '16px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '4px 8px',
            '&:hover': {
              borderColor: '#1976d2',
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
            }
          }}
        >
          <Typography variant="caption" sx={{ mr: 0.5 }}>
            -{fastForwardSeconds}s
          </Typography>
          <FastRewindIcon />
        </IconButton>

        <IconButton 
          onClick={handleStepBackward} 
          disabled={tabValue === 4 || tabValue === 5}
          size="small"
          className="playback-control-btn"
          sx={{ 
            transform: 'scale(1.2)',
            border: '2px solid #e0e0e0',
            borderRadius: '16px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '4px 8px',
            '&:hover': {
              borderColor: '#1976d2',
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
            }
          }}
        >
          <Typography variant="caption" sx={{ mr: 0.5 }}>
            {stepUnit === 'second' ? '-1s' : '-1f'}
          </Typography>
          <FastRewindIcon fontSize="small" />
        </IconButton>

        <IconButton 
          onClick={handlePlayPause}
          disabled={tabValue === 4 || tabValue === 5}
          className="playback-control-btn"
          sx={{ 
            transform: 'scale(1.5) !important',
            backgroundColor: '#d32f2f',
            borderRadius: '8px',
            color: 'white',
            height: '24px',
            minWidth: '40px',
            '&:hover': {
              backgroundColor: '#b71c1c'
            }
          }}
        >
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>

        <IconButton 
          onClick={handleStepForward} 
          disabled={tabValue === 4 || tabValue === 5}
          size="small"
          className="playback-control-btn"
          sx={{ 
            transform: 'scale(1.2)',
            border: '2px solid #e0e0e0',
            borderRadius: '16px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '4px 8px',
            '&:hover': {
              borderColor: '#1976d2',
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
            }
          }}
        >
          <Typography variant="caption" sx={{ mr: 0.5 }}>
            {stepUnit === 'second' ? '+1s' : '+1f'}
          </Typography>
          <FastForwardIcon fontSize="inherit" />
        </IconButton>

        <IconButton 
          onClick={handleFastForward} 
          disabled={tabValue === 4 || tabValue === 5}
          size="small"
          className="playback-control-btn"
          sx={{ 
            transform: 'scale(1.2)',
            border: '2px solid #e0e0e0',
            borderRadius: '16px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '4px 8px',
            '&:hover': {
              borderColor: '#1976d2',
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
            }
          }}
        >
          <Typography variant="caption" sx={{ mr: 0.5 }}>
            +{fastForwardSeconds}s
          </Typography>
          <FastForwardIcon />
        </IconButton>
      </Stack>

      {/* 第二排：數字按鈕面板 */}
      <Box sx={{ 
        flex: '0 0 auto', // 🔧 修改：改為固定高度，不參與彈性分配
        minHeight: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        mt: '1 !important', // 修改：從0.5改為1，將粉紅色區塊進一步往下移
        mb: '0px !important', // 🔧 新增：強制與下方保持零間距
        // 整體縮小到90%
        transform: 'scale(0.9)',
        transformOrigin: 'center center',
        // 手機橫式佈局模式下，進一步縮小
        ...(aspectRatio > 1.8 && windowDimensions.width > windowDimensions.height ? {
          transform: 'scale(0.81)', // 0.9 * 0.9 = 0.81
          transformOrigin: 'center center'
        } : {})
      }}>
        <NumberKeyPanel
          numberKeys={tabValue === 3 ? numberKeys : (tabNumberKeys[tabValue] || {})}
          isDeleteMode={isDeleteMode}
          mButtonState={mButtonState}
          vbids={vbids as any}
          handlers={{
            onNumberKeyClick: handleNumberKeyUp,
            onNoteDisplayChange: handleNoteDisplayChange, // 🔧 新增：只切換 Note 顯示，不觸發跳轉
            onDeleteModeToggle: () => setIsDeleteMode(!isDeleteMode),
            onSettings: handleSettings,
            onMButtonClick: handleMButtonClick,
            onModeChange: handleModeChange,
            onNoteToggle: handleNoteToggle,
            onCheckClick: handleCheckClick,
            onDeleteButtonClick: handleDeleteButtonClick,
            onDeleteButtonPress: handleDeleteButtonPress,
            onDeleteButtonRelease: handleDeleteButtonRelease,
            onBookmarkClick: handleBookmarkClick,
            onSwitchClick: handleSwitchClick,
            onBButtonClick: handleBButtonClick,
            onSpeedChange: handleSpeedChange,
            onThemeDialogOpen: handleThemeDialogOpen,
            onOpenNoteRecords: () => openDialog('noteRecords'),
            onRequestLogin: () => openDialog('auth')
          }}
          config={{
            showTimecode: true,
            isUserLoggedIn: !!user,
            maxNumberKeys,
            keySetIndex,
            duration: ppMode.duration,
            playbackRate,
            hasSRTCache: hasSRTCache,
            cachedSRTLanguage: cachedSRTLanguage,
            isAutoFillMode: isAutoFillMode,
            showAITooltip: uiDisplay.aiTooltip,
            currentTime: currentTime,
            isPlaying: isPlaying
          }}
          getSButtonStyle={getSButtonStyle}
          setVbids={() => {}}
          exportVBIDsToFile={(vbids) => exportVBIDsToFile(vbids)}
          currentTime={currentTime}  // 🔧 新增：傳遞當前播放時間以實現放大動畫
          isPlaying={isPlaying}  // 🔧 新增：傳遞播放狀態
          autoShowNoteOnPass={false}  // 🔧 關閉自動切換 Note，避免干擾快進/快退
          exportFirestoreData={async () => {
            if (!videoUrl || !user?.uid) {
              console.log('❌ 無法匯出：缺少 videoUrl 或用戶資訊');
              return;
            }
            
            try {
              // 調試日誌已移除
              await exportVideoFirestoreData(videoUrl, user.email);
            } catch (error) {
              console.error('❌ 匯出 Firestore 資料失敗:', error);
            }
          }}
        />
      </Box>



      {/* 分享 Icon 工具列 - 包含上下遮黑和重點截圖 */}
      <Box sx={{ 
        width: '100%', 
        mb: { xs: 0, sm: 0, md: 0 },     // 修改：設為0，讓第四行控制列貼近底部粉紅色框
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        mt: { xs: 0, sm: 0, md: 0 }, // 修改：設為0，讓第四列控制面板貼近粉紅色底部
        // 🔧 修改：手機橫式佈局模式下的統一設定
        ...(aspectRatio > 1.8 && windowDimensions.width > windowDimensions.height ? {
          transform: 'scale(0.9)',
          transformOrigin: 'center center',
          mt: '0px !important', // 手機橫式：強制移除頂部邊距
          // 🔧 新增：調試信息，確認手機橫式模式生效 (已移除)
          // border: '2px solid blue !important', // 手機橫式：藍色邊框，確認樣式生效
        } : {})
      }}>
        <Stack 
          direction="row" 
          spacing={{ xs: 0.8, sm: 1, md: 1 }}  // 手機版增加按鈕間距：從0.3改為0.8 (12.8px)
          alignItems="center" 
          sx={{ 
            flexWrap: 'nowrap',
            overflow: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            padding: 0, // 新增：設為0，讓第四行控制列完全貼近底部粉紅色框
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&:hover': {
              '&::-webkit-scrollbar': { display: 'block' },
              scrollbarWidth: 'thin'
            }
          }}
        >
        <Tooltip title="安全框" enterDelay={200} leaveDelay={0}>
          <span>
            <IconButton 
              onClick={handleSafetyFrameClick}
              color={uiDisplay.safetyFrame ? "primary" : "default"}
              size="small"
              sx={{ 
                opacity: uiDisplay.safetyFrame ? 1 : 0.5,
                '& .MuiSvgIcon-root': { color: uiDisplay.safetyFrame ? '#1976d2' : '#666666' },
                flexShrink: 0  // 防止圖標被壓縮
              }}
            >
              <CropFreeIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="格線" enterDelay={200} leaveDelay={0}>
          <span>
            <IconButton 
              onClick={handleGridLinesClick}
              color={gridMode !== 'none' ? "primary" : "default"}
              size="small"
              sx={{ 
                opacity: gridMode !== 'none' ? 1 : 0.5,
                '& .MuiSvgIcon-root': { color: gridMode !== 'none' ? '#1976d2' : '#666666' },
                flexShrink: 0  // 防止圖標被壓縮
              }}
            >
              <GridOnIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        
        <Tooltip title="上下遮黑" enterDelay={200} leaveDelay={0}>
          <span>
            <IconButton 
              onClick={() => toggleUI('movieMask')}
              color={uiDisplay.movieMask ? "primary" : "default"}
              disabled={tabValue === 4 || tabValue === 5}
              size="small"
              sx={{
                flexShrink: 0  // 防止圖標被壓縮
              }}
            >
              <MovieIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="時間碼" enterDelay={200} leaveDelay={0}>
          <span>
            <IconButton
              onClick={handleTimecodeClick}
              disabled={tabValue > 3}
              size="small"
              sx={{ 
                opacity: showTimecode ? 1 : 0.6, // 確保隱藏時仍然可見
                '& .MuiTypography-root': { color: '#000000' },
                flexShrink: 0,
                visibility: 'visible', // 強制按鈕始終可見
                display: 'flex' // 確保按鈕始終顯示
              }}
            >
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                00:00:00
              </Typography>
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="分享" enterDelay={200} leaveDelay={0}>
          <span>
            <IconButton 
              onClick={handleShare}
              size="small"
              sx={{ 
                flexShrink: 0,
                '& .MuiSvgIcon-root': { 
                  color: videoUrl ? '#1976d2' : '#666666' 
                }
              }}
            >
              <ShareIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="截圖" enterDelay={200} leaveDelay={0}>
          <span>
            <IconButton 
              onClick={handleScreenshot}
              size="small"
              sx={{ flexShrink: 0 }}
            >
              <CameraAltIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="重點截圖" enterDelay={200} leaveDelay={0}>
          <span>
            <IconButton 
              onClick={handleContinuousScreenshot}
              disabled={tabValue === 3 || tabValue === 4 || tabValue === 5}
              size="small"
              sx={{ 
                '& .MuiSvgIcon-root': { color: isBurstMode ? '#1976d2' : '#666666' },
                flexShrink: 0
              }}
            >
              <BurstModeIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="圖片管理" enterDelay={200} leaveDelay={0}>
          <span>
            <IconButton 
              onClick={handleImageManager}
              size="small"
              sx={{ flexShrink: 0 }}
            >
              <PhotoLibraryIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="同步連線播放" enterDelay={200} leaveDelay={0}>
          <span>
            <IconButton 
              onClick={() => {
                if (isOnAir || syncManagerOpen) {
                  // 如果已開啟，點擊關閉同步
                  // 調試日誌已移除
                  setSyncManagerOpen(false);
                  setIsOnAir(false);
                } else {
                  // 如果未開啟，點擊開啟同步播放管理
                  // 調試日誌已移除
                  setSyncManagerOpen(true);
                }
              }}
              color={(isOnAir || syncManagerOpen) ? "primary" : "default"}
              size="small"
              sx={{ 
                flexShrink: 0,
                '& .MuiSvgIcon-root': { 
                  color: (isOnAir || syncManagerOpen) ? '#1976d2' : '#666666' 
                }
              }}
            >
              <CastConnectedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        </Stack>
      </Box>

      {/* 暫時移除宣告文字區域，測試Layout是否正常 */}
      {/* 宣告文字區域 - 整合到控制面板內部 */}
      {/* <Box sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        mt: { xs: 0.5, sm: 1, md: 1 },  // 手機版：0.5 (8px)，其他：1 (16px)
        mb: 1,                            // 底部間距
        px: 1.25,                         // 左右邊距 20px (1.25 * 16px = 20px)
        // 響應式調整
        '@media (max-width: 800px)': {
          px: 1,                  // 平板版左右邊距 16px
        },
        '@media (max-width: 768px)': {
          px: 0.75,               // 手機版左右邊距 12px
        }
      }}>
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#666666',
            fontSize: { xs: '0.7rem', sm: '0.8rem', md: 0.9rem },
            textAlign: 'center',
            opacity: 0.8,
            fontFamily: 'Arial, sans-serif',
            // 響應式樣式
            fontWeight: 500,
            letterSpacing: '0.5px'
          }}
        >
          Copyright © 2025 Red Ball Studio | 紅球
        </Typography>
      </Box> */}
    </Box>
  );

  // 渲染音量控制按鈕和浮動滑桿
  const renderVolumeControl = () => (
    <>
      <Tooltip title={isMuted ? "取消靜音" : "音量控制"} enterDelay={200} leaveDelay={0}>
        <IconButton 
          onClick={handleVolumeClick}
          sx={{
            color: isMuted ? 'action.disabled' : 'action.active',
          }}
        >
          {isMuted || volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
        </IconButton>
      </Tooltip>

      {/* 音量控制弹出框 */}
      <VolumeControlPopover
        open={isVolumeOpen}
        anchorEl={volumeAnchorEl}
        onClose={handleVolumeClose}
        volume={volume}
        isMuted={isMuted}
        onVolumeChange={handleVolumeChange}
        onMuteToggle={handleMuteToggle}
      />
    </>
  );

  const handleYouTubeUrl = useCallback((url: string) => {
    try {
      // 清理 URL（移除多餘的參數）
      const videoId = url.includes('youtu.be/') 
        ? url.split('youtu.be/')[1].split('?')[0]
        : url.includes('v=')
          ? url.split('v=')[1].split('&')[0]
          : url;

      console.log('Setting YouTube URL with video ID:', videoId);
      setVideoUrl(`https://www.youtube.com/watch?v=${videoId}`);
      setCurrentTab(1); // 切換到 YouTube 標籤
      setVideoMode('YouTube');
    } catch (error) {
      console.error('Error processing YouTube URL:', error);
      setError('無法處理 YouTube URL');
    }
  }, []);

  const handleNumberKeyPress = async (key: number) => {
    if (!playerRef.current) {
      console.error('Player reference is not available');
      return;
    }

    const currentMode = getCurrentVideoMode();
    if (!currentMode) {
      console.error('No valid video mode detected');
      return;
    }

    const savedTimecode = savedTimecodes[key];
    console.log('Debug - Number key pressed:', key);
    console.log('Debug - Current mode:', currentMode);
    console.log('Debug - Saved timecode:', savedTimecode);
    console.log('Debug - Is delete mode:', isDeleteMode);
    console.log('Debug - Player ready:', playerRef.current.getInternalPlayer()?.readyState);

    if (savedTimecode) {
      try {
        const parts = savedTimecode.split(':');
        console.log('Debug - Timecode parts:', parts);
        const timeValues = parts.map(part => parseInt(part, 10));
        console.log('Debug - Parsed time values:', timeValues);
        
        if (timeValues.some(isNaN)) {
          console.error('Invalid timecode format:', savedTimecode);
          return;
        }

        const [hours = 0, minutes = 0, seconds = 0, frames = 0] = timeValues;
        const fps = currentMode === 'VIDEO' ? 30 : 30; // 默认使用30fps
        const targetTime = hours * 3600 + minutes * 60 + seconds + (frames / fps);

        console.log('Debug - Calculated target time:', targetTime);
        console.log('Debug - Current duration:', duration);
        console.log('[Checkpoint] preparing seek', {
          key,
          targetTime,
          currentVolume: volumeRef.current,
          isMuted: mutedRef.current,
          fadeEnabled: enableFadeEffect
        });

        if (!isFinite(targetTime)) {
          console.error('Invalid target time calculated:', targetTime);
          return;
        }

        if (currentMode === 'VIDEO') {
          try {
            console.log('Debug - Attempting to seek to:', targetTime);
            await playerRef.current.seekTo(targetTime);
            setPlayedSeconds(targetTime);
            setProgress((targetTime / duration) * 100);
            console.log('Debug - Successfully seeked to:', targetTime);
            console.log('[Checkpoint] seek complete (direct)', {
              key,
              volume: volumeRef.current,
              isMuted: mutedRef.current
            });
          } catch (error) {
            console.error('Error seeking to time:', error);
          }
        } else {
          console.log('[Checkpoint] delegating to handleSeek', {
            key,
            targetTime
          });
          handleSeek(targetTime);
        }

        console.log('[Checkpoint] post seek state', {
          key,
          volume: volumeRef.current,
          isMuted: mutedRef.current
        });
      } catch (error) {
        console.error('Error processing timecode:', error);
      }
    } else {
      console.log('Debug - No saved timecode for key:', key);
    }
  };

  // 處理 D 按鈕點擊
  const handleDeleteButtonClick = () => {
    // 播放按鈕點擊音效
    playButtonSound('click');
    
    // 檢查是否有任何數字鍵被啟動
    const hasActiveKeys = Object.values(numberKeys).some(key => key.isActive);
    
    if (!hasActiveKeys) {
      // 如果沒有數字鍵被啟動，顯示提示訊息
      setSnackbarMessage('請先按下數字記憶鍵');
      setSnackbarOpen(true);
      return;
    }

    // 切換刪除模式
    const newDeleteMode = !isDeleteMode;
    setIsDeleteMode(newDeleteMode);

    if (newDeleteMode) {
      // 進入刪除模式時開始計時
      startDeleteTimer();
    } else {
      // 離開刪除模式時清除計時器
      resetDeleteTimer();
    }

    // 如果進入刪除模式，將 M/AB 按鈕重置為 M
    setMButtonState('M');
  };

  // 處理 D 按鈕長按
  const handleDeleteButtonPress = () => {
    deletePressTimer.current = setTimeout(() => {
      // 清除所有時間碼記憶 - 完全重置為初始狀態
      setNumberKeys(() => {
        // 創建一個全新的初始狀態，只包含 1-18 的鍵
        const initialNumberKeys: Record<string, NumberKeyState> = {};
        for (let i = 1; i <= 18; i++) {
          initialNumberKeys[String(i)] = {
            keyNo: i,
            isActive: false,
            isABPoint: false,
            isPickUpState: false,
            timestamp: null,
            timecode: '00:00:00 00',
            videoMode: null,
            frame: null,
            note: '',
            screenshot: '',
            flag: 'off',
            playMode: 'M'
          };
        }
        // 清理日誌已移除
        return initialNumberKeys;
      });

      // 清除 AB 點
      setABPoints({
        pointA: { key: null, timestamp: null },
        pointB: { key: null, timestamp: null }
      });

      // 同步更新 tabNumberKeys
      setTabNumberKeys(prev => {
        const newState = { ...prev };
        // 創建一個全新的初始狀態，只包含 1-18 的鍵
        const initialNumberKeys: Record<string, NumberKeyState> = {};
        for (let i = 1; i <= 18; i++) {
          initialNumberKeys[String(i)] = {
            keyNo: i,
            isActive: false,
            isABPoint: false,
            isPickUpState: false,
            timestamp: null,
            timecode: '00:00:00 00',
            videoMode: null,
            frame: null,
            note: '',
            screenshot: '',
            flag: 'off',
            playMode: 'M'
          };
        }
        newState[tabValue] = initialNumberKeys;
        // 清理日誌已移除
        return newState;
      });

      // 播放重置音效
      playButtonSound('reset');

      // 顯示成功訊息
      setSnackbarMessage('已清除所有時間碼記憶');
      setSnackbarOpen(true);

      // 重置刪除模式
      setIsDeleteMode(false);
      
      // 調試日誌已移除
      // 調試日誌已移除
      
      // 同步清除操作到 Firestore
      const syncClearAllToFirestore = async () => {
        try {
          // 載入日誌已移除
          
          // 直接創建清除後的狀態，不依賴舊的 numberKeys
          const clearedNumberKeys: Record<number, NumberKeyState> = {};
          
          // 獲取當前的 maxNumberKeys 範圍
          const maxKeys = Math.max(maxNumberKeys, 24); // 確保至少 24 個鍵
          
          // 調試日誌已移除
          
          for (let i = 1; i <= maxKeys; i++) {
            clearedNumberKeys[i] = {
              keyNo: i,
              isActive: false,
              isABPoint: false,
              isPickUpState: false,
              timestamp: null,
              timecode: '00:00:00 00',
              videoMode: null,
              frame: null,
              note: '',
              screenshot: '',
              flag: 'off',
              playMode: 'M'
            };
          }
          
          // 調試日誌已移除
          
          const vbids = convertNumberKeysToVBIDs(clearedNumberKeys, getCurrentVideoMode() || 'YouTube', mButtonState);
          
          // 調試日誌已移除
          // 調試日誌已移除
          // 調試日誌已移除
          
          if (vbids.length >= 0) {
            // 調試日誌已移除
            
            await saveVbidsToFirestore({
              vbids,
              user,
              videoUrl,
              currentMode: getCurrentVideoMode() || 'YouTube',
              numberKeys: clearedNumberKeys,
              mButtonState,
              checkpointCount: vbids.length,
              fileName: originalFileName,
              fileSize: selectedFile?.size
            });
            // 調試日誌已移除
            
            // 徹底清除快取，確保清除所有檢查點後立即從 Firestore 重新載入
            const cacheKey = `firestore_${id}`;
            sessionStorage.removeItem(cacheKey);
            sessionStorage.removeItem(`${cacheKey}_timestamp`);
            sessionStorage.removeItem(`${cacheKey}_force_reload`);
            // 設置強制重新載入標記，確保下次載入時跳過快取
            sessionStorage.setItem(`${cacheKey}_force_reload`, 'true');
            // 清理日誌已移除
          } else {
            console.log('⚠️ vbids.length < 0，跳過 Firestore 同步');
          }
        } catch (error) {
          console.error('❌ 同步 D 鍵清除操作到 Firestore 失敗:', error);
          console.error('❌ 錯誤詳情:', {
            message: error instanceof Error ? error.message : error,
            stack: error instanceof Error ? error.stack : undefined
          });
        }
      };
      
      // 異步同步，不阻塞 UI
      // 調試日誌已移除
      setTimeout(() => {
        // 調試日誌已移除
        syncClearAllToFirestore();
      }, 100); // 延遲 100ms 確保 state 更新完成
    }, 2000);
  };

  // 處理 D 按鈕釋放
  const handleDeleteButtonRelease = () => {
    if (deletePressTimer.current) {
      clearTimeout(deletePressTimer.current);
      deletePressTimer.current = null;
    }
  };



  // 修改按鈕渲染函數
  const renderButtons = () => (
    <Stack direction="row" spacing={1} sx={{ mt: 2, mb: 2, justifyContent: 'center' }}>
      {/* 原有的按鈕 */}
      {/* ... */}
      
      {/* D 按鈕 */}
      <IconButton
        onMouseDown={handleDeleteButtonPress}
        onMouseUp={handleDeleteButtonRelease}
        onMouseLeave={handleDeleteButtonRelease}
        onClick={handleDeleteButtonClick}
        sx={{
          minWidth: '40px',
          height: '36px',
          backgroundColor: isDeleteMode ? '#ffffff' : '#ffffff',  // 刪除模式時改為粉紅色背景
          border: '1px solid',
          borderColor: isDeleteMode ? '#ff0000' : '#888888',  // 刪除模式時改為深粉紅色邊框
          borderRadius: '8px',
          color: isDeleteMode ? '#ff0000' : 'rgba(0, 0, 0, 0.54)',  // 預設時灰色文字
          '&:hover': {
            backgroundColor: '#f5f5f5',  // 刪除模式時懸停改為深粉紅色
            borderColor: 'primary.main'
          }
        }}
      >
        <Typography 
          variant="body2" 
          sx={{
            fontWeight: 500,
            fontSize: '1rem'
          }}
        >
          D
        </Typography>
      </IconButton>

      {/* My CheckPoint 按鈕 */}
      <span>
        <IconButton 
          onClick={handleBookmarkClick}
          sx={{ 
            minWidth: '40px',
            height: '36px',
            backgroundColor: '#ffffff',
            border: '1px solid',
            borderColor: '#888888',
            borderRadius: '8px',
            '& .MuiSvgIcon-root': { 
              color: '#ff0000',
              fontSize: '1.2rem'
            }
          }}
        >
          <Box
            sx={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              backgroundColor: '#ff0000',
              background: 'radial-gradient(circle at 30% 30%, #ff6666, #ff0000)',
              boxShadow: 'inset -2px -2px 4px rgba(0,0,0,0.3), inset 2px 2px 4px rgba(255,255,255,0.2), 0 2px 4px rgba(0,0,0,0.2)',
              border: '1px solid #cc0000',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '20%',
                left: '25%',
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.8)',
                boxShadow: '0 0 2px rgba(255,255,255,0.5)'
              }
            }}
          />
        </IconButton>
      </span>
    </Stack>
  );

  // VBID related functions
  const handleBookmarkClick = async () => {
    console.log('Bookmark button clicked');
    
    // 🔧 修復：在離開前強制保存當前的 Note 內容（優化版）
    if (currentSubtitleKey !== null && subtitleContent.trim() !== '') {
      // 調試日誌已移除
      
      // 同時更新本地狀態和 Firestore
      const currentTabNumberKeys = tabValue === 3 ? numberKeys : tabNumberKeys[tabValue];
      const keyState = currentTabNumberKeys[currentSubtitleKey];
      
      if (keyState) {
        const updatedKeyState = {
          ...keyState,
          note: subtitleContent
        };
        
        // 更新本地狀態
        if (tabValue === 3) {
          setNumberKeys(prev => ({
            ...prev,
            [String(currentSubtitleKey)]: updatedKeyState
          }));
        } else {
          setTabNumberKeys(prev => {
            const newState = { ...prev };
            newState[tabValue] = { ...newState[tabValue] };
            newState[tabValue][String(currentSubtitleKey)] = updatedKeyState;
            return newState;
          });
        }
        
        // 保存到 Firestore
        await saveNoteToFirestore(currentSubtitleKey, subtitleContent, tabValue);
        // 操作完成（日誌已移除）
      }
    }
    
    const mode = getCurrentVideoMode();
    console.log('Current mode:', mode);

    // 獲取當前 VBIDs
    const isYouTubeTab = tabValue === 3;
    const currentNumberKeys = isYouTubeTab ? numberKeys : (tabNumberKeys[tabValue] || {});
    // 調試日誌已移除
    // 調試日誌已移除
    // 調試日誌已移除
    const vbids = convertNumberKeysToVBIDs(currentNumberKeys, mode);
    console.log('handleBookmarkClick - vbids:', vbids);

    // 過濾重複的 VBIDs
    const uniqueCurrentVBIDs = filterDuplicateVBIDs(vbids);
    console.log('handleBookmarkClick - uniqueCurrentVBIDs:', uniqueCurrentVBIDs);

    // 🔧 修復：獲取檔案的修改日期作為發佈日期
    const fileAddedDate = await getFileAddedDate(originalFileName || fileRelativePath);

    // 確保所有 VBID 都有必要的欄位
    const safeVBIDs: VBID[] = uniqueCurrentVBIDs.map(vbid => ({
      ...vbid,
      userId: user?.uid || '',
      privacy: 'Public',
      mediaMode: mode,
      path: videoUrl || '',
      keyNo: vbid.keyNo,
      timestamp: vbid.timestamp || Date.now(),
      timecode: vbid.timecode,
      frame: vbid.frame || 0,
      note: vbid.note || '',
      screenshot: vbid.screenshot || '',
      flag: vbid.flag || 'on',
      addedDate: fileAddedDate,
      ttimeCodeStart: '00:00:00',
      ttimeCodeEnd: '00:00:00'
    }));
    console.log('handleBookmarkClick - safeVBIDs:', safeVBIDs);

    // 取得 YouTube 詳細資訊
    let youtubeInfo = undefined;
    if (mode === 'YouTube') {
      const videoId = youtubeApi.extractVideoId(videoUrl);
      if (videoId) {
        if (youtubeInfoCache[videoId]) {
          youtubeInfo = youtubeInfoCache[videoId];
          console.log('從快取取得 YouTube 詳細資訊:', youtubeInfo);
        } else {
          try {
            youtubeInfo = await youtubeApi.getVideoDetails(videoId);
            youtubeInfoCache[videoId] = youtubeInfo;
            console.log('取得 YouTube 詳細資訊:', youtubeInfo);
          } catch (e) {
            console.error('取得 YouTube 詳細資訊失敗:', e);
          }
        }
      }
    }

    // 更新當前標籤頁的 VBIDs
    const newTabVbids = { ...tabVbids };
    newTabVbids[tabValue] = safeVBIDs;
    setTabVbids(newTabVbids);
    console.log('handleBookmarkClick - newTabVbids:', newTabVbids);

    // 更新當前 VBIDs
    setCurrentVbids(safeVBIDs);
    console.log('handleBookmarkClick - final safeVBIDs length:', safeVBIDs.length);

    // Firestore 寫入
    try {
      await saveVbidsToFirestore({
        vbids: safeVBIDs,
        user,
        videoUrl,
        currentMode: mode || 'YouTube',
        numberKeys: currentNumberKeys,
        mButtonState,
        youtubeInfo,
        fileName: originalFileName,
        fileSize: selectedFile?.size,
        fileRelativePath: fileRelativePath,
        documentId: currentFirestoreId
      });
      // 若 safeVBIDs 為空，仍寫入一筆影片資訊
      if (safeVBIDs.length === 0) {
        await saveVbidsToFirestore({
          vbids: [],
          user,
          videoUrl,
          currentMode: mode || 'YouTube',
          numberKeys: currentNumberKeys,
          mButtonState,
          youtubeInfo,
          fileName: originalFileName,
          fileSize: selectedFile?.size,
          fileRelativePath: fileRelativePath,
          documentId: currentFirestoreId
        });
      }
    } catch (e) {
      if (!handlePermissionError(e)) {
        // alert('儲存到雲端失敗：' + (e instanceof Error ? e.message : '未知錯誤'));
        // 註解掉提示，直接 byPass
      }
    }

    // 跳轉
    handleMyFavorite();
  };

  const checkAndClearTimecodes = () => {
    console.log('Debug - Starting checkAndClearTimecodes...');
    console.log('Debug - Current numberKeys:', JSON.stringify(numberKeys, null, 2));
    console.log('Debug - Current ABPoints:', JSON.stringify(abPoints, null, 2));
    console.log('Debug - Current savedTimecodes:', JSON.stringify(savedTimecodes, null, 2));
    
    // 清除所有時間碼記憶
    clearAllMemory();
    
    console.log('Debug - After clearAllMemory:');
    console.log('Debug - New numberKeys:', JSON.stringify(numberKeys, null, 2));
    console.log('Debug - New ABPoints:', JSON.stringify(abPoints, null, 2));
    console.log('Debug - New savedTimecodes:', JSON.stringify(savedTimecodes, null, 2));
    
    // 播放清除音效
    playShutterSound();
    
    console.log('Debug - checkAndClearTimecodes completed');
  };

  // 添加處理 URL 參數的邏輯
  const [isParamProcessed, setIsParamProcessed] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // 處理登入對話框參數
  useEffect(() => {
    // 檢查 URL 是否有 showLogin 參數
    const urlParams = new URLSearchParams(window.location.search);
    const showLogin = urlParams.get('showLogin');
    
    if (showLogin === 'true') {
      // 自動開啟登入對話框
      openDialog('auth');
      setIsLogin(true);
      
      // 移除 URL 參數以避免重新整理時再次觸發
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // 修改 handleUrlParams 函數
  useEffect(() => {
    const handleUrlParams = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const shareParam = params.get('share');
        if (shareParam) {
          const decodedVbids = JSON.parse(atob(shareParam)) as VBID[];
          if (decodedVbids && decodedVbids.length > 0) {
            const firstVbid = decodedVbids[0];
            const restoredPlayMode = firstVbid.playMode || 'M';
            
            // 重建 numberKeys
            const newNumberKeys = getInitialNumberKeyState(maxNumberKeys);
            decodedVbids.forEach((vbid) => {
              const [keyStr, timeStr] = vbid.timecode.split('_');
              const key = parseInt(keyStr);
              if (!isNaN(key) && key >= 1 && key <= maxNumberKeys) {
                const hours = parseInt(timeStr.substring(0, 2));
                const minutes = parseInt(timeStr.substring(2, 4));
                const seconds = parseInt(timeStr.substring(4, 6));
                const frames = parseInt(timeStr.substring(6, 8));
                const timestamp = hours * 3600 + minutes * 60 + seconds;
                
                newNumberKeys[key.toString()] = {
                  keyNo: key,
                  isActive: true,
                  isABPoint: false,
                  isPickUpState: false,
                  timestamp: timestamp,
                  videoMode: vbid.mediaMode || getCurrentVideoMode() || 'Unknown',
                  frame: frames,
                  timecode: vbid.timecode || '',
                  note: vbid.note || '',
                  screenshot: vbid.screenshot || '',
                  flag: vbid.flag || 'off',
                  playMode: restoredPlayMode || 'M',
                };
              }
            });
            
            // 🔧 修復：獲取檔案的修改日期作為發佈日期
            const fileAddedDate = await getFileAddedDate(originalFileName || fileRelativePath);

            // 設置 VBIDs
            const fullVbids: VBID[] = decodedVbids.map((vbid) => ({
              userId: user?.uid || '',
              privacy: 'Public' as const,
              mediaMode: vbid.mediaMode || 'Unknown',
              path: firstVbid.path || '',
              timecode: vbid.timecode,
              timestamp: vbid.timestamp || 0,
              flag: vbid.flag || 'off',
              note: vbid.note || '',
              screenshot: vbid.screenshot || '',
              keyNo: vbid.keyNo,
              addedDate: fileAddedDate,
              playMode: restoredPlayMode,
              ttimeCodeStart: vbid.ttimeCodeStart || '00:00:00',
              ttimeCodeEnd: vbid.ttimeCodeEnd || '00:00:00'
            }));
            
            setCurrentVbids(fullVbids);
            setMButtonState(restoredPlayMode);
            setNumberKeys(newNumberKeys);
          }
        }
      } catch (error) {
        console.error('【解析分享連結失敗】', error);
        setError('無法解析分享連結');
      }
    };
    handleUrlParams();
    return () => {
      setTabVbids({});
      setCurrentVbids([]);
    };
  }, [initialLoad, location.hash, user, maxNumberKeys]);

  // 自動播放 5 秒重點模式，然後自動切回模式
  useEffect(() => {
    // 自動播放 useEffect 被觸發（日誌已移除）
    
    // 僅在分享連結且影片已載入時觸發，且避免重複執行
    if (
      location.pathname.includes('/share/') && 
      videoUrl && 
      !window.__checkplay_autoplay_flag
    ) {
      // 條件滿足，等待影片準備就緒
      window.__checkplay_autoplay_flag = true;
      
      let attempts = 0;
      const maxAttempts = 20; // 最多嘗試20次，總共10秒
      
      // 等待影片完全載入後再觸發自動播放
      const waitForVideoReady = () => {
        attempts++;
        
        if (playerRef.current && isPlaying) {
          // 調試日誌已移除
          
          // 使用 handleModeChange 而不是直接 setMButtonState
          handleModeChange('PB');
          
          setTimeout(() => {
            // 調試日誌已移除
            handleModeChange('M');
            window.__checkplay_autoplay_flag = false;
            // 調試日誌已移除
          }, 5000);
        } else if (attempts >= maxAttempts) {
          console.log('【自動播放】超時，停止等待影片準備就緒');
          window.__checkplay_autoplay_flag = false;
        } else {
          console.log(`【自動播放】等待影片準備就緒... (${attempts}/${maxAttempts})`);
          setTimeout(waitForVideoReady, 500); // 每500ms檢查一次
        }
      };
      
      waitForVideoReady();
    } else {
      // 條件不滿足或已執行過
    }
  }, [location.pathname, videoUrl]);
  
  // 新增：影片載入完成後自動設置最後一個藍色鍵為 pickup 狀態
  useEffect(() => {
    if (videoUrl && Object.keys(numberKeys).length > 0) {
      // 檢查是否已經有 pickup 狀態的鍵，如果有則不重複設置
      const hasPickUpState = Object.values(numberKeys).some(state => state.isPickUpState);
      if (hasPickUpState) {
        // 處理日誌已移除
        return;
      }
      
      // 處理日誌已移除
      
      // 找到最後一個藍色鍵（isActive = true 且 isPickUpState = false）
      const findLastBlueKey = () => {
        const blueKeys = Object.entries(numberKeys)
          .filter(([_, state]) => state.isActive && !state.isPickUpState && !state.isABPoint)
          .map(([k, _]) => Number(k))
          .sort((a, b) => a - b);
        return blueKeys.length > 0 ? blueKeys[blueKeys.length - 1] : null;
      };
      
      const lastBlueKey = findLastBlueKey();
      if (lastBlueKey !== null) {
        // 調試日誌已移除
        
        // 自動翻頁到最後一個藍色鍵所在的頁面
        const lastBlueKeyPage = Math.floor((lastBlueKey - 1) / 6);
        if (lastBlueKeyPage !== keySetIndex) {
          // 調試日誌已移除
          setKeySetIndex(lastBlueKeyPage);
        }
        
        // 設置最後一個藍色鍵為 pickup 狀態
        setNumberKeys(prev => {
          const newState = { ...prev };
          // 清除所有其他鍵的 pickup 狀態
          Object.keys(newState).forEach(k => {
            newState[k] = { ...newState[k], isPickUpState: false };
          });
          // 設置最後一個藍色鍵的 pickup 狀態
          if (newState[lastBlueKey]) {
            newState[lastBlueKey] = { ...newState[lastBlueKey], isPickUpState: true };
          }
          return newState;
        });
      }
    }
  }, [videoUrl]); // 移除 numberKeys 和 keySetIndex 依賴，避免無限循環
  
  // 優化：預載入常用資料，減少等待時間
  useEffect(() => {
    if (videoUrl && !isPlaying) {
      // 載入日誌已移除
      // 這裡可以添加預載入邏輯
    }
  }, [videoUrl]);
  
  // 防止組件重複掛載的性能優化
  const prevVideoUrlRef = useRef<string | null>(null);
  useEffect(() => {
    if (videoUrl && videoUrl !== prevVideoUrlRef.current) {
      // 調試日誌已移除
      prevVideoUrlRef.current = videoUrl;
    }
  }, [videoUrl]);

  // 在工具欄中添加分享按鈕
  const renderToolbar = () => {
    return (
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        {/* 現有的按鈕 */}
        <Button
          variant="contained"
          startIcon={<ShareIcon />}
          onClick={handleShare}
          disabled={!videoUrl || Object.keys(savedTimecodes).length === 0}
          sx={{
            backgroundColor: videoUrl ? '#1976d2' : '#666666',
            '&:hover': {
              backgroundColor: videoUrl ? '#1565c0' : '#666666',
            },
            '&:disabled': {
              backgroundColor: '#666666',
            }
          }}
        >
          分享視頻標記
        </Button>
      </Box>
    );
  };

  // 添加分享處理函數
  

  // 添加分享對話框
  

  // 🔧 保留：分享相關狀態（非對話框開關）
  const [currentFirestoreId, setCurrentFirestoreId] = useState<string>('');
  const [currentShareUrl, setCurrentShareUrl] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  

  // 添加一個 useEffect 來處理播放位置的恢復
  useEffect(() => {
    if (playerRef.current && tabPlaybackPositions[tabValue] !== undefined) {
      const savedPosition = tabPlaybackPositions[tabValue];
      playerRef.current.seekTo(savedPosition);
      setPlayedSeconds(savedPosition);
      setProgress(savedPosition / duration);
    }
  }, [tabValue, tabPlaybackPositions]);

  // 添加處理標題點擊的函數
  const handleTitleClick = () => {
    // 如果有傳遞的 handleTitleClick prop，使用它；否則使用本地邏輯
    if (propHandleTitleClick) {
      propHandleTitleClick();
      return;
    }
    
    // 開啟 Welcome 視窗（備用邏輯）
    openDialog('welcome');
    
    // 防止重複調用時的狀態重置問題
    // 使用靜態標誌記錄是否已經檢查過
    if ((window as any).__titleClickProcessed) {
      console.log('Title click already processed, ignoring duplicate call');
      return;
    }
    
    // 檢查多個來源以確定是否保留狀態
    const returnFromFavoritesLS = localStorage.getItem('returnFromFavorites');
    const returnFromFavoritesSS = sessionStorage.getItem('returnFromFavorites');
    const preserveMainPageStateLS = localStorage.getItem('preserveMainPageState') === 'true';
    const preserveMainPageStateSS = localStorage.getItem('preserveMainPageState') === 'true';
    
    // 檢查是否有全局保護標誌（從useEffect中設置）
    const hasGlobalProtection = !!(window as any).__mainPagePreserveState;
    
    // 如果任一標誌存在或有全局保護，就保留狀態
    const shouldPreserveState = !!(returnFromFavoritesLS || returnFromFavoritesSS || 
      preserveMainPageStateLS || preserveMainPageStateSS || hasGlobalProtection);
    
    // 分隔線（日誌已移除）
    // 調試日誌已移除
    
    // 標記為已處理
    (window as any).__titleClickProcessed = true;
    
    // 設置一個短暫的定時器重置這個標誌
    setTimeout(() => {
      (window as any).__titleClickProcessed = false;
    }, 300);
    
    // 清理所有標誌
    localStorage.removeItem('returnFromFavorites');
    sessionStorage.removeItem('returnFromFavorites');
    localStorage.removeItem('preserveMainPageState');
    sessionStorage.removeItem('preserveMainPageState');
    
    // 如果是從收藏頁面返回或有全局保護，不重置狀態
    if (shouldPreserveState) {
      // 狀態檢測日誌已移除
      
      // 延遲清除全局保護標誌，避免立即重置狀態的風險
      setTimeout(() => {
        console.log('Cleaning up global protection flag');
        (window as any).__mainPagePreserveState = false;
      }, 1000);
      
      return;
    }
    
    // 否則，執行正常的重置操作
    // 調試日誌已移除
    
    // 重置所有狀態
    // ... 其他重置代碼不變
    setTabValue(3); // 回到 YouTube 標籤
    setVideoUrl('');
    setVideoMode(null);
    setCurrentTab(0);
    setSavedTimecodes({});
    setBookmarkEnabled(false);
    setVbids([]);
    setIsBookmarkEnabled(false);
    closeDialog('vbid');
    setCurrentVbids([]);
    setTabVbids({});
    setTabVideoUrls({});
    setTabPlaybackPositions({});
    setNumberKeys(getInitialNumberKeyState());
    setMButtonState('M');
    setIsDeleteMode(false);
    setABPoints({
      pointA: { key: null, timestamp: null },
      pointB: { key: null, timestamp: null }
    });
    setIsPlaying(false);
    setProgress(0);
    setDuration(0);
    setVolume(0.7);
    setIsMuted(false);
    setPlaybackRate(1);
    setPlayedSeconds(0);
    setCurrentFrame(0);
    setCurrentTimecode('00:00:00 00');
    setShowTimecode(true);
    setShowTimecodeBackground(localStorage.getItem('showTimecodeBackground') !== 'false');
    setTimecodePosition(localStorage.getItem('timecodePosition') as TimecodePosition || 'bottomRight');
    hideUI('safetyFrame');
    setUiDisplay((prev) => ({ ...prev, crosshair: localStorage.getItem('showCrosshair') !== 'false' }));
    hideUI('gridLines');
    setGridMode(localStorage.getItem('gridMode') as 'none' | '3x3' | '6x4' | '10x6' || 'none');
    setUiDisplay((prev) => ({ ...prev, movieMask: localStorage.getItem('showMovieMask') === 'true' }));
    setUiDisplay((prev) => ({ ...prev, watermarkInOutput: localStorage.getItem('showWatermarkInOutput') !== 'false' }));

    // 顯示重置成功的提示
    setSnackbarMessage('已重置所有狀態');
    setSnackbarOpen(true);
    openDialog('welcome');
    // 分隔線（日誌已移除）
  };

  // 修改處理標籤切換時的時間碼顯示邏輯
  useEffect(() => {
    // 分隔線（日誌已移除）
    
    // 🔧 修復：Tab 切換時清理數字鍵狀態，避免狀態污染
    if (tabValue === 3) {
      // YouTube Tab：使用 numberKeys
      // YouTube Tab：使用 numberKeys 狀態
    } else if (tabValue === 2) {
      // 🔧 修復：Video Tab 保留雲端檔案的重點，不清理
      // 影片日誌已移除
    } else {
      // 其他 Tab：徹底清理數字鍵狀態，確保每個 Tab 完全獨立
      // 調試日誌已移除
      
      // 清理當前 Tab 的數字鍵狀態
      setTabNumberKeys(prev => {
        const newState = { ...prev };
        if (newState[tabValue]) {
          Object.keys(newState[tabValue]).forEach(key => {
            if (newState[tabValue][key]) {
              newState[tabValue][key] = {
                ...newState[tabValue][key],
                isPickUpState: false, // 清除 pickup 狀態
                note: '', // 🔧 清除 Note 內容
              };
            }
          });
        }
        return newState;
      });
      
      // 清理 numberKeys 的狀態（避免影響 YouTube Tab）
      setNumberKeys(prev => {
        const newState = { ...prev };
        Object.keys(newState).forEach(key => {
          if (newState[key]) {
            newState[key] = {
              ...newState[key],
              isPickUpState: false, // 清除 pickup 狀態
                              note: '', // 🔧 清除 Note 內容
            };
          }
        });
        return newState;
      });
      
      // 重置相關狀態
      setCurrentSubtitleKey(null);
      setSubtitleContent('');
      
      // 🔧 修復：Video Tab 不要清理數字鍵（保留雲端檔案的重點）
      if (tabValue === 2) {
        // 影片日誌已移除
        // 🔧 不再清理數字鍵，讓雲端檔案的重點能正確顯示
        // setTabNumberKeys(prev => {
        //   const newState = { ...prev };
        //   if (newState[2]) {
        //     Object.keys(newState[2]).forEach(key => {
        //       if (newState[2][key]) {
        //         newState[2][key] = {
        //           ...newState[2][key],
        //           isActive: false, // 🔧 清除藍色狀態
        //           isPickUpState: false, // 清除 pickup 狀態
        //           note: '', // 清除 Note 內容
        //           timestamp: null, // 🔧 清除時間戳記
        //           timecode: '00:00:00 00' // 🔧 重置時間碼
        //         };
        //       }
        //     });
        //   }
        //   return newState;
        // });
      }
    }
    
    // 在影片和圖片模式下都允許時間碼
    if (tabValue <= 5) {
      // Enabling timecode for all modes
      setIsTimecodeDisabled(false);
    }
  }, [tabValue]);

  // 🔧 移除重複的 timecode 更新 useEffect，避免與第一個衝突

  // 重置當前 Tab 的所有按鈕狀態
  const resetAllButton = () => {
    try {
      const defaultState: Record<string, NumberKeyState> = {};
      for (let i = 1; i <= maxNumberKeys; i++) {
        defaultState[i.toString()] = {
          keyNo: i,
          isActive: false,
          isABPoint: false,
          isPickUpState: false,
          timestamp: null,
          timecode: '00:00:00 00',
          videoMode: null,
          frame: null,
          note: '',
          screenshot: '',
          flag: 'off',
          playMode: 'M'
        };
      }
      // MV模式下不重置數字鍵狀態
      if (mButtonState !== 'MV') {
        setNumberKeys(defaultState);
      }

      // 2. 重置 M/AB 鍵狀態（但在 Multiview Tab 時保持 MV 模式）
      if (tabValue !== 6) {
        setMButtonState('M');
      }

      // 3. 清除 D 鍵計時器
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current);
        deleteTimeoutRef.current = null;
      }
      setIsDeleteMode(false);

      // 4. 重置 AB 點
      setABPoints({
        pointA: { key: null, timestamp: null },
        pointB: { key: null, timestamp: null }
      });

      // 5. 顯示重置成功消息
      setSnackbarMessage('所有按鈕已重置');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('重置按鈕時發生錯誤:', error);
      setSnackbarMessage('重置按鈕失敗');
      setSnackbarOpen(true);
    }
  };

  // 版本管理處理函數（用於NoteRecordsDialog）
  const handleVersionManagement = useCallback(async () => {
    if (!videoUrl || !user?.uid) {
      console.log('❌ 版本管理需要登入用戶和影片URL');
      return;
    }

    try {
      // 提取影片ID
      let videoId = videoUrl;
      if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
        const extractYouTubeId = (url: string): string | null => {
          const match = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/|live\/)([a-zA-Z0-9_-]{11})/);
          return match ? match[1] : null;
        };
        const extractedId = extractYouTubeId(videoUrl);
        if (extractedId) {
          videoId = extractedId;
        }
      }

      // 獲取當前版本信息
      const { getCurrentVersionInfo, getUserVideoVersions } = await import('../utils/vbidIO');
      const versionInfo = await getCurrentVersionInfo(videoId, user.email);
      
      if (versionInfo) {
        setCurrentVersion(versionInfo.currentVersion);
        
        // 獲取版本建立時間
        try {
          const versions = await getUserVideoVersions(user.email, videoId);
          if (versions.length > 0) {
            const latestVersion = versions[versions.length - 1];
            setVersionCreatedAt(latestVersion.data.addedDate || latestVersion.data.createdAt || '');
          } else {
            setVersionCreatedAt('');
          }
        } catch (error) {
          console.error('獲取版本建立時間失敗:', error);
          setVersionCreatedAt('');
        }
        
        openDialog('versionManagement');
        // 調試日誌已移除
      } else {
        setCurrentVersion('v1');
        setVersionCreatedAt('');
        openDialog('versionManagement');
      }
    } catch (error) {
      console.error('❌ 版本管理處理失敗:', error);
      setCurrentVersion('v1');
      openDialog('versionManagement');
    }
  }, [videoUrl, user]);

  // 版本操作處理函數
  const handleVersionAction = useCallback(async (
    action: 'create_new_reset' | 'create_new_keep' | 'collaborative',
    version: string
  ) => {
    if (!videoUrl || !user?.uid) {
      console.log('❌ 版本操作需要登入用戶和影片URL');
      return;
    }

    try {
      // 提取影片ID
      let videoId = videoUrl;
      if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
        const extractYouTubeId = (url: string): string | null => {
          const match = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/|live\/)([a-zA-Z0-9_-]{11})/);
          return match ? match[1] : null;
        };
        const extractedId = extractYouTubeId(videoUrl);
        if (extractedId) {
          videoId = extractedId;
        }
      }

      const { createNewVersionReset, createNewVersionKeep, enableCollaborativeMode } = await import('../utils/vbidIO');
      let newDocId: string | null = null;

      switch (action) {
        case 'create_new_reset':
          newDocId = await createNewVersionReset(videoId, user.email);
          // 調試日誌已移除
          break;
        case 'create_new_keep':
          newDocId = await createNewVersionKeep(videoId, user.email);
          // 調試日誌已移除
          break;
        case 'collaborative':
          await enableCollaborativeMode(videoId, user.email, currentVersion);
          // 成功日誌已移除
          break;
      }

      // 通知父組件版本已變更
      if (newDocId && onVersionChange) {
        onVersionChange(newDocId);
      }

      // 重新載入頁面以更新數據
      if (action !== 'collaborative') {
        window.location.reload();
      }
    } catch (error) {
      console.error('❌ 版本操作失敗:', error);
    }
  }, [videoUrl, user, currentVersion]);

  // 版本變更處理函數
  const handleVersionChange = useCallback(async (newDocId: string) => {
    try {
      // 調試日誌已移除
      
      // 重新載入當前影片的數據
      if (videoUrl && user?.email) {
        // 狀態追蹤日誌已移除
        
        // 提取影片ID
        let videoId = videoUrl;
        if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
          const extractYouTubeId = (url: string): string | null => {
            const match = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/|live\/)([a-zA-Z0-9_-]{11})/);
            return match ? match[1] : null;
          };
          const extractedId = extractYouTubeId(videoUrl);
          if (extractedId) {
            videoId = extractedId;
          }
        }
        
        // 從新版本載入數據
        const { getSpecificVersion } = await import('../utils/vbidIO');
        const versionMatch = newDocId.match(/_v(\d+)$/);
        if (versionMatch) {
          const version = `v${versionMatch[1]}`;
          const versionData = await getSpecificVersion(user.email, videoId, version);
          
          if (versionData) {
            // 調試日誌已移除
            
            // 更新狀態
            if (versionData.data.vbids) {
              setVbids(versionData.data.vbids);
            }
            if (versionData.data.numberKeys) {
              setNumberKeys(versionData.data.numberKeys);
            }
            
            setSnackbarMessage(`已切換到版本 ${version}`);
            setSnackbarOpen(true);
          }
        }
      }
    } catch (error) {
      console.error('❌ 版本變更處理失敗:', error);
      setSnackbarMessage('版本切換失敗');
      setSnackbarOpen(true);
    }
  }, [videoUrl, user]);

  // 協作模式處理函數
  const handleCollaborationAction = useCallback(async (
    action: 'create_new_reset' | 'create_new_keep' | 'collaborative'
  ) => {
    // 調試日誌已移除
    try {
      if (!user?.uid || !originalDocumentInfo) {
        console.log('❌ 缺少必要參數:', { user: user?.email, originalDocumentInfo });
        return;
      }
      
      const { 
        createUserVersionReset, 
        createUserVersionKeep, 
        enterCollaborativeMode,
        getOriginalDocumentData
      } = await import('../utils/collaborationIO');
      
      // 獲取原始文檔數據
      const originalData = await getOriginalDocumentData(
        originalDocumentInfo.videoId,
        originalDocumentInfo.userId,
        originalDocumentInfo.version
      );
      
      if (!originalData) {
        setSnackbarMessage('無法獲取原始文檔數據');
        setSnackbarOpen(true);
        return;
      }
      
      let newDocId: string;
      
      if (action === 'create_new_reset') {
        newDocId = await createUserVersionReset(
          originalDocumentInfo.videoId,
          user.email,
          originalData,
          originalFileName,
          selectedFile?.size
        );
        setSnackbarMessage('已創建屬於您的新版本（重點歸零）');
      } else if (action === 'create_new_keep') {
        newDocId = await createUserVersionKeep(
          originalDocumentInfo.videoId,
          user.email,
          originalData,
          originalFileName,
          selectedFile?.size
        );
        setSnackbarMessage('已創建屬於您的新版本（保留重點）');
      } else if (action === 'collaborative') {
        newDocId = await enterCollaborativeMode(
          originalDocumentInfo.videoId,
          originalDocumentInfo.userId,
          originalDocumentInfo.version,
          user.email
        );
        setSnackbarMessage('已進入協同作業模式');
      }
      
      // 更新當前文檔ID
      setCurrentDocumentId(newDocId);
      
      // 重新載入數據
      if (videoUrl) {
        await loadVideo(videoUrl);
      }
      
      // 關閉協作模式對話框
      closeDialog('collaboration');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('協作模式操作失敗:', error);
      setSnackbarMessage('協作模式操作失敗');
      setSnackbarOpen(true);
    }
  }, [user, originalDocumentInfo, originalFileName, selectedFile?.size, videoUrl]);

  const handleCheckClick = async () => {
    try {
      console.log('CHECK button clicked');
      
      // 🔧 新增：檢查用戶登入狀態，未登入用戶顯示登入對話框
      if (!user?.uid) {
        // 調試日誌已移除
        openDialog('auth');
        return;
      }
      
      // 🔧 暫時註解：檢查是否為其他用戶的文檔，如果是則顯示協作模式對話框
      // 目前使用「觀點」功能來控管版本，不再使用 revise 功能
      /*
      if (currentDocumentId && videoUrl) {
        const { 
          isDocumentFromOtherUser, 
          checkUserHasVersion, 
          checkCollaborativeMode 
        } = await import('../utils/collaborationIO');
        const videoId = extractYouTubeId(videoUrl) || videoUrl;
        const docInfo = await isDocumentFromOtherUser(videoId, user.email, currentDocumentId);
        
        if (docInfo.isOtherUser) {
          // 調試日誌已移除
            // 設置原始文檔信息
            setOriginalDocumentInfo({
              userId: docInfo.originalUserId!,
              version: docInfo.originalVersion!,
              videoId: videoId
            });
            
            // 顯示協作模式對話框
            openDialog('collaboration');
          } catch (error) {
            console.error('檢查協作模式狀態失敗:', error);
            setCollaborationState(prev => ({ ...prev, isLoading: false }));
            setSnackbarMessage('檢查協作模式狀態失敗');
            setSnackbarOpen(true);
          }
          return;
        }
      }
      */
      
      
      playButtonSound('click');
      
      // MV 模式特殊處理
      if (mButtonState === 'MV') {
        if (!videoUrl) {
          setSnackbarMessage('請先輸入影片 URL');
          setSnackbarOpen(true);
          return;
        }
        
        // 根據當前 Tab 選擇正確的數字鍵狀態
        const isYouTubeTab = tabValue === 3;
        const currentNumberKeys = isYouTubeTab ? numberKeys : tabNumberKeys[tabValue];
        
        // 找到已記錄的MV影片數量
        const recordedMVVideos = Object.entries(currentNumberKeys)
          .filter(([key, state]) => state.isActive && state.mvUrl)
          .map(([key]) => parseInt(key))
          .sort((a, b) => a - b);
        
        const nextVideoNumber = recordedMVVideos.length + 1;
        const nextKeyNumber = nextVideoNumber;
        
        console.log('MV mode - recording URL for key:', nextKeyNumber, 'URL:', videoUrl);
        // 調試日誌已移除
        
        if (isYouTubeTab) {
          setNumberKeys(prev => ({
            ...prev,
            [nextKeyNumber]: {
              ...prev[nextKeyNumber],
              isActive: true,
              timestamp: null, // MV 模式不需要時間戳
              frame: null,
              videoMode: 'Multiview',
              isPickUpState: false,
              isABPoint: false,
              keyNo: nextKeyNumber,
              mvUrl: videoUrl, // 儲存影片 URL
              mvTitle: '', // 可以後續添加標題
              mvDescription: '' // 可以後續添加描述
            }
          }));
        } else {
          setTabNumberKeys(prev => {
            const newState = { ...prev };
            newState[tabValue] = { ...newState[tabValue] };
            newState[tabValue][nextKeyNumber] = {
              ...newState[tabValue][nextKeyNumber],
              isActive: true,
              timestamp: null, // MV 模式不需要時間戳
              frame: null,
              videoMode: 'Multiview',
              isPickUpState: false,
              isABPoint: false,
              keyNo: nextKeyNumber,
              mvUrl: videoUrl, // 儲存影片 URL
              mvTitle: '', // 可以後續添加標題
              mvDescription: '' // 可以後續添加描述
            };
            return newState;
          });
        }
        
        // 動態計算組索引
        const targetKeySetIndex = Math.floor((nextKeyNumber - 1) / 6);
        setKeySetIndex(targetKeySetIndex);
        
        // 清空URL輸入框，準備下一支影片
        setVideoUrl('');
        
        // 顯示下一支影片的提示
        const nextVideoNumberForDisplay = nextVideoNumber + 1;
        setSnackbarMessage(`已記錄第 ${nextVideoNumber} 支影片，請輸入第 ${nextVideoNumberForDisplay} 支影片，完成後請按下愛心鈕儲存`);
        setSnackbarOpen(true);
        
        return;
      }
      
      // 原有的時間碼記錄邏輯（非 MV 模式）
      if (!playerRef.current) {
        console.log('Player not ready');
        return;
      }
      // 🔧 修復：使用 playedSeconds 狀態而非 getCurrentTime() 避免返回 0 的問題
      let currentTime: number;
      
      // 優先使用 playedSeconds 狀態，它通過 onProgress 回調實時更新，更可靠
      if (playedSeconds > 0) {
        currentTime = playedSeconds;
        // 調試日誌已移除
      } else {
        // 如果 playedSeconds 為 0，嘗試從播放器獲取（但通常不可靠）
        try {
          currentTime = playerRef.current.getCurrentTime();
          if (currentTime === undefined || currentTime === null || isNaN(currentTime) || currentTime === 0) {
            console.warn('⚠️ 播放器時間為 0 或無效，請等待影片開始播放');
            setSnackbarMessage('請等待影片開始播放後再創建重點');
            setSnackbarOpen(true);
            return;
          }
          console.log('⚠️ 使用 getCurrentTime() 備用方案:', currentTime);
        } catch (error) {
          console.error('❌ 獲取當前時間失敗:', error);
          setSnackbarMessage('無法獲取播放時間，請等待影片載入完成');
          setSnackbarOpen(true);
          return;
        }
      }
      
      const currentFrame = calculateFrameFromTime(currentTime);
      // 調試日誌已移除
      checkClickCount.current += 1;
      if (checkClickTimer.current) {
        clearTimeout(checkClickTimer.current);
      }
      checkClickTimer.current = setTimeout(() => {
        console.log('Timer executed with click count:', checkClickCount.current);
        
        // 根據當前 Tab 選擇正確的數字鍵狀態
        const isYouTubeTab = tabValue === 3;
        const currentNumberKeys = isYouTubeTab ? numberKeys : tabNumberKeys[tabValue];
        
        // 首先嘗試找到空白鍵 - 修正邏輯，檢查 1 到 maxNumberKeys 的所有鍵
        const allPossibleKeys = Array.from({ length: maxNumberKeys }, (_, i) => i + 1);
        const usedKeys = Object.entries(currentNumberKeys)
          .filter(([key, state]) => (state as any).isActive && parseInt(key) > 0)
          .map(([key]) => parseInt(key))
          .filter(key => !isNaN(key))
          .sort((a, b) => a - b);
        
        console.log('Used keys:', usedKeys);
        
        // 找到第一個未使用的鍵號
        const emptyKeys = allPossibleKeys.filter(key => !usedKeys.includes(key));
        console.log('Empty keys:', emptyKeys);
        
        let selectedKey: number;
        if (emptyKeys.length > 0) {
          selectedKey = emptyKeys[0]; // 選擇最小的空白鍵
          console.log('Selected empty key:', selectedKey);
        } else {
          // 如果沒有空白鍵，使用下一個可用的鍵號
          if (usedKeys.length > 0) {
            const maxUsedKey = Math.max(...usedKeys);
            selectedKey = maxUsedKey + 1;
            console.log('No empty keys, using next available key:', selectedKey);
          } else {
            selectedKey = 1;
            console.log('No used keys, starting with key 1');
          }
        }
        
        // 檢查是否有跳過的鍵號 - 但允許使用空鍵
        const allActiveKeys = Object.entries(currentNumberKeys)
          .filter(([key, state]) => (state as any).isActive && parseInt(key) > 0)
          .map(([key]) => parseInt(key))
          .filter(key => !isNaN(key))
          .sort((a, b) => a - b);
        
        console.log('All active keys:', allActiveKeys);
        
        // 只有在沒有空鍵時才強制連續
        if (allActiveKeys.length > 0 && emptyKeys.length === 0) {
          const expectedNextKey = Math.max(...allActiveKeys) + 1;
          if (selectedKey !== expectedNextKey) {
            console.log(`⚠️ 沒有空鍵，檢測到鍵號跳過: 預期=${expectedNextKey}, 實際=${selectedKey}`);
            selectedKey = expectedNextKey;
            // 調試日誌已移除
          }
        } else if (emptyKeys.length > 0) {
          // 調試日誌已移除
        }
        
        // 添加詳細的調試信息
        // 調試日誌已移除
        // 調試日誌已移除
        
        // 如果選擇的鍵號超過當前的 maxNumberKeys，自動擴展
        // 調試日誌已移除
        if (selectedKey > maxNumberKeys) {
          const newMaxKeys = Math.ceil(selectedKey / 6) * 6; // 擴展到 6 的倍數
          // 調試日誌已移除
          handleMaxNumberKeysChange(newMaxKeys);
        } else {
          console.log(`❌ 不需要擴展: selectedKey=${selectedKey} <= maxNumberKeys=${maxNumberKeys}`);
        }
        
        // 移除強制擴展邏輯，讓數字鍵只在真正需要時才擴展
        // 這樣可以避免在只有少數重點時就擴展到 36 個鍵
          
          if (isYouTubeTab) {
            setNumberKeys(prev => {
              console.log('Updating number keys state');
              const newState: Record<number, NumberKeyState> = { ...prev };
              
              // 先清除所有鍵的紅色狀態，確保同時只有1個紅色鍵
              // 調試日誌已移除
              Object.keys(newState).forEach(key => {
                if (newState[parseInt(key)]) {
                  if (newState[parseInt(key)].isPickUpState) {
                    // 調試日誌已移除
                  }
                  newState[parseInt(key)].isPickUpState = false;
                }
              });
              
              // 設置新選中的鍵為紅色狀態
              newState[selectedKey] = {
                ...newState[selectedKey],
                isActive: true,
                timestamp: currentTime,
                frame: currentFrame,
                videoMode: getCurrentVideoMode(),
                isPickUpState: true, // 新加入的藍色鍵同時也呈現被 Pickup 的紅色狀態
                isABPoint: false,
                keyNo: selectedKey,
                flag: 'on' // 添加 flag: 'on' 確保 checkpoint 會被轉換為 vbids
              };
              return newState;
            });
          } else {
            setTabNumberKeys(prev => {
              console.log('Updating tab number keys state');
              const newState = { ...prev };
              newState[tabValue] = { ...newState[tabValue] };
              
              // 先清除當前 Tab 中所有鍵的紅色狀態，確保同時只有1個紅色鍵
              // 調試日誌已移除
              Object.keys(newState[tabValue]).forEach(key => {
                if (newState[tabValue][parseInt(key)]) {
                  if (newState[tabValue][parseInt(key)].isPickUpState) {
                    // 調試日誌已移除
                  }
                  newState[tabValue][parseInt(key)].isPickUpState = false;
                }
              });
              
              // 設置新選中的鍵為紅色狀態
              newState[tabValue][selectedKey] = {
                ...newState[tabValue][selectedKey],
                isActive: true,
                timestamp: currentTime,
                frame: currentFrame,
                videoMode: getCurrentVideoMode(),
                isPickUpState: true, // 新加入的藍色鍵同時也呈現被 Pickup 的紅色狀態
                isABPoint: false,
                keyNo: selectedKey,
                flag: 'on' // 添加 flag: 'on' 確保 checkpoint 會被轉換為 vbids
              };
              return newState;
            });
          }
          
          // 動態計算組索引
          const targetKeySetIndex = Math.floor((selectedKey - 1) / 6);
          setKeySetIndex(targetKeySetIndex);
          
          // 即時保存新的 checkpoint 到 Firestore
          const saveNewCheckpointToFirestore = async () => {
            try {
              // 🔧 修復：確保包含所有已存在的 checkpoints
              const currentState = tabNumberKeys[tabValue] || {};
              const existingNumberKeys = numberKeys || {};
              
              // 🔧 關鍵修復：確保所有已存在的重點都保持 isActive: true
              const tempNumberKeys = { ...existingNumberKeys };
              
              // 從 currentState 合併，但確保 isActive 狀態正確
              Object.keys(currentState).forEach(key => {
                if (currentState[key] && currentState[key].isActive) {
                  tempNumberKeys[key] = {
                    ...tempNumberKeys[key],
                    ...currentState[key],
                    isActive: true // 🔧 確保 isActive 狀態正確
                  };
                }
              });
              
              
              // 先清除所有鍵的紅色狀態，確保同時只有1個紅色鍵
              Object.keys(tempNumberKeys).forEach(key => {
                if (tempNumberKeys[parseInt(key)]) {
                  tempNumberKeys[parseInt(key)].isPickUpState = false;
                }
              });
              
                        // 添加新的 checkpoint
          tempNumberKeys[selectedKey] = {
            ...tempNumberKeys[selectedKey],
            isActive: true,
            timestamp: currentTime,
            frame: currentFrame,
            videoMode: getCurrentVideoMode(),
            isPickUpState: true, // 新加入的藍色鍵同時也呈現被 Pickup 的紅色狀態
            isABPoint: false,
            keyNo: selectedKey,
            flag: 'on' // 添加 flag: 'on' 確保 checkpoint 會被轉換為 vbids
          };
      
      
      // 🔧 新增：在清空字幕內容之前，先保存當前數字鍵的 Note 內容
      if (currentSubtitleKey !== null && subtitleContent.trim() !== '') {
        // 調試日誌已移除
        
        // 更新本地狀態
        tempNumberKeys[currentSubtitleKey] = {
          ...tempNumberKeys[currentSubtitleKey],
          note: subtitleContent
        };
        
        // 保存到 Firestore
        saveNoteToFirestore(currentSubtitleKey, subtitleContent, tabValue);
      }
      
      // 🔧 立即更新本地 numberKeys 狀態，確保字幕白板能立即顯示時間碼
      setNumberKeys(prev => {
        const newState = { ...prev };
        newState[selectedKey] = {
          ...newState[selectedKey],
          isActive: true,
          timestamp: currentTime,
          frame: currentFrame,
          videoMode: getCurrentVideoMode(),
          isPickUpState: true,
          isABPoint: false,
          keyNo: selectedKey,
          flag: 'on',
          timecode: formatTimecode(currentTime, currentFrame) // 添加 timecode 欄位
        };
        
        // 🔧 新增：確保保留之前保存的 Note 內容
        if (currentSubtitleKey !== null && subtitleContent.trim() !== '') {
          newState[currentSubtitleKey] = {
            ...newState[currentSubtitleKey],
            note: subtitleContent
          };
        }
        
        // 狀態變化（日誌已移除）
        return newState;
      });
      
      // 🔧 強制更新字幕白板焦點到新生成的數字鍵
      setCurrentSubtitleKey(selectedKey);
      // 調試日誌已移除
      
      // 🔧 清空字幕內容，準備輸入新的字幕
      setSubtitleContent('');
      // 狀態追蹤日誌已移除
      
      // 🔧 新增：全自動字幕填充功能 - 在 CHECK 鍵創建新重點後自動填充
      if (isAutoFillMode && hasSRTCache && srtContent && srtContent !== '_READY_' && currentTime !== null) {
        console.log(`🤖 全自動填充模式：為新創建的數字鍵 ${selectedKey} 自動填充字幕`);
        console.log(`   條件: isAutoFillMode=${isAutoFillMode}, hasSRTCache=${hasSRTCache}, srtContent=${srtContent?.length || 0} bytes, currentTime=${currentTime?.toFixed(2)}s`);
        
        // 延遲一點時間確保 UI 更新完成
        setTimeout(async () => {
          console.log(`⏰ [CHECK 自動填充] setTimeout 開始執行，數字鍵: ${selectedKey}`);
          try {
            console.log(`🔍 [CHECK 自動填充] 當前 srtContent 狀態:`, {
              hasSrtContent: !!srtContent,
              srtContentLength: srtContent?.length,
              isReady: srtContent === '_READY_'
            });
            
            // 使用現有的 fillNoteWithSRT 邏輯
            const { findClosestSRTEntries, formatSRTEntriesSimple } = await import('../utils/srtUtils');
            
            // 將 timestamp 轉換為 timecode 格式
            const seconds = currentTime;
            const totalSeconds = Math.floor(seconds);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const secs = totalSeconds % 60;
            const milliseconds = Math.floor((seconds % 1) * 1000);
            
            const pad = (num: number) => num.toString().padStart(2, '0');
            const searchTimecode = `${pad(hours)}:${pad(minutes)}:${pad(secs)},${milliseconds.toString().padStart(3, '0')}`;
            
            const closestEntries = findClosestSRTEntries(srtContent, searchTimecode, 1, subtitleFillLines - 2);
            
            if (closestEntries.length > 0) {
              const finalNoteText = formatSRTEntriesSimple(closestEntries);
              setSubtitleContent(finalNoteText);
              setSubtitleContentWithSource({
                text: finalNoteText,
                isFromSRT: true
              });
              
              // 同時保存到 Firestore
              saveNoteToFirestore(selectedKey, finalNoteText, tabValue);
              
              // 調試日誌已移除
            }
          } catch (error) {
            console.error('🤖 全自動填充失敗:', error);
          }
        }, 300); // 增加延遲時間，確保 currentSubtitleKey 更新完成
      }
      
      const vbids = convertNumberKeysToVBIDs(tempNumberKeys, getCurrentVideoMode() || 'YouTube', mButtonState);
      
      if (vbids.length > 0) {
        try {
          await saveVbidsToFirestore({
            vbids,
            user,
            videoUrl,
            currentMode: getCurrentVideoMode() || 'YouTube',
            numberKeys: tempNumberKeys,
            mButtonState,
            checkpointCount: vbids.length,
            fileName: originalFileName,
            fileSize: selectedFile?.size,
            fileRelativePath: fileRelativePath,
            documentId: currentFirestoreId  // 🔧 傳遞觀點文檔 ID
          });
          // 調試日誌已移除
          
          // 🔧 新增：通知 ManagePages 清除快取，確保統計數據同步
          sessionStorage.setItem('clearManagePagesCache', Date.now().toString());
          // 調試日誌已移除
        } catch (error) {
          if (!handlePermissionError(error)) {
            console.error('❌ 保存 Check 鍵新增的 checkpoint 失敗:', error);
          }
          // 🔧 暫時註解：協作模式檢查，目前使用「觀點」功能來控管版本
          /*
          if (error instanceof Error && error.message === 'COLLABORATION_REQUIRED') {
            console.log('需要協作模式，顯示協作模式對話框');
            
            // 設置加載狀態
            setCollaborationState(prev => ({ ...prev, isLoading: true }));
            
            try {
              const { 
                checkUserHasVersion, 
                checkCollaborativeMode 
              } = await import('../utils/collaborationIO');
              const videoId = extractYouTubeId(videoUrl) || videoUrl;
              
              // 檢查用戶是否已有該影片的版本
              const versionInfo = await checkUserHasVersion(videoId, user.email);
              
              // 從錯誤中獲取原始用戶信息
              const originalUserId = (error as any).originalUserId || 'unknown';
              const originalVersion = (error as any).originalVersion || 'v1';
              
              // 檢查協同作業模式是否已啟用
              const isCollaborativeEnabled = await checkCollaborativeMode(
                videoId, 
                originalUserId,
                originalVersion
              );
              
              // 設置協作模式狀態
              setCollaborationState({
                hasExistingVersion: versionInfo.hasVersion,
                isCollaborativeEnabled: isCollaborativeEnabled,
                isLoading: false
              });
              
              // 設置原始文檔信息
              setOriginalDocumentInfo({
                userId: originalUserId,
                version: originalVersion,
                videoId: videoId
              });
              
              // 顯示協作模式對話框
              openDialog('collaboration');
              return;
            } catch (collabError) {
              console.error('檢查協作模式狀態失敗:', collabError);
              setCollaborationState(prev => ({ ...prev, isLoading: false }));
              setSnackbarMessage('需要選擇協作模式');
              setSnackbarOpen(true);
            }
            return;
          }
          */
          throw error; // 重新拋出其他錯誤
        }
        
        // 🔧 同步更新 tabNumberKeys，確保數據一致性
        setTabNumberKeys(prev => {
          const newState = { ...prev };
          newState[tabValue] = tempNumberKeys;
          // 調試日誌已移除
          return newState;
        });
        
        // 🔧 同步更新快取，確保數據一致性
        if (id) {
          const cacheKey = `firestore_${id}`;
          const now = Date.now();
          
          // 🔧 修復：獲取檔案的修改日期作為發佈日期
          const fileAddedDate = await getFileAddedDate(originalFileName || fileRelativePath);
          
          // 更新快取數據 - 確保包含所有已存在的 checkpoint
          const allVbids = convertNumberKeysToVBIDs(tempNumberKeys, getCurrentVideoMode() || 'YouTube', mButtonState);
          const updatedCacheData = {
            path: videoUrl,
            mediaMode: getCurrentVideoMode() || 'YouTube',
            numberKeys: tempNumberKeys,
            vbids: allVbids,  // 使用完整的 vbids，包含所有 checkpoint
            checkpointCount: allVbids.length,
            userId: user?.uid,
            addedDate: fileAddedDate
          };
          
          sessionStorage.setItem(cacheKey, JSON.stringify(updatedCacheData));
          sessionStorage.setItem(`${cacheKey}_timestamp`, now.toString());
          // 狀態追蹤日誌已移除
        }
      } else {
        console.warn('⚠️ vbids.length 為 0，跳過 Firestore 保存');
      }
    } catch (error) {
      console.error('❌ 保存 Check 鍵新增的 checkpoint 失敗:', error);
    }
          };
      
      // 立即保存，使用計算出的狀態
      saveNewCheckpointToFirestore();
        if (checkClickCount.current === 1) {
          pauseVideo();
        } else if (checkClickCount.current >= 2) {
          if (!isPlaying) setIsPlaying(true);
        }
        checkClickCount.current = 0;
        checkClickTimer.current = null;
      }, 300);
    } catch (e) {
      alert('錯誤：' + e);
      console.error(e);
    }
  };

  // 確保 handlePreviewImage 存在，作為 handlePreviewClick 的別名
  const handlePreviewImage = handlePreviewClick;

  // 新增 handleDownloadImage 函數
  const handleDownloadImage = (image: ImageData) => {
    const link = document.createElement('a');
    link.download = image.name;
    link.href = image.url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleMyFavorite = () => {
    // 載入日誌已移除
    
    // 🔧 新增：獲取用戶最後訪問的分頁
    const lastVisitedSection = localStorage.getItem('lastVisitedSection');
    const targetSection = lastVisitedSection || 'personalHistory'; // 預設為瀏覽記錄
    // 調試日誌已移除
    
    // 如果是MV模式，確保所有MV影片都被記錄
    if (mButtonState === 'MV') {
      // 記錄日誌已移除
      
      // 獲取所有MV模式的VBIDs
      const allMVVbids = convertNumberKeysToVBIDs(numberKeys, videoMode, mButtonState);
      // 調試日誌已移除
      
      // 更新currentVbids，包含所有MV影片
      const updatedCurrentVbids = [...currentVbids, ...allMVVbids];
      // 調試日誌已移除
      
      // 備份主頁狀態
      const stateToBackup = {
        videoUrl,
        tabValue,
        currentVbids: updatedCurrentVbids,
        tabVbids,
        numberKeys,
        // ...如有其他要還原的 state 也可加進來
      };
      localStorage.setItem('mainPageStateBackup', JSON.stringify(stateToBackup));
      sessionStorage.setItem('mainPageState', JSON.stringify(stateToBackup));

      const navigationState = { 
        currentVbids: updatedCurrentVbids,
        currentSection: targetSection // 🔧 使用記錄的最後訪問分頁
      };
      
      // 調試日誌已移除
      
      // 跳轉到 /favorite 並攜帶 currentVbids 和 currentSection
      navigate('/favorite', { 
        state: navigationState
      });
      
      // 🔧 新增：更新瀏覽器歷史記錄，為智能上一頁功能做準備
      setTimeout(() => {
        window.history.replaceState(
          { currentSection: targetSection }, 
          '', 
          `/manage?section=${targetSection}`
        );
        // 調試日誌已移除
      }, 100);
      
      // 成功日誌已移除
    } else {
      // 原有的邏輯
      const stateToBackup = {
        videoUrl,
        tabValue,
        currentVbids,
        tabVbids,
        numberKeys,
        // ...如有其他要還原的 state 也可加進來
      };
      localStorage.setItem('mainPageStateBackup', JSON.stringify(stateToBackup));
      sessionStorage.setItem('mainPageState', JSON.stringify(stateToBackup));

      const navigationState = { 
        currentVbids,
        currentSection: targetSection // 🔧 使用記錄的最後訪問分頁
      };
      
      // 調試日誌已移除
      
      // 跳轉到 /favorite 並攜帶 currentVbids 和 currentSection
      navigate('/favorite', { 
        state: navigationState
      });
      
      // 🔧 新增：更新瀏覽器歷史記錄，為智能上一頁功能做準備
      setTimeout(() => {
        window.history.replaceState(
          { currentSection: targetSection }, 
          '', 
          `/manage?section=${targetSection}`
        );
        // 調試日誌已移除
      }, 100);
      
      // 成功日誌已移除
    }
  };

  // 添加影片管理導航函數
  const handleNavigateToManagePages = () => {
    // 處理日誌已移除
    // 調試日誌已移除
    // 調試日誌已移除
    
    try {
      // 🔧 新增：獲取用戶最後訪問的分頁
      const lastVisitedSection = localStorage.getItem('lastVisitedSection');
      const targetSection = lastVisitedSection || 'myVideos'; // 預設為我的創作
      // 調試日誌已移除
      
      // 備份主頁狀態（與 handleMyFavorite 保持一致）
      const stateToBackup = {
        videoUrl,
        tabValue,
        currentVbids,
        tabVbids,
        numberKeys,
      };
      localStorage.setItem('mainPageStateBackup', JSON.stringify(stateToBackup));
      sessionStorage.setItem('mainPageState', JSON.stringify(stateToBackup));

      const navigationState = { 
        currentVbids,
        currentSection: targetSection // 🔧 使用記錄的最後訪問分頁
      };
      
      // 調試日誌已移除
      
      // 使用與 handleMyFavorite 相同的路徑 /favorite
      navigate('/favorite', { 
        state: navigationState
      });
      
      // 🔧 新增：更新瀏覽器歷史記錄，為智能上一頁功能做準備
      setTimeout(() => {
        window.history.replaceState(
          { currentSection: targetSection }, 
          '', 
          `/manage?section=${targetSection}`
        );
        // 調試日誌已移除
      }, 100);
      
      // 調試日誌已移除
    } catch (error) {
      console.error('🎬 navigate 錯誤:', error);
    }
  };

  // 新的 handleShare，使用 UnifiedShareDialog
  const handleShare = () => {
    // 調試日誌已移除
    if (!videoUrl) {
      console.log('❌ 沒有 videoUrl，無法分享');
      return;
    }
    // 成功日誌已移除
    openDialog('share');
  };

  // 取得目前可分享的重點（activeTimecodesWithFlag）
  const getVbidsForShare = () => {
    const vbids = convertNumberKeysToVBIDs(numberKeys, videoMode || 'VIDEO', mButtonState);
    return vbids.map(vbid => ({
      timecode: vbid.timecode,
      note: vbid.note || '',
      screenshot: vbid.screenshot || '',
      isActive: vbid.flag === 'on',
      keyNo: vbid.keyNo,
      mediaMode: vbid.mediaMode, // 改這裡
      flag: vbid.flag
    }));
  };

  const handleMyFav = () => {
    console.warn('Using deprecated handleMyFav from components/MainPage.tsx. Please use handleMyFavorite from pages/MainPage.tsx');
    
    // 備份主頁狀態
    const stateToBackup = {
      videoUrl,
      tabValue,
      currentVbids,
      tabVbids,
      numberKeys,
      // ...如有其他要還原的 state 也可加進來
    };
    localStorage.setItem('mainPageStateBackup', JSON.stringify(stateToBackup));
    sessionStorage.setItem('mainPageState', JSON.stringify(stateToBackup));

    // 跳轉到 /favorite 並攜帶 currentVbids
    navigate('/favorite', { state: { currentVbids } });
  };

  function filterYoutubeUrl(url: string): string {
    try {
      url = url.trim().replace(/^@/, ''); // 移除開頭的 @
      if (url.includes('youtu.be')) {
        // youtu.be/VIDEO_ID?xxx 或 youtu.be/VIDEO_ID&xxx
        const videoId = url.split('youtu.be/')[1]?.split(/[?&]/)[0];
        if (videoId) return `https://www.youtube.com/watch?v=${videoId}`;
      }
      if (url.includes('youtube.com')) {
        const urlObj = new URL(url);
        const videoId = urlObj.searchParams.get('v');
        if (videoId) return `https://www.youtube.com/watch?v=${videoId}`;
      }
      return url;
    } catch {
      return url;
    }
  }


  // 🔧 重構：移除 MainPage 的 BottomBar，統一使用 MainLayout 的 BottomBar
  // MainPage 不再管理自己的 menuItems，所有菜單項由 MainLayout 統一管理
  
  // Drawer states
  
  // 修改標題部分的 JSX
  return (
    <Box sx={{ 
      flexGrow: 1, 
      position: 'relative', 
      minHeight: '100vh',
      pb: 0
    }}>
      

      
      {/* Welcome Dialog */}
      <WelcomeDialog open={dialogs.welcome} onClose={handleWelcomeDialogClose} />

      {/* 檔案選擇對話框 */}
      <FileSelectionDialog
        open={dialogs.fileSelection}
        onClose={() => closeDialog('fileSelection')}
        selectedFileName={selectedFile?.name || null}
        fileRelativePath={fileRelativePath}
        onSmartMatch={handleLocalPlay}
        onUploadToCloud={handleUploadToCloud}
      />

      {/* 上傳進度對話框 */}
      <UploadProgressDialog
        open={isUploading}
        fileName={selectedFile?.name || null}
        progress={uploadProgress}
      />

      {/* 上方 Bar - 手機直式模式支援滑動隱藏 */}
      <AppBar 
        position="fixed" 
        color="default" 
        elevation={1} 
        sx={{ 
          backgroundColor: '#1976d2',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: '100%',
          // 直式佈局時增加AppBar高度，其他佈局保持正常高度
          height: isPortrait ? '60px' : ((aspectRatio > 1.8 && windowDimensions.width > windowDimensions.height) ? '40px' : '56px'),
          // 手機直式模式：支援滑動隱藏，增加互動作效果
          // 滑動效果（手機模式和桌面模式）
          transform: 'translateY(0)',
          transition: 'transform 0.3s ease-in-out',
          '&.scrolled-up': {
            // 手機直式模式：只往下滑48px，不完全隱藏
            transform: (aspectRatio <= 1.8 || (aspectRatio > 1.8 && windowDimensions.width > windowDimensions.height)) 
              ? 'translateY(-100%)'  // 桌面模式/手機橫式：完全隱藏
              : 'translateY(-48px)'  // 手機直式模式：只往下滑48px
          },
          // 增加滑動後的彈性回彈效果
          '&.bounce-back': {
            animation: 'bounceBack 0.6s ease-out'
          },
          // 定義彈性回彈動畫
          '@keyframes bounceBack': {
            '0%': { transform: 'translateY(-100%)' },
            '50%': { transform: 'translateY(-20%)' },
            '100%': { transform: 'translateY(0)' }
          }
        }}
      >
        <Toolbar sx={{ 
          // 直式佈局時增加Toolbar高度，其他佈局保持正常高度
          minHeight: isPortrait ? '60px !important' : ((aspectRatio > 1.8 && windowDimensions.width > windowDimensions.height) ? '40px !important' : '56px !important'),
          height: isPortrait ? '60px' : ((aspectRatio > 1.8 && windowDimensions.width > windowDimensions.height) ? '40px' : '56px'),
          padding: (aspectRatio > 1.8 && windowDimensions.width > windowDimensions.height) ? '4px 8px' : '0 16px'
        }}>
          {/* 返回按鈕 - 只在嵌入式模式且有來源頁面時顯示 */}
          {isEmbedded && sourcePage && onNavigateBack && (
            <Button
              color="inherit"
              onClick={() => onNavigateBack(sourcePage)}
              sx={{
                color: 'white',
                minWidth: 'auto',
                px: 1,
                fontSize: '0.875rem',
                mr: 2
              }}
            >
              ← 返回
            </Button>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, cursor: 'pointer' }} onClick={handleTitleClick}>
            <img 
              src="/logo.png" 
              alt="Check Play Logo" 
              style={{ 
                width: '28px',
                height: 'auto',
                marginRight: '8px'
              }} 
            />
            <Typography
              variant="subtitle1"
              component="div"
              sx={{
                flexGrow: 1,
                fontWeight: 'bold',
                cursor: 'pointer',
                userSelect: 'none',
                fontSize: '1rem',
                '& .english': {
                  color: '#FFFFFF'
                },
                '& .chinese': {
                  color: '#FFFFFF',
                  marginLeft: '4px'
                },
                '& .bold-text': {
                  fontWeight: 'bold',
                  color: '#FFFFFF'
                }
              }}
            >
              <span className="english">CHECK PLAY</span>
              <span className="chinese">｜<span className="bold-text">簡 播</span></span>
            </Typography>
          </Box>
          {isAuthenticated ? (
            <>
              <Typography variant="body2" sx={{ mr: 0.25, color: 'white', fontSize: '0.875rem' }}>
                Hi {(user?.name || user?.email || '').slice(0, 4)}
              </Typography>
              <Button 
                color="inherit" 
                onClick={handleLogoutClick} 
                sx={{ 
                  mr: 0.5,
                  px: 1,
                  minWidth: 'auto',
                  color: 'white',
                  fontSize: '0.875rem',
                  height: '32px'
                }}
              >
                登出
              </Button>
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              <Button color="inherit" onClick={() => openDialog('register')} sx={{ color: '#fff', minWidth: 0, px: 1, fontSize: '0.9rem' }}>
                註冊
              </Button>
              <Typography sx={{ color: '#fff', fontWeight: 400 }}>｜</Typography>
              <Button color="inherit" onClick={handleLogin} sx={{ color: '#fff', minWidth: 0, px: 1, fontSize: '0.9rem' }}>
                登入
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      {/* 移除Toolbar，讓內容更靠近頂部 */}
      {/* 移除 Container 的 maxWidth 限制，讓播放器可以佔據更多空間 */}
      <Box sx={{ 
        width: '100%', 
        // 精確計算頂部邊距，讓父容器貼齊輸入框下方
        mt: (aspectRatio > 1.8 && windowDimensions.width > windowDimensions.height) 
          ? 7.75  // 手機橫式：124px頂部邊距 (AppBar + Tab列 + 輸入框)
          : 7.75,  // 其他模式：124px頂部邊距 (AppBar + Tab列 + 輸入框)
        // 移除所有左右邊距，讓紅色框100% fit視窗寬度
        px: 0, // 所有模式：無邊距，100%寬度
        maxWidth: '100vw', // 允許使用全螢幕寬度
        // 手機直式模式：增加內容高度，支援向上滑動
        '@media (max-width: 800px) and (min-height: 700px)': {
          minHeight: '95vh' // 調整為95vh，支援滑動，移除paddingBottom避免觸發自動滾動
        }
      }}>
        <Box sx={{ width: '100%', mb: 0 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              variant="scrollable" 
              scrollButtons="auto"
              allowScrollButtonsMobile={true}
              aria-label="video source tabs"
              sx={{
                minHeight: '40px', // 增加Tab列高度，從24px增加到40px
                margin: 0, // 強制移除所有margin
                padding: 0, // 強制移除所有padding
                // 滾動按鈕樣式
                '& .MuiTabs-scrollButtons': {
                  color: 'primary.main',
                  '&.Mui-disabled': {
                    opacity: 0.3,
                  },
                },
                '& .MuiTab-root': {
                  minHeight: '40px', // 增加Tab高度，從24px增加到40px
                  padding: '8px 16px', // 增加內邊距，從2px 8px增加到8px 16px
                  fontSize: '1rem', // 放大字體，從0.875rem增加到1rem
                  fontWeight: 500, // 添加字體粗細
                  minWidth: 'auto', // 允許Tab自動調整寬度
                  flexShrink: 0, // 防止Tab被壓縮
                  '@media (max-width: 600px)': {
                    padding: '6px 12px', // 手機版也增加內邊距
                    fontSize: '0.9rem', // 手機版字體也放大
                    minHeight: '36px', // 手機版高度也增加
                  }
                },
                '& .MuiTabs-flexContainer': {
                  margin: 0, // 強制移除flexContainer的margin
                  padding: 0, // 強制移除flexContainer的padding
                },
                '& .MuiTabs-indicator': {
                  margin: 0, // 強制移除indicator的margin
                }
              }}
            >
              <Tab 
                icon={<PlayArrowIcon />} 
                label="SHORT"
                iconPosition="start"
                sx={{ display: 'none' }} // 🔧 暫時隱藏 SHORT Tab
              />
              <Tab 
                icon={<GoogleIcon />} 
                label="GOOGLE"
                iconPosition="start"
                sx={{ display: 'none' }} // 🔧 暫時隱藏 GOOGLE Tab
              />
              <Tab 
                icon={<MovieIcon />} 
                label="VIDEO"
                iconPosition="start"
              />
              <Tab 
                icon={<YouTubeIcon />} 
                label="YouTube"
                iconPosition="start"
              />
              <Tab 
                icon={<PhotoLibraryIcon />} 
                label="PHOTO"
                iconPosition="start"
                sx={{ display: 'none' }} // 🔧 暫時隱藏 PHOTO Tab
              />
              <Tab 
                icon={<AttachFileIcon />} 
                label="PDF"
                iconPosition="start"
                sx={{ display: 'none' }} // 🔧 暫時隱藏 PDF Tab
              />
              <Tab 
                icon={<GridOnIcon />} 
                label="Multiview"
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* 輸入區域 */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ width: '100%', mb: 0 }}>
              <input
                type="file"
                accept="video/mp4,video/x-m4v,video/quicktime,image/jpeg,image/png,image/heic"
                style={{ display: 'none' }}
                id="short-video-input"
                onChange={(e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                    handleFileSelect(file, true);
                  }
                }}
              />
              <label htmlFor="short-video-input">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  sx={{ height: 48, fontSize: '1rem' }} // 增加高度和字體大小
                >
                  {originalFileName || '選擇直式影片或照片（MP4、M4V、MOV、JPG、PNG、HEIC）'}
                </Button>
              </label>
              {error && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {error}
                </Typography>
              )}
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <TextField
              fullWidth
              id="google-drive-url"
              name="googleDriveUrl"
              label="Google Drive 影片網址"
              value={videoUrl}
              onChange={(e) => {
                const url = e.target.value;
                resetAllButton();
                const embedUrl = handleGoogleDriveUrl(url) || '';
                setVideoUrl(embedUrl);
              }}
              placeholder="請輸入 Google Drive 影片分享連結（目前 OAuth 設定有問題，暫時無法使用）"
              size="medium"
              sx={{ 
                mb: 0, // 移除底部margin，消除隱藏空白
                '& .MuiOutlinedInput-root': {
                  height: 48, // 從36px增加到48px
                  fontSize: '1rem' // 放大字體
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '1rem', // 放大輸入文字字體
                  position: 'relative',
                  top: 0 // 調整位置以配合新的高度
                }
              }}
              error={!!error}
              disabled={isLoading}
            />
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress />
              </Box>
            )}
        </TabPanel>

    <TabPanel value={tabValue} index={2}>
      <Box sx={{ width: '100%', mb: 0 }}>
        <input
          type="file"
          accept=".mp4,.m4v,.mpeg,.mov"
          style={{ display: 'none' }}
          id="local-file-input"
          name="localFile"
          onChange={(e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) {
              const validTypes = ['video/mp4', 'video/x-m4v', 'video/mpeg', 'video/quicktime'];
              if (!validTypes.includes(file.type.toLowerCase())) {
                setError('請上傳 MP4、M4V、MPEG 或 MOV 格式的影片');
                return;
              }
              
              // 🔧 修復：檢查是否來自 ManagePages
              const locationState = location.state as any;
              if (locationState?.fromManagePages && locationState?.firestoreId) {
                // 調試日誌已移除
                
                // 創建本地播放 URL
                const url = URL.createObjectURL(file);
                setVideoUrl(url);
                setFileRelativePath(file.name);
                setOriginalFileName(file.name);
                setVideoMode('VIDEO');
                
                // 載入 Firestore 資料
                loadFirestoreData(locationState.firestoreId);
                
                // 清空之前的錯誤
                setError(null);
                return;
              }
              
              // 🔧 新增：選擇新檔案時立即重置數字鍵狀態
              // 狀態變化（日誌已移除）
              
              // 重置 numberKeys 狀態
              setNumberKeys({});
              
              // 重置 tabNumberKeys 中 VIDEO Tab (index 2) 的狀態
              setTabNumberKeys(prev => {
                const newState = { ...prev };
                if (newState[2]) {
                  // 清空 VIDEO Tab 的所有數字鍵狀態
                  newState[2] = {};
                }
                return newState;
              });
              
              // 重置相關狀態
              setCurrentSubtitleKey(null);
              setSubtitleContent('');
              setVbids([]);
              setTabVbids(prev => ({
                ...prev,
                2: [] // 清空 VIDEO Tab 的 vbids
              }));
              
              // 清空影片相關狀態
              setVideoUrl('');
              setFileRelativePath('');
              setOriginalFileName('');
              setVideoMode(null);
              
              // 重置播放相關狀態
              setCurrentTime(0);
              setCurrentTimecode('00:00:00 00');
              setIsPlaying(false);
              
              // 顯示檔案選擇對話框
              setSelectedFile(file);
              openDialog('fileSelection');
              
              // 清空之前的錯誤
              setError(null);
            }
          }}
        />
        <TextField
          fullWidth
          id="video-file-path"
          value={fileRelativePath || ''}
          placeholder="請選擇影片檔案 (MP4、M4V、MPEG、MOV)"
          size="small"
          InputProps={{
            readOnly: true,
            endAdornment: (
              <label htmlFor="local-file-input">
                <Button
                  variant="contained"
                  component="span"
                  size="small"
                  sx={{ ml: 1 }}
                >
                  瀏覽
                </Button>
              </label>
            ),
          }}
          sx={{ 
            '& .MuiOutlinedInput-root': { 
              height: 48, // 從36px增加到48px
              fontSize: '1rem' // 放大字體
            },
            '& .MuiOutlinedInput-input': {
              fontSize: '1rem' // 放大輸入文字字體
            }
          }}
        />
        {/* 拖曳上傳區塊已隱藏 */}
        {/*
        <Box
          sx={{ 
            border: '1px dashed #aaa',
            borderRadius: 2,
            p: 2,
            mt: 2,
            textAlign: 'center',
            color: '#888',
            cursor: 'pointer',
            background: '#fafafa'
          }}
          onDrop={e => {
            e.preventDefault();
            const file = e.dataTransfer.files[0];
            if (file) {
              const validTypes = ['video/mp4', 'video/x-m4v', 'video/mpeg', 'video/quicktime'];
              if (!validTypes.includes(file.type.toLowerCase())) {
                setError('請上傳 MP4、M4V、MPEG 或 MOV 格式的影片');
                return;
              }
              resetAllButton();
              const filePath = file.name;
              const url = URL.createObjectURL(file);
              setVideoUrl(url);
              setFileRelativePath(filePath);
              setOriginalFileName(file.name);
              setVideoMode('VIDEO');
              const fileInput = document.getElementById('video-file-path') as HTMLInputElement;
              if (fileInput) {
                fileInput.value = filePath;
              }
              setError(null);
            }
          }}
          onDragOver={e => e.preventDefault()}
        >
          或拖曳影片檔案到這裡
        </Box>
        */}
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Box sx={{ p: 0, m: 0, pt: 0, pb: 0, mb: 0 }}>
              <TextField
                fullWidth
                id="youtube-video-url"
                name="youtubeVideoUrl"
                label="YouTube 影片網址"
                value={videoUrl}
                onChange={(e) => {
                  const url = e.target.value;
                  const clean = filterYoutubeUrl(url);
                  resetAllButton();
                  setVideoUrl(clean);
                  loadVideo(clean);
                }}
                placeholder="請輸入 YouTube 影片網址"
                size="medium"
                sx={{ 
                  mb: 0, // 移除底部margin，消除與父容器的空白
                  '& .MuiOutlinedInput-root': {
                    height: 48, // 從36px增加到48px
                    fontSize: '1rem' // 放大字體
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '1rem', // 放大輸入文字字體
                    position: 'relative',
                    top: 0 // 調整位置以配合新的高度
                  }
                }}
                onFocus={e => e.target.select()}
              />
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <Box sx={{ width: '100%', mb: 0 }}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/heic"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
                id="photo-upload"
              />
              <label htmlFor="photo-upload">
                <Button
                  component="span"
                  variant="outlined"
                  fullWidth
                  data-mode="PHOTO"
                  sx={{ height: 48, fontSize: '1rem' }} // 增加高度和字體大小
                >
                  {originalFileName || '選擇照片（JPG、PNG、HEIC）'}
                </Button>
              </label>
              {error && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {error}
                </Typography>
              )}
            </Box>
          </TabPanel>

    <TabPanel value={tabValue} index={5}>
            <Box sx={{ width: '100%', mb: 0 }}>
              <input
                type="file"
                accept=".ppt,.pptx,.pdf"
                style={{ display: 'none' }}
                id="document-file-input"
                onChange={(e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                    // 保存檔案名稱和路徑
                    const filePath = file.name;
                    setVideoUrl('');
                    setOriginalFileName(file.name);
                    setFileRelativePath(filePath);

                    // 更新按鈕文字
                    const fileInput = document.querySelector('button[data-mode="PDF"]') as HTMLButtonElement;
                    if (fileInput) {
                      fileInput.textContent = file.name;
                    }
                  }
                }}
              />
              <label htmlFor="document-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  data-mode="PDF"
                  sx={{ height: 48, fontSize: '1rem' }} // 增加高度和字體大小
                >
                  選擇 PPT 或 PDF 檔案
                </Button>
              </label>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={6}>
            <TextField
              fullWidth
              id="multiview-youtube-url"
              name="multiviewYoutubeUrl"
              label="YouTube 影片網址"
              value={videoUrl}
              onChange={(e) => {
                const url = e.target.value;
                const clean = filterYoutubeUrl(url);
                resetAllButton();
                setVideoUrl(clean);
                loadVideo(clean);
              }}
              placeholder="請輸入 YouTube 影片網址"
              size="medium"
              sx={{ 
                mb: 0, // 移除底部margin，消除隱藏空白
                '& .MuiOutlinedInput-root': {
                  height: 48, // 從36px增加到48px
                  fontSize: '1rem' // 放大字體
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '1rem', // 放大輸入文字字體
                  position: 'relative',
                  top: 0 // 調整位置以配合新的高度
                }
              }}
              onFocus={e => e.target.select()}
            />
          </TabPanel>
          
          {/* 影片播放區域 - 響應式佈局：橫式時左右分割，桌面版時維持原樣 */}
          <Box 
            className="player-wrapper" 
                          // 強制內聯樣式，確保手機模式下解除尺寸限制
              style={windowDimensions.width <= 800 && windowDimensions.height < 1200 ? {
                width: '100%',
                // 移除height設定，讓sx中的calc設定生效
                minWidth: 'auto',
                minHeight: 'auto', // 改為auto，移除固定高度限制
                maxWidth: 'none',
                // 移除maxHeight設定，讓sx中的設定生效
                aspectRatio: 'none',
                paddingTop: '0',
              } : {}}
            sx={{
              position: 'relative',
              // 基本樣式：簡化並確保主容器正確顯示
              width: '100%',
              backgroundColor: '#f5f5f5', // 預設淺灰色背景
          // 調試：主容器邊框 - 紅色 (已移除)
          // border: '3px solid #ff0000',
              // 移除所有左右padding和margin，讓紅色框100% fit視窗寬度
              marginLeft: 0,
              marginRight: 0,
              paddingLeft: 0,
              paddingRight: 0,
              // 基本佈局設定：優先判斷直式佈局條件，然後是橫式佈局
              // 直式佈局條件：使用 isPortrait 變量判斷
              display: isPortrait ? 'block' : 'flex',
              flexDirection: isPortrait ? 'column' : 'row', // 直式佈局：垂直排列，橫式佈局：水平排列
              // 調試：強制顯示，確保手機橫式佈局可見
              visibility: 'visible !important',
              opacity: 1,
              // 高度設定：根據佈局類型分別處理
              ...(isPortrait ? {
                // 直式佈局：使用最小高度，允許滾動
                minHeight: Math.max(
                  aspectRatio <= 1.8 
                    ? (windowDimensions.height > 1200 ? windowDimensions.height - 130 : windowDimensions.height - 200) 
                    : (windowDimensions.height >= 1200 ? windowDimensions.height - 128 : windowDimensions.height - 128),
                  330 // 強制最小高度，確保容器可見
                ),
                height: 'auto', // 直式佈局使用自動高度
                overflow: 'auto' // 直式佈局允許滾動
              } : {
                // 橫式佈局：使用固定高度，不允許滾動
                height: windowDimensions.width > 800 
                  ? (windowDimensions.height > 500 ? 'calc(95vh - 130px)' : 'calc(100vh - 500px)') 
                  : (windowDimensions.height >= 500 ? '95vh' : 'calc(95vh - 48px)'),
                minHeight: '330px', // 強制最小高度，確保容器可見
                overflow: 'hidden' // 橫式佈局不允許滾動
              }),
              // 手機直式模式：移除 paddingBottom，減少空白
              ...(aspectRatio > 1.8 && windowDimensions.height > windowDimensions.width ? {
                paddingBottom: '0vh' // 移除多餘的空白
              } : {}),
              // 基本樣式
              borderRadius: '4px', // 圓角邊框
              marginTop: '4px !important', // 父容器設定4px頂部邊距，減少空白
              paddingTop: '0px', // 移除頂部內邊距
              // overflow 已在上面根據佈局類型設定
              boxSizing: 'border-box',

              // 高度和滾動設定已在上面的條件邏輯中處理
            }}
          >
            {/* 左側：影片播放區 (橫式時動態調整，桌面版時100%) */}
            <Box 
                            sx={{
                ...(!isPortrait) ? {
                  flex: controlGroupPosition === 'left' ? '2.33' : '2.33', // 🔧 修改：影片區域佔 2.33 份空間 (70%)
                  order: controlGroupPosition === 'left' ? 2 : 1,
                  position: 'relative',
                  backgroundColor: '#000',
                  borderRadius: 0, // 移除圓角，避免間隔
                  overflow: 'visible',
                  // 調試：橫式佈局播放器區域邊框 - 黃色 (已移除)
                  // border: '3px solid #ffff00',
                  // 播放器高度100%填滿主容器，並上下置中對齊
                  height: '100%',
                  minHeight: '100%', // 確保最小高度也是100%
                  // 移除所有可能的間隔
                  margin: 0, // 移除邊距
                  padding: 0, // 移除內邊距
                  // 新增：橫式佈局時也啟用置中對齊
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center', // 上下置中對齊
                  alignItems: 'center', // 左右置中對齊
                  // 強制覆蓋 VideoPlayer 的內部樣式，確保影片置中
                  '& > div, & > div > div, & > div > div > div': {
                    display: 'flex !important',
                    justifyContent: 'center !important',
                    alignItems: 'center !important',
                    width: '100% !important',
                    height: '100% !important',
                    margin: '0 !important', // 強制移除邊距
                    padding: '0 !important' // 強制移除內邊距
                  },
                  // 動態調整：影片區域會自動佔據剩餘空間
                  minWidth: 0, // 允許flex項目縮小
                  flexGrow: 1, // 允許flex項目增長
                  flexShrink: 1, // 允許flex項目縮小
                  // 響應式高度調整，充分利用視窗高度
                } : {
                  position: 'relative',
                  width: '100%',
                  // 直式佈局時播放器高度設為70vh
                  height: '70vh', // 播放器區域佔70vh
                  display: 'flex', // 改為flex佈局，便於置中對齊
                  flexDirection: 'column', // 垂直排列
                  justifyContent: 'center', // 上下置中對齊
                  alignItems: 'center', // 左右置中對齊
                  backgroundColor: '#000', // 確保黑色背景
                  minHeight: '70vh', // 確保播放器區域最小高度也是70vh
                  // 調試：播放器區域邊框 - 藍色 (已移除)
                  // border: '3px solid #0000ff',
                  // 移除所有可能的間隔
                  margin: 0, // 移除邊距
                  padding: 0, // 移除內邊距
                  borderRadius: 0, // 移除圓角，避免間隔

                  // 子元素樣式：確保影片內容置中，並強制覆蓋 VideoPlayer 的樣式
                  '& > div': {
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    display: 'flex !important',
                    justifyContent: 'center !important',
                    alignItems: 'center !important',
                    margin: '0 !important', // 強制移除邊距
                    padding: '0 !important', // 強制移除內邊距
                    // 強制覆蓋 VideoPlayer 的內部樣式
                    '& > div, & > div > div, & > div > div > div': {
                      display: 'flex !important',
                      justifyContent: 'center !important',
                      alignItems: 'center !important',
                      width: '100% !important',
                      height: '100% !important',
                      margin: '0 !important', // 強制移除邊距
                      padding: '0 !important' // 強制移除內邊距
                    },
                    // 強制YouTube影片填滿容器
                    '& iframe': {
                      width: '100% !important',
                      height: '100% !important',
                      objectFit: 'cover !important',
                      objectPosition: 'center !important'
                    }
                  }
                }
              }}
            >
            {tabValue === 4 ? (
              // 圖片顯示模式
              <>
                {!imageUrl && !isLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: 'url(/playbackground.png)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography 
                        variant="h6" 
                        color="white" 
                        sx={{ 
                          textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                          textAlign: 'center',
                          fontWeight: 'bold'
                        }}
                      >
                        請選擇照片
                      </Typography>
                    </Box>
                  </Box>
                )}
                {imageUrl && (
                  <Box
                            sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#000',
                      zIndex: 1,
                      overflow: 'visible'
                    }}
                  >
                    {/* 外框 - 始終顯示淺灰色 */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '99.8%',
                        height: '99.8%',
                        border: '1.5px solid #808080',
                        boxSizing: 'border-box',
                        pointerEvents: 'none',
                        zIndex: 2
                      }}
                    />
                    <img
                      src={imageUrl}
                      alt="已選擇的照片"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        cursor: 'zoom-in'
                      }}
                      onClick={() => setIsLightboxOpen(true)}
                    />
                    {/* 🔧 优化：懒加载 Lightbox（只在打开时加载） */}
                    {isLightboxOpen && (
                      <LazyLightbox
                        open={isLightboxOpen}
                        close={() => setIsLightboxOpen(false)}
                        slides={[{ src: imageUrl }]}
                      />
                    )}
                  </Box>
                )}
                {isLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: 'url(/playbackground.png)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  </Box>
                )}

                {/* 輔助線層 */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 20
                  }}
                >
                  {/* 安全框 - 當安全框開啟時顯示 */}
                  {uiDisplay.safetyFrame && (() => {
                    // 調試日誌已移除
                    return true;
                  })() && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        // 安全框尺寸相對於影片內容區域 - 修正計算
                        width: `${safetyFrameSize * 0.8}%`, // 縮小到影片區域的80%
                        height: `${safetyFrameSize * 0.6}%`, // 縮小到影片區域的60%
                        // 限制安全框在影片內容區域內
                        maxWidth: '80%', // 限制最大寬度為容器的80%
                        maxHeight: '60%', // 限制最大高度為容器的60%
                        minWidth: '10%', // 限制最小寬度
                        minHeight: '10%', // 限制最小高度
                        border: `2px solid ${
                          safetyFrameColor === 'magenta' ? '#FF00FF' :
                          safetyFrameColor === 'blue' ? '#0000FF' :
                          safetyFrameColor === 'yellow' ? '#FFFF00' :
                          '#00FF00'
                        }`,
                        boxSizing: 'border-box',
                        pointerEvents: 'none',
                        zIndex: 30, // 確保安全框在最上層
                        // 添加背景色以便調試
                        backgroundColor: 'rgba(255, 0, 0, 0.1)'
                      }}
                    />
                  )}
                  
                  {/* 十字線 - 只在 YouTube 影片時顯示 */}
                  {uiDisplay.crosshair && crosshairColor !== 'none' && videoUrl && (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) && (
                    <>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '2px',
                          height: '100%',
                          backgroundColor: crosshairColor === 'blue' ? '#0000FF' : 
                                          crosshairColor === 'yellow' ? '#FFFF00' : '#00FF00',
                          opacity: 0.8,
                          pointerEvents: 'none'
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: 0,
                          transform: 'translateY(-50%)',
                          width: '100%',
                          height: '2px',
                          backgroundColor: crosshairColor === 'blue' ? '#0000FF' : 
                                          crosshairColor === 'yellow' ? '#FFFF00' : '#00FF00',
                          opacity: 0.8,
                          pointerEvents: 'none'
                        }}
                      />
                    </>
                  )}
                  
                  {/* 井字線 - 當格線模式不是 none 時顯示 */}
                  {gridMode !== 'none' && (() => {
                    // 調試日誌已移除
                    return true;
                  })() && (
                    <>
                      {gridMode === '3x3' && (
                        <>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              width: '80%',
                              height: '60%',
                              border: '1px solid rgba(255, 0, 0, 0.3)',
                              pointerEvents: 'none',
                              zIndex: 25
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              width: '80%',
                              height: '60%',
                              backgroundImage: `
                                linear-gradient(to right, #808080 0%, #808080 1px, transparent 1px, transparent 33.33%, #808080 33.33%, #808080 34.83%, transparent 34.83%, transparent 66.67%, #808080 66.67%, #808080 68.17%, transparent 68.17%),
                                linear-gradient(to bottom, #808080 0%, #808080 1px, transparent 1px, transparent 33.33%, #808080 33.33%, #808080 34.83%, transparent 34.83%, transparent 66.67%, #808080 66.67%, #808080 68.17%, transparent 68.17%)
                              `,
                              backgroundSize: '100% 100%',
                              opacity: 0.8,
                              pointerEvents: 'none',
                              zIndex: 25
                            }}
                          />
                        </>
                      )}
                      {gridMode === '6x4' && (
                        <>
                          {[...Array(5)].map((_, i) => (
                            <Box
                              key={`v-${i}`}
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: `${((i + 1) * 100) / 6}%`,
                                width: '1.5px',
                                height: '100%',
                                backgroundColor: '#808080',
                                opacity: 0.8,
                                pointerEvents: 'none',
                                zIndex: 25
                              }}
                            />
                          ))}
                          {[...Array(3)].map((_, i) => (
                            <Box
                              key={`h-${i}`}
                              sx={{
                                position: 'absolute',
                                top: `${((i + 1) * 100) / 4}%`,
                                left: 0,
                                width: '100%',
                                height: '1.5px',
                                backgroundColor: '#808080',
                                opacity: 0.8,
                                pointerEvents: 'none',
                                zIndex: 25
                              }}
                            />
                          ))}
                        </>
                      )}
                      {gridMode === '10x6' && (
                        <>
                          {[...Array(9)].map((_, i) => (
                            <Box
                              key={`v-${i}`}
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: `${((i + 1) * 100) / 10}%`,
                                width: '1.5px',
                                height: '100%',
                                backgroundColor: '#808080',
                                opacity: 0.8,
                                pointerEvents: 'none',
                                zIndex: 25
                              }}
                            />
                          ))}
                          {[...Array(5)].map((_, i) => (
                            <Box
                              key={`h-${i}`}
                              sx={{
                                position: 'absolute',
                                top: `${((i + 1) * 100) / 6}%`,
                                left: 0,
                                width: '100%',
                                height: '1.5px',
                                backgroundColor: '#808080',
                                opacity: 0.8,
                                pointerEvents: 'none',
                                zIndex: 25
                              }}
                            />
                          ))}
                        </>
                      )}
                    </>
                  )}

                  {/* 電影感遮罩 - 當遮黑功能開啟時顯示，且只覆蓋影片區域 */}
                  {uiDisplay.movieMask && (() => {
                    // 調試日誌已移除
                    return true;
                  })() && (
                    <>
                      {/* 上方遮黑區域 - 只遮住影片上方一小部分 */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '80%', // 限制在影片內容區域內
                          height: '10%', // 調整高度，遮住上方10%
                          backgroundColor: 'black',
                          pointerEvents: 'none',
                          zIndex: 30, // 確保在最上層
                          // 添加調試邊框
                          border: '1px solid rgba(255, 0, 0, 0.5)'
                        }}
                      />
                      {/* 下方遮黑區域 - 只遮住影片下方一小部分 */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, 50%)',
                          width: '80%', // 限制在影片內容區域內
                          height: '10%', // 調整高度，遮住下方10%
                          backgroundColor: 'black',
                          pointerEvents: 'none',
                          zIndex: 30, // 確保在最上層
                          // 添加調試邊框
                          border: '1px solid rgba(255, 0, 0, 0.5)'
                        }}
                      />
                    </>
                  )}
                </Box>
              </>
            ) : (
              // 影片播放模式
              <>
                {!videoUrl && !isVideoLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: 'url(/playbackground.png)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography 
                        variant="h6" 
                        color="white" 
                        sx={{ 
                          textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                          textAlign: 'center',
                          fontWeight: 'bold'
                        }}
                      >
                        請輸入影片網址
                      </Typography>
                    </Box>
                  </Box>
                )}
                {isVideoLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: 'url(/playbackground.png)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  </Box>
                )}
                {/* 基礎調試標籤：已刪除 */}
                
                {videoUrl && (
                  <Box sx={{ 
                    position: 'relative', 
                    width: '100%', 
                    height: '100%'
                  }}>
                    {/* 內部調試標籤：暫時隱藏 */}
                    {/* <Box sx={{...}}>容器內部可訪問 | 條件: {videoUrl ? 'true' : 'false'}</Box> */}
                    {/* 外部調試標籤：暫時隱藏 */}
                    {/* <Box sx={{...}}>容器已渲染 | videoUrl: {videoUrl ? '存在' : '不存在'}</Box> */}
                    {/* 外框 - 已移除調試邊框 */}
                    
                    {/* 調試標籤：暫時隱藏 */}
                    {/* <Box sx={{...}}>佈局: {isLandscape ? '手機橫式' : (windowDimensions.width > 800 ? '桌面' : '手機直式')} | 寬度: {windowDimensions.width}px | 高度: {windowDimensions.height}px | 滾動: {Math.round(window.pageYOffset || 0)}px | 顯示: {(isLandscape || windowDimensions.width > 800) ? 'flex' : 'block'} | isLandscape: {isLandscape ? 'true' : 'false'}</Box> */}
                    
                    {/* 畫面 fade overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'black',
                        opacity: videoFadeOpacity,
                        transition: 'opacity 0.2s ease-in-out',
                        pointerEvents: 'none',
                        zIndex: 10
                      }}
                    />
                    
                    {/* 根據影片模式選擇播放器 */}
                    {videoMode === 'Google' ? (
                      // Google Drive 專用 iframe 播放器
                      <GoogleDriveIframe
                        ref={googleDriveRef}
                        src={videoUrl}
                        onError={() => {
                          // 狀態追蹤日誌已移除
                          // 自動嘗試下一個 Google Drive URL
                          if (typeof window !== 'undefined' && (window as any).googleDriveUrls) {
                            const urls = (window as any).googleDriveUrls;
                            let currentIndex = (window as any).currentGoogleDriveUrlIndex || 0;
                            
                            currentIndex++;
                            if (currentIndex < urls.length) {
                              // 調試日誌已移除
                              setVideoUrl(urls[currentIndex]);
                              (window as any).currentGoogleDriveUrlIndex = currentIndex;
                            } else {
                              console.log('❌ 所有 Google Drive 連結都失敗了');
                              // 顯示錯誤訊息
                              setError('Google Drive 影片無法播放，可能原因：\n1. 檔案不存在或已被刪除\n2. 檔案沒有公開分享權限\n3. 檔案類型不支援播放\n4. 需要檢查 Google Drive 檔案權限設定');
                            }
                          }
                        }}
                        onLoad={() => {
                          // 成功日誌已移除
                          setError(null); // 清除錯誤
                        }}
                        onPlay={() => {
                          // 開始處理（日誌已移除）
                          setIsPlaying(true);
                        }}
                        onPause={() => {
                          console.log('⏸️ Google Drive 播放暫停');
                          setIsPlaying(false);
                        }}
                        onSeek={(time) => {
                          console.log('⏭️ Google Drive 跳轉到:', time);
                          setPlayedSeconds(time);
                        }}
                        onVolumeChange={(vol) => {
                          // 調試日誌已移除
                          setVolume(vol);
                        }}
                        onFullscreenChange={(isFullscreen) => {
                          // 調試日誌已移除
                        }}
                      />
                    ) : (
                      // 其他影片使用 VideoPlayer
                      <VideoPlayer
              ref={playerRef}
              url={videoUrl}
              videoId={videoId}
              firestoreId={currentFirestoreId || undefined}
              videoTitle={videoTitle}
              playing={isPlaying}
              volume={volume}
              muted={isMuted}
              playbackRate={playbackRate}
              initialStartTime={initialStartTime} // 🔧 新增：傳遞初始起始時間給 YouTube player
              onProgress={handleProgress}
              onDuration={handleDuration}
              onReady={() => {
                handlePlayerReady();
                if (videoUrl && (videoUrl.includes('drive.google.com') || videoUrl.includes('docs.google.com'))) {
                  handleGoogleDriveReady(videoUrl);
                }
                // 🔧 新增：影片載入完成後執行自動滾動
                autoScrollBasedOnVideoRatio();
                // 🔧 新增：影片載入完成後清空 initialStartTime 和 isFromNotesPage，避免影響下次播放
                if (initialStartTime !== null) {
                  setInitialStartTime(null);
                  // 調試日誌已移除
                }
                if (isFromNotesPage) {
                  // 延遲清空，確保 VideoPlayer 已經使用了該標記
                  setTimeout(() => {
                    setIsFromNotesPage(false);
                    // 調試日誌已移除
                  }, 1000);
                }
              }}
              onVerticalVideoChange={(isVertical) => {
                // 調試日誌已移除
                setIsVerticalVideo(isVertical);
              }}
              onError={handlePlayerError}
              onCaptionTextChange={setCurrentCaptionText}
              currentTime={playedSeconds}
              disableWatchProgress={isFromNotesPage || initialStartTime !== null || isJumpingToCheckpointRef.current} // 🔧 關鍵修復：從 NotesPage 來或有 initialStartTime 或正在跳轉到重點時禁用觀看進度對話框（使用 ref 確保立即生效）
              style={{ 
                position: 'relative', // 改為相對定位，避免絕對定位的問題
                top: 'auto', // 移除絕對定位的 top 值
                left: 'auto', // 移除絕對定位的 left 值
                width: '100%',
                height: '100%',
                zIndex: 1, 
                backgroundColor: '#000',
                display: 'block', // 確保影片元素可見
                overflow: 'hidden' // 確保內容不溢出
              }}
            />
                    )}
                    
                    {/* 輔助線層 */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        zIndex: 20
                      }}
                    >
                      {/* 安全框 */}
                      {uiDisplay.safetyFrame && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: `${safetyFrameSize}%`,
                            height: `${safetyFrameSize}%`,
                            border: `1.5px solid ${
                              safetyFrameColor === 'magenta' ? '#FF00FF' :
                              safetyFrameColor === 'blue' ? '#0000FF' :
                              safetyFrameColor === 'yellow' ? '#FFFF00' :
                              '#00FF00'
                            }`,
                            boxSizing: 'border-box',
                            pointerEvents: 'none'
                          }}
                        />
                      )}
                      
                      {/* 十字線 */}
                      {uiDisplay.crosshair && crosshairColor !== 'none' && (
                        <>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '2px',
                              height: '100%',
                              backgroundColor: crosshairColor === 'blue' ? '#0000FF' : 
                                              crosshairColor === 'yellow' ? '#FFFF00' : '#00FF00',
                              opacity: 0.8,
                              pointerEvents: 'none'
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: 0,
                              transform: 'translateY(-50%)',
                              width: '100%',
                              height: '2px',
                              backgroundColor: crosshairColor === 'blue' ? '#0000FF' : 
                                              crosshairColor === 'yellow' ? '#FFFF00' : '#00FF00',
                              opacity: 0.8,
                              pointerEvents: 'none'
                            }}
                          />
                        </>
                      )}
                      
                      {uiDisplay.movieMask && (
                        <>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '15%',
                              backgroundColor: 'black',
                              pointerEvents: 'none',
                              zIndex: 25
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              width: '100%',
                              height: '15%',
                              backgroundColor: 'black',
                              pointerEvents: 'none',
                              zIndex: 25
                            }}
                          />
                        </>
                      )}
                      
                      {/* 🔧 重構：使用 PlayerOverlay 元件顯示時間碼 */}
                      <PlayerOverlay
                        currentTimecode={currentTimecode}
                        showTimecode={showTimecode}
                        showTimecodeBackground={showTimecodeBackground}
                        timecodePosition={timecodePosition}
                        timecodeScale={timecodeScale}
                        timecodeElRef={timecodeElRef} // 🔧 新增：傳遞 ref，啟用高性能 rAF + DOM 直接操作
                        isPortrait={windowDimensions.width <= windowDimensions.height}
                      />

                      {/* 時鐘顯示 */}
                      {uiDisplay.clock && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            pointerEvents: 'none',
                            zIndex: 5
                          }}
                        >
                          {/* 當前時間 - 左下角 */}
                          {uiDisplay.currentTime && (
                            <Typography
                              variant="body2"
                              sx={{
                                position: 'absolute',
                                color: 'white',
                                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                                fontFamily: 'monospace',
                                fontSize: '0.9rem',
                                backgroundColor: 'rgba(0, 100, 255, 0.7)',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                ...getClockPositionStyle('current'),
                                transition: 'all 0.3s ease',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {formatCurrentTimeWithDuration()}
                            </Typography>
                          )}

                          {/* 剩餘時間 - 右下角 */}
                          {uiDisplay.remainingTime && playerRef.current && (
                            <Typography
                              variant="body2"
                              sx={{
                                position: 'absolute',
                                color: 'white',
                                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                                fontFamily: 'monospace',
                                fontSize: '0.9rem',
                                backgroundColor: 'rgba(255, 0, 0, 0.7)',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                ...getClockPositionStyle('remaining'),
                                transition: 'all 0.3s ease',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {formatRemainingTime()}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </>
            )}
            </Box>

            {/* 右側：控制 Group UI (固定寬度) - 根據螢幕尺寸選擇不同組件 */}
            {/* 橫式佈局時顯示控制面板 */}
            {!isPortrait && (
              <Box 
                sx={{
                  // 🔧 修改：讓控制面板 Group UI 也參與 Flexbox 分配
                  flex: aspectRatio <= 1.8 ? '1 1 auto' : '1 1 auto',  // 🔧 修改：允許增長和縮小，佔 1 份空間 (30%)
                  width: 'auto',                                                // 🔧 修改：改為自動寬度
                  minWidth: aspectRatio <= 1.8 ? '150px' : '150px',      // 🔧 修改：設定最小寬度 150px，讓70%:30%比例更明顯
                  maxWidth: 'none',                                            // 🔧 修改：移除最大寬度限制
                  // 調試：橫式佈局控制面板邊框 - 紫色 (已移除)
                  // border: '3px solid #ff00ff',
                  order: controlGroupPosition === 'left' ? 1 : 2,
                  display: 'flex',
                  flexDirection: 'column',
                  // 控制面板整體下移更多
                  marginTop: '1.25', // 修改：整體下移10px (1.25 * 8px = 10px)
                  // 控制面板在父容器中靠上對齊，實現往上平移
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  height: '100%', // 控制面板高度100%填滿父容器，避免底部空白
                  minHeight: '100%', // 確保最小高度也是100%
                  maxHeight: '100%', // 確保最大高度也是100%
                  // 強制手機模式下與父容器高度一致
                  ...(windowDimensions.width <= 800 ? {  // 移除高度限制，只要寬度 ≤ 800px 就觸發
                    height: '100% !important',        // 改為100%，與父容器保持一致
                    minHeight: 'auto !important',     // 改為auto，移除固定高度限制
                    maxHeight: '100% !important',     // 改為100%，與父容器保持一致
                    alignSelf: 'stretch !important',
                    // 強制覆蓋所有可能的高度設定
                    '&': {
                      height: '100% !important',
                      minHeight: 'auto !important',
                      maxHeight: '100% !important',
                    },
                  } : {}),
                  // 確保控制列不會被壓縮
                  flexShrink: 0,
                  flexGrow: 0
                }}
                className="control-panel-group-ui"
              >
                {/* 根據螢幕尺寸選擇不同的控制面板 */}
                {/* 橫式佈局時使用原有控制面板 */}
                {renderControlGroupUI()}
              </Box>
            )}

            {/* 直式佈局時顯示 SwipeableControlPanel - 移到紅色框內部 */}
            {/* 🔧 修复：改用 CSS display 而非条件渲染 */}
            <Box 
              sx={useMemo(() => ({
                display: isPortrait ? 'flex' : 'none', // 关键：CSS 隐藏
                width: '100%',
                height: 'auto', // 改為自動高度，讓內容決定高度
                minHeight: '60vh', // 最小高度增加到60vh
                maxHeight: '80vh', // 最大高度限制為80vh
                // 🔧 修復：避免618px高度突變，使用更平滑的高度計算
                ...(windowDimensions.height < 650 ? {
                  minHeight: `${Math.max(350, windowDimensions.height * 0.55)}px`, // 動態最小高度
                  maxHeight: `${Math.min(500, windowDimensions.height * 0.8)}px`,   // 動態最大高度
                } : {}),
                backgroundColor: '#f5f5f5',
                borderTop: '1px solid #cccccc',
                // 調試：控制面板區域邊框 - 綠色 (已移除)
                // border: '3px solid #00ff00',
                flexDirection: 'column',
                overflow: 'visible', // 🔧 关键修复：改为 visible，避免内容被隐藏
                // 🔧 修復：確保移動端重點按鈕可見，最小高度已在上方設定
                position: 'relative',
                zIndex: 1000,
                // 添加底部間距，避免被BottomBar遮擋
                paddingBottom: '80px' // 為BottomBar預留空間
              }), [isPortrait, windowDimensions.height])}>
              <SwipeableControlPanel
                numberKeys={tabValue === 3 ? numberKeys : (tabNumberKeys[tabValue] || {})}
                isDeleteMode={isDeleteMode}
                mButtonState={mButtonState}
                vbids={vbids as any}
                handlers={useMemo(() => ({
                  onNumberKeyClick: handleNumberKeyUp,
                  onNoteDisplayChange: handleNoteDisplayChange,
                  onDeleteModeToggle: () => setIsDeleteMode(!isDeleteMode),
                  onSettings: handleSettings,
                  onMButtonClick: handleMButtonClick,
                  onModeChange: handleModeChange,
                  onNoteToggle: handleNoteToggle,
                  onCheckClick: handleCheckClick,
                  onDeleteButtonClick: handleDeleteButtonClick,
                  onDeleteButtonPress: handleDeleteButtonPress,
                  onDeleteButtonRelease: handleDeleteButtonRelease,
                  onBookmarkClick: handleBookmarkClick,
                  onSwitchClick: handleSwitchClick,
                  onBButtonClick: handleBButtonClick,
                  onSpeedChange: handleSpeedChange,
                  onThemeDialogOpen: handleThemeDialogOpen,
                  onOpenNoteRecords: () => openDialog('noteRecords'),
                  onRequestLogin: () => openDialog('auth'),
                  onVersionChange: handleVersionChange,
                  onDownloadCaptions: handleDownloadCaptions,
                  onAutoFillNoteFromSRT: handleAutoFillNoteFromSRT
                }), [handleNumberKeyUp, handleNoteDisplayChange, isDeleteMode, handleSettings, handleMButtonClick, handleModeChange, handleNoteToggle, handleCheckClick, handleDeleteButtonClick, handleDeleteButtonPress, handleDeleteButtonRelease, handleBookmarkClick, handleSwitchClick, handleBButtonClick, handleSpeedChange, handleThemeDialogOpen, handleVersionChange, handleDownloadCaptions, handleAutoFillNoteFromSRT])}
                config={useMemo(() => ({
                  showTimecode: true,
                  isUserLoggedIn: !!user,
                  maxNumberKeys,
                  keySetIndex,
                  duration: ppMode.duration,
                  playbackRate,
                  videoUrl: videoUrl,
                  hasSRTCache: hasSRTCache,
                  cachedSRTLanguage: cachedSRTLanguage,
                  isAutoFillMode: isAutoFillMode,
                  showAITooltip: uiDisplay.aiTooltip,
                  isDownloadingSubtitles: isDownloadingSubtitles,
                  showSubtitleFillTooltip: uiDisplay.subtitleFillTooltip,
                  srtContent: srtContent || ''
                  // 🔧 移除 currentTime 和 isPlaying，因为它们频繁变化会导致无限重新渲染
                  // currentTime 和 isPlaying 通过单独的 props 传递
                }), [user, maxNumberKeys, keySetIndex, ppMode.duration, playbackRate, videoUrl, hasSRTCache, cachedSRTLanguage, isAutoFillMode, uiDisplay.aiTooltip, isDownloadingSubtitles, uiDisplay.subtitleFillTooltip, srtContent])}
                videoUrl={videoUrl}
                getSButtonStyle={getSButtonStyle}
                setVbids={() => {}}
                exportVBIDsToFile={(vbids) => exportVBIDsToFile(vbids)}
                exportFirestoreData={async () => {
                  if (!videoUrl || !user?.uid) {
                    console.log('❌ 無法匯出：缺少 videoUrl 或用戶資訊');
                    return;
                  }
                  
                  try {
                    // 調試日誌已移除
                    await exportVideoFirestoreData(videoUrl, user.email);
                  } catch (error) {
                    console.error('❌ 匯出 Firestore 資料失敗:', error);
                  }
                }}
                user={user}
                // 播放控制相關
                isPlaying={isPlaying}
                onPlayPause={handlePlayPause}
                onRewind={handleRewind}
                onStepBackward={handleStepBackward}
                onStepForward={handleStepForward}
                onFastForward={handleFastForward}
                fastForwardSeconds={fastForwardSeconds}
                stepUnit={stepUnit}
                // 工具列相關
                showSafetyFrame={uiDisplay.safetyFrame}
                onSafetyFrameClick={handleSafetyFrameClick}
                gridMode={gridMode}
                onGridLinesClick={handleGridLinesClick}
                showMovieMask={uiDisplay.movieMask}
                onMovieMaskToggle={() => toggleUI('movieMask')}
                showTimecode={showTimecode}
                onTimecodeClick={handleTimecodeClick}
                onScreenshotClick={handleScreenshotClick}
                onShareClick={handleShare}
                onFullScreenClick={handleFullScreenClick}
                // 同步播放相關
                isOnAir={isOnAir}
                syncManagerOpen={syncManagerOpen}
                onSyncToggle={() => {
                  if (isOnAir || syncManagerOpen) {
                    // 如果已開啟，點擊關閉同步
                    // 調試日誌已移除
                    setSyncManagerOpen(false);
                    setIsOnAir(false);
                  } else {
                    // 如果未開啟，點擊開啟同步播放管理
                    // 調試日誌已移除
                    setSyncManagerOpen(true);
                  }
                }}
                // Note 記錄相關
                onOpenNoteRecords={() => openDialog('noteRecords')}
                noteRecordsDialogOpen={dialogs.noteRecords}
                setNoteRecordsDialogOpen={(open) => open ? openDialog('noteRecords') : closeDialog('noteRecords')}
                // 文字內容相關
                currentSubtitleKey={currentSubtitleKey?.toString() || undefined}
                subtitleContent={subtitleContent}
                setSubtitleContent={setSubtitleContent}
                setShowAuthDialog={(open) => open ? openDialog('auth') : closeDialog('auth')}
                tabNumberKeys={tabNumberKeys}
                currentDocumentId={currentDocumentId}
                currentFirestoreId={currentFirestoreId}
                currentViewpointName={currentViewpointName}
                // 其他
                isMobile={true}
                tabValue={tabValue}
                // 知識管理相關
                onOpenKnowledgeManagement={handleOpenKmDialog}
                // 導航相關
                onNavigateToManagePages={handleNavigateToManagePages}
                // 初始狀態控制
                initialActivePanel={activePanel}
                onActivePanelChange={handleActivePanelChange}
              />
            </Box>
            {/* 🔧 修复：移除 )} */}
          </Box>

          {/* 控制按鈕區域 - 已移除，統一使用上方的控制面板 */}
          {false && (
            <Box sx={{ 
              position: 'fixed',
              bottom: { xs: 56, sm: 56 }, // 緊貼 BottomBar 上方 (BottomBar 高度為 56px)
              left: 0,
              right: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderTop: '1px solid rgba(0, 0, 0, 0.1)',
              zIndex: 1000,
              // 增加上下各10px的高度：從 p: 0.5 (8px) 改為 py: 1.125 (18px)
              py: 1.125,  // 上下內邊距：18px (原本8px + 10px)
              px: 0.5,    // 左右內邊距：保持8px
              // 手機直式佈局時縮小底部控制按鈕90%，但播放功能列按鈕放大到120%
              '& .MuiButton-root:not(.playback-control-btn), & .MuiIconButton-root:not(.playback-control-btn), & .MuiFormControl-root, & .MuiTextField-root, & .MuiSwitch-root, & .MuiSlider-root, & .MuiFormControlLabel-root': {
                transform: 'scale(0.9)',
                transformOrigin: 'center center',
              },
              // 播放功能列按鈕放大到120%
              '& .playback-control-btn': {
                transform: 'scale(1.2)',
                transformOrigin: 'center center',
              }
            }}>
          <Stack spacing={1}>
            {/* 第一排：播放控制按鈕 */}
            <Stack 
              direction="row" 
              spacing={3} 
              alignItems="center" 
              justifyContent="center"
              sx={{ 
                width: '100%',
                py: 2,
                pt: 0.625, // 修改：上方間距設為5px (0.625 * 8px = 5px)
                opacity: tabValue === 4 || tabValue === 5 ? 0.5 : 1,
                pointerEvents: tabValue === 4 || tabValue === 5 ? 'none' : 'auto'
              }}
            >
              {/* n秒退按鈕 - 支持長按 2x 倒退 */}
                              <IconButton 
                onClick={handleRewind} 
                onMouseDown={handleRewindMouseDown}
                onMouseUp={handleRewindMouseUp}
                onMouseLeave={handleRewindMouseLeave}
                disabled={tabValue === 4 || tabValue === 5}
                className="playback-control-btn"
                sx={{ 
                  transform: 'scale(1.2)',
                  border: '2px solid #e0e0e0',
                  borderRadius: '16px',
                  // 長按時變紅色背景，白色文字
                  backgroundColor: isLongPressingRewind ? '#d32f2f' : 'rgba(255, 255, 255, 0.9)',
                  padding: '4px 8px',
                  '&:hover': {
                    borderColor: isLongPressingRewind ? '#b71c1c' : '#d32f2f',
                    backgroundColor: isLongPressingRewind ? '#b71c1c' : 'rgba(211, 47, 47, 0.1)',
                  }
                }}
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    mr: 0.5,
                    color: isLongPressingRewind ? 'white' : 'inherit'
                  }}
                >
                  -{fastForwardSeconds}s
                </Typography>
                <FastRewindIcon 
                  sx={{ 
                    color: isLongPressingRewind ? 'white' : 'inherit'
                  }}
                />
              </IconButton>

              {/* 秒退按鈕 - 支持長按 1x 倒退 */}
                              <IconButton 
                onClick={handleStepBackward} 
                onMouseDown={handleStepBackwardMouseDown}
                onMouseUp={handleStepBackwardMouseUp}
                onMouseLeave={handleStepBackwardMouseLeave}
                disabled={tabValue === 4 || tabValue === 5}
                className="playback-control-btn"
                sx={{ 
                  transform: 'scale(1.2)',
                  border: '2px solid #e0e0e0',
                  borderRadius: '16px',
                  // 長按時變紅色背景，白色文字
                  backgroundColor: isLongPressingStepBackward ? '#d32f2f' : 'rgba(255, 255, 255, 0.9)',
                  padding: '4px 8px',
                  '&:hover': {
                    borderColor: isLongPressingStepBackward ? '#b71c1c' : '#d32f2f',
                    backgroundColor: isLongPressingStepBackward ? '#b71c1c' : 'rgba(211, 47, 47, 0.1)',
                  }
                }}
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    mr: 0.5,
                    color: isLongPressingStepBackward ? 'white' : 'inherit'
                  }}
                >
                  {stepUnit === 'second' ? '-1s' : '-1f'}
                </Typography>
                <FastRewindIcon 
                  fontSize="small"
                  sx={{ 
                    color: isLongPressingStepBackward ? 'white' : 'inherit'
                  }}
                />
              </IconButton>

              <IconButton 
                onClick={handlePlayPause}
                disabled={tabValue === 4 || tabValue === 5}
                className="playback-control-btn"
                sx={{ 
                  transform: 'scale(1.5) !important',
                  backgroundColor: '#d32f2f',
                  borderRadius: '8px',
                  color: 'white',
                  height: '24px',
                  minWidth: '40px',
                  '&:hover': {
                    backgroundColor: '#b71c1c'
                  }
                }}
              >
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>

                            {/* 秒進按鈕 - 支持長按 1x 播放 */}
                              <IconButton 
                onClick={handleStepForward} 
                onMouseDown={handleStepForwardMouseDown}
                onMouseUp={handleStepForwardMouseUp}
                onMouseLeave={handleStepForwardMouseLeave}
                disabled={tabValue === 4 || tabValue === 5}
                className="playback-control-btn"
                sx={{ 
                  transform: 'scale(1.2)',
                  border: '2px solid #e0e0e0',
                  borderRadius: '16px',
                  // 長按時變紅色背景，白色文字
                  backgroundColor: isLongPressingStepForward ? '#d32f2f' : 'rgba(255, 255, 255, 0.9)',
                  padding: '4px 8px',
                  '&:hover': {
                    borderColor: isLongPressingStepForward ? '#b71c1c' : '#d32f2f',
                    backgroundColor: isLongPressingStepForward ? '#b71c1c' : 'rgba(211, 47, 47, 0.1)',
                  }
                }}
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    mr: 0.5,
                    color: isLongPressingStepForward ? 'white' : 'inherit'
                  }}
                >
                  {stepUnit === 'second' ? '+1s' : '+1f'}
                </Typography>
                <FastForwardIcon 
                  fontSize="small"
                  sx={{ 
                    color: isLongPressingStepForward ? 'white' : 'inherit'
                  }}
                />
              </IconButton>

                            {/* n秒進按鈕 - 支持長按 2x 播放 */}
                              <IconButton 
                onClick={handleFastForward} 
                onMouseDown={handleFastForwardMouseDown}
                onMouseUp={handleFastForwardMouseUp}
                onMouseLeave={handleFastForwardMouseLeave}
                disabled={tabValue === 4 || tabValue === 5}
                className="playback-control-btn"
                sx={{ 
                  transform: 'scale(1.2)',
                  border: '2px solid #e0e0e0',
                  borderRadius: '16px',
                  // 長按時變紅色背景，白色文字
                  backgroundColor: isLongPressingFastForward ? '#d32f2f' : 'rgba(255, 255, 255, 0.9)',
                  padding: '4px 8px',
                  '&:hover': {
                    borderColor: isLongPressingFastForward ? '#b71c1c' : '#d32f2f',
                    backgroundColor: isLongPressingFastForward ? '#b71c1c' : 'rgba(211, 47, 47, 0.1)',
                  }
                }}
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    mr: 0.5,
                    color: isLongPressingFastForward ? 'white' : 'inherit'
                  }}
                >
                  +{fastForwardSeconds}s
                </Typography>
                <FastForwardIcon 
                  sx={{ 
                    color: isLongPressingFastForward ? 'white' : 'inherit'
                  }}
                />
              </IconButton>
            </Stack>

            {/* 第二排：數字按鈕 - 已移除重複的 NumberKeyPanel，統一使用上方的控制面板 */}

                  {/* 第三排：功能按鈕 - 往上移動，為底部版權文字留出空間 */}
      <Stack 
        direction="row" 
        spacing={{ xs: 0.3, sm: 1, md: 1 }} 
        alignItems="center" 
        justifyContent="center"
        sx={{ 
          width: '100%', 
          mb: { xs: 0.25, sm: 0.25, md: 0.25 },  // 🔧 修改：與上方保持更近距離 (0.25 * 8px = 2px)
          flexWrap: 'nowrap',  // 強制不換行
          overflow: 'auto',     // 如果內容過寬，允許滾動
          '&::-webkit-scrollbar': { display: 'none' },  // 隱藏滾動條
          msOverflowStyle: 'none',  // IE 隱藏滾動條
          scrollbarWidth: 'none'     // Firefox 隱藏滾動條
        }}
      >
              <Tooltip title="安全框" enterDelay={200} leaveDelay={0}>
                <span>
                  <IconButton 
                    onClick={handleSafetyFrameClick}
                    color={uiDisplay.safetyFrame ? "primary" : "default"}
                    sx={{ 
                      opacity: uiDisplay.safetyFrame ? 1 : 0.5,
                      '& .MuiSvgIcon-root': { color: uiDisplay.safetyFrame ? '#1976d2' : '#666666' }
                    }}
                  >
                    <CropFreeIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="格線" enterDelay={200} leaveDelay={0}>
                <span>
                  <IconButton 
                    onClick={handleGridLinesClick}
                    color={gridMode !== 'none' ? "primary" : "default"}
                    sx={{ 
                      opacity: gridMode !== 'none' ? 1 : 0.5,
                      '& .MuiSvgIcon-root': { color: gridMode !== 'none' ? '#1976d2' : '#666666' }
                    }}
                  >
                    <GridOnIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="上下遮黑" enterDelay={200} leaveDelay={0}>
                <span>
                  <IconButton 
                    onClick={() => toggleUI('movieMask')}
                    color={uiDisplay.movieMask ? "primary" : "default"}
                    disabled={tabValue === 4 || tabValue === 5}
                  >
                    <MovieIcon sx={{ opacity: 1 }} />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="時間碼" enterDelay={200} leaveDelay={0}>
                <span>
                  <IconButton
                    onClick={handleTimecodeClick}
                    disabled={tabValue > 3}
                    sx={{ 
                      opacity: showTimecode ? 1 : 0.6, // 確保隱藏時仍然可見
                      '& .MuiTypography-root': { color: '#000000' },
                      visibility: 'visible', // 強制按鈕始終可見
                      display: 'flex' // 確保按鈕始終顯示
                    }}
                  >
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      00:00:00
                    </Typography>
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="分享" enterDelay={200} leaveDelay={0}>
                <span>
                  <IconButton 
                    onClick={handleShare}
                    sx={{ 
                      '& .MuiSvgIcon-root': { 
                        color: videoUrl ? '#1976d2' : '#666666' 
                      } 
                    }}
                  >
                    <ShareIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="截圖" enterDelay={200} leaveDelay={0}>
                <span>
                  <IconButton 
                    onClick={handleScreenshot}
                    sx={{ '& .MuiSvgIcon-root': { color: '#666666' } }}
                  >
                    <CameraAltIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="重點截圖" enterDelay={200} leaveDelay={0}>
                <span>
                  <IconButton 
                    onClick={handleContinuousScreenshot}
                    disabled={tabValue === 3 || tabValue === 4 || tabValue === 5}
                    sx={{ 
                      '& .MuiSvgIcon-root': { color: isBurstMode ? '#1976d2' : '#666666' }
                    }}
                  >
                    <BurstModeIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="圖片管理" enterDelay={200} leaveDelay={0}>
                <span>
                  <IconButton 
                    onClick={handleImageManager}
                    sx={{ '& .MuiSvgIcon-root': { color: '#666666' } }}
                  >
                    <PhotoLibraryIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="字幕 (CC)">
                <span>
                  <IconButton 
                    onClick={() => toggleUI('caption')}
                    sx={{
                      '& .MuiSvgIcon-root': { 
                        color: uiDisplay.caption ? '#1976d2' : '#666666',
                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' }
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.1)'
                      },
                      // 手機模式特殊處理
                      '@media (max-width: 600px)': {
                        padding: '8px',
                        '& .MuiSvgIcon-root': {
                          fontSize: '1.1rem'
                        }
                      }
                    }}
                  >
                    <SubtitlesIcon />
                  </IconButton>
                </span>
              </Tooltip>

            </Stack>
          </Stack>
          
          {/* 時間碼顯示 - 已移除重複的版權文字 */}
            </Box>
          )}
          
          {/* 版權宣告已移至 BottomBar 中 */}
        </Box>

        {/* 版權宣告已移至 BottomBar 中 */}
        
        {/* 橫式控制面板時在播放器父容器下方顯示版權文字 */}
        {/* 版權宣告已移至 BottomBar 中 */}

      <Dialog 
        open={screenshotOpen} 
        onClose={() => setScreenshotOpen(false)}
        sx={{ zIndex: 9999 }}
      >
        <DialogTitle>截圖預覽</DialogTitle>
        <DialogContent>
          {screenshotData && (
            <img 
              src={screenshotData} 
              alt="截圖預覽" 
              style={{ width: '100%', height: 'auto' }} 
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScreenshotOpen(false)}>關閉</Button>
        </DialogActions>
      </Dialog>

      {imageManagerOpen && (
        <Suspense fallback={<DialogFallback />}>
          <ImageManager
            images={capturedImages}
            onDelete={handleDeleteImages}
            onPreview={handlePreviewImage}
            onDownload={handleDownloadImage}
            open={imageManagerOpen}
            onClose={handleImageManagerClose}
          />
        </Suspense>
      )}
      {dialogs.auth && (
        <Suspense fallback={<DialogFallback />}>
          <AuthDialog
            open={dialogs.auth}
            onClose={handleCloseAuthDialog}
            onShowRegister={() => {
              closeDialog('auth');
              openDialog('register');
            }}
          />
        </Suspense>
      )}
      {dialogs.register && (
        <Suspense fallback={<DialogFallback />}>
          <RegisterDialog
            open={dialogs.register}
            onClose={() => closeDialog('register')}
          />
        </Suspense>
      )}

      {/* 🔧 重構：移除 SettingsMenu，統一由 MainLayout 管理 */}

      {/* 時間碼選單 */}
      <TimecodeSettingsMenu
        anchorEl={timecodeMenuAnchorEl}
        open={Boolean(timecodeMenuAnchorEl)}
        onClose={handleTimecodeMenuClose}
        timecodePosition={timecodePosition}
        setTimecodePosition={setTimecodePosition}
        timecodeScale={timecodeScale}
        setTimecodeScale={setTimecodeScale}
        showTimecode={showTimecode}
        setShowTimecode={setShowTimecode}
        setIsTimecodeDisabled={setIsTimecodeDisabled}
        showClock={uiDisplay.clock}
        setShowClock={(value) => setUiDisplay((prev) => ({ ...prev, clock: value }))}
        showCurrentTime={uiDisplay.currentTime}
        setShowCurrentTime={(value) => setUiDisplay((prev) => ({ ...prev, currentTime: value }))}
        showRemainingTime={uiDisplay.remainingTime}
        setShowRemainingTime={(value) => setUiDisplay((prev) => ({ ...prev, remainingTime: value }))}
        timeFormat={timeFormat}
        setTimeFormat={setTimeFormat}
      />

      {/* 🔧 重構：移除 ApiMonitorDialog，統一由 MainLayout 管理 */}

      {/* 通知管理器 */}
      <NotificationManager
        snackbarOpen={snackbarOpen}
        setSnackbarOpen={setSnackbarOpen}
        snackbarMessage={snackbarMessage}
        snackbarSeverity={snackbarSeverity}
        autoSaveSnackbarOpen={autoSaveSnackbarOpen}
        setAutoSaveSnackbarOpen={setAutoSaveSnackbarOpen}
        autoSaveMessage={autoSaveMessage}
        videoError={videoError}
        setVideoError={setVideoError}
      />

      {/* 浮水印設定 */}
      <Popover
        open={watermarkOpen}
        anchorEl={watermarkAnchorEl}
        onClose={handleWatermarkClose}
        sx={{
          zIndex: 9999
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            浮水印設定
          </Typography>
          {/* ... 其他浮水印設定內容 ... */}
        </Box>
      </Popover>
      <VbidDialog
        open={dialogs.vbid}
        onClose={handleVbidDialogClose}
        vbids={currentVbids}
        onImport={handleImportCheckpoints}
        onMyFavorite={handleMyFavorite}
        mButtonState={mButtonState}
      />
      <EnhancedShareDialog
        open={dialogs.share}
        onClose={() => closeDialog('share')}
        video={{
          id: currentFirestoreId || id || videoUrl, // 優先使用 Firestore document ID
          videoId: videoUrl,
          url: videoUrl,
          title: videoUrl,
          vbids: getVbidsForShare()
        }}
        user={user}
      />
      {screenshotOpen && (
        <Suspense fallback={<DialogFallback />}>
          <ScreenshotDialog
            open={screenshotOpen}
            onClose={() => setScreenshotOpen(false)}
            screenshotData={screenshotData}
          />
        </Suspense>
      )}
      {watermarkOpen && (
        <Suspense fallback={<DialogFallback />}>
          <WatermarkDialog
            open={watermarkOpen}
            anchorEl={watermarkAnchorEl}
            onClose={handleWatermarkClose}
        showTimecode={showTimecode}
        setShowTimecode={setShowTimecode}
        showTimecodeBackground={showTimecodeBackground}
        setShowTimecodeBackground={setShowTimecodeBackground}
        timecodePosition={timecodePosition}
        setTimecodePosition={handleTimecodePositionChange}
        timecodeScale={timecodeScale}
        setTimecodeScale={setTimecodeScale}
        showWatermarkInOutput={uiDisplay.watermarkInOutput}
        setShowWatermarkInOutput={(value) => setUiDisplay((prev) => ({ ...prev, watermarkInOutput: value }))}
          />
        </Suspense>
      )}
      {dialogs.profile && (
        <Suspense fallback={<DialogFallback />}>
          <ProfileDialog open={dialogs.profile} onClose={() => closeDialog('profile')} />
        </Suspense>
      )}
      
      {/* OnAir 浮動標籤 */}
      {(isOnAir || isSyncParticipant) && (
        <OnAirIndicator
          isOnAir={isOnAir}
          onClick={handleOnAirClick}
          participantCount={isHost ? participantCount : 0}
        />
      )}

      {/* 同步播放管理器 */}
      <SyncPlayManager
        isOpen={syncManagerOpen}
        onClose={handleSyncManagerClose}
        currentVideoUrl={videoUrl}
        currentTime={playedSeconds}
        isPlaying={isPlaying}
        numberKeys={numberKeys}
        onSyncUpdate={handleSyncUpdate}
        onSyncStatusChange={handleSyncStatusChange}
        user={user}
        onParticipantCountChange={handleParticipantCountChange}
        initialSessionId={sessionId}
      />
      
      {/* 🔧 重構：已移除 MainPage 的 BottomBar，統一使用 MainLayout 的 BottomBar */}
      {/* 預留底部空間，避免內容被 MainLayout 的 BottomBar 遮住 */}
      <Box sx={{ height: { xs: 48, sm: 48 } }} />
      
      {/* 字幕區域 */}
      {uiDisplay.caption && currentCaptionText && (
        <Box
          id="caption-area"
          sx={{
            position: 'fixed',
            bottom: { xs: 310, sm: 150, md: 150 }, // 手機版調整為 310px，其他保持 150px
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: '95%', sm: '90%', md: '90%' }, // 手機模式增加寬度
            maxWidth: { xs: 'none', sm: 600, md: 600 },
            minHeight: { xs: 60, sm: 55, md: 60 }, // 縮小高度以減少間距
            maxHeight: { xs: 120, sm: 110, md: 100 }, // 限制最大高度，避免超過版權信息
            background: 'rgba(0, 0, 0, 0.85)', // 增加透明度
            color: '#fff',
            fontSize: { xs: '2rem', sm: '2.2rem', md: '2.4rem' }, // 加大字體 2 倍
            textAlign: 'center',
            py: { xs: 1, sm: 1.5, md: 2 },
            px: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: { xs: 1, sm: 2, md: 2 },
            zIndex: 1000,
            letterSpacing: 1,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(10px)',
            overflow: 'hidden', // 防止內容溢出
            display: 'block',
            // 手機模式特殊處理
            '@media (max-width: 600px)': {
              bottom: 310, // 手機版調整為 310px
              fontSize: '1.8rem', // 手機模式加大字體
              py: 0.5,
              px: 1.5,
              minHeight: 50,
              maxHeight: 100, // 手機模式限制最大高度
            }
          }}
        >
          {currentCaptionText}
        </Box>
      )}

              {/* 重點播放時長選擇 Dialog */}
        <PlaybackDurationDialog
          open={dialogs.playbackDuration}
          onClose={() => closeDialog('ppMode.playbackDuration')}
          onConfirm={handlePlaybackDurationConfirm}
          defaultDuration={ppMode.duration}
          numberKeys={numberKeys}
        />

        {/* AB 播放循環次數選擇 Dialog */}
        <ABLoopCountDialog
          open={dialogs.abLoopCount}
          onClose={() => closeDialog('abLoopCount')}
          onConfirm={handleABLoopCountConfirm}
          defaultLoopCount={typeof ppMode.abLoopCount === 'number' ? ppMode.abLoopCount : 3}
        />

        {/* 播放模式選擇 Dialog */}
        <PlaybackModeSelectionDialog
          open={dialogs.playbackModeSelection}
          onClose={() => closeDialog('playbackModeSelection')}
          onConfirm={handlePlaybackModeSelectionConfirm}
          mode={ppMode.sharedMode?.mode || ''}
          selectedKeys={ppMode.sharedMode?.selectedKeys || []}
          duration={ppMode.sharedMode?.duration}
          loopCount={ppMode.sharedMode?.loopCount}
        />
      
      

      {/* MV 模式主題輸入對話框 */}
      {dialogs.theme && (
        <Suspense fallback={<DialogFallback />}>
          <MVThemeDialog
            open={dialogs.theme}
            onClose={handleThemeDialogClose}
            onSubmit={handleThemeSubmit}
            themeValue={themeInputValue}
            onThemeChange={handleThemeInputChange}
            onThumbnailChange={handleThumbnailChange}
            thumbnailPreview={thumbnailPreview}
          />
        </Suspense>
      )}

      {/* Note Records Dialog */}
      {dialogs.noteRecords && (
        <Suspense fallback={<DialogFallback />}>
          <NoteRecordsDialog
            open={dialogs.noteRecords}
            onClose={handleCloseNoteRecords}
        numberKeys={tabValue === 3 ? numberKeys : (tabNumberKeys[tabValue] || {})}
        // 🔧 新增：傳遞當前正在編輯的數字鍵和內容，確保即時顯示
        currentEditingKey={currentSubtitleKey}
        currentEditingContent={subtitleContent}
        onReorder={(newOrder: number[]) => {
          // 載入日誌已移除
          // 調試日誌已移除
          // 調試日誌已移除
          // 調試日誌已移除
          
          // 🔧 恢復原始設計：完全重新分配為 NO1, NO2, NO3... 連續排序
          const reorderedNumberKeys: Record<string, any> = {};
          
          // 按照 newOrder 的順序，重新分配為連續的 NO1, NO2, NO3...
          newOrder.forEach((keyNumber, index) => {
            const originalKey = keyNumber.toString();
            // 調試日誌已移除
            if (numberKeys[originalKey]) {
              // 重新分配為連續的數字鍵：1, 2, 3, 4, 5...
              const newKeyNo = index + 1;
              reorderedNumberKeys[newKeyNo.toString()] = {
                ...numberKeys[originalKey],
                keyNo: newKeyNo
              };
              // 調試日誌已移除
            } else {
              console.log(`❌ 數字鍵 ${keyNumber} 不存在於 numberKeys 中`);
            }
          });
          
          // 調試日誌已移除
          // 調試日誌已移除
          // 調試日誌已移除
          
          // 更新本地狀態
          setNumberKeys(reorderedNumberKeys);
          // 調試日誌已移除
          
          // 🔧 修復：同步更新 currentSubtitleKey 以匹配新的排序
          if (currentSubtitleKey !== null) {
            // 找到當前選中的數字鍵在新的排序中的位置
            const currentKeyIndex = newOrder.indexOf(currentSubtitleKey);
            if (currentKeyIndex !== -1) {
              // 新的數字鍵編號是 index + 1
              const newCurrentSubtitleKey = currentKeyIndex + 1;
              // 調試日誌已移除
              setCurrentSubtitleKey(newCurrentSubtitleKey);
              
              // 同步更新字幕內容
              const newKeyState = reorderedNumberKeys[newCurrentSubtitleKey.toString()];
              if (newKeyState && newKeyState.note) {
                setSubtitleContent(newKeyState.note);
                // 調試日誌已移除
              }
            } else {
              console.warn(`⚠️ 當前選中的數字鍵 ${currentSubtitleKey} 在重新排序後找不到，重置為 null`);
              setCurrentSubtitleKey(null);
              setSubtitleContent('');
            }
          }
          
          // 🔧 修復：保存到 Firestore，使用清理後的 videoId 作為路徑
          if (user && videoUrl) {
            try {
              // 提取乾淨的 videoId，移除特殊字符
              const cleanVideoId = videoUrl.replace(/[\/\\:*"<>|]/g, '_');
              const userDocRef = doc(db, 'users', user.uid);
              updateDoc(userDocRef, {
                [`numberKeys.${cleanVideoId}`]: reorderedNumberKeys
              }).then(() => {
                // 成功日誌已移除
              }).catch((error) => {
                console.error('❌ 保存重新排序到 Firestore 失敗:', error);
              });
              
              // 🔧 新增：同步更新 vbids 和 userNotes 中的 keyNo
              const syncVbidsReorder = async () => {
                try {
                  // 狀態追蹤日誌已移除
                  // 調試日誌已移除
                  
                  // 轉換重新排序後的 numberKeys 為 vbids
                  const vbids = convertNumberKeysToVBIDs(reorderedNumberKeys, getCurrentVideoMode() || 'YouTube', mButtonState);
                  
                  // 調試日誌已移除
                  // 調試日誌已移除
                  
                  if (vbids.length > 0) {
                    await saveVbidsToFirestore({
                      vbids,
                      user,
                      videoUrl,
                      currentMode: getCurrentVideoMode() || 'YouTube',
                      numberKeys: reorderedNumberKeys,
                      mButtonState,
                      checkpointCount: vbids.length,
                      fileName: originalFileName,  // 🔧 關鍵：傳遞原始檔名，確保生成正確的 videoId
                      fileSize: selectedFile?.size,
                      fileRelativePath: fileRelativePath,  // 🔧 關鍵：傳遞檔案路徑
                      documentId: currentFirestoreId  // 🔧 關鍵：明確指定文檔 ID，避免重複創建
                    });
                    // 調試日誌已移除
                  } else {
                    console.log('⚠️ 沒有活躍的重點需要同步到 vbids');
                  }
                  
                  // 🔧 修復：跳過 userNotes 同步，因為資料已完全遷移到 sharedVbids
                  // 避免 videoId 為 undefined 的錯誤（雲端檔案沒有 URL 中的 id 參數）
                  // 操作完成（日誌已移除）
                  
                } catch (error) {
                  console.error('❌ 同步更新 vbids 失敗:', error);
                }
              };
              
              // 異步執行同步更新
              syncVbidsReorder();
              
            } catch (error) {
              console.error('❌ 修復 Firestore 路徑失敗:', error);
            }
          }
        }}
        onEditNote={(keyNumber: number, note: string) => {
          // 調試日誌已移除
          
          // 更新本地狀態
          if (tabValue === 3) {
            setNumberKeys(prev => ({
              ...prev,
              [keyNumber]: {
                ...prev[keyNumber],
                note: note
              }
            }));
          } else {
            setTabNumberKeys(prev => ({
              ...prev,
              [tabValue]: {
                ...prev[tabValue],
                [keyNumber]: {
                  ...prev[tabValue]?.[keyNumber],
                  note: note
                }
              }
            }));
          }
          
          // 🔧 新增：如果編輯的是當前 pickup 的數字鍵，同步更新 subtitleContent
          if (currentSubtitleKey === keyNumber) {
            // 調試日誌已移除
            setSubtitleContent(note);
          }
          
          // 保存到 Firestore
          saveNoteToFirestore(keyNumber, note, tabValue);
        }}
        onDeleteNote={(keyNumber: number) => {
          console.log(`🗑️ NoteRecordsDialog 刪除 Note: 數字鍵 ${keyNumber}`);
          
          // 更新本地狀態 - 完全重置該數字鍵
          if (tabValue === 3) {
            setNumberKeys(prev => ({
              ...prev,
              [keyNumber]: {
                keyNo: keyNumber,
                isActive: false,
                isABPoint: false,
                isPickUpState: false,
                timestamp: null,
                timecode: '00:00:00 00',
                videoMode: null,
                frame: null,
                note: '',
                screenshot: '',
                flag: 'off',
                playMode: 'M'
              }
            }));
          } else {
            setTabNumberKeys(prev => ({
              ...prev,
              [tabValue]: {
                ...prev[tabValue],
                [keyNumber]: {
                  keyNo: keyNumber,
                  isActive: false,
                  isABPoint: false,
                  isPickUpState: false,
                  timestamp: null,
                  timecode: '00:00:00 00',
                  videoMode: null,
                  frame: null,
                  note: '',
                  screenshot: '',
                  flag: 'off',
                  playMode: 'M'
                }
              }
            }));
          }
          
          // 🔧 新增：如果刪除的是當前 pickup 的數字鍵，同步清空 subtitleContent
          if (currentSubtitleKey === keyNumber) {
            // 調試日誌已移除
            setSubtitleContent('');
          }
          
          // 保存到 Firestore - 完全重置該數字鍵
          saveNoteToFirestore(keyNumber, '', tabValue);
          
          // 🔧 新增：同步刪除 vbids 中的對應重點
          const syncDeleteToFirestore = async () => {
            try {
              // 調試日誌已移除
              
              // 獲取更新後的 numberKeys 狀態
              const getUpdatedNumberKeys = () => {
                if (tabValue === 3) {
                  const updatedKeys = { ...numberKeys };
                  updatedKeys[keyNumber] = {
                    keyNo: keyNumber,
                    isActive: false,
                    isABPoint: false,
                    isPickUpState: false,
                    timestamp: null,
                    timecode: '00:00:00 00',
                    videoMode: null,
                    frame: null,
                    note: '',
                    screenshot: '',
                    flag: 'off',
                    playMode: 'M'
                  };
                  return updatedKeys;
                } else {
                  const updatedKeys = { ...(tabNumberKeys[tabValue] || {}) };
                  updatedKeys[keyNumber] = {
                    keyNo: keyNumber,
                    isActive: false,
                    isABPoint: false,
                    isPickUpState: false,
                    timestamp: null,
                    timecode: '00:00:00 00',
                    videoMode: null,
                    frame: null,
                    note: '',
                    screenshot: '',
                    flag: 'off',
                    playMode: 'M'
                  };
                  return updatedKeys;
                }
              };
              
              const updatedNumberKeys = getUpdatedNumberKeys();
              const vbids = convertNumberKeysToVBIDs(updatedNumberKeys, getCurrentVideoMode() || 'YouTube', mButtonState);
              
              if (vbids.length >= 0) {
                await saveVbidsToFirestore({
                  vbids,
                  user,
                  videoUrl,
                  currentMode: getCurrentVideoMode() || 'YouTube',
                  numberKeys: updatedNumberKeys,
                  mButtonState,
                  checkpointCount: vbids.length,
                  fileName: originalFileName,
                  fileSize: selectedFile?.size
                });
                // 調試日誌已移除
              }
            } catch (error) {
              console.error('❌ NoteRecordsDialog 同步刪除操作到 Firestore 失敗:', error);
            }
          };
          
          // 異步執行同步刪除
          syncDeleteToFirestore();
        }}
        onTimecodeClick={(keyNumber: number, timestamp: number) => {
          // 調試日誌已移除
          
          // 1. 設置該數字鍵為 pickup 狀態（變紅色）
          // 調試日誌已移除
          
          if (tabValue === 3) {
            // YouTube 模式
            setNumberKeys(prev => {
              const updated = { ...prev };
              // 先清除所有 pickup 狀態
              Object.keys(updated).forEach(key => {
                if (updated[key]) {
                  updated[key] = { ...updated[key], isPickUpState: false };
                }
              });
              // 設置指定數字鍵為 pickup 狀態
              if (updated[keyNumber]) {
                updated[keyNumber] = { ...updated[keyNumber], isPickUpState: true };
              }
              return updated;
            });
          } else {
            // 其他模式
            setTabNumberKeys(prev => {
              const updated = { ...prev };
              if (updated[tabValue]) {
                // 先清除所有 pickup 狀態
                Object.keys(updated[tabValue]).forEach(key => {
                  if (updated[tabValue][key]) {
                    updated[tabValue][key] = { ...updated[tabValue][key], isPickUpState: false };
                  }
                });
                // 設置指定數字鍵為 pickup 狀態
                if (updated[tabValue][keyNumber]) {
                  updated[tabValue][keyNumber] = { ...updated[tabValue][keyNumber], isPickUpState: true };
                }
              }
              return updated;
            });
          }
          
          // 2. 同步設置 currentSubtitleKey，讓控制面板上半部顯示正確的數字鍵
          // 設置狀態（日誌已移除）
          setCurrentSubtitleKey(keyNumber);
          
          // 3. 載入該數字鍵的 Note 內容到字幕白板
          // 🔧 修復：優先使用 numberKeys（包含從 NotesPage 傳來的資料），然後才使用 tabNumberKeys
          const currentTabNumberKeys = tabValue === 3 ? numberKeys : tabNumberKeys[tabValue];
          let keyState = currentTabNumberKeys[keyNumber];
          
          // 🔧 修復：如果當前 tab 的資料中沒有 note，嘗試從 numberKeys 中獲取（適用於從 NotesPage 跳轉的情況）
          if ((!keyState || !keyState.note) && numberKeys[keyNumber] && numberKeys[keyNumber].note) {
            // 調試日誌已移除
            keyState = numberKeys[keyNumber];
          }
          
          if (keyState && keyState.note) {
            // 調試日誌已移除
            setSubtitleContent(keyState.note);
            
            // 🔧 新增：檢查內容是否來自 SRT（這裡假設現有內容是手動輸入的）
            setSubtitleContentWithSource({
              text: keyState.note,
              isFromSRT: false // 現有內容預設為手動輸入
            });
          } else {
            // 調試日誌已移除
            setSubtitleContent('');
            setSubtitleContentWithSource({ text: '', isFromSRT: false });
          }
          
          // 4. 跳轉到指定時間點並自動播放
          if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
            // 調試日誌已移除
            const timestampValue = convertTimestampToNumber(timestamp);
            playerRef.current.seekTo(timestampValue);
            
            // 🔧 優化：設置播放狀態為 true，確保狀態一致
            setIsPlaying(true);
            
            // 🔧 優化：延遲檢查播放狀態，避免與 YouTube 內建行為衝突
            setTimeout(() => {
              // 5. 檢查播放狀態，只有在暫停狀態下才強制播放
              if (playerRef.current && playerRef.current.getInternalPlayer && typeof playerRef.current.getInternalPlayer === 'function') {
                const internalPlayer = playerRef.current.getInternalPlayer();
                if (internalPlayer && typeof internalPlayer.play === 'function') {
                  // 檢查播放狀態，避免重複播放
                  if (internalPlayer.paused !== undefined && internalPlayer.paused) {
                    console.log(`▶️ 檢測到播放器暫停，強制開始播放`);
                    internalPlayer.play().catch((error: any) => {
                      console.log('⚠️ 自動播放失敗，可能需要用戶手動點擊:', error);
                    });
                  } else {
                    console.log(`▶️ 播放器已在播放中，跳過自動播放`);
                  }
                }
              }
            }, 200); // 進一步減少延遲到 200ms，提升響應速度
          } else if (googleDriveRef.current && typeof googleDriveRef.current.seekTo === 'function') {
            // 調試日誌已移除
            const timestampValue = convertTimestampToNumber(timestamp);
            googleDriveRef.current.seekTo(timestampValue);
            
            // 🔧 優化：延遲檢查播放狀態，避免與播放器內建行為衝突
            setTimeout(() => {
              // 5. 檢查播放狀態，只有在暫停狀態下才強制播放
              if (googleDriveRef.current && googleDriveRef.current.play && typeof googleDriveRef.current.play === 'function') {
                // 對於 Google Drive 播放器，我們假設跳轉後需要重新播放
                // 調試日誌已移除
                googleDriveRef.current.play();
              }
            }, 200); // 延遲 200ms 確保跳轉完成並檢查播放狀態
          } else {
            console.warn('⚠️ 無法找到可用的播放器引用進行跳轉');
            // 調試日誌已移除
          }
        }}
        onCleanupData={() => {}}
        onSaveReorderedData={() => {}}
        onVersionManagement={handleVersionManagement}
        isUserLoggedIn={!!user}
        videoUrl={videoUrl}
        user={user}
      />

      {/* 版本管理對話框 */}
      <VersionManagementDialog
        open={dialogs.versionManagement}
        onClose={() => closeDialog('versionManagement')}
        currentVersion={currentVersion}
        videoId={videoUrl || ''}
        userId={user?.uid || ''}
        onVersionAction={handleVersionAction}
        versionCreatedAt={versionCreatedAt}
      />

      {/* 🔧 暫時註解：協作模式對話框，目前使用「觀點」功能來控管版本 */}
      {/*
      {originalDocumentInfo && (
        <CollaborationModeDialog
          open={dialogs.collaboration}
          onClose={() => closeDialog('collaboration')}
          onAction={handleCollaborationAction}
          originalUserId={originalDocumentInfo.userId}
          originalVersion={originalDocumentInfo.version}
          currentUserId={user?.uid || ''}
          videoTitle={videoUrl || '影片'}
          hasExistingVersion={collaborationState.hasExistingVersion}
          isCollaborativeEnabled={collaborationState.isCollaborativeEnabled}
          isLoading={collaborationState.isLoading}
        />
      )}
      */}

      {/* 知識管理對話框 */}
      <EnhancedKnowledgeManagementDialog
        open={dialogs.km}
        onClose={handleCloseKmDialog}
        isLoggedIn={!!user}
        onRequestLogin={() => openDialog('auth')}
        videoUrl={videoUrl}
        videoTitle={videoTitle}
        videoId={extractYouTubeId(videoUrl) || ''}
        numberKeys={tabValue === 3 ? numberKeys : tabNumberKeys[tabValue]}
        user={user}
        zones={videoZones}
        tags={videoTags}
        groupIds={videoGroupIds}
        onZoneChange={handleZoneChange}
        onTagChange={handleTagChange}
        currentDocId={currentViewpointDocId}
        onViewpointChange={handleViewpointChange}
        onOpenViewpointManager={handleOpenViewpointManager}
        onGroupChange={handleGroupChange}
          />
        </Suspense>
      )}

      {/* 🆕 觀點管理對話框 */}
      {user?.uid && videoUrl && viewpointManagerOpen && (
        <Suspense fallback={<DialogFallback />}>
          <ViewpointManager
          open={viewpointManagerOpen}
          videoId={extractYouTubeId(videoUrl) || ''}
          userId={user.uid}
          videoUrl={videoUrl}
          currentDocId={currentViewpointDocId}
          onClose={handleCloseViewpointManager}
          onViewpointChange={handleViewpointChange}
          onViewpointCreated={handleViewpointCreated}
          />
        </Suspense>
      )}

      {/* 🔧 新增：SRT 字幕顯示對話框 */}
      {dialogs.srt && (
        <Suspense fallback={<DialogFallback />}>
          <SRTDisplayDialog
            open={dialogs.srt}
            onClose={() => closeDialog('srt')}
        srtContent={srtContent}
        videoId={currentVideoId}
        videoTitle={videoTitle} // 🔧 新增：傳入影片標題
        existingNotes={numberKeys} // 🔧 新增：傳入現有的 NO 數據用於映射檢查
        onDownload={handleDownloadCaptions}
        // 調試日誌已移除
        onDeleteNote={handleDeleteNoteFromSRT} // 🔧 新增：傳入刪除NO重點的處理函數
        onSRTCorrected={handleSRTCorrected} // 🆕 新增：傳入 SRT 校正後的處理函數
        onScrollToNoTag={(keyNo: number) => {
          // 🔧 新增：滾動NO標籤到正中央
          // 滾動日誌已移除
          
          // 🔧 通過設置 pickup 狀態來觸發滾動
          setNumberKeys(prev => ({
            ...prev,
            [keyNo]: {
              ...prev[keyNo],
              isPickUpState: true
            }
          }));
          
          // 🔧 延遲取消 pickup 狀態，避免干擾其他邏輯
          setTimeout(() => {
            setNumberKeys(prev => ({
              ...prev,
              [keyNo]: {
                ...prev[keyNo],
                isPickUpState: false
              }
            }));
          }, 500); // 保持0.5秒的pickup狀態
        }}
        onAddNote={(timecode: string, text: string) => {
          // 🔧 修復：處理從閱讀模式點擊文字新增筆記
          console.log('從閱讀模式新增筆記:', timecode, text);
          
          // 轉換時間碼格式（從 00:00:00,000 到 00:00:00）
          const formattedTimecode = timecode.replace(/,\d{3}$/, '');
          
          // 🔧 修復：使用與 Check 按鈕相同的邏輯，找到第一個空白 NO
          const isYouTubeTab = tabValue === 3;
          const currentNumberKeys = isYouTubeTab ? numberKeys : tabNumberKeys[tabValue];
          
          // 找到所有已使用的鍵號
          const allPossibleKeys = Array.from({ length: maxNumberKeys }, (_, i) => i + 1);
          const usedKeys = Object.entries(currentNumberKeys)
            .filter(([key, state]) => (state as any).isActive && parseInt(key) > 0)
            .map(([key]) => parseInt(key))
            .filter(key => !isNaN(key))
            .sort((a, b) => a - b);
          
          // 找到第一個未使用的鍵號（空白 NO）
          const emptyKeys = allPossibleKeys.filter(key => !usedKeys.includes(key));
          
          let newKey: number;
          if (emptyKeys.length > 0) {
            newKey = emptyKeys[0]; // 選擇最小的空白鍵
            // 調試日誌已移除
          } else {
            // 如果沒有空白鍵，使用下一個可用的鍵號
            if (usedKeys.length > 0) {
              const maxUsedKey = Math.max(...usedKeys);
              newKey = maxUsedKey + 1;
              console.log('⚠️ 沒有空白 NO，使用下一個可用編號:', newKey);
              setMaxNumberKeys(newKey); // 只有在超過最大值時才更新
            } else {
              newKey = 1;
              console.log('⚠️ 沒有已使用的 NO，從 1 開始');
            }
          }
          
          // 🔧 修復：將 SRT 時間碼轉換為 timestamp（秒數）
          const timecodeToSeconds = (timecode: string): number => {
            const match = timecode.match(/(\d{2}):(\d{2}):(\d{2}),(\d{3})/);
            if (!match) return 0;
            
            const [, hours, minutes, seconds, milliseconds] = match;
            return parseInt(hours) * 3600 + parseInt(minutes) * 60 + parseInt(seconds) + parseInt(milliseconds) / 1000;
          };
          
          const timestamp = timecodeToSeconds(timecode);
          // 調試日誌已移除
          
          // 🔧 修復：正確設置時間碼和 timestamp 到 numberKeys 和 tabNumberKeys
          const newNumberKeys = { ...numberKeys };
          newNumberKeys[newKey] = { 
            keyNo: newKey,
            isActive: true,
            isABPoint: false,
            isPickUpState: false,
            timestamp: timestamp, // 🔧 修復：使用正確的 timestamp
            videoMode: 'YouTube',
            frame: null,
            timecode: formattedTimecode, // 🔧 新增：設置時間碼
            note: text, // 🔧 新增：直接設置筆記內容
            hasNote: true // 🔧 新增：標記有筆記
          };
          setNumberKeys(newNumberKeys);
          
          // 更新當前 tab 的 numberKeys
          setTabNumberKeys(prevTabKeys => {
            const updatedTabKeys = { ...prevTabKeys };
            // 更新當前 tabValue 的 numberKeys
            if (!updatedTabKeys[tabValue]) {
              updatedTabKeys[tabValue] = {};
            }
            updatedTabKeys[tabValue][newKey] = { 
              keyNo: newKey,
              isActive: true,
              isABPoint: false,
              isPickUpState: false,
              timestamp: timestamp, // 🔧 修復：使用正確的 timestamp
              videoMode: 'YouTube',
              frame: null,
              timecode: formattedTimecode, // 🔧 新增：設置時間碼
              note: text, // 🔧 新增：直接設置筆記內容
              hasNote: true // 🔧 新增：標記有筆記
            };
            return updatedTabKeys;
          });
          
          // 🔧 修復：只更新當前選中的 NO，不更新全局狀態避免污染其他 NO
          setCurrentSubtitleKey(newKey);
          // 注意：不更新 setSubtitleContent 和 setSubtitleContentWithSource，避免污染其他 NO
          
          // 調試日誌已移除
          
          // 🔧 修復：立即執行填充，不使用 setTimeout 避免異步競爭
          const fillNoteImmediately = async () => {
            try {
              // 創建 keyState 對象，包含必要的時間信息
              const keyState = {
                timecode: formattedTimecode,
                timestamp: timestamp,
                keyNo: newKey
              };
              
              // 使用現有的 fillNoteWithSRTContent 函數來填充多行文字
              if (srtContent) {
                await fillNoteWithSRTContent(srtContent, keyState);
                // 調試日誌已移除
                
                // 🔧 新增：滾動NO標籤到正中央
                setTimeout(() => {
                  setNumberKeys(prev => ({
                    ...prev,
                    [newKey]: {
                      ...prev[newKey],
                      isPickUpState: true
                    }
                  }));
                  
                  // 滾動日誌已移除
                  
                  // 0.5秒後取消 pickup 狀態
                  setTimeout(() => {
                    setNumberKeys(prev => ({
                      ...prev,
                      [newKey]: {
                        ...prev[newKey],
                        isPickUpState: false
                      }
                    }));
                  }, 500);
                }, 200); // 延遲200ms確保狀態已更新
              } else {
                // 如果沒有 SRT 內容，直接填充單行文字
                setNumberKeys(prevKeys => {
                  const updatedKeys = { ...prevKeys };
                  if (updatedKeys[newKey]) {
                    updatedKeys[newKey] = {
                      ...updatedKeys[newKey],
                      note: text
                    };
                  }
                  return updatedKeys;
                });
                
                setTabNumberKeys(prevTabKeys => {
                  const updatedTabKeys = { ...prevTabKeys };
                  if (updatedTabKeys[tabValue] && updatedTabKeys[tabValue][newKey]) {
                    updatedTabKeys[tabValue][newKey] = {
                      ...updatedTabKeys[tabValue][newKey],
                      note: text
                    };
                  }
                  return updatedTabKeys;
                });
                
                console.log(`⚠️ 沒有 SRT 內容，直接填充單行文字: ${text}`);
                
                // 🔧 新增：滾動NO標籤到正中央
                setTimeout(() => {
                  setNumberKeys(prev => ({
                    ...prev,
                    [newKey]: {
                      ...prev[newKey],
                      isPickUpState: true
                    }
                  }));
                  
                  // 滾動日誌已移除
                  
                  // 0.5秒後取消 pickup 狀態
                  setTimeout(() => {
                    setNumberKeys(prev => ({
                      ...prev,
                      [newKey]: {
                        ...prev[newKey],
                        isPickUpState: false
                      }
                    }));
                  }, 500);
                }, 200);
              }
            } catch (error) {
              console.error('❌ 填充多行文字失敗:', error);
              // 降級到單行文字填充
              setNumberKeys(prevKeys => {
                const updatedKeys = { ...prevKeys };
                if (updatedKeys[newKey]) {
                  updatedKeys[newKey] = {
                    ...updatedKeys[newKey],
                    note: text
                  };
                }
                return updatedKeys;
              });
            }
          };
          
          // 🔧 修復：立即執行填充，確保每次點擊都能立即看到結果
          fillNoteImmediately();
        }}
          />
        </Suspense>
      )}

      {/* 🔧 新增：語言選擇對話框 */}
      {dialogs.language && (
        <Suspense fallback={<DialogFallback />}>
          <LanguageSelectionDialog
            open={dialogs.language}
            onClose={() => closeDialog('language')}
            onConfirm={handleLanguageConfirm}
            defaultLanguage={selectedLanguage}
            // 🔧 關鍵修復：只有 YouTube 影片才傳遞 videoId，雲端檔案傳遞 undefined
            videoId={videoMode === 'YouTube' || tabValue === 3 ? extractYouTubeId(videoUrl) : undefined}
          />
        </Suspense>
      )}

      {/* 🔧 新增：SRT 生成進度對話框 */}
      {dialogs.srtGeneration && (
        <Suspense fallback={<DialogFallback />}>
          <SRTGenerationDialog
            open={dialogs.srtGeneration}
            onClose={() => closeDialog('srtGeneration')}
            status={srtGenerationStatus}
            progress={srtGenerationProgress}
            message={srtGenerationMessage}
            error={srtGenerationError}
          />
        </Suspense>
      )}
    </Box>
  );
}

// 🔧 在組件外部使用 useEffect 更新 ref（避免在組件內部造成初始化順序問題）
// 註：這個邏輯已整合到 handleSyncUpdate 內部，不需要額外的 useEffect

export default MainPage;
// Updated: 1761753050
